/**
 * Konfigurator natywny C+ - logika UI
 *
 * Architektura:
 *   - warianty Shopify: style (21 w 2026-09, lista w styles_config) × 20 zakresów m² × 2 strony
 *   - User wpisuje szer + wys (mm), JS liczy m² i round-up do 0.1, wybiera variant
 *   - Cena = variant.price (z Shopify, bez liczenia client-side)
 *   - Wszystko poza (styl, zakres, strona) idzie jako Line Item Property
 *
 * Założenia:
 *   - Section ma data-knf-* attributes z konfiguracją (styles, colors, rules, variants)
 *   - product.variants ma option1=Styl, option2="X.Y" (zakres m²), option3=Strona lakierowania
 */

(function () {
  "use strict";

  const root = document.querySelector("[data-knf]");
  if (!root) return;

  const cfg = {
    styles:     JSON.parse(root.dataset.styles  || '{"styles":[]}'),
    colors:     JSON.parse(root.dataset.colors  || "{}"),
    rules:      JSON.parse(root.dataset.rules   || '{"rules":{},"definitions":{},"common":{}}'),
    dimensions: JSON.parse(root.dataset.dimensions || '{"dimensions_per_system_type":{}}'),
    variants:   (() => {
      // Preferred: kompaktowy metafield (custom.all_variants) → omija Liquid limit 250 wariantów na produkt.
      // Format: { s: [styles], ss: [strona], v: [[id, sIdx, m2, stIdx, price], ...] }
      const compactRaw = root.dataset.variantsCompact || "";
      if (compactRaw) {
        try {
          const c = JSON.parse(compactRaw);
          if (c && c.v && c.s && c.ss) {
            return c.v.map(row => ({
              id: row[0],
              option1: c.s[row[1]],
              option2: row[2],
              option3: c.ss[row[3]],
              // price w metafieldzie to "64.90" (zł) - JS legacy oczekuje cents (Liquid p.variants daje int cents)
              price: Math.round(parseFloat(row[4]) * 100),
            }));
          }
        } catch (e) { /* fallthrough do legacy */ }
      }
      // Legacy fallback: data-variants z p.variants (limit 250)
      return JSON.parse(root.dataset.variants || "[]");
    })(),
  };
  // 10.2026 (TEMPO 6.2): indeks styl|zakres m²|strona -> wariant (kafelki i lista licza ceny setki razy). Pierwszy wygrywa, jak Array.find.
  const VARIANT_INDEX = new Map();
  cfg.variants.forEach(v => { const k = `${v.option1}|${v.option2}|${v.option3}`; if (!VARIANT_INDEX.has(k)) VARIANT_INDEX.set(k, v); });

  // Redesign 2026-09-24 (prosba Konrada): strona zawiasow dla frontow z frezowanym uchwytem. Opcja zyje w KODZIE motywu,
  // nie w metafieldzie conditional_rules, bo metafield jest wspolny z motywem live. Trafia do "Opcje" i _pl_summary
  // jak kazda opcja warunkowa. Pokazywana tylko przy drzwiach (syncHingeOption); uchwyt z boku wymusza przeciwna strone.
  const HINGE_KEY = "strona_zawiasow", HINGE_LABEL = "Strona zawiasów";
  if (cfg.rules && cfg.rules.rules && cfg.rules.definitions && !cfg.rules.definitions[HINGE_KEY]) {
    cfg.rules.definitions[HINGE_KEY] = {
      label: HINGE_LABEL, label_en: "Hinge side", label_de: "Scharnierseite", label_cs: "Strana pantů",
      values: ["Lewa", "Prawa"], values_en: ["Left", "Right"], values_de: ["Links", "Rechts"], values_cs: ["Vlevo", "Vpravo"],
      default: "",
      helptexts: { "Lewa": "Zawiasy po lewej stronie, patrząc na front od przodu.", "Prawa": "Zawiasy po prawej stronie, patrząc na front od przodu." },
      helptexts_en: { "Lewa": "Hinges on the left, looking at the front.", "Prawa": "Hinges on the right, looking at the front." },
      helptexts_de: { "Lewa": "Scharniere links, von vorne gesehen.", "Prawa": "Scharniere rechts, von vorne gesehen." },
      helptexts_cs: { "Lewa": "Panty vlevo při pohledu zepředu.", "Prawa": "Panty vpravo při pohledu zepředu." }
    };
    Object.values(cfg.rules.rules).forEach(r => {
      if (r && Array.isArray(r.options) && r.options.includes("lokalizacja_uchwytu") && !r.options.includes(HINGE_KEY)) r.options.push(HINGE_KEY);
    });
  }
  // 10.2026 (Z1): style z uchwytem = zawiasy WYMAGANE; pozostale przy HINGE_OPTIONAL dostaja opcje „Bez znaczenia” (styleOpts)
  const HINGE_REQ_STYLES = new Set(Object.keys((cfg.rules && cfg.rules.rules) || {}).filter(k => {
    const r = cfg.rules.rules[k]; return !!(r && Array.isArray(r.options) && r.options.includes("lokalizacja_uchwytu"));
  }));
  // 24.09.2026 (Konrad): Round i Round Oak - uchwyt takze w rogach. Wartosci dopisane do definicji lokalizacja_uchwytu
  // (tlumaczenia i opisy szukane sa po etykiecie), a lista dla stylu wybierana w renderConditional (perStyle / hideElse).
  const LOC_DEF = cfg.rules && cfg.rules.definitions && cfg.rules.definitions.lokalizacja_uchwytu;
  if (LOC_DEF && Array.isArray(LOC_DEF.values) && !LOC_DEF.values.includes("Lewa góra")) {
    const C = ["Lewa góra", "Prawa góra", "Lewa dół", "Prawa dół"];
    const add = (k, arr) => { if (Array.isArray(LOC_DEF[k])) LOC_DEF[k] = LOC_DEF[k].concat(arr); };
    add("values", C);
    add("values_en", ["Top left", "Top right", "Bottom left", "Bottom right"]);
    add("values_de", ["Oben links", "Oben rechts", "Unten links", "Unten rechts"]);
    add("values_cs", ["Vlevo nahoře", "Vpravo nahoře", "Vlevo dole", "Vpravo dole"]);
    const H = (k, o) => { if (LOC_DEF[k]) Object.assign(LOC_DEF[k], o); };
    H("helptexts", { "Lewa góra": "Uchwyt w lewym górnym rogu", "Prawa góra": "Uchwyt w prawym górnym rogu", "Lewa dół": "Uchwyt w lewym dolnym rogu", "Prawa dół": "Uchwyt w prawym dolnym rogu" });
    H("helptexts_en", { "Lewa góra": "Handle in the top left corner", "Prawa góra": "Handle in the top right corner", "Lewa dół": "Handle in the bottom left corner", "Prawa dół": "Handle in the bottom right corner" });
    H("helptexts_de", { "Lewa góra": "Griff oben links", "Prawa góra": "Griff oben rechts", "Lewa dół": "Griff unten links", "Prawa dół": "Griff unten rechts" });
    H("helptexts_cs", { "Lewa góra": "Úchyt vlevo nahoře", "Prawa góra": "Úchyt vpravo nahoře", "Lewa dół": "Úchyt vlevo dole", "Prawa dół": "Úchyt vpravo dole" });
    const RND = ["Góra", "Dół", "Lewa góra", "Prawa góra", "Lewa dół", "Prawa dół", "Niestandardowa (opisz w uwagach)"];
    LOC_DEF.perStyle = { "Round": RND, "Round Oak": RND };
    LOC_DEF.hideElse = C;
  }
  // 10.2026 (TEMPO 6.2): needsHinge(typ) = ten sam regex na parametrze; „Drzwi łamane B” laczy sie z A (bez zawiasow).
  // W zestawie „Narożnik” (karty A/B) uzywa ustawien zawiasow dodawacza, „Nadstawka” ma wlasna karte.
  function needsHinge(typ) { return /^(Drzwi|Korpus wysoki|Przedłużone|A\+B)/.test(typ || "") && !/^Drzwi łamane B/.test(typ || ""); }
  const isDoorTyp = () => { const typ = $("[data-knf-typ]")?.value || ""; return needsHinge(typ) || (SET_MODE && typ === "Narożnik"); };
  // "required" = styl z uchwytem i drzwi, "optional" = Z1 (Bez znaczenia / Lewe / Prawe), "none" = typ bez zawiasow
  function hingeMode(styl, typ) {
    if (!needsHinge(typ) && !(SET_MODE && typ === "Narożnik")) return "none";
    if (HINGE_REQ_STYLES.has(styl)) return "required";
    return HINGE_OPTIONAL ? "optional" : "none";
  }
  // Strona zawiasow jest WYMAGANA przy drzwiach (audyt 24.09: domyslna „Lewa” szla do produkcji bez decyzji klienta).
  // Pusta opcja na poczatku selecta = „nie wybrano”; uchwyt z boku wymusza strone przeciwna.
  const HINGE_MISS = { en: "Choose hinges", de: "Scharniere wählen", cs: "Vyberte panty", pl: "Wybierz zawiasy" };
  function hingeSel() { return root.querySelector(`select[data-knf-cond="${HINGE_LABEL}"]`); }
  function hingeMissing() {
    const sel = hingeSel();
    return !!(sel && !state.sample_mode && isDoorTyp() && !sel.value && hingeMode(state.styl, $("[data-knf-typ]")?.value || "") === "required");
  }
  // 24.09.2026 (Konrad): przy „Drzwi przedłużane” klient MUSI wskazac, z ktorej strony front jest dluzszy od korpusu.
  // Wybor w kroku typu (pod kafelkami), wartosc idzie przez state.conditional do „Opcje” i _pl_summary.
  const EXT_LABEL = "Przedłużenie", EXT_TYP = "Drzwi przedłużane";
  if (cfg.rules && cfg.rules.definitions && !cfg.rules.definitions.przedluzenie) {
    cfg.rules.definitions.przedluzenie = {
      label: EXT_LABEL, label_en: "Extension", label_de: "Verlängerung", label_cs: "Prodloužení",
      values: ["Góra", "Dół", "Bok"], values_en: ["Top", "Bottom", "Side"], values_de: ["Oben", "Unten", "Seite"], values_cs: ["Nahoře", "Dole", "Bok"],
      default: "",
      helptexts: { "Góra": "Front dłuższy od korpusu u góry.", "Dół": "Front dłuższy od korpusu u dołu.", "Bok": "Front szerszy od korpusu z boku." },
      helptexts_en: { "Góra": "The front is longer than the cabinet at the top.", "Dół": "The front is longer than the cabinet at the bottom.", "Bok": "The front is wider than the cabinet at the side." },
      helptexts_de: { "Góra": "Die Front ist oben länger als der Korpus.", "Dół": "Die Front ist unten länger als der Korpus.", "Bok": "Die Front ist seitlich breiter als der Korpus." },
      helptexts_cs: { "Góra": "Čelo je nahoře delší než korpus.", "Dół": "Čelo je dole delší než korpus.", "Bok": "Čelo je na boku širší než korpus." }
    };
  }
  const EXT_MISS = { en: "Choose extension side", de: "Verlängerung wählen", cs: "Vyberte prodloužení", pl: "Wybierz przedłużenie" };
  const EXT_ASK = { en: "Where is the front longer than the cabinet?", de: "Wo ist die Front länger als der Korpus?", cs: "Kde je čelo delší než korpus?", pl: "Z której strony front jest dłuższy od korpusu?" };
  // 24.09 (Konrad): to samo przy „Inny wymiar” w systemie IKEA dla elementu z nawiertami (szuflada, drzwi, cargo...) - od tego zaleza nawierty
  const EXT_ASK_CUSTOM = { en: "Your size: on which side is the front extended? The drilling layout depends on it.", de: "Ihr Maß: Auf welcher Seite ist die Front verlängert? Davon hängt die Lage der Bohrungen ab.", cs: "Váš rozměr: na které straně je čelo prodloužené? Podle toho rozmístíme vrtání.", pl: "Twój wymiar: z której strony front jest przedłużony? Od tego zależy rozmieszczenie nawiertów." };
  const NO_DRILL = /(bez nawiert|^Panel|Cokoł|Listwa|^Blenda)/i;   // 10.2026: + Blenda (TEMPO 4.1); „Panel dzielący” lapie ^Panel
  function extSel() { return root.querySelector(`select[data-knf-cond="${EXT_LABEL}"]`); }
  function extMissing() { const sel = extSel(); return !!(sel && !sel.disabled && !state.sample_mode && !sel.value); }
  function syncExtOption() {
    const dimsFs = $("[data-knf-dims-manual]")?.closest(".knf__fs");
    if (!dimsFs) return;
    let col = root.querySelector("[data-knf-ext]");
    if (!col) {
      const def = cfg.rules.definitions.przedluzenie, loc = locale !== "pl" && def[`values_${locale}`] ? def[`values_${locale}`] : def.values;
      col = document.createElement("div");
      col.className = "knf__col knf__ext"; col.setAttribute("data-knf-ext", ""); col.hidden = true;
      col.innerHTML = `<label class="knf__label">${escapeHtml(t(def, "label"))}</label>
        <select class="knf__select" data-knf-cond="${EXT_LABEL}" name="properties[${EXT_LABEL}]" disabled><option value=""></option>${def.values.map((v, i) => `<option value="${escapeAttr(v)}">${escapeHtml(loc[i] || v)}</option>`).join("")}</select>
        <small class="knf__cond-help" data-knf-ext-help></small>`;
      // 10.2026 zestaw: kolumna w „ustawieniach dodawanych elementow” nad kafelkami (TEMPO 8.2)
      const adder = SET_MODE ? $("[data-knf-adder-opts]") : null;
      (adder || dimsFs).appendChild(col);   // pod wymiarami - dotyczy i drzwi przedluzanych, i wymiaru spoza siatki IKEA
      const sel = col.querySelector("select");
      sel.addEventListener("change", () => { updateM2Display(); tryUpdate(); });
    }
    const typ = $("[data-knf-typ]")?.value || "", sys = $("[data-knf-system]")?.value || "";
    const customDim = SET_MODE ? (customOpen && !customPreset()) : $("[data-knf-dim-label]")?.value === "Niestandardowy";
    const custom = !!typ && !!sys && sys !== "NA WYMIAR" && customDim && !NO_DRILL.test(typ);
    const sel = col.querySelector("select"), on = (typ === EXT_TYP || custom) && !state.sample_mode;
    col.hidden = !on || SET_MODE; sel.disabled = !on;   // UX v2 pkt 4: w zestawie przedluzenie tylko w okienku pytania przy dodaniu
    if (on && sel.value) state.conditional[EXT_LABEL] = sel.value; else delete state.conditional[EXT_LABEL];
    const def = cfg.rules.definitions.przedluzenie, hts = (locale !== "pl" && def[`helptexts_${locale}`]) || def.helptexts;
    const h = col.querySelector("[data-knf-ext-help]");
    const ask = typ !== EXT_TYP ? EXT_ASK_CUSTOM : EXT_ASK;
    if (h) h.textContent = sel.value ? (hts[sel.value] || "") : (ask[locale] || ask.pl);
  }
  // 24.09.2026 (Konrad): fornir i sklejka - element wezszy niz 10 cm (cokol, listwa, niska szuflada) nie moze miec slojow
  // w poprzek. Sloje ida wzdluz dluzszego boku, druga opcja jest zablokowana; wybor klienta wraca, gdy wymiar przestaje wymuszac.
  const GRAIN_LABEL = "Kierunek usłojenia", GRAIN_MIN_MM = 100;
  const GRAIN_MSG = { en: "Under 10 cm the grain runs lengthwise.", de: "Unter 10 cm verläuft die Maserung in Längsrichtung.", cs: "Pod 10 cm vedou léta po délce.", pl: "Przy elemencie węższym niż 10 cm słoje biegną wzdłuż." };
  function syncGrainOption() {
    const sel = root.querySelector(`select[data-knf-cond="${GRAIN_LABEL}"]`);
    if (!sel) return;
    const st = (cfg.styles.styles || []).find(x => x.name === state.styl);
    const wood = !!(st && st.category && st.category !== "lakier");
    // 10.2026 zestaw: wymuszenie tylko dla wymiaru z formularza „Inny wymiar”; kafelki licza forcedOpts przy dodaniu
    const dimOn = !SET_MODE || customOpen;
    const w = dimOn ? Number(state.szer) || 0 : 0, h = dimOn ? Number(state.wys) || 0 : 0;
    const forced = wood && !state.sample_mode && w > 0 && h > 0 && w !== h && Math.min(w, h) < GRAIN_MIN_MM ? (h < w ? "Poziomy" : "Pionowy") : "";
    if (forced) { if (!sel.disabled) sel.dataset.user = sel.value; if (sel.value !== forced) sel.value = forced; }
    else if (sel.disabled && sel.dataset.user) sel.value = sel.dataset.user;
    sel.disabled = !!forced;
    if (sel.value) state.conditional[GRAIN_LABEL] = sel.value;
    const help = root.querySelector(`[data-knf-cond-help="${GRAIN_LABEL}"]`);
    if (help) {
      const def = cfg.rules.definitions.kierunek_slojow, hts = def ? ((locale !== "pl" && def[`helptexts_${locale}`]) || def.helptexts || {}) : {};
      help.textContent = forced ? (GRAIN_MSG[locale] || GRAIN_MSG.pl) : (hts[sel.value] || "");
    }
  }
  // 24.09.2026 (Konrad): sloje przechodzace (+20%) - produkcja musi wiedziec, z ktorym frontem front sie laczy.
  // Przy „Tak” opis w uwagach jest wymagany; podpowiedz w opisie opcji i w polu uwag.
  const SLOJE_LABEL = "Słoje przechodzące między frontami (+20%)";
  const SLOJE_ADD = { pl: " W uwagach napisz, z którym frontem ma się łączyć.", en: " In the notes, say which front it should match.", de: " Schreiben Sie in die Anmerkungen, mit welcher Front sie verlaufen soll.", cs: " V poznámce napište, na které čelo má navazovat." };
  const SLOJE_PH = { pl: "Np. słoje łączą się z szufladą 60×20 cm nad tym frontem", en: "E.g. the grain continues into the 60×20 cm drawer above this front", de: "Z. B. Maserung läuft in die Schublade 60×20 cm über dieser Front weiter", cs: "Např. léta navazují na zásuvku 60×20 cm nad tímto čelem" };
  const SLOJE_MISS = { pl: "Opisz w uwagach łączenie słojów", en: "Describe the grain match in notes", de: "Maserverlauf in Anmerkungen beschreiben", cs: "Popište návaznost let v poznámce" };
  {
    const d = cfg.rules && cfg.rules.definitions && cfg.rules.definitions.sloje_przechodzace;
    if (d) ["", "_en", "_de", "_cs"].forEach(k => { const h = d["helptexts" + k], add = SLOJE_ADD[k.slice(1) || "pl"]; if (h && h["Tak"] && !h["Tak"].includes(add.trim())) h["Tak"] = h["Tak"].replace(/[.\s]*$/, "") + "." + add; });
  }
  function notesEl() { return root.querySelector('textarea[name="properties[Uwagi klienta]"]'); }
  function slojeNotesMissing() {
    const n = notesEl();
    return !!(n && !state.sample_mode && (state.conditional || {})[SLOJE_LABEL] === "Tak" && !n.value.trim());
  }
  function syncSlojeNotes() {
    const n = notesEl(); if (!n) return;
    if (n.dataset.ph === undefined) { n.dataset.ph = n.placeholder; n.addEventListener("input", () => tryUpdate()); }
    const need = (state.conditional || {})[SLOJE_LABEL] === "Tak" && !state.sample_mode;
    n.placeholder = need ? (SLOJE_PH[locale] || SLOJE_PH.pl) : n.dataset.ph;
    // naglowek pola: przy slojach przechodzacych uwagi nie sa juz „opcjonalne”
    const tt = n.closest(".knf__fs")?.querySelector(".knf__step-title") || n.closest("details")?.querySelector("summary");   // zestaw: pole w details listy
    if (tt) { if (tt.dataset.t === undefined) tt.dataset.t = tt.textContent; tt.textContent = need ? tt.dataset.t.replace(/\s*\([^)]*\)\s*$/, "") : tt.dataset.t; }
  }
  function syncHingeOption() {
    const sel = hingeSel();
    if (!sel) return;
    if (!sel.querySelector('option[value=""]')) {
      sel.insertAdjacentHTML("afterbegin", '<option value=""></option>');
      sel.value = state.conditional[HINGE_LABEL] || "";
    }
    // Z1: w trybie opcjonalnym pusta opcja = „Bez znaczenia” i jest przyciskiem (data-seg, knf-rysunek.js enhanceSelects)
    const optMode = hingeMode(state.styl, $("[data-knf-typ]")?.value || "") === "optional";
    const empty = sel.querySelector('option[value=""]');
    const emptyTxt = optMode ? sx("T_HINGE_ANY") : "";
    if (empty.textContent !== emptyTxt) empty.textContent = emptyTxt;
    if (optMode) empty.setAttribute("data-seg", "1"); else empty.removeAttribute("data-seg");
    const hLbl = sel.closest(".knf__col")?.querySelector(".knf__label");
    if (hLbl) { if (hLbl.dataset.t === undefined) hLbl.dataset.t = hLbl.textContent; const lt = optMode ? sx("T_HINGE_OPT") : SET_MODE ? sx("T_HINGE_SHORT") : hLbl.dataset.t; if (hLbl.textContent !== lt) hLbl.textContent = lt; }
    const col = sel.closest(".knf__col"), door = isDoorTyp();
    // UX v2 pkt 4: styl bez uchwytu - zawiasy tylko w Opcjach wiersza listy („Bez znaczenia”), nad wymiarami nic
    const hideOpt = SET_MODE && optMode;
    if (hideOpt && sel.value && !sel.disabled) { sel.value = ""; sel.dataset.user = ""; }
    if (col) col.hidden = !door || hideOpt;
    if (!door) { if (!sel.disabled) sel.dataset.user = sel.value; delete state.conditional[HINGE_LABEL]; sel.disabled = true; return; }
    const loc = state.conditional["Lokalizacja uchwytu"];
    const forced = /^Prawa/.test(loc || "") ? "Lewa" : /^Lewa/.test(loc || "") ? "Prawa" : "";   // takze rogi Round (Prawa góra itd.)
    // pusty wybor tez zapamietujemy - inaczej po cofnieciu uchwytu z boku zostawala strona wymuszona (wer2 24.09)
    if (forced) { if (!sel.disabled) sel.dataset.user = sel.value; if (sel.value !== forced) sel.value = forced; }
    else if (sel.disabled) sel.value = sel.dataset.user || "";   // po zdjeciu blokady wraca wybor klienta albo „nie wybrano”
    sel.disabled = !!forced;
    if (sel.value) state.conditional[HINGE_LABEL] = sel.value; else delete state.conditional[HINGE_LABEL];
    const help = root.querySelector(`[data-knf-cond-help="${HINGE_LABEL}"]`);
    if (help) {
      const def = cfg.rules.definitions[HINGE_KEY], hts = (locale !== "pl" && def[`helptexts_${locale}`]) || def.helptexts;
      help.textContent = forced ? ({ en: "Set by the handle position.", de: "Ergibt sich aus der Griffposition.", cs: "Určeno polohou úchytu." }[locale] || "Wynika z położenia uchwytu.")
        : (sel.value ? (hts[sel.value] || "") : ({ en: "Looking at the front: which side are the hinges on?", de: "Von vorne gesehen: auf welcher Seite sind die Scharniere?", cs: "Při pohledu zepředu: na které straně jsou panty?" }[locale] || "Patrząc na front od przodu: po której stronie są zawiasy?"));
    }
  }


  // Największy realnie istniejący koszyk m² - czytany Z DANYCH, nie hardkodowany.
  // Powód (bug 2026-07-30): próg "powyżej 2,0 m² skontaktuj się z nami" był wpisany na sztywno,
  // a warianty kończą się na 1,95. Przedział (1,95 - 2,00] wpadał w dziurę: findVariant() nic nie
  // znajdował, ale strażnik ">2.0" nie reagował, więc CTA zostawało aktywne → /cart/add zwracał 422
  // i klient dostawał alert "Nie udało się dodać do koszyka" bez żadnego wyjścia.
  // Dzięki liczeniu z cfg.variants dołożenie koszyka 2,00 automatycznie przesunie próg - bez zmian w kodzie.
  // Pomijamy pseudo-koszyki panelu giętego ("Panel 80 cm" itd.), bo tam cena nie idzie per m².
  const MAX_M2_BUCKET = (() => {
    const nums = (cfg.variants || [])
      .map(v => parseFloat(v.option2))
      .filter(n => Number.isFinite(n));
    return nums.length ? Math.max(...nums) : 2.0;
  })();

  // 24.09.2026 (Konrad): Witryna na razie ukryta - wypada z listy u zrodla, wiec nie ma kafelka, deep linku ani przywracania.
  // Metafield styles_config jest wspolny z live, dlatego filtr w kodzie. Powrot: usunac nazwe z listy.
  // 29.09.2026 (Konrad): Monako tez ukryte.
  const HIDDEN_STYLES = ["Witryna", "Monako"];
  if (cfg.styles && Array.isArray(cfg.styles.styles)) cfg.styles.styles = cfg.styles.styles.filter(s => !HIDDEN_STYLES.includes(s.name));
  // 10.2026 zestaw frontow (TEMPO + decyzje A): flagi w jednym miejscu. SAMPLE_ONLY jest zdefiniowane nizej, stad odczyt z data-*.
  const SET_MODE = root.dataset.sampleOnly !== "1";   // = !SAMPLE_ONLY; false = stara sciezka jednego frontu (wylacznik awaryjny, Z9)
  const GROUPS_UI_ON = true;          // dividery UI: grupy typow + naglowki szerokosci (nasze usprawnienie, wylaczane jedna zmiana)
  const HINGE_OPTIONAL = true;        // Z1: zawiasy opcjonalne („Bez znaczenia”) w stylach bez uchwytu
  const DIVIDER_TYPE_ON = true;       // „Panel dzielący (divider)” - decyzja Konrada 03.10
  const BLEND_RATE = "styl";          // stawka blend i paneli dzielacych: "styl" = styl zestawu, "gladki" = wariant Gładki dla lakierow
  const NADSTAWKA_ON = true;          // korpusy PAX (sekcja C); kafelek znika, gdy brak data-korpus z Liquid
  const KORPUS_FULL_ON = false;       // korpus pelnej wysokosci - poza zakresem (C4), bez UI

  // Kolejność kafelków stylów (2026-07-30): najpierw lakierowane, potem forniry, na końcu sklejki.
  // W metafieldzie styles_config sklejki i forniry były wymieszane z lakierami (Hampton, Quadro,
  // Shaker i Witryna siedziały PO fornirach), przez co grupy materiałowe się przeplatały.
  // Sortowanie jest STABILNE: wewnątrz każdej grupy zostaje kolejność z metafieldu, więc ręczne
  // układanie stylów w Shopify Admin nadal działa - zmienia się tylko podział na grupy materiałowe.
  // Sortujemy cfg.styles raz, u źródła, żeby każde miejsce czytające tę listę było spójne.
  (() => {
    const RANGA = { lakier: 0, fornir: 1, sklejka: 2 };
    const lista = (cfg.styles && cfg.styles.styles) || [];
    if (!Array.isArray(lista) || lista.length < 2) return;
    lista.forEach((s, i) => { s._kolejnosc = i; });
    lista.sort((a, b) => {
      const ra = RANGA[a.category] === undefined ? 3 : RANGA[a.category];
      const rb = RANGA[b.category] === undefined ? 3 : RANGA[b.category];
      return ra - rb || a._kolejnosc - b._kolejnosc;
    });
    lista.forEach(s => { delete s._kolejnosc; });
  })();

  // -- I18n: czyta locale z [data-locale], wybiera *_en pole jeśli locale to "en"; fallback na PL
  const locale = (root.dataset.locale || "pl").toLowerCase().slice(0, 2);
  const L_M2 = {
    sloje:     { en: "+20% grain", cs: "+20% léta", de: "+20% Maserung", pl: "+20% słoje" },
    polysk:    { en: "+15% gloss", cs: "+15% lesk", de: "+15% Glanz", pl: "+15% połysk" },
    after:     { en: "after", cs: "po", de: "nach", pl: "po" },
    areaOver:  { en: "Area exceeds {max} m²", cs: "Plocha přesahuje {max} m²", de: "Die Fläche überschreitet {max} m²", pl: "Powierzchnia przekracza {max} m²" },
    contact:   { en: "Contact us", cs: "Kontaktujte nás", de: "Kontaktieren Sie uns", pl: "Skontaktuj się z nami" },
    dimRange:  { en: "Dimensions out of range", cs: "Rozměry mimo rozsah", de: "Maße außerhalb des Bereichs", pl: "Wymiary poza zakresem" },
    widthW:    { en: "width", cs: "šířka", de: "Breite", pl: "szer" },
    heightW:   { en: "height", cs: "výška", de: "Höhe", pl: "wys" },
    areaOver2: { en: "Area >{max} m² - contact us", cs: "Plocha >{max} m² - kontaktujte nás", de: "Fläche >{max} m² - kontaktieren Sie uns", pl: "Powierzchnia >{max} m² - skontaktuj się z nami" },
    grubosc:   { en: "+10% thickness", cs: "+10% tloušťka", de: "+10% Stärke", pl: "+10% grubość" },
    // Zapasowa treść, gdy dla poprawnej konfiguracji nie ma wariantu z innego powodu niż powierzchnia
    // (np. luka w siatce stylów/stron). Lepsze niż aktywne CTA kończące się błędem 422.
    unavailable: { en: "This configuration is unavailable - contact us", cs: "Tato konfigurace není dostupná - kontaktujte nás", de: "Diese Konfiguration ist nicht verfügbar - kontaktieren Sie uns", pl: "Ta konfiguracja jest niedostępna - skontaktuj się z nami" },
  };
  const LM = k => L_M2[k][locale] || L_M2[k].pl;
  // Wersja LM() z podstawionym realnym limitem m² (liczonym z wariantów, patrz MAX_M2_BUCKET).
  const LMax = k => LM(k).replace("{max}", MAX_M2_BUCKET.toFixed(2).replace(".", locale === "en" ? "." : ","));
  // Grubość frontu: select NA WYMIAR (front bez korpusu IKEA) i wybor per element w zestawie. 16 i 30 mm = +10% (Konrad 04.10.2026).
  const THICKNESS_I18N = {
    pl: { label: "Grubość frontu", short: "Grubość", o12: "12 mm", o16: "16 mm (+10%)", o18: "18 mm (standard)", o30: "30 mm (+10%)" },
    en: { label: "Front thickness", short: "Thickness", o12: "12 mm", o16: "16 mm (+10%)", o18: "18 mm (standard)", o30: "30 mm (+10%)" },
    de: { label: "Frontstärke", short: "Stärke", o12: "12 mm", o16: "16 mm (+10%)", o18: "18 mm (Standard)", o30: "30 mm (+10%)" },
    cs: { label: "Tloušťka čela", short: "Tloušťka", o12: "12 mm", o16: "16 mm (+10%)", o18: "18 mm (standard)", o30: "30 mm (+10%)" },
  };
  const t = (obj, key) => {
    if (!obj) return "";
    const localized = obj[`${key}_${locale}`];
    return localized || obj[key] || "";
  };
  // Placeholder pustej opcji w shadow-selectach (wizualnie ukryte, ale czytane przez screen reader).
  const SELECT_PH = { en: "- select -", cs: "- vyberte -", de: "- bitte wählen -", pl: "- wybierz -" };
  const selectPh = SELECT_PH[locale] || SELECT_PH.pl;
  // Placeholder pola opisu przy opcji "Niestandardowa" (np. lokalizacja uchwytu)
  const COND_CUSTOM_PH = {
    pl: "Opisz: gdzie ma być uchwyt, jaka odległość od krawędzi",
    en: "Describe: where the handle should be, distance from the edge",
    cs: "Popište: kde má být úchyt, jaká vzdálenost od hrany",
    de: "Beschreiben Sie: wo der Griff sitzen soll, Abstand zur Kante",
  };
  const condCustomPh = COND_CUSTOM_PH[locale] || COND_CUSTOM_PH.pl;

  // -- I18n helpers dla types_element + systems_helptexts (per-locale parallel arrays/dict z metafield)
  // Po fallback do PL gdy brak tłumaczenia
  // Typy spoza metafielda types_element (wstawiane programowo, np. tryb NA WYMIAR)
  const TYPE_SPECIAL = { "Front niestandardowy": { en: "Custom front", cs: "Atypické čelo", de: "Sonderfront" },
    // 10.2026 zestaw (TEMPO 4.2, Z6; nadstawki C3) - wartosci PL ida do koszyka i Order Printera dosłownie
    "Blenda": { en: "Filler", de: "Blende", cs: "Výplňová lišta" },
    "Panel dzielący (divider)": { en: "Divider panel", de: "Trennpaneel", cs: "Dělicí panel" },
    "Narożnik": { en: "Corner cabinet", de: "Eckschrank", cs: "Rohová skříňka" },
    "Drzwi łamane A (przy korpusie)": { en: "Bi-fold door A (hinged to cabinet)", de: "Falttür A (am Korpus)", cs: "Lomená dvířka A (u korpusu)" },
    "Drzwi łamane B (łączone z A)": { en: "Bi-fold door B (joined to A)", de: "Falttür B (mit A verbunden)", cs: "Lomená dvířka B (spojená s A)" },
    "Nadstawka": { en: "Top unit", de: "Aufsatz", cs: "Nástavec" },
    "Nadstawka (korpus)": { en: "Top unit (carcass)", de: "Aufsatzkorpus", cs: "Nástavec (korpus)" },
    "Drzwi nadstawki": { en: "Top unit door", de: "Aufsatztür", cs: "Dvířka nástavce" },
    "Panele górne": { en: "Top panels", de: "Oberpaneele", cs: "Horní panely" } };
  function tType(plName) {
    if (locale === 'pl') return plName;
    const sp = TYPE_SPECIAL[plName];
    if (sp && sp[locale]) return sp[locale];
    const pl = (cfg && cfg.rules && cfg.rules.common && cfg.rules.common.types_element) || [];
    const loc = (cfg && cfg.rules && cfg.rules.common && cfg.rules.common[`types_element_${locale}`]) || [];
    const idx = pl.indexOf(plName);
    return (idx >= 0 && loc[idx]) || plName;
  }
  // Para (label, wartość) conditionala przetłumaczona przez lookup w definitions
  // (values_{locale} to tablica równoległa do values - mapowanie po indeksie).
  function tCondPair(label, val) {
    if (locale === "pl") return { label, val };
    const defs = (cfg && cfg.rules && cfg.rules.definitions) || {};
    const key = Object.keys(defs).find(k => defs[k].label === label);
    const def = key ? defs[key] : null;
    if (!def) return { label, val };
    const idx = (def.values || []).indexOf(val);
    const valsLoc = def[`values_${locale}`] || [];
    return { label: t(def, "label") || label, val: (idx >= 0 && valsLoc[idx]) || val };
  }
  function tSysHelp(sysName) {
    const all = (cfg && cfg.rules && cfg.rules.common) || {};
    const localDict = all[`systems_helptexts_${locale}`];
    if (localDict && localDict[sysName]) return localDict[sysName];
    return (all.systems_helptexts || {})[sysName] || "";
  }

  // -- I18n: cart compound properties (KEYS + uniwersalne VALUES) per locale.
  // VALUES specyficzne dla styl/kolor/typ idą przez tStyle()/tColor()/tType() (lookup w cfg).
  const PROP_I18N = {
    pl: {
      K_FRONT: "Front", K_KOLOR: "Kolor", K_SYS_TYP: "System / Typ", K_OPCJE: "Opcje",
      K_FRONT_PROBKI: "Front próbki", K_WYK_PROBKI: "Wykończenie próbki",
      V_JEDNOSTR: "Jednostronnie", V_DWUSTR: "Dwustronnie",
      V_SLOJE_TAK: "Słoje przechodzące: Tak (+20%)",
      V_INNY: "Inny", V_POLMAT: "Półmat", V_POLYSK: "Połysk (+15%)",
      V_NAT_DREWNO: "Naturalne drewno", V_GRUBOSC: "Grubość",
    },
    en: {
      K_FRONT: "Front", K_KOLOR: "Color", K_SYS_TYP: "System / Type", K_OPCJE: "Options",
      K_FRONT_PROBKI: "Sample front", K_WYK_PROBKI: "Sample finish",
      V_JEDNOSTR: "Single-sided", V_DWUSTR: "Double-sided",
      V_SLOJE_TAK: "Matching grain: Yes (+20%)",
      V_INNY: "Custom", V_POLMAT: "Semi-matt", V_POLYSK: "Gloss (+15%)",
      V_NAT_DREWNO: "Natural wood", V_GRUBOSC: "Thickness",
    },
    cs: {
      K_FRONT: "Čelo", K_KOLOR: "Barva", K_SYS_TYP: "Systém / Typ", K_OPCJE: "Možnosti",
      K_FRONT_PROBKI: "Vzorek čela", K_WYK_PROBKI: "Povrchová úprava vzorku",
      V_JEDNOSTR: "Jednostranně", V_DWUSTR: "Oboustranně",
      V_SLOJE_TAK: "Procházející léta: Ano (+20%)",
      V_INNY: "Jiný", V_POLMAT: "Polomatný", V_POLYSK: "Lesk (+15%)",
      V_NAT_DREWNO: "Přírodní dřevo", V_GRUBOSC: "Tloušťka",
    },
    de: {
      K_FRONT: "Front", K_KOLOR: "Farbe", K_SYS_TYP: "System / Typ", K_OPCJE: "Optionen",
      K_FRONT_PROBKI: "Musterfront", K_WYK_PROBKI: "Musterausführung",
      V_JEDNOSTR: "Einseitig", V_DWUSTR: "Beidseitig",
      V_SLOJE_TAK: "Durchlaufende Maserung: Ja (+20%)",
      V_INNY: "Sonder", V_POLMAT: "Halbmatt", V_POLYSK: "Hochglanz (+15%)",
      V_NAT_DREWNO: "Naturholz", V_GRUBOSC: "Stärke",
    },
  };
  const L_PROP = PROP_I18N[locale] || PROP_I18N.pl;

  // Lakierowanie jedno/dwustronne - renderowane jako <select> w siatce "Opcje stylu" (renderStrona).
  // Etykieta i opisy siedzą tu, bo cały widget powstaje w JS; wartości selecta zostają po polsku,
  // bo findVariant() porównuje je z option3 wariantu Shopify.
  // Od 2026-08-11 BEZ procentu w etykiecie: dopłata to stała kwota za m² (ceny wariantów
  // przestawione tego samego dnia), więc jej udział różni się per styl (Gładki ~51%,
  // Witryna ~30%) i żaden jeden procent nie byłby prawdziwy dla wszystkich.
  // Klient i tak widzi przeliczoną cenę na żywo po zmianie opcji.
  // 2026-09-19 (audyt K16): przy "Dwustronnie" kwota dopłaty jak przy "Połysk (+15%)". Stała kwota
  // z kanonu fakty.json (double_sided): 440 zł/m², w EUR 126,24 €/m² (od 23.10.2026; katalog EUR = PLN × 0,2869).
  // Dotyczy tylko stylów lakierowanych - dla fornirów i sklejek select znika (renderStrona).
  const STRONA_I18N = {
    pl: { label: "Lakierowanie", one: "Jednostronnie", two: "Dwustronnie (+440 zł/m²)",
          helpOne: "Tył frontu zostaje biały", helpTwo: "Kolor z każdej strony, razem z tyłem" },
    en: { label: "Lacquering", one: "One-sided", two: "Two-sided (+€126.24/m²)",
          helpOne: "The back of the front stays white", helpTwo: "Colour on every side, the back included" },
    cs: { label: "Lakování", one: "Jednostranně", two: "Oboustranně",
          helpOne: "Zadní strana čela zůstává bílá", helpTwo: "Barva ze všech stran, i zezadu" },
    de: { label: "Lackierung", one: "Einseitig", two: "Beidseitig (+126,24 €/m²)",
          helpOne: "Die Rückseite der Front bleibt weiß", helpTwo: "Farbe auf allen Seiten, auch hinten" },
  };
  const L_STRONA = STRONA_I18N[locale] || STRONA_I18N.pl;

  // Grubość frontu wg stylu (styles_config.thickness_mm, od 2026-07-24). Brak pola → null → grubość
  // nigdzie się nie renderuje (defensywnie: stare dane / nowy styl bez uzupełnionego pola).
  // 24.09.2026 (Konrad, wymiary frezow Pro-Hand): Linea i Hampton maja 19 mm. Metafield styles_config (wspolny z live)
  // mowi 18 - poprawka w kodzie; przy wdrozeniu na live poprawic tez metafield (i konfigurator probek).
  const THICKNESS_FIX = { "Linea": 19, "Hampton": 19 };
  ((cfg.styles && cfg.styles.styles) || []).forEach(s => { if (THICKNESS_FIX[s.name]) s.thickness_mm = THICKNESS_FIX[s.name]; });
  window.knfStyleThickness = n => {   // dla rysunku (knf-rysunek.js): grubosc wybrana dla dodawanych elementow, inaczej grubosc stylu
    const g = SET_MODE && !state.sample_mode ? adderGrub($("[data-knf-system]")?.value || "", $("[data-knf-typ]")?.value || "") : null;
    return g ? parseInt(g, 10) : frontThicknessMm(n);
  };
  // 24.09.2026 (Konrad): forniry to flader - Dąb flader, Dąb Przydymiony flader bejcowany, Orzech amerykański flader.
  // Opis w kodzie, bo metafield styles_config jest wspolny z live; przy wdrozeniu przeniesc do metafieldu (tabela w liquid ma to samo).
  const HELP_FIX = {
    "Dąb": { helptext: "Fornir dębowy flader, bezbarwny lakier poliuretanowy", helptext_en: "Crown-cut oak veneer, clear polyurethane lacquer", helptext_de: "Eichenfurnier im Fladerschnitt, transparenter Polyurethanlack", helptext_cs: "Dubová dýha, fládrový řez" },
    "Dąb Przydymiony": { helptext: "Fornir dębowy flader, bejcowany na ciemny kolor", helptext_en: "Crown-cut oak veneer, stained dark", helptext_de: "Eichenfurnier im Fladerschnitt, dunkel gebeizt", helptext_cs: "Dubová dýha, fládrový řez, mořená do tmava" },
    "Orzech": { helptext: "Fornir z orzecha amerykańskiego, flader", helptext_en: "American walnut veneer, crown cut", helptext_de: "Amerikanisches Nussbaumfurnier im Fladerschnitt", helptext_cs: "Dýha amerického ořechu, fládrový řez" }
  };
  ((cfg.styles && cfg.styles.styles) || []).forEach(s => { if (HELP_FIX[s.name]) Object.assign(s, HELP_FIX[s.name]); });
  function styleThicknessMm(stylName) {
    const s = ((cfg && cfg.styles && cfg.styles.styles) || []).find(x => x.name === stylName);
    const th = s && Number(s.thickness_mm);
    return (Number.isFinite(th) && th > 0) ? th : null;
  }
  function frontThicknessMm(stylName) { return styleThicknessMm(stylName); }
  // 04.10.2026 (Konrad): grubosc per element w zestawie, tylko style lakierowane (MDF). Bez nawiertow (blendy, panele, cokoly, listwy,
  // panel dzielacy): 12 / 16 (+10%) / standard, panele i blendy tez 30 (+10%). Fronty z nawiertami tylko w Gładkim: 16 (+10%) / 18 - pod puszki min. 16 mm.
  // Drewno, sklejka, panel giety, karty narożnika i nadstawki: grubosc stylu. W pozycji null = standard stylu (18, Linea i Hampton 19).
  const THICK_FREE = /^(Blenda|Panel dzielący|Panele |Cokoł|Cokół|Listwa)/;
  const THICK_30 = /^(Blenda|Panel dzielący|Panele )/;   // 04.10.2026 (Konrad, decyzja 3): 30 mm tylko panele i blendy
  const GRUB_KEY = "__grub";   // edycja grubosci w „Opcjach” wiersza listy
  const thickCls = typ => THICK_FREE.test(typ || "") ? "nd" : "fr";
  const thickStd = styl => `${frontThicknessMm(styl) || 18} mm`;
  function thickOpts(styl, typ, sys) {
    const so = ((cfg.styles && cfg.styles.styles) || []).find(x => x.name === styl);
    if (!SET_MODE || state.sample_mode || !typ || sys === "NA WYMIAR" || !so || so.category !== "lakier" || isPanelTypName(typ) || typ === NAD_TYPE || typ === NAD_DOOR_TYP || typ === CORNER_TYP) return [];
    const std = thickStd(styl);
    if (sys === "BESTÅ" && typ === BESTA_SIDE) return ["12 mm", std];   // panele górne BESTÅ maja wymiary tylko pod boki 12 i 18 mm
    if (thickCls(typ) === "nd") return ["12 mm", "16 mm", std].concat(THICK_30.test(typ) ? ["30 mm"] : []);
    return styl === "Gładki" ? ["16 mm", std] : [];
  }
  function thickLbl(v, std) {
    const TI = THICKNESS_I18N[locale] || THICKNESS_I18N.pl;
    return v === std ? TI.o18.replace("18", std.slice(0, 2)) : v === "12 mm" ? TI.o12 : v === "16 mm" ? TI.o16 : TI.o30;
  }
  // Grubosc dodawanej pozycji: wybor dla klasy aktywnego typu (blenda z kompletu na karcie drzwi = standard); null = standard
  function adderGrub(sys, typ) {
    const act = $("[data-knf-typ]")?.value || "";
    if (thickCls(typ) !== thickCls(act)) return null;
    const v = state.grubAdd[thickCls(typ)];
    return v && v !== thickStd(state.styl) && thickOpts(state.styl, typ, sys).includes(v) ? v : null;
  }

  // ==========================================================================
  // ZESTAW FRONTOW (10.2026, TEMPO sekcja 7 + decyzje B/C): wszystkie nowe teksty w jednym slowniku.
  // Zwykle stringi (bez funkcji strzalkowych w literalach - minifier Shopify), {x} = podstawienie (sx()).
  // ==========================================================================
  const SET_I18N = {
    pl: {
      T_WIDTH: "Szerokość {n} cm", T_BESTA_SIDES: "Do boków {n} mm", HELP_BESTA_SIDE: "Bok zabudowy BESTÅ, równo z korpusem.", HELP_BESTA_TOP: "Płyta na zabudowę BESTÅ, nakłada się na panele boczne: wybierz grubość boków.",
      T_HINGE_OPT: "Zawiasy (opcjonalnie)", T_HINGE_ANY: "Bez znaczenia", T_HINGE_Q: "Zawiasy?", T_EXT_Q: "Przedłużenie?",
      T_TILE_ADD: "Dodaj 1 szt.: {name}, {price}", T_TILE_SUB: "Odejmij 1 szt.: {name}", T_LIVE_ADDED: "Dodano: {name}. W zestawie: {n} szt.", T_LIVE_SUB: "Odjęto: {name}. W zestawie: {n} szt.", T_MAX50: "Maks. 50 szt. w jednej pozycji",
      T_CUSTOM_LBL: "Twój wymiar", T_CUSTOM_MM: "wpisz w mm", T_CUSTOM_HINGE: "Otwory pod zawiasy dopasujemy do wymiaru.", T_CUSTOM_DRILL: "Nawierty dopasujemy do wymiaru.", T_CUSTOM_ADD: "Dodaj do listy · {price}", T_CUSTOM_IN: "W zestawie: {n} szt.", T_CUSTOM_STD: "To wymiar standardowy {cm} - dodamy go jak kafelek.",
      T_NARROW: "To blenda?", T_NARROW_BTN: "Przełącz na Blendę",
      T_SET: "Twój zestaw", T_SET_COUNT: "{rows} poz. · {qty} szt.", T_SET_EMPTY: "Zestaw jest pusty.", T_SET_CLEAR: "Wyczyść", T_SET_CLEARED: "Wyczyszczono zestaw ({n} szt.)", T_UNDO: "Cofnij", T_SET_MORE: "Pokaż wszystkie ({n})",
      T_ROW_OPTS: "Opcje", T_ROW_NOTE: "Uwaga", T_ROW_NOTE_PH: "Uwaga do tej pozycji (opcjonalnie)", T_ROW_DEL: "Usuń", T_ROW_REMOVED: "Usunięto: {name} ({n} szt.)", T_ROW_DEC: "Mniej: {name}", T_ROW_INC: "Więcej: {name}", T_ROW_QTY: "Ilość: {name}",
      T_ROW_UNIT: "{price} / szt.", T_ROW_UNAVAIL: "Niedostępne w stylu {styl}",
      T_SET_NOTES: "Uwagi do zestawu (opcjonalnie)", T_SET_PREFIX: "Do całego zestawu: ", T_TOTAL: "Razem · {qty} szt.",
      T_COMMON: "Styl i kolor zestawu", T_CHANGE: "Zmień", T_DONE: "Gotowe", T_COMMON_NOTE: "Zmiany obejmą cały zestaw ({qty} szt.)",
      T_RECALC: "Przeliczono zestaw dla stylu {styl}: z {old} na {new}.", T_RECALC_COLOR: "Kolor zmieniony dla całego zestawu ({qty} szt.).", T_RECALC_DROP: "Usunięto opcje niedostępne w tym stylu: {list}.", T_RECALC_DEF: "{label}: {value} (domyślnie) - zmienisz w Opcjach pozycji.", T_RECALC_GRUB: "Grubość {mm} niedostępna w stylu {styl} - wraca standard.", T_TO_FIX: "{n} poz. do uzupełnienia",
      T_RESTORED: "Przywrócono zestaw z {date}: {rows} poz., {qty} szt. Sprawdź ilości.", T_SHOW: "Pokaż",
      T_BAR_LABEL: "Zestaw · {qty} szt.", T_BAR_TAP: "Dotknij wymiaru", T_BAR_FIX: "Uzupełnij {n} poz.", T_BAR_CLICK: "Kliknij wymiar",
      T_ADDED_SET: "Dodano {rows} poz. ({qty} szt.)", T_ADD_MORE: "Dodaj kolejne elementy w tym stylu", T_VIEW_CART: "Zobacz koszyk", T_PARTIAL: "Nie dodano {n} z {m} poz.: {list}", T_ADD_MISSING: "Dodaj brakujące", T_ADD_CONTACT: "Tych pozycji nie da się teraz dodać - napisz do nas:",
      T_FORGET: "Nie zapomnij:", T_YES: "Tak", T_NO: "Nie",
      T_TYPE_CORNER_HELP: "Wybierz swoją szafkę narożną IKEA - dodamy wszystkie potrzebne fronty.",
      T_KIT_A: "Szafka narożna 128×68", T_KIT_A_DESC: "Drzwi 60×80 + 2 blendy 10×80 (w linii drzwi i prostopadła)", T_KIT_B: "Szafka narożna 88×88", T_KIT_B_DESC: "Drzwi łamane A + B, 2 × 25×80 (dwie osobne pozycje)",
      T_KIT_C: "Szafka wisząca narożna 68×68", T_KIT_C_DESC: "Zwykłe drzwi 40 cm - dotknij wysokości:", T_KIT_C_DESC_CLICK: "Zwykłe drzwi 40 cm - kliknij wysokość:",T_KIT_CODE: "IKEA {codes}", T_KIT_BLEND: "Szerokość blend", T_KIT_REC: "{n} cm (zalecane)",
      T_KIT_BLEND_HINT: "Blendy wysyłamy z zapasem, docinasz je na miejscu. W szafce 128×68 widać ok. 7,5 cm.", T_KIT_ADD: "Dodaj narożnik · {price}", T_KIT_ADDED: "Dodano: {n}", T_KIT_SUB: "Odejmij 1 narożnik",
      TAG_128: "Szafka narożna 128×68", TAG_88: "Szafka narożna 88×88", V_NO_DRILL: "Bez nawiertów", TAG_NAD: "Do nadstawki: {d}",
      TYPE_NAD_CHIP: "Nadstawka",
      HELP_BLENDA: "Listwa wypełniająca bez nawiertów, lakierowana z 4 stron, do docięcia na miejscu.", HELP_DIVIDER: "Gładki panel między korpusami, bez nawiertów. Jego grubość wydłuża ciąg szafek.", HELP_NAD: "Biały korpus na wymiar nad szafę PAX. Półka i zawiasy w cenie, fronty w Twoim stylu.",
      LIM_BLENDA: "Blenda: krótszy bok 3-15 cm (30-150 mm).", LIM_BLENDA_WIDE: "Szerszy element zamów jako panel.", LIM_BLENDA_WIDE_BTN: "Przełącz na Panele boczne", LIM_DIVIDER: "Panel dzielący: krótszy bok 3-5 cm (30-50 mm).",
      NAD_TITLE: "Nadstawka nad szafę PAX", NAD_DESC: "Biały korpus na wymiar. Półka i zawiasy w cenie, fronty w Twoim stylu: {styl}", NAD_W: "Szerokość szafy pod spodem", NAD_W_NOTE: "Szafa 50 lub 100 cm? Napisz do nas:",
      NAD_W_ASK: "Szerokość {n} cm: wycena indywidualna - napisz do nas", NAD_D: "Głębokość szafy", NAD_H: "Wysokość szafy", NAD_GAP: "Wolne miejsce do sufitu", NAD_GAP_HINT: "Zmierz w najniższym miejscu.",
      NAD_MM: "Wpisano mm? {v} mm = {cm} cm", NAD_FIX: "Popraw", NAD_OUT: "Korpus {k} · drzwi {d} · sufit ok. {c} cm - zgadza się?", NAD_OUT_NOD: "Korpus {k} · sufit ok. {c} cm - zgadza się?",
      NAD_SHELF: "Półka w środku", NAD_NO: "nie", NAD_YES: "tak", NAD_HINGES: "Zawiasy w komplecie: 2 na drzwi", NAD_QTY: "Ile takich nadstawek", NAD_QTY_DEC: "Mniej nadstawek", NAD_QTY_INC: "Więcej nadstawek", NAD_FRONTS: "Dodaj fronty do nadstawek", NAD_NOFRONTS: "Bez frontów - fronty zamówisz osobno",
      NAD_SUM_K: "{n}× korpus · {price}", NAD_SUM_D: "{n}× drzwi · {styl}", NAD_ADD: "Dodaj nadstawki do listy", NAD_LOW: "Szczelina poniżej 25 cm - doradzimy, napisz do nas.",
      NAD_HIGH: "Przygotujemy wycenę - napisz do nas.", NAD_GAP_REQ: "Wpisz wolne miejsce do sufitu (25-100 cm).", NAD_ROW: "Nadstawka PAX {d}", NAD_ROW_SUB: "biały korpus, półka, zawiasy w cenie", NAD_ROW_SUB0: "biały korpus, zawiasy w cenie", NAD_DEL_Q: "Usunąć też drzwi tej nadstawki?",
      // UX v2 (03.10.2026)
      T_HINT_LINE: "{sys} · {typ} - dotknij wymiaru, aby dodać sztukę", T_HINT_LINE_CLICK: "{sys} · {typ} - kliknij wymiar, aby dodać sztukę",
      T_BAR_SYS: "Wybierz system IKEA", T_TO_CART: "Do koszyka", T_SET_SENT: "Lista wysłana do koszyka.",
      T_TOAST: "Dodano: {name} · w zestawie {n}\u00a0szt.", T_POPULAR: "popularny", T_CORNER_TILE: "Narożne · 2\u00a0szt.", T_CORNER_NAME: "Blendy narożne", TAG_CORNER: "Do szafki narożnej",
      T_PAIR_TILE: "{cm} · para A+B", T_PAIR_NAME: "Drzwi łamane A+B", T_HINGE_SHORT: "Zawiasy", T_LOC_SHORT: "Uchwyt", TYPE_DIVIDER_CHIP: "Panel dzielący",
      NAD_BTN_GAP: "Wpisz wolne miejsce do sufitu",
      KP_SYSTYP: "PAX · Nadstawka (korpus)", KP_KOLOR: "Biały", KP_SHELF0: "Półka: brak", KP_SHELF1: "Półka: 1 szt.", KP_HINGES: "Zawiasy: {n} szt. w komplecie", KP_CEIL: "Do sufitu: {cm} cm", KP_NOFRONTS: "Fronty: bez frontów",
    },
    en: {
      T_WIDTH: "Width {n} cm", T_BESTA_SIDES: "For {n} mm sides", HELP_BESTA_SIDE: "Side panel for BESTÅ, flush with the frame.", HELP_BESTA_TOP: "Top panel for BESTÅ, overlapping the side panels: pick the side thickness.",
      T_HINGE_OPT: "Hinges (optional)", T_HINGE_ANY: "Either side", T_HINGE_Q: "Hinges?", T_EXT_Q: "Extension?",
      T_TILE_ADD: "Add 1 pc: {name}, {price}", T_TILE_SUB: "Remove 1 pc: {name}", T_LIVE_ADDED: "Added: {name}. In the set: {n} pcs", T_LIVE_SUB: "Removed: {name}. In the set: {n} pcs", T_MAX50: "Max. 50 pcs per item",
      T_CUSTOM_LBL: "Your size", T_CUSTOM_MM: "enter in mm", T_CUSTOM_HINGE: "Hinge holes placed to match your size.", T_CUSTOM_DRILL: "Drilling placed to match your size.", T_CUSTOM_ADD: "Add to list · {price}", T_CUSTOM_IN: "In the set: {n} pcs", T_CUSTOM_STD: "This is the standard size {cm} - it is added like the tile.",
      T_NARROW: "Is this a filler?", T_NARROW_BTN: "Switch to Filler",
      T_SET: "Your set", T_SET_COUNT: "{rows} items · {qty} pcs", T_SET_EMPTY: "Your set is empty.", T_SET_CLEAR: "Clear", T_SET_CLEARED: "Set cleared ({n} pcs)", T_UNDO: "Undo", T_SET_MORE: "Show all ({n})",
      T_ROW_OPTS: "Options", T_ROW_NOTE: "Note", T_ROW_NOTE_PH: "Note for this item (optional)", T_ROW_DEL: "Remove", T_ROW_REMOVED: "Removed: {name} ({n} pcs)", T_ROW_DEC: "Fewer: {name}", T_ROW_INC: "More: {name}", T_ROW_QTY: "Quantity: {name}",
      T_ROW_UNIT: "{price} / pc", T_ROW_UNAVAIL: "Not available in {styl}",
      T_SET_NOTES: "Notes for the whole set (optional)", T_TOTAL: "Total · {qty} pcs",
      T_COMMON: "Set style and colour", T_CHANGE: "Change", T_DONE: "Done", T_COMMON_NOTE: "Changes apply to the whole set ({qty} pcs)",
      T_RECALC: "Set recalculated for {styl}: from {old} to {new}.", T_RECALC_COLOR: "Colour changed for the whole set ({qty} pcs).", T_RECALC_DROP: "Removed options not available in this style: {list}.", T_RECALC_DEF: "{label}: {value} (default) - change it in the item options.", T_RECALC_GRUB: "{mm} thickness is not available in {styl} - back to standard.", T_TO_FIX: "{n} items need input",
      T_RESTORED: "Set restored from {date}: {rows} items, {qty} pcs. Check the quantities.", T_SHOW: "Show",
      T_BAR_LABEL: "Set · {qty} pcs", T_BAR_TAP: "Tap a size", T_BAR_FIX: "Complete {n} items", T_BAR_CLICK: "Click a size",
      T_ADDED_SET: "Added {rows} items ({qty} pcs)", T_ADD_MORE: "Add more items in this style", T_VIEW_CART: "View cart", T_PARTIAL: "{n} of {m} items were not added: {list}", T_ADD_MISSING: "Add the missing ones", T_ADD_CONTACT: "These items cannot be added right now - write to us:",
      T_FORGET: "Don't forget:", T_YES: "Yes", T_NO: "No",
      T_TYPE_CORNER_HELP: "Choose your IKEA corner cabinet - we add all the fronts it needs.",
      T_KIT_A: "Corner base cabinet 128×68", T_KIT_A_DESC: "Door 60×80 + 2 fillers 10×80 (in the door line and at a right angle)", T_KIT_B: "Corner base cabinet 88×88", T_KIT_B_DESC: "Bi-fold doors A + B, 2 × 25×80 (two separate items)",
      T_KIT_C: "Corner wall cabinet 68×68", T_KIT_C_DESC: "Standard 40 cm door - tap the height:", T_KIT_C_DESC_CLICK: "Standard 40 cm door - click the height:",T_KIT_CODE: "IKEA {codes}", T_KIT_BLEND: "Filler width", T_KIT_REC: "{n} cm (recommended)",
      T_KIT_BLEND_HINT: "Fillers come oversized, you trim them on site. About 7.5 cm is visible on a 128×68 cabinet.", T_KIT_ADD: "Add corner · {price}", T_KIT_ADDED: "Added: {n}", T_KIT_SUB: "Remove 1 corner",
      TAG_128: "Corner base cabinet 128×68", TAG_88: "Corner base cabinet 88×88", V_NO_DRILL: "No drilling", TAG_NAD: "For top unit: {d}",
      TYPE_NAD_CHIP: "Top unit",
      HELP_BLENDA: "Filler strip without drilling, finished on 4 edges, trimmed on site.", HELP_DIVIDER: "Plain panel between cabinets, no drilling. Its thickness adds to the run length.", HELP_NAD: "White made-to-measure carcass above a PAX wardrobe. Shelf and hinges included, fronts in your style.",
      LIM_BLENDA: "Filler: short side 3-15 cm (30-150 mm).", LIM_BLENDA_WIDE: "Order wider pieces as a panel.", LIM_BLENDA_WIDE_BTN: "Switch to Side panels", LIM_DIVIDER: "Divider panel: short side 3-5 cm (30-50 mm).",
      NAD_TITLE: "Top unit above a PAX wardrobe", NAD_DESC: "White carcass made to measure. Shelf and hinges included, fronts in your style: {styl}", NAD_W: "Width of the wardrobe below", NAD_W_NOTE: "Wardrobe 50 or 100 cm? Write to us:",
      NAD_W_ASK: "Width {n} cm: individual quote - write to us", NAD_D: "Wardrobe depth", NAD_H: "Wardrobe height", NAD_GAP: "Free space up to the ceiling", NAD_GAP_HINT: "Measure at the lowest point.",
      NAD_MM: "Entered mm? {v} mm = {cm} cm", NAD_FIX: "Fix", NAD_OUT: "Carcass {k} · doors {d} · ceiling approx. {c} cm - correct?", NAD_OUT_NOD: "Carcass {k} · ceiling approx. {c} cm - correct?",
      NAD_SHELF: "Shelf inside", NAD_NO: "no", NAD_YES: "yes", NAD_HINGES: "Hinges included: 2 per door", NAD_QTY: "How many of these top units", NAD_QTY_DEC: "Fewer top units", NAD_QTY_INC: "More top units", NAD_FRONTS: "Add fronts for the top units", NAD_NOFRONTS: "Without fronts - order the fronts separately",
      NAD_SUM_K: "{n}× carcass · {price}", NAD_SUM_D: "{n}× doors · {styl}", NAD_ADD: "Add top units to the list", NAD_LOW: "Gap under 25 cm - we will advise you, write to us.",
      NAD_HIGH: "We will prepare a quote - write to us.", NAD_GAP_REQ: "Enter the free space up to the ceiling (25-100 cm).", NAD_ROW: "PAX top unit {d}", NAD_ROW_SUB: "white carcass, shelf, hinges included", NAD_ROW_SUB0: "white carcass, hinges included", NAD_DEL_Q: "Remove the doors of this top unit too?",
      T_HINT_LINE: "{sys} · {typ} - tap a size to add a piece", T_HINT_LINE_CLICK: "{sys} · {typ} - click a size to add a piece",
      T_BAR_SYS: "Choose IKEA system", T_TO_CART: "Add to cart", T_SET_SENT: "The list has been sent to the cart.",
      T_TOAST: "Added: {name} · {n}\u00a0pcs in the set", T_POPULAR: "popular", T_CORNER_TILE: "Corner · 2\u00a0pcs", T_CORNER_NAME: "Corner fillers", TAG_CORNER: "For corner cabinet",
      T_PAIR_TILE: "{cm} · pair A+B", T_PAIR_NAME: "Bi-fold doors A+B", T_HINGE_SHORT: "Hinges", T_LOC_SHORT: "Handle", TYPE_DIVIDER_CHIP: "Divider panel",
      NAD_BTN_GAP: "Enter the free space up to the ceiling",
      KP_SYSTYP: "PAX · Top unit (carcass)", KP_KOLOR: "White", KP_SHELF0: "Shelf: none", KP_SHELF1: "Shelf: 1 pc", KP_HINGES: "Hinges: {n} pcs included", KP_CEIL: "To ceiling: {cm} cm", KP_NOFRONTS: "Fronts: none",
    },
    de: {
      T_WIDTH: "Breite {n} cm", T_BESTA_SIDES: "Für {n}-mm-Seiten", HELP_BESTA_SIDE: "Seitenpaneel für BESTÅ, bündig mit dem Korpus.", HELP_BESTA_TOP: "Oberpaneel für BESTÅ, liegt auf den Seitenpaneelen auf: Seitenstärke wählen.",
      T_HINGE_OPT: "Scharniere (optional)", T_HINGE_ANY: "Egal", T_HINGE_Q: "Scharniere?", T_EXT_Q: "Verlängerung?",
      T_TILE_ADD: "1 Stk. hinzufügen: {name}, {price}", T_TILE_SUB: "1 Stk. entfernen: {name}", T_LIVE_ADDED: "Hinzugefügt: {name}. Im Set: {n} Stk.", T_LIVE_SUB: "Entfernt: {name}. Im Set: {n} Stk.", T_MAX50: "Max. 50 Stk. pro Position",
      T_CUSTOM_LBL: "Ihr Maß", T_CUSTOM_MM: "in mm eingeben", T_CUSTOM_HINGE: "Scharnierbohrungen passend zu Ihrem Maß.", T_CUSTOM_DRILL: "Bohrungen passend zu Ihrem Maß.", T_CUSTOM_ADD: "Zur Liste hinzufügen · {price}", T_CUSTOM_IN: "Im Set: {n} Stk.", T_CUSTOM_STD: "Das ist das Standardmaß {cm} - es wird wie die Kachel hinzugefügt.",
      T_NARROW: "Ist das eine Blende?", T_NARROW_BTN: "Zu Blende wechseln",
      T_SET: "Ihr Set", T_SET_COUNT: "{rows} Pos. · {qty} Stk.", T_SET_EMPTY: "Ihr Set ist leer.", T_SET_CLEAR: "Leeren", T_SET_CLEARED: "Set geleert ({n} Stk.)", T_UNDO: "Rückgängig", T_SET_MORE: "Alle anzeigen ({n})",
      T_ROW_OPTS: "Optionen", T_ROW_NOTE: "Notiz", T_ROW_NOTE_PH: "Notiz zu dieser Position (optional)", T_ROW_DEL: "Entfernen", T_ROW_REMOVED: "Entfernt: {name} ({n} Stk.)", T_ROW_DEC: "Weniger: {name}", T_ROW_INC: "Mehr: {name}", T_ROW_QTY: "Menge: {name}",
      T_ROW_UNIT: "{price} / Stk.", T_ROW_UNAVAIL: "In {styl} nicht verfügbar",
      T_SET_NOTES: "Anmerkungen zum ganzen Set (optional)", T_TOTAL: "Summe · {qty} Stk.",
      T_COMMON: "Stil und Farbe des Sets", T_CHANGE: "Ändern", T_DONE: "Fertig", T_COMMON_NOTE: "Änderungen gelten für das ganze Set ({qty} Stk.)",
      T_RECALC: "Set für {styl} neu berechnet: von {old} auf {new}.", T_RECALC_COLOR: "Farbe für das ganze Set geändert ({qty} Stk.).", T_RECALC_DROP: "Optionen entfernt, die es in diesem Stil nicht gibt: {list}.", T_RECALC_DEF: "{label}: {value} (Standard) - in den Optionen der Position änderbar.", T_RECALC_GRUB: "Stärke {mm} in {styl} nicht verfügbar - zurück zum Standard.", T_TO_FIX: "{n} Pos. zu ergänzen",
      T_RESTORED: "Set vom {date} wiederhergestellt: {rows} Pos., {qty} Stk. Bitte Mengen prüfen.", T_SHOW: "Anzeigen",
      T_BAR_LABEL: "Set · {qty} Stk.", T_BAR_TAP: "Maß antippen", T_BAR_FIX: "{n} Pos. ergänzen", T_BAR_CLICK: "Maß anklicken",
      T_ADDED_SET: "{rows} Pos. ({qty} Stk.) hinzugefügt", T_ADD_MORE: "Weitere Elemente in diesem Stil", T_VIEW_CART: "Warenkorb ansehen", T_PARTIAL: "{n} von {m} Pos. nicht hinzugefügt: {list}", T_ADD_MISSING: "Fehlende hinzufügen", T_ADD_CONTACT: "Diese Positionen lassen sich gerade nicht hinzufügen - schreiben Sie uns:",
      T_FORGET: "Nicht vergessen:", T_YES: "Ja", T_NO: "Nein",
      T_TYPE_CORNER_HELP: "Wählen Sie Ihren IKEA-Eckschrank - wir fügen alle nötigen Fronten hinzu.",
      T_KIT_A: "Eckunterschrank 128×68", T_KIT_A_DESC: "Tür 60×80 + 2 Blenden 10×80 (in Türflucht und quer)", T_KIT_B: "Eckunterschrank 88×88", T_KIT_B_DESC: "Falttür A + B, 2 × 25×80 (zwei Positionen)",
      T_KIT_C: "Eckhängeschrank 68×68", T_KIT_C_DESC: "Standardtür 40 cm - Höhe antippen:", T_KIT_C_DESC_CLICK: "Standardtür 40 cm - Höhe anklicken:",T_KIT_CODE: "IKEA {codes}", T_KIT_BLEND: "Blendenbreite", T_KIT_REC: "{n} cm (empfohlen)",
      T_KIT_BLEND_HINT: "Blenden kommen mit Übermaß und werden vor Ort gekürzt. Beim 128×68-Schrank sind etwa 7,5 cm sichtbar.", T_KIT_ADD: "Ecke hinzufügen · {price}", T_KIT_ADDED: "Hinzugefügt: {n}", T_KIT_SUB: "1 Ecke entfernen",
      TAG_128: "Eckunterschrank 128×68", TAG_88: "Eckunterschrank 88×88", V_NO_DRILL: "Ohne Bohrungen", TAG_NAD: "Für Aufsatz: {d}",
      TYPE_NAD_CHIP: "Aufsatz",
      HELP_BLENDA: "Blende ohne Bohrungen, an 4 Kanten lackiert, wird vor Ort gekürzt.", HELP_DIVIDER: "Glattes Paneel zwischen den Korpussen, ohne Bohrungen. Seine Stärke verlängert die Schrankzeile.", HELP_NAD: "Weißer Korpus nach Maß über dem PAX-Schrank. Einlegeboden und Scharniere inklusive, Fronten in Ihrem Stil.",
      LIM_BLENDA: "Blende: kurze Seite 3-15 cm (30-150 mm).", LIM_BLENDA_WIDE: "Breitere Teile bitte als Paneel bestellen.", LIM_BLENDA_WIDE_BTN: "Zu Seitenpaneelen wechseln", LIM_DIVIDER: "Trennpaneel: kurze Seite 3-5 cm (30-50 mm).",
      NAD_TITLE: "Aufsatz über dem PAX-Schrank", NAD_DESC: "Weißer Korpus nach Maß. Einlegeboden und Scharniere inklusive, Fronten in Ihrem Stil: {styl}", NAD_W: "Breite des Schranks darunter", NAD_W_NOTE: "Schrank 50 oder 100 cm? Schreiben Sie uns:",
      NAD_W_ASK: "Breite {n} cm: individuelles Angebot - schreiben Sie uns", NAD_D: "Schranktiefe", NAD_H: "Schrankhöhe", NAD_GAP: "Freier Platz bis zur Decke", NAD_GAP_HINT: "An der niedrigsten Stelle messen.",
      NAD_MM: "mm eingegeben? {v} mm = {cm} cm", NAD_FIX: "Korrigieren", NAD_OUT: "Korpus {k} · Türen {d} · Decke ca. {c} cm - stimmt das?", NAD_OUT_NOD: "Korpus {k} · Decke ca. {c} cm - stimmt das?",
      NAD_SHELF: "Einlegeboden", NAD_NO: "nein", NAD_YES: "ja", NAD_HINGES: "Scharniere inklusive: 2 pro Tür", NAD_QTY: "Wie viele solche Aufsätze", NAD_QTY_DEC: "Weniger Aufsätze", NAD_QTY_INC: "Mehr Aufsätze", NAD_FRONTS: "Fronten für die Aufsätze hinzufügen", NAD_NOFRONTS: "Ohne Fronten - Fronten separat bestellen",
      NAD_SUM_K: "{n}× Korpus · {price}", NAD_SUM_D: "{n}× Türen · {styl}", NAD_ADD: "Aufsätze zur Liste hinzufügen", NAD_LOW: "Spalt unter 25 cm - wir beraten Sie, schreiben Sie uns.",
      NAD_HIGH: "Wir erstellen ein Angebot - schreiben Sie uns.", NAD_GAP_REQ: "Freien Platz bis zur Decke eingeben (25-100 cm).", NAD_ROW: "PAX-Aufsatz {d}", NAD_ROW_SUB: "weißer Korpus, Einlegeboden, Scharniere inklusive", NAD_ROW_SUB0: "weißer Korpus, Scharniere inklusive", NAD_DEL_Q: "Auch die Türen dieses Aufsatzes entfernen?",
      T_HINT_LINE: "{sys} · {typ} - Maß antippen, um ein Stück hinzuzufügen", T_HINT_LINE_CLICK: "{sys} · {typ} - Maß anklicken, um ein Stück hinzuzufügen",
      T_BAR_SYS: "IKEA-System wählen", T_TO_CART: "In den Warenkorb", T_SET_SENT: "Die Liste liegt im Warenkorb.",
      T_TOAST: "Hinzugefügt: {name} · im Set {n}\u00a0Stk.", T_POPULAR: "beliebt", T_CORNER_TILE: "Eck · 2\u00a0Stk.", T_CORNER_NAME: "Eckblenden", TAG_CORNER: "Für Eckschrank",
      T_PAIR_TILE: "{cm} · Paar A+B", T_PAIR_NAME: "Falttür A+B", T_HINGE_SHORT: "Scharniere", T_LOC_SHORT: "Griff", TYPE_DIVIDER_CHIP: "Trennpaneel",
      NAD_BTN_GAP: "Freien Platz bis zur Decke eingeben",
      KP_SYSTYP: "PAX · Aufsatzkorpus", KP_KOLOR: "Weiß", KP_SHELF0: "Einlegeboden: keiner", KP_SHELF1: "Einlegeboden: 1 Stk.", KP_HINGES: "Scharniere: {n} Stk. inklusive", KP_CEIL: "Bis zur Decke: {cm} cm", KP_NOFRONTS: "Fronten: ohne",
    },
    cs: {
      T_WIDTH: "Šířka {n} cm", T_BESTA_SIDES: "Pro boky {n} mm", HELP_BESTA_SIDE: "Boční panel BESTÅ, v rovině s korpusem.", HELP_BESTA_TOP: "Horní panel BESTÅ, překrývá boční panely: vyberte tloušťku boků.",
      T_HINGE_OPT: "Panty (volitelné)", T_HINGE_ANY: "Nezáleží", T_HINGE_Q: "Panty?", T_EXT_Q: "Prodloužení?",
      T_TILE_ADD: "Přidat 1 ks: {name}, {price}", T_TILE_SUB: "Odebrat 1 ks: {name}", T_LIVE_ADDED: "Přidáno: {name}. V sadě: {n} ks", T_LIVE_SUB: "Odebráno: {name}. V sadě: {n} ks", T_MAX50: "Max. 50 ks na položku",
      T_CUSTOM_LBL: "Váš rozměr", T_CUSTOM_MM: "zadejte v mm", T_CUSTOM_HINGE: "Otvory pro panty rozmístíme podle rozměru.", T_CUSTOM_DRILL: "Vrtání rozmístíme podle rozměru.", T_CUSTOM_ADD: "Přidat do seznamu · {price}", T_CUSTOM_IN: "V sadě: {n} ks", T_CUSTOM_STD: "To je standardní rozměr {cm} - přidáme ho jako dlaždici.",
      T_NARROW: "Je to výplňová lišta?", T_NARROW_BTN: "Přepnout na lištu",
      T_SET: "Vaše sada", T_SET_COUNT: "{rows} pol. · {qty} ks", T_SET_EMPTY: "Sada je prázdná.", T_SET_CLEAR: "Vymazat", T_SET_CLEARED: "Sada vymazána ({n} ks)", T_UNDO: "Zpět", T_SET_MORE: "Zobrazit vše ({n})",
      T_ROW_OPTS: "Možnosti", T_ROW_NOTE: "Poznámka", T_ROW_NOTE_PH: "Poznámka k této položce (volitelné)", T_ROW_DEL: "Odebrat", T_ROW_REMOVED: "Odebráno: {name} ({n} ks)", T_ROW_DEC: "Méně: {name}", T_ROW_INC: "Více: {name}", T_ROW_QTY: "Množství: {name}",
      T_ROW_UNIT: "{price} / ks", T_ROW_UNAVAIL: "Ve stylu {styl} nedostupné",
      T_SET_NOTES: "Poznámky k celé sadě (volitelné)", T_TOTAL: "Celkem · {qty} ks",
      T_COMMON: "Styl a barva sady", T_CHANGE: "Změnit", T_DONE: "Hotovo", T_COMMON_NOTE: "Změny platí pro celou sadu ({qty} ks)",
      T_RECALC: "Sada přepočítána pro styl {styl}: z {old} na {new}.", T_RECALC_COLOR: "Barva změněna pro celou sadu ({qty} ks).", T_RECALC_DROP: "Odebrány možnosti, které tento styl nemá: {list}.", T_RECALC_DEF: "{label}: {value} (výchozí) - změníte v možnostech položky.", T_RECALC_GRUB: "Tloušťka {mm} ve stylu {styl} není - vracíme standard.", T_TO_FIX: "{n} pol. k doplnění",
      T_RESTORED: "Sada obnovena z {date}: {rows} pol., {qty} ks. Zkontrolujte množství.", T_SHOW: "Zobrazit",
      T_BAR_LABEL: "Sada · {qty} ks", T_BAR_TAP: "Klepněte na rozměr", T_BAR_FIX: "Doplňte {n} pol.", T_BAR_CLICK: "Klikněte na rozměr",
      T_ADDED_SET: "Přidáno {rows} pol. ({qty} ks)", T_ADD_MORE: "Přidat další prvky v tomto stylu", T_VIEW_CART: "Zobrazit košík", T_PARTIAL: "Nepřidáno {n} z {m} pol.: {list}", T_ADD_MISSING: "Přidat chybějící", T_ADD_CONTACT: "Tyto položky teď nelze přidat - napište nám:",
      T_FORGET: "Nezapomeňte:", T_YES: "Ano", T_NO: "Ne",
      T_TYPE_CORNER_HELP: "Vyberte svou rohovou skříňku IKEA - přidáme všechna potřebná čela.",
      T_KIT_A: "Rohová spodní skříňka 128×68", T_KIT_A_DESC: "Dvířka 60×80 + 2 lišty 10×80 (v linii dvířek a příčná)", T_KIT_B: "Rohová spodní skříňka 88×88", T_KIT_B_DESC: "Lomená dvířka A + B, 2 × 25×80 (dvě položky)",
      T_KIT_C: "Rohová horní skříňka 68×68", T_KIT_C_DESC: "Běžná dvířka 40 cm - klepněte na výšku:", T_KIT_C_DESC_CLICK: "Běžná dvířka 40 cm - klikněte na výšku:",T_KIT_CODE: "IKEA {codes}", T_KIT_BLEND: "Šířka lišt", T_KIT_REC: "{n} cm (doporučeno)",
      T_KIT_BLEND_HINT: "Lišty posíláme s přesahem, zkrátíte je na místě. U skříňky 128×68 je vidět asi 7,5 cm.", T_KIT_ADD: "Přidat roh · {price}", T_KIT_ADDED: "Přidáno: {n}", T_KIT_SUB: "Odebrat 1 roh",
      TAG_128: "Rohová spodní skříňka 128×68", TAG_88: "Rohová spodní skříňka 88×88", V_NO_DRILL: "Bez vrtání", TAG_NAD: "K nástavci: {d}",
      TYPE_NAD_CHIP: "Nástavec",
      HELP_BLENDA: "Výplňová lišta bez vrtání, lakovaná ze 4 stran, zkracuje se na místě.", HELP_DIVIDER: "Hladký panel mezi korpusy, bez vrtání. Jeho tloušťka prodlužuje linku.", HELP_NAD: "Bílý korpus na míru nad skříň PAX. Police a panty v ceně, čela ve vašem stylu.",
      LIM_BLENDA: "Lišta: kratší strana 3-15 cm (30-150 mm).", LIM_BLENDA_WIDE: "Širší díl objednejte jako panel.", LIM_BLENDA_WIDE_BTN: "Přepnout na boční panely", LIM_DIVIDER: "Dělicí panel: kratší strana 3-5 cm (30-50 mm).",
      NAD_TITLE: "Nástavec nad skříň PAX", NAD_DESC: "Bílý korpus na míru. Police a panty v ceně, čela ve vašem stylu: {styl}", NAD_W: "Šířka skříně pod ním", NAD_W_NOTE: "Skříň 50 nebo 100 cm? Napište nám:",
      NAD_W_ASK: "Šířka {n} cm: individuální nabídka - napište nám", NAD_D: "Hloubka skříně", NAD_H: "Výška skříně", NAD_GAP: "Volné místo ke stropu", NAD_GAP_HINT: "Měřte v nejnižším místě.",
      NAD_MM: "Zadáno v mm? {v} mm = {cm} cm", NAD_FIX: "Opravit", NAD_OUT: "Korpus {k} · dvířka {d} · strop cca {c} cm - souhlasí?", NAD_OUT_NOD: "Korpus {k} · strop cca {c} cm - souhlasí?",
      NAD_SHELF: "Police uvnitř", NAD_NO: "ne", NAD_YES: "ano", NAD_HINGES: "Panty v ceně: 2 na dvířka", NAD_QTY: "Kolik takových nástavců", NAD_QTY_DEC: "Méně nástavců", NAD_QTY_INC: "Více nástavců", NAD_FRONTS: "Přidat čela k nástavcům", NAD_NOFRONTS: "Bez čel - čela objednáte zvlášť",
      NAD_SUM_K: "{n}× korpus · {price}", NAD_SUM_D: "{n}× dvířka · {styl}", NAD_ADD: "Přidat nástavce do seznamu", NAD_LOW: "Mezera pod 25 cm - poradíme, napište nám.",
      NAD_HIGH: "Připravíme nabídku - napište nám.", NAD_GAP_REQ: "Zadejte volné místo ke stropu (25-100 cm).", NAD_ROW: "Nástavec PAX {d}", NAD_ROW_SUB: "bílý korpus, police, panty v ceně", NAD_ROW_SUB0: "bílý korpus, panty v ceně", NAD_DEL_Q: "Odebrat i dvířka tohoto nástavce?",
      T_HINT_LINE: "{sys} · {typ} - klepnutím na rozměr přidáte kus", T_HINT_LINE_CLICK: "{sys} · {typ} - kliknutím na rozměr přidáte kus",
      T_BAR_SYS: "Vyberte systém IKEA", T_TO_CART: "Do košíku", T_SET_SENT: "Seznam je v košíku.",
      T_TOAST: "Přidáno: {name} · v sadě {n}\u00a0ks", T_POPULAR: "oblíbený", T_CORNER_TILE: "Rohové · 2\u00a0ks", T_CORNER_NAME: "Rohové lišty", TAG_CORNER: "Pro rohovou skříňku",
      T_PAIR_TILE: "{cm} · pár A+B", T_PAIR_NAME: "Lomená dvířka A+B", T_HINGE_SHORT: "Panty", T_LOC_SHORT: "Úchyt", TYPE_DIVIDER_CHIP: "Dělicí panel",
      NAD_BTN_GAP: "Zadejte volné místo ke stropu",
      KP_SYSTYP: "PAX · Nástavec (korpus)", KP_KOLOR: "Bílá", KP_SHELF0: "Police: bez", KP_SHELF1: "Police: 1 ks", KP_HINGES: "Panty: {n} ks v ceně", KP_CEIL: "Ke stropu: {cm} cm", KP_NOFRONTS: "Čela: bez čel",
    },
  };
  function sx(key, vars) {
    const d = SET_I18N[locale] || SET_I18N.pl;
    let s = d[key] !== undefined ? d[key] : (SET_I18N.pl[key] || "");
    if (vars) Object.keys(vars).forEach(k => { s = s.split(`{${k}}`).join(String(vars[k])); });
    return s;
  }

  // -- Nakladki danych zestawu (C1: metafield custom.dimensions jest wspolny z live, wiec typy wirtualne zyja w kodzie)
  const BLENDA = "Blenda", DIVIDER = "Panel dzielący (divider)", CORNER_TYP = "Narożnik", NAD_TYPE = "Nadstawka";
  const NAD_KORPUS_TYP = "Nadstawka (korpus)", NAD_DOOR_TYP = "Drzwi nadstawki";
  const KIT_TYP_A = "Drzwi łamane A (przy korpusie)", KIT_TYP_B = "Drzwi łamane B (łączone z A)";
  const TAG_128 = "Szafka narożna 128×68", TAG_88 = "Szafka narożna 88×88";
  const presetLabel = (wCm, hCm) => `${wCm}cm x ${hCm}cm`;   // format etykiet z metafieldu („60cm x 80cm”)
  const help4 = k => ({ _typ_helptext: SET_I18N.pl[k], _typ_helptext_en: SET_I18N.en[k], _typ_helptext_de: SET_I18N.de[k], _typ_helptext_cs: SET_I18N.cs[k] });
  // TEMPO 4.1: [szer cm, wys cm (etykieta), szer mm, wys mm, bestseller]
  const VIRTUAL_TYPES = {
    METOD: {
      [BLENDA]: { on: true, help: "HELP_BLENDA", p: [["10", "80", 100, 797, 1], ["15", "80", 150, 797], ["10", "100", 100, 997], ["15", "100", 150, 997], ["10", "220", 100, 2200], ["10", "240", 100, 2400], ["80", "10", 797, 100]] },
      [DIVIDER]: { on: DIVIDER_TYPE_ON, help: "HELP_DIVIDER", p: [["5", "80", 50, 797, 1], ["5", "100", 50, 997]] },
    },
    FAKTUM: {
      [BLENDA]: { on: true, help: "HELP_BLENDA", p: [["10", "70", 100, 695, 1], ["10", "92", 100, 918], ["60", "13", 596, 126]] },
      [DIVIDER]: { on: DIVIDER_TYPE_ON, help: "HELP_DIVIDER", p: [["5", "70", 50, 695, 1], ["5", "92", 50, 918]] },
    },
  };
  if (SET_MODE && cfg.dimensions && cfg.dimensions.dimensions_per_system_type) {
    const dps = cfg.dimensions.dimensions_per_system_type;
    Object.keys(VIRTUAL_TYPES).forEach(sys => {
      if (!dps[sys]) return;
      Object.keys(VIRTUAL_TYPES[sys]).forEach(typ => {
        const vt = VIRTUAL_TYPES[sys][typ];
        if (!vt.on || dps[sys][typ]) return;
        dps[sys][typ] = vt.p.map((r, i) => Object.assign({ label: presetLabel(r[0], r[1]), szer_mm: r[2], wys_mm: r[3], bestseller: !!r[4] }, i === 0 ? help4(vt.help) : {}));
      });
    });
  }
  // 04.10.2026 (Konrad: „ogarnąć w BESTÅ, rozdzielić”): BESTÅ „Panele maskujące” w zestawie jako dwa typy - boczne i górne
  // (metafield bez zmian; w koszyku „BESTÅ · Panele boczne” / „BESTÅ · Panele górne”)
  const BESTA_SIDE = "Panele boczne", BESTA_TOP = "Panele górne", BESTA_MASK = "Panele maskujące";
  if (SET_MODE && cfg.dimensions && cfg.dimensions.dimensions_per_system_type) {
    const bd = cfg.dimensions.dimensions_per_system_type["BESTÅ"];
    const all = bd && bd[BESTA_MASK];
    const side = Array.isArray(all) ? all.filter(p => /Panel Boczny/i.test(p.label || "")) : [];
    const top = Array.isArray(all) ? all.filter(p => /Panel Górny/i.test(p.label || "")) : [];
    if (side.length && top.length && !bd[BESTA_SIDE] && !bd[BESTA_TOP]) {
      const out = {};
      Object.keys(bd).forEach(k => {
        if (k !== BESTA_MASK) { out[k] = bd[k]; return; }
        out[BESTA_SIDE] = side.map((p, i) => Object.assign({}, p, i === 0 ? help4("HELP_BESTA_SIDE") : {}));
        out[BESTA_TOP] = top.map((p, i) => Object.assign({}, p, i === 0 ? help4("HELP_BESTA_TOP") : {}));
      });
      cfg.dimensions.dimensions_per_system_type["BESTÅ"] = out;
    }
  }
  // TEMPO 4.2: DE „Eck-Hochschrank mit 2 Fronten” w metafieldzie (FAKTUM A+B) to blad - poprawka nakladka
  Object.values((cfg.dimensions && cfg.dimensions.dimensions_per_system_type) || {}).forEach(sd => Object.values(sd || {}).forEach(arr => {
    (Array.isArray(arr) ? arr : []).forEach(p => { if (p && p._typ_helptext_de === "Eck-Hochschrank mit 2 Fronten") p._typ_helptext_de = "Eckunterschrank mit 2 Fronten"; });
  }));
  // Limity „Inny wymiar” dla nowych typow (krotszy bok, mm)
  const TYPE_LIMITS = {
    [BLENDA]: { min: 30, max: 150, msg: "LIM_BLENDA", wide: "LIM_BLENDA_WIDE", wideBtn: "LIM_BLENDA_WIDE_BTN", wideTo: "Panele boczne" },
    [DIVIDER]: { min: 30, max: 50, msg: "LIM_DIVIDER" },   // 04.10 Konrad: 5 cm wystarczy
  };
  // Etykieta chipu typu (tylko UI; wartosc w koszyku zostaje pelna)
  function typChipLabel(sys, typ) {
    if (typ === NAD_TYPE) return sx("TYPE_NAD_CHIP");
    if (SET_MODE && typ === DIVIDER) return sx("TYPE_DIVIDER_CHIP");
    return tType(typ);
  }
  // Wiersz typow przewijany w bok (telefon) - aktywny typ na srodku widoku wiersza, bez przewijania strony
  function revealTypChip() {
    const a = $("[data-knf-typ-grid] .knf__typchip.is-active"), bar = a && a.closest(".knf__typgrid-row");
    if (!a || bar.scrollWidth <= bar.clientWidth + 1) return;
    const br = bar.getBoundingClientRect(), ar = a.getBoundingClientRect();
    bar.scrollLeft = Math.max(0, bar.scrollLeft + (ar.left - br.left) - (br.width - ar.width) / 2);
  }
  // UX v2 (03.10.2026): karty narożnika A/B/C bez UI (kod zostaje za flaga); pasek typow: fronty, potem wykonczenia
  const CORNER_CARDS_ON = false;
  // Typ chipu, do ktorego liczy sie pozycja listy (para A+B -> „Drzwi narożne”, korpus nadstawki -> „Nadstawka”, drzwi nadstawki - nie licza sie)
  function chipTypOf(l) {
    if (l.kind === "korpus") return NAD_TYPE;
    if (l.typ === KIT_TYP_A || l.typ === KIT_TYP_B) return "Drzwi narożne";
    return l.typ === NAD_DOOR_TYP ? "" : l.typ;
  }
  function updateTypCounts() {
    const sys = $("[data-knf-system]")?.value || "";
    $$("[data-knf-typ-grid] .knf__typchip").forEach(ch => {
      const n = state.set.reduce((a, l) => a + (l.sys === sys && chipTypOf(l) === ch.dataset.typ ? l.qty : 0), 0);
      const el = ch.querySelector("[data-knf-typ-n]"), v = n ? String(n) : "";
      if (el && el.textContent !== v) el.textContent = v;
      ch.classList.toggle("has-count", n > 0);
    });
  }
  // Narożnik METOD (TEMPO 3): karty A/B/C. Wymiary drzwi = presety z metafieldu (etykiety jak dzis), blendy 7,5/10/15 cm.
  const KITS = {
    A: { h: "T_KIT_A", d: "T_KIT_A_DESC", codes: "ME 188 / ME 189", tag: TAG_128, door: { typ: "Drzwi narożne", szer: 597, wys: 797 }, blends: [75, 100, 150], blendH: 797 },
    B: { h: "T_KIT_B", d: "T_KIT_B_DESC", codes: "ME 190 / ME 190S", tag: TAG_88, doors: [{ typ: KIT_TYP_A, szer: 254, wys: 797 }, { typ: KIT_TYP_B, szer: 254, wys: 797 }] },
    C: { h: "T_KIT_C", d: "T_KIT_C_DESC", codes: "ME 353 / ME 354 / ME 360", szer: 397, wys: [597, 797, 997] },
  };
  const KIT_PLAN = {
    A: '<svg class="knf__kit-plan" viewBox="0 0 56 56" width="56" height="56" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h48v24H28v24H4Z"/><path d="M28 28v-4M28 28h-4" stroke-width="3"/><path d="M8 52h16" stroke-width="3"/></svg>',
    B: '<svg class="knf__kit-plan" viewBox="0 0 56 56" width="56" height="56" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h48v20H24v28H4Z"/><path d="M24 24l-9 9M24 24l9-9" stroke-width="3"/></svg>',
    C: '<svg class="knf__kit-plan" viewBox="0 0 56 56" width="56" height="56" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6h44v18H24v26H6Z"/><path d="M24 24h-14" stroke-width="3"/></svg>',
  };
  const blendLabel = mm => presetLabel(String(mm / 10).replace(".", ","), "80");   // 75 -> „7,5cm x 80cm”
  function kitPresetLabel(typ, szer, wys) {
    const arr = (((cfg.dimensions && cfg.dimensions.dimensions_per_system_type) || {}).METOD || {})[typ] || [];
    const p = arr.find(x => x.szer_mm === szer && x.wys_mm === wys);
    return p ? p.label : presetLabel(Math.round(szer / 10), Math.round(wys / 10));
  }
  // Nadstawka PAX (decyzje C, raport techniczny 2-3): formuly, zakresy, cennik
  const NAD = { CLEAR_MM: 10, FRONT_MINUS_MM: 3, GAP_MIN_MM: 250, GAP_MAX_MM: 1000, SHELF_MIN_MM: 400, QTY_MAX: 10,
    W: [500, 750, 1000], D: [58, 35], H: [236, 201],
    DOORS: { 500: [496], 750: [371, 371], 1000: [496, 496] }, DEPTH_MM: { 58: 580, 35: 350 } };
  // 04.10.2026 (Konrad): 499 zl za korpus nad rama 50, 75 i 100 cm (ten sam wariant). null = „wycena indywidualna - napisz do nas”.
  const NAD_PRICES = { 750: "variant", 500: "variant", 1000: "variant" };
  const KORPUS = (() => {
    try { const k = JSON.parse(root.dataset.korpus || "null"); return (k && k.id && k.a !== false && Number(k.p) > 0) ? k : null; } catch (_) { return null; }
  })();
  const NAD_OK = NADSTAWKA_ON && SET_MODE && !!KORPUS;
  function nadCompute(w, gapMm, depthCm) {
    const kH = gapMm - NAD.CLEAR_MM;            // KCa: 290 -> 280
    const dH = kH - NAD.FRONT_MINUS_MM;         // 280 -> 277
    return { korpus: { szer: w, wys: kH, gl: NAD.DEPTH_MM[depthCm] || 580 }, doors: (NAD.DOORS[w] || []).map(s => ({ szer: s, wys: dH })) };
  }
  // Opcje per element (E) - reszta opcji warunkowych jest wspolna dla zestawu (G). Kolejnosc segmentow w „Opcje”.
  const E_ORDER = ["Lokalizacja uchwytu", HINGE_LABEL, "Kierunek usłojenia", EXT_LABEL];
  const isELabel = label => E_ORDER.includes(label);
  // Segmenty „Opcje” z kontekstem narożnika / nadstawki: PL do _pl_summary, tlumaczenie do widocznej wartosci
  function tTag(pl) {
    if (!pl) return "";
    if (pl === TAG_128) return sx("TAG_128");
    if (pl === TAG_88) return sx("TAG_88");
    if (pl === "Do szafki narożnej") return sx("TAG_CORNER");   // UX v2: blendy narożne (TAG_CORNER)
    const m = /^Do nadstawki: (.+)$/.exec(pl);
    return m ? sx("TAG_NAD", { d: m[1] }) : pl;
  }
  const noDrillTyp = typ => typ === BLENDA || typ === DIVIDER;   // segment „Bez nawiertów” (Z6), bez uchwytu i zawiasow (Z10)
  // 04.10.2026 (Konrad): cokoly, panele (tez BESTÅ boczne/gorne) i listwy tez bez uchwytu; noDrillTyp zostaje (stawka, segment „Bez nawiertów”)
  const noHandleTyp = typ => noDrillTyp(typ) || /^(Panel|Cok[oó]ł|Listwa)/.test(typ || "");
  const isPlinthTyp = typ => /^Cok[oó]ł/.test(typ || "");
  // 05.10.2026 (Konrad): FAKTUM „A+B (2-frontowe)” - jedna pozycja = 2 fronty (drzwi A i B): kafelek dodaje 2 szt., cena na kafelku za parę
  const abPairTyp = typ => /^A\+B/.test(typ || "");
  const qtyStep = typ => (abPairTyp(typ) ? 2 : 1);

  // Tag dla wymiaru spoza presetów IKEA (Custom dimension / NA WYMIAR / systemy bez presetów) - dopisywany w cart.
  const CUSTOM_DIM_TAG = { pl: "wymiar niestandardowy", en: "custom size", cs: "vlastní rozměr", de: "Sondermaß" };

  // System IKEA: nazwy globalne (METOD/FAKTUM/PAX/PLATSA/BESTÅ) - IDEM. Tylko "NA WYMIAR" tłumaczone.
  const SYSTEM_I18N = { en: { "NA WYMIAR": "CUSTOM" }, cs: { "NA WYMIAR": "NA MÍRU" }, de: { "NA WYMIAR": "NACH MASS" } };
  function tSystem(plName) {
    return (SYSTEM_I18N[locale] && SYSTEM_I18N[locale][plName]) || plName;
  }
  function tStyle(plName) {
    if (locale === "pl") return plName;
    const list = (cfg && cfg.styles && cfg.styles.styles) || [];
    const found = list.find(s => s.name === plName);
    return (found && found[`name_${locale}`]) || plName;
  }
  function tColor(plName) {
    if (locale === "pl" || !plName) return plName;
    const palettes = Object.values((cfg && cfg.colors) || {});
    for (const p of palettes) {
      for (const c of (p.colors || [])) {
        if (c.name === plName) return c[`name_${locale}`] || c.name;
      }
    }
    return plName;
  }
  function tFinish(plVal) {
    // "Pół-mat" / "Półmat" → V_POLMAT, "Połysk (+15%)" → V_POLYSK
    if (!plVal) return "";
    if (/połysk/i.test(plVal)) return L_PROP.V_POLYSK;
    if (/(pół-?mat|polmat)/i.test(plVal)) return L_PROP.V_POLMAT;
    return plVal;
  }

  // Pełne tłumaczenia helptextów typów na DE (rynek niemiecki realny) - kluczowane
  // polskim źródłem z metafielda custom.dimensions. EN/CS składają template z typu+systemu,
  // ale helptexty są opisowe (np. "Dłuższe fronty dla szafek wiszących…"), więc DE wymaga
  // pełnych tłumaczeń - inaczej fallback do surowego PL (memory reference_email_template_de).
  const TYP_HELP_DE = {
    "Bok korpusu (wystaje z boku zabudowy)": "Korpusseite (steht seitlich über)",
    "Bok korpusu szafy": "Korpusseite des Schranks",
    "Bok korpusu": "Korpusseite",
    "Dłuższe fronty dla szafek wiszących - niwelują konieczność montażu uchwytu": "Verlängerte Fronten für Hängeschränke - machen einen Griff überflüssig",
    "Do systemu MAXIMERA": "Für das MAXIMERA-System",
    "Drzwi szafki dolnej, górnej lub stojącej": "Tür für Unter-, Ober- oder Hochschrank",
    "Drzwi szafki standardowe": "Standard-Schranktür",
    "Front do koszy cargo (szuflada z koszem)": "Front für Cargo-Körbe (Schublade mit Korb)",
    "Front do koszy typu cargo (szuflada z koszem drucianym)": "Front für Cargo-Körbe (Schublade mit Drahtkorb)",
    "Front do szafki w rogu, ścięty pod 45°": "Front für Eckschrank, 45° abgeschrägt",
    "Front do szuflad BESTÅ": "Front für BESTÅ-Schubladen",
    "Front do szuflad systemu FAKTUM": "Front für Schubladen des FAKTUM-Systems",
    "Front montowany na podnośniku UTRUSTA, otwierany do góry": "Front mit UTRUSTA-Klappenbeschlag, nach oben öffnend",
    "Front otwierający się do góry": "Nach oben öffnende Front",
    "Fronty do korpusu wysokiego (z lodówką, AGD do zabudowy)": "Fronten für Hochschrank (mit Kühlschrank, Einbaugeräten)",
    "Fronty do szafki w rogu (z 2 blendami lub łamane)": "Fronten für Eckschrank (mit 2 Blenden oder gefaltet)",
    "Listwa pomiędzy podłogą a dnem szafek": "Leiste zwischen Boden und Schrankunterkante",
    "Listwy zakrywające szczeliny / cokół": "Leisten zum Abdecken von Spalten / Sockel",
    "Panel gięty wykończeniowy R150, ślepy (bez nawiertów). Cena zależna od wysokości.": "Gebogenes Abschlusspaneel R150, blind (ohne Bohrungen). Preis je nach Höhe.",
    "Panele boczne / górne BESTÅ": "Seiten- / Oberpaneele BESTÅ",
    "Panele zakrywające szczeliny / boki PLATSA": "Paneele zum Abdecken von Spalten / Seiten PLATSA",
    "Przykrywają cokół z przodu do ziemi - spójne wykończenie": "Verdecken den Sockel vorne bis zum Boden - einheitlicher Abschluss",
    "Standardowe drzwi BESTÅ": "Standard-BESTÅ-Tür",
    "Standardowe drzwi modułów PLATSA": "Standard-Tür für PLATSA-Module",
    "Standardowe drzwi szafy/garderoby PAX": "Standard-Tür für PAX-Schrank/Kleiderschrank",
    "Szafka stojąca narożna 2-frontowa": "Eckunterschrank mit 2 Fronten",   // 10.2026: bylo „Eck-Hochschrank” (blad, TEMPO 4.2)
    "Ze względu na różnice między modelami AGD front bez nawiertów": "Aufgrund der Unterschiede zwischen Gerätemodellen ohne Bohrungen",
    "Ze względu na różnice między modelami AGD front dostarczany jest bez nawiertów": "Aufgrund der Unterschiede zwischen Gerätemodellen wird die Front ohne Bohrungen geliefert",
  };

  // Generic translated helptext per system×typ - zastępuje surowy PL `_typ_helptext`
  // gdy locale != pl (PL helptexts są deklinowane: "Front do szuflad systemu FAKTUM",
  // EN/CS używamy template "Typ front for SYSTEM").
  function tTypHelp(plText, system, typ) {
    if (!plText) return "";
    if (locale === "pl") return plText;
    if (locale === "de") return TYP_HELP_DE[plText] || plText;
    const typTr = tType(typ); // "Door"/"Drawer"/"Dveře"/"Zásuvka" itp.
    const sysTr = tSystem(system); // "NA WYMIAR" → "CUSTOM"/"NA MÍRU"
    if (locale === "en") return `${typTr} front for ${sysTr} system`;
    if (locale === "cs") return `Čelo (${typTr.toLowerCase()}) pro systém ${sysTr}`;
    return plText;
  }

  // -- I18n: submit button text + missing hints (cart & sample mode)
  const BTN_I18N = {
    pl: {
      DODAJ: "Dodaj do koszyka", ADDING: "Dodawanie…",
      MISS_STYL: "Wybierz styl frontu", MISS_KOLOR: "Wybierz kolor",
      MISS_SYSTEM: "Wybierz system IKEA", MISS_TYP: "Wybierz typ elementu",
      MISS_WYMIAR: "Podaj wymiary", PICK_TYP_DIM: "Wybierz typ i wymiar",
      MISS_OPIS: "Uzupełnij opis (np. lokalizacja uchwytu)", MISS_KOLOR_PROBKI: "Wybierz kolor próbki",
    },
    en: {
      DODAJ: "Add to cart", ADDING: "Adding…",
      MISS_STYL: "Select front style", MISS_KOLOR: "Select color",
      MISS_SYSTEM: "Select IKEA system", MISS_TYP: "Select element type",
      MISS_WYMIAR: "Enter dimensions", PICK_TYP_DIM: "Select type and dimensions",
      MISS_OPIS: "Complete the description (e.g. handle position)", MISS_KOLOR_PROBKI: "Select sample color",
    },
    cs: {
      DODAJ: "Přidat do košíku", ADDING: "Přidávání…",
      MISS_STYL: "Vyberte styl čela", MISS_KOLOR: "Vyberte barvu",
      MISS_SYSTEM: "Vyberte systém IKEA", MISS_TYP: "Vyberte typ prvku",
      MISS_WYMIAR: "Zadejte rozměry", PICK_TYP_DIM: "Vyberte typ a rozměry",
      MISS_OPIS: "Doplňte popis (např. umístění úchytu)", MISS_KOLOR_PROBKI: "Vyberte barvu vzorku",
    },
    de: {
      DODAJ: "In den Warenkorb", ADDING: "Wird hinzugefügt…",
      MISS_STYL: "Frontstil wählen", MISS_KOLOR: "Farbe wählen",
      MISS_SYSTEM: "IKEA-System wählen", MISS_TYP: "Elementtyp wählen",
      MISS_WYMIAR: "Maße angeben", PICK_TYP_DIM: "Typ und Maße wählen",
      MISS_OPIS: "Beschreibung ergänzen (z. B. Griffposition)", MISS_KOLOR_PROBKI: "Musterfarbe wählen",
    },
  };
  const L_BTN = BTN_I18N[locale] || BTN_I18N.pl;
  // Komunikat walidacji na przycisku (bez strzałki "→" - w tym kontekście prefiksem jest "← ")
  const missMsg = k => String(L_BTN[k] || BTN_I18N.pl[k] || "").replace(/\s*→$/, "");

  // Stan po dodaniu do koszyka (2026-09-12): panel inline w .knf__cta zamiast toastu (toast
  // usunięto w lipcu, klient nie widział potwierdzenia i dodał ten sam front 14 razy),
  // pytanie o duplikat, błąd inline z ponowieniem. Tylko zwykłe myślniki.
  const ADD_I18N = {
    pl: { ADDED_TITLE: "Dodano do koszyka", ADDED_SAMPLE: "Próbka dodana do koszyka", IN_CART: "w koszyku: {n} {pcs}", GO_CART: "Przejdź do koszyka", ADD_NEXT: "Dodaj kolejny front", ADD_NEXT_SAMPLE: "Dodaj kolejną próbkę", DUP_Q_SAMPLE: "Ta próbka jest już w koszyku. Dodać drugą?", DUP_YES: "Tak, dodaj", DUP_NO: "Nie, zostaw", ERR_TITLE: "Nie udało się dodać do koszyka.", ERR_RETRY: "Spróbuj ponownie", ERR_CHECKING: "Sprawdzam koszyk...", ERR_VERIFIED: "Pozycja jest już w koszyku.", BTN_DONE: "Dodano ✓", ERR_BTN: "Błąd, spróbuj ponownie" },
    en: { ADDED_TITLE: "Added to cart", ADDED_SAMPLE: "Sample added to cart", IN_CART: "in cart: {n} {pcs}", GO_CART: "Go to cart", ADD_NEXT: "Add another front", ADD_NEXT_SAMPLE: "Add another sample", DUP_Q_SAMPLE: "This sample is already in your cart. Add a second one?", DUP_YES: "Yes, add it", DUP_NO: "No, keep it", ERR_TITLE: "Could not add to cart.", ERR_RETRY: "Try again", ERR_CHECKING: "Checking your cart...", ERR_VERIFIED: "The item is already in your cart.", BTN_DONE: "Added ✓", ERR_BTN: "Error, try again" },
    cs: { ADDED_TITLE: "Přidáno do košíku", ADDED_SAMPLE: "Vzorek přidán do košíku", IN_CART: "v košíku: {n} {pcs}", GO_CART: "Přejít do košíku", ADD_NEXT: "Přidat další dvířka", ADD_NEXT_SAMPLE: "Přidat další vzorek", DUP_Q_SAMPLE: "Tento vzorek už je v košíku. Přidat druhý?", DUP_YES: "Ano, přidat", DUP_NO: "Ne, nechat", ERR_TITLE: "Nepodařilo se přidat do košíku.", ERR_RETRY: "Zkusit znovu", ERR_CHECKING: "Kontroluji košík...", ERR_VERIFIED: "Položka už je v košíku.", BTN_DONE: "Přidáno ✓", ERR_BTN: "Chyba, zkuste to znovu" },
    de: { ADDED_TITLE: "In den Warenkorb gelegt", ADDED_SAMPLE: "Muster in den Warenkorb gelegt", IN_CART: "im Warenkorb: {n} {pcs}", GO_CART: "Zum Warenkorb", ADD_NEXT: "Weitere Front hinzufügen", ADD_NEXT_SAMPLE: "Weiteres Muster hinzufügen", DUP_Q_SAMPLE: "Dieses Muster liegt bereits im Warenkorb. Ein zweites hinzufügen?", DUP_YES: "Ja, hinzufügen", DUP_NO: "Nein, so lassen", ERR_TITLE: "Konnte nicht in den Warenkorb gelegt werden.", ERR_RETRY: "Erneut versuchen", ERR_CHECKING: "Warenkorb wird geprüft...", ERR_VERIFIED: "Der Artikel liegt bereits im Warenkorb.", BTN_DONE: "Hinzugefügt ✓", ERR_BTN: "Fehler, bitte erneut versuchen" },
  };
  const L_ADD = ADD_I18N[locale] || ADD_I18N.pl;
  const PCS_I18N = { en: "pcs", cs: "ks", de: "Stk.", pl: "szt." };
  const pcsWord = () => PCS_I18N[locale] || PCS_I18N.pl;
  // Prefiks locale (/en-eu/ itd.) - bez niego /cart/add.js odpowiada w domyślnym języku (PL)
  const CART_ROOT = window.Shopify?.routes?.root || "/";
  // Id sekcji z cart drawerem - po migracji Horizon drawer żyje w "cart-drawer-section",
  // nie w "header" (stary klucz zwracał undefined → drawer otwierał się ze stanem sprzed dodania).
  const DRAWER_SECTION_ID = (() => {
    const sec = document.querySelector("cart-drawer-component")?.closest(".shopify-section");
    return sec ? sec.id.replace(/^shopify-section-/, "") : "cart-drawer-section";
  })();

  const state = {
    styl: null,
    szer: null,
    wys: null,
    strona: "Jednostronnie",
    grubAdd: { nd: null, fr: null },   // 04.10.2026 (Konrad): grubosc dodawanych elementow - bez nawiertow / z nawiertami (null = standard stylu)
    kolor: null,
    custom_kolor: null,
    conditional: {},
    qty: 1,
    sample_mode: false,
    // zestaw (TEMPO 6.1): pozycje, uwagi do zestawu, szerokosc blend karty A, ustawienia karty nadstawki
    set: [],
    setNote: "",
    kit: { A: { blend: 100 } },
    nad: { w: 750, d: 58, h: 236, gap: "", polka: null, qty: 1, fronty: true },
  };

  // Tryb próbki: variant ID + flat price 89 zł, dorzucone Wykończenie (Półmat/Połysk)
  const SAMPLE_VARIANT_ID = root.dataset.sampleVariantId || "";
  const SAMPLE_ONLY = root.dataset.sampleOnly === "1";   // 24.09: konfigurator samych probek (strona probki)
  const SAMPLE_PRICE_CENTS = Math.round(parseFloat(root.dataset.samplePrice || "89") * 100);
  // Cena próbki sformatowana przez Liquid (money) - JUŻ w walucie rynku. Używamy jej do
  // wyświetlania zamiast formatPrice(SAMPLE_PRICE_CENTS), bo sample_price_raw na rynkach obcych
  // jest już skonwertowany przez Markets → formatPrice ×rate dawałby podwójną konwersję.
  const SAMPLE_PRICE_FMT = root.dataset.samplePriceFmt || "";

  // -- Promocja procentowa na fronty (window.knfPromo z snippets/promo-js.liquid, tylko gdy trwa).
  //    Rachunek w formatPricePromo. Data końca sprawdzana też tutaj - cache stron Shopify może
  //    podać stary HTML po końcu promocji.
  const PROMO = (window.knfPromo && window.knfPromo.pct > 0 && Date.now() <= window.knfPromo.untilTs) ? window.knfPromo : null;
  // Wymiar próbki zależny od stylu: Gładki ma mniejszy format, reszta standard 284×396 mm.
  // Liczbowo (szer/wys osobno) - string display wyliczany; liczby idą też do _szer_mm/_wys_mm dla Pro-Hand.
  const SAMPLE_DIMS_MM = { default: { szer: 284, wys: 396 }, "Gładki": { szer: 145, wys: 230 } };
  const sampleDims = (styl) => (styl && SAMPLE_DIMS_MM[styl]) || SAMPLE_DIMS_MM.default;
  const sampleDimMM = (styl) => { const d = sampleDims(styl); return `${d.szer} × ${d.wys} mm`; };
  // Sample button text - pobiera z Liquid (data-t-sample-add-btn) z dynamic price
  // Liquid już ma t_sample_add_btn z ceną próbki z produktu (money) per locale
  const SAMPLE_ADD_BTN_TEXT = root.dataset.tSampleAddBtn || (
    (locale === "en" ? "Add sample" :
     locale === "cs" ? "Přidat vzorek" :
     locale === "de" ? "Muster hinzufügen" :
     "Dodaj próbkę") + (SAMPLE_PRICE_FMT ? ` · ${SAMPLE_PRICE_FMT}` : "")
  );
  // Połysk dostępny tylko dla wybranych stylów lakierowanych (gładkie powierzchnie + Round Oak).
  // Frezowane (Linea/Monako/Pasmo/Rytm/Wiedeń) → tylko Półmat (frez ginie w połysku).
  // SYNC z konfigurator-probek.js: STYLES_WITH_GLOSS, SAMPLE_DIM_*, PROP_I18N (klucze próbki), buildSampleProperties
  // wer3: polysk probki tylko dla stylow, ktore maja opcje polysk_polmat w regulach (jak konfigurator-probek.js) - Round/Round Oak bez polysku
  const STYLES_WITH_GLOSS = (() => {
    const r = (cfg.rules && cfg.rules.rules) || {};
    const set = new Set(Object.keys(r).filter(k => ((r[k] && r[k].options) || []).includes("polysk_polmat")));
    return set.size ? set : new Set(["Gładki", "Grip", "Frezowany uchwyt"]);
  })();
  const SAMPLE_CTA_TITLE_BASE_PL = "Niepewny? Zamów najpierw próbkę";
  const SAMPLE_CTA_TITLE_BASE_EN = "Not sure? Order a sample first";

  // -- I18n: etykiety używane przez summary box; wartości przychodzą z [data-knf-i18n-*]
  // -- WebP ikony per system×typ (z Globo CDN, zoptymalizowane do ~1KB każda).
  // Asset base wyciągnięty z sample asset_url (Shopify pakuje wszystko w jeden katalog assets/).
  const ICON_SAMPLE = root.dataset.iconSample || "";
  const ICON_BASE = ICON_SAMPLE.replace(/[^/]+(\?.*)?$/, "");
  const ICON_QS = (ICON_SAMPLE.match(/\?.*$/) || [""])[0];  // zachowaj ?v= cache-bust hash z Shopify
  const SYS_ICON_URL = (sys) => {
    const slug = String(sys).toLowerCase().replace(/å/g, 'a').replace(/[^a-z0-9]+/g, '-');
    return ICON_BASE + `knf-sys-${slug}.webp` + ICON_QS;
  };

  // UX v2 (03.10.2026): jeden komplet ikon knf-ico-<slug>.svg (GPT Image 2) dla wszystkich typow; webp i TYPE_ICONS juz nieuzywane dla typow
  const ICO = (() => { try { return JSON.parse(root.dataset.ico || "{}") || {}; } catch (_) { return {}; } })();   // Liquid: slug -> asset_url (wlasny ?v=)
  const ICO_BASE = ICON_SAMPLE.replace(/[^/]+(\?.*)?$/, ""), ICO_QS = (ICON_SAMPLE.match(/\?.*$/) || [""])[0];   // zapas bez data-ico
  const ICO_SLUG = { "Drzwi": "drzwi", "Szuflada": "szuflada", "Drzwi przedłużane": "drzwi-przedluzane", "Przedłużone": "pax-przedluzone", "Cargo": "cargo",
    "Front zmywarki bez nawiertów": "front-zmywarki", "Drzwi narożne": "drzwi-narozne-45", "A+B (2-frontowe)": "a-plus-b", "Horyzontalne szafki": "horyzontalne", "Drzwi poziome": "horyzontalne",
    "Korpus wysoki": "korpus-wysoki", "Panele boczne": "panel-boczny", "Panele maskujące": "panele-maskujace", "Panele Maskujące": "panele-maskujace", "Cokoły": "cokol", "Cokół": "cokol",
    "Blenda": "blenda", "Listwa maskująca": "blenda", "Panel dzielący (divider)": "panel-dzielacy", "Nadstawka": "nadstawka", "Panel gięty ślepy R150": "panel-boczny", "naroznik": "naroznik" };
  function icoUrl(sys, typ) {
    let slug = ICO_SLUG[typ] || "drzwi";
    if (sys === "PAX" && typ === "Drzwi") slug = "pax-drzwi";
    if ((sys === "BESTÅ" || sys === "PLATSA") && (typ === "Drzwi" || typ === "Szuflada")) slug = typ === "Drzwi" ? "rtv-drzwi" : "rtv-szuflada";
    if (sys === "METOD" && typ === "Drzwi narożne") slug = "a-plus-b";
    if (sys === "BESTÅ" && typ === BESTA_SIDE) slug = "besta-panel-boczny";   // 04.10.2026: osobne ikony bokow i gory BESTÅ (GPT Image 2)
    if (typ === BESTA_TOP) slug = "besta-panel-gorny";
    return ICO[slug] || (ICO_BASE ? `${ICO_BASE}knf-ico-${slug}.svg${ICO_QS}` : "");
  }
  const icoImg = (sys, typ, cls) => { const u = icoUrl(sys, typ); return u ? `<img class="${cls}" src="${escapeAttr(u)}" width="32" height="32" alt="" loading="lazy" decoding="async">` : ""; };

  const i18nLabels = {
    pl: {
      styl: "Styl frontu", system: "System IKEA", typ: "Typ elementu",
      wymiary: "Wymiary", grubosc: "Grubość", powierzchnia: "Powierzchnia (rozliczana)", strona: "Strona lakierowania",
      kolor: "Kolor", uchwyt_opis: "Opis uchwytu", cena_szt: "Cena za sztukę", ilosc: "Ilość",
      typ_with_presets: (sys) => `Z presetami IKEA (${sys})`,
      typ_no_presets: (sys) => `Wymiary niestandardowe dla ${sys}`,
      typ_other: "Inne typy (wymiary ręcznie)",
      typ_choose_first: "Wybierz najpierw System IKEA, by zobaczyć dostępne typy.",
    },
    en: {
      styl: "Front style", system: "IKEA system", typ: "Element type",
      wymiary: "Dimensions", grubosc: "Thickness", powierzchnia: "Area (billed)", strona: "Lacquering side",
      kolor: "Color", uchwyt_opis: "Handle description", cena_szt: "Price per piece", ilosc: "Quantity",
      typ_with_presets: (sys) => `With IKEA presets (${sys})`,
      typ_no_presets: (sys) => `No presets for ${sys} - manual dimensions`,
      typ_other: "Other types (manual dimensions)",
      typ_choose_first: "Choose IKEA system first to see available types.",
    },
    cs: {
      styl: "Styl čela", system: "Systém IKEA", typ: "Typ prvku",
      wymiary: "Rozměry", grubosc: "Tloušťka", powierzchnia: "Plocha (účtovaná)", strona: "Strana lakování",
      kolor: "Barva", uchwyt_opis: "Popis úchytu", cena_szt: "Cena za kus", ilosc: "Množství",
      typ_with_presets: (sys) => `S předvolbami IKEA (${sys})`,
      typ_no_presets: (sys) => `Vlastní rozměry pro ${sys}`,
      typ_other: "Jiné typy (ruční rozměry)",
      typ_choose_first: "Nejprve vyberte Systém IKEA, abyste viděli dostupné typy.",
    },
    de: {
      styl: "Frontstil", system: "IKEA-System", typ: "Elementtyp",
      wymiary: "Maße", grubosc: "Stärke", powierzchnia: "Fläche (abgerechnet)", strona: "Lackierseite",
      kolor: "Farbe", uchwyt_opis: "Griffbeschreibung", cena_szt: "Preis pro Stück", ilosc: "Stückzahl",
      typ_with_presets: (sys) => `Mit IKEA-Voreinstellungen (${sys})`,
      typ_no_presets: (sys) => `Sondermaße für ${sys}`,
      typ_other: "Andere Typen (manuelle Maße)",
      typ_choose_first: "Bitte zuerst IKEA-System auswählen, um verfügbare Typen zu sehen.",
    },
  };
  const L = i18nLabels[locale] || i18nLabels.pl;

  const $ = (sel, ctx = root) => ctx.querySelector(sel);
  const $$ = (sel, ctx = root) => Array.from(ctx.querySelectorAll(sel));

  // -- Panel gięty ślepy R150 (METOD, wyłącznie styl "Gładki"): cena STAŁA per wysokość, nie per m².
  // Dedykowane warianty Shopify: option2 = "Panel <wys_cm> cm", strona = Jednostronnie.
  const PANEL_TYPE = "Panel gięty ślepy R150";
  const PANEL_HIDDEN = true;
  const isPanelTyp = () => isPanelTypName($("[data-knf-typ]")?.value);
  function isPanelTypName(typ) { return typ === PANEL_TYPE; }
  function panelVariantFor(wysMm) {
    const hcm = Math.round((wysMm || 0) / 10);
    return cfg.variants.find(v =>
      v.option1 === "Gładki" && v.option2 === `Panel ${hcm} cm` && v.option3 === "Jednostronnie");
  }

  // -- localStorage persistence: per-product key, TTL 7 dni
  const STORAGE_KEY = `knf:state:${root.dataset.productHandle || root.dataset.productId || "default"}${SAMPLE_ONLY ? ":probka" : ""}`;   // probka nie nadpisuje konfiguracji frontu
  const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  // Polyfill CSS.escape (Safari ≤ 9, starsze Edge): escape ASCII problematyczne znaki w selektorach
  const cssEscape = (typeof CSS !== "undefined" && CSS.escape)
    ? CSS.escape
    : (s) => String(s).replace(/[^a-zA-Z0-9_-]/g, c => "\\" + c);

  // Debounce: chroni localStorage przed write na każde keystroke (jank + zbędne IO)
  let saveTimer = null;
  function saveState() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveStateNow, 250);
  }
  function saveStateNow() {
    try {
      const payload = {
        ts: Date.now(),
        state: { ...state },
        form: {
          szer: $("[data-knf-szer]")?.value || "",
          wys: $("[data-knf-wys]")?.value || "",
          system: $("[data-knf-system]")?.value || "",
          typ: $("[data-knf-typ]")?.value || "",
          dimLabel: $("[data-knf-dim-label]")?.value || "",
          customColor: $("[data-knf-kolor-custom]")?.value || "",
          notes: $('textarea[name="properties[Uwagi klienta]"]')?.value || "",
          condCustom: $$("[data-knf-cond-custom]").reduce((acc, inp) => {
            if (inp.value) acc[inp.dataset.knfCondCustom] = inp.value;
            return acc;
          }, {}),
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) { /* private mode / quota - silently ignore */ }
  }

  // Czy payload faktycznie zawiera wartościowe wybory (nie tylko default qty=1)?
  function isPayloadMeaningful(payload) {
    if (!payload || !payload.state) return false;
    const s = payload.state;
    return Boolean(s.styl || s.szer || s.wys || s.kolor || s.custom_kolor);
  }

  function clearStateStorage() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload || (Date.now() - payload.ts) > STORAGE_TTL_MS) {
        clearStateStorage();
        return null;
      }
      return payload;
    } catch (_) { return null; }
  }

  // Restore: replay user actions tak żeby render handlery wystrzeliły efekty uboczne
  function restoreState() {
    const payload = loadState();
    if (!isPayloadMeaningful(payload)) {
      if (payload) clearStateStorage();  // wyczyść pusty/tylko-qty payload, żeby notice nie kłamał
      return false;
    }

    const f = payload.form;
    const restoredFields = [];  // do flash animacji

    // 0. Styl PIERWSZY - żeby renderDimensionPresets miał state.styl dla pricePerM2 calculation w title
    // 24.09: styl ukryty (HIDDEN_STYLES) albo usuniety z metafieldu nie wraca z zapisu
    if (payload.state.styl && cfg.styles.styles.some(s => s.name === payload.state.styl)) {
      state.styl = payload.state.styl;  // bezpośredni set (klik na koniec dla aria + onStyleChange)
    }

    // 1. System + typ (musi być przed wymiarami, żeby renderTypesForSystem zarejestrował presety)
    const sysSel = $("[data-knf-system]");
    const typSel = $("[data-knf-typ]");
    if (f.system) {
      sysSel.value = f.system;
      sysSel.dispatchEvent(new Event("change"));
      restoredFields.push(sysSel);
    }
    if (f.typ) {
      // Verify że typ faktycznie został dostępny po renderTypesForSystem (system mógł zmienić listę)
      if ([...typSel.options].some(o => o.value === f.typ)) {
        typSel.value = f.typ;
        typSel.dispatchEvent(new Event("change"));
        restoredFields.push(typSel);
      }
    }

    // 2. Wymiary - pokaż manual i przywróć wartości
    // Zestaw (TEMPO 5.7, Z5): zapis wymiaru niczego nie dodaje i nie wpisuje pol - tylko podswietla kafelek
    if (SET_MODE && f.szer && f.wys) highlightTile(parseInt(f.szer, 10), parseInt(f.wys, 10), f.typ, true);
    if (!SET_MODE) {
    if (f.szer || f.wys) {
      const manualWrap = $("[data-knf-dims-manual]");
      if (manualWrap) manualWrap.hidden = false;
    }
    const inSzer = $("[data-knf-szer]");
    const inWys = $("[data-knf-wys]");
    if (f.szer) { inSzer.value = f.szer; restoredFields.push(inSzer); }
    if (f.wys)  { inWys.value = f.wys;  restoredFields.push(inWys); }
    if (f.dimLabel) $("[data-knf-dim-label]").value = f.dimLabel;
    state.szer = parseInt(f.szer, 10) || null;
    state.wys = parseInt(f.wys, 10) || null;
    updateM2Display();

    // 2a. Aktywuj preset chip jeśli wymiary matchują któryś preset (UX feedback)
    const presetMatch = $$("[data-knf-presets] .knf__preset").find(b =>
      parseInt(b.dataset.szerMm, 10) === state.szer && parseInt(b.dataset.wysMm, 10) === state.wys
    );
    if (presetMatch) presetMatch.classList.add("is-active");
    }

    // 3. Styl - symuluj kliknięcie żeby wywołać renderColors + renderConditional
    if (payload.state.styl) {
      const styleBtn = $(`.knf__style[data-style="${cssEscape(payload.state.styl)}"]`);
      if (styleBtn) styleBtn.click();
    }

    // 4. Strona lakierowania - select powstaje dopiero w renderStrona(), więc ustawiamy sam stan;
    //    renderStrona zsynchronizuje kontrolkę (i skasuje wybór, jeśli styl nie jest lakierowany).
    if (payload.state.strona) {
      state.strona = payload.state.strona;
      const sel = $("[data-knf-strona]");
      if (sel) sel.value = payload.state.strona;
    }

    // 5. Kolor - po renderColors (które wystrzeli z punktu 3)
    if (payload.state.kolor) {
      const colorBtn = $(`.knf__color[data-color="${cssEscape(payload.state.kolor)}"]`);
      if (colorBtn) colorBtn.click();
    } else if (payload.state.custom_kolor) {
      const customBtn = $('.knf__color[data-color="__custom__"]');
      if (customBtn) {
        customBtn.click();
        const inp = $("[data-knf-kolor-custom]");
        if (inp) { inp.value = payload.state.custom_kolor; inp.dispatchEvent(new Event("input")); }
      }
    }

    // 6. Conditional opcje (po renderConditional z punktu 3)
    Object.entries(payload.state.conditional || {}).forEach(([label, val]) => {
      const sel = $(`[data-knf-cond="${cssEscape(label)}"]`);
      // wer3: tylko istniejaca opcja (stary zapis Round „Lewa”/„Prawa” zostawial puste polozenie uchwytu)
      if (sel && [...sel.options].some(o => o.value === val)) { sel.value = val; sel.dispatchEvent(new Event("change")); }
    });

    // 6b. Grubość frontu (NA WYMIAR) - select poza pętlą conditional, ustaw z zapisanego stanu
    if (payload.state.grubosc) {
      const gsel = $("[data-knf-grubosc]");
      if (gsel && !gsel.disabled) { gsel.value = payload.state.grubosc; state.grubosc = payload.state.grubosc; }
    }

    // 7. Custom uchwyt opisy (po conditional)
    Object.entries(f.condCustom || {}).forEach(([label, val]) => {
      const inp = $(`[data-knf-cond-custom="${cssEscape(label)}"]`);
      if (inp) { inp.value = val; inp.hidden = false; inp.dispatchEvent(new Event("input")); }
    });

    // 8. Quantity: celowo NIE przywracana (2026-09-12) - cicho przywrócone "14 szt." po tygodniu
    //    to kolejne 14 sztuk w koszyku bez wiedzy klienta. Ilość zawsze startuje od 1.

    // 9. Notatki
    if (f.notes) {
      const ta = $('textarea[name="properties[Uwagi klienta]"]');
      if (ta) { ta.value = f.notes; restoredFields.push(ta); if (SET_MODE) state.setNote = f.notes; }
    }

    // Final tryUpdate - odzwierciedla pełen restored state (po condCustom z pkt 7)
    tryUpdate();

    // Wizualny flash przywróconych pól: subtelne gold tło 1.5s
    restoredFields.forEach(el => {
      el.classList.add("knf__restored-flash");
      setTimeout(() => el.classList.remove("knf__restored-flash"), 1500);
    });

    return true;
  }

  // Restore notice usunięty (Konrad: niepotrzebny). Stan przywracany silent - bez UI komunikatu.

  const BESTSELLER_STYLES = new Set(["Gładki"]);

  // -- 1. Renderowanie style picker (z tooltipem helptext). Runda 3 (04.10.2026): w zestawie jak na live - jedna siatka, znaczki BESTSELLER / FAKTUM
  function renderStyles() {
    const wrap = $("[data-knf-styles]");
    wrap.innerHTML = cfg.styles.styles.map(s => {
      const displayName = t(s, "name");
      const displayHelp = t(s, "helptext") || displayName;
      const badgeLabel = s.badge || (BESTSELLER_STYLES.has(s.name) ? "BESTSELLER" : "");
      const isBestseller = !!badgeLabel;
      return `
      <button type="button" class="knf__style${isBestseller ? ' knf__style--bestseller' : ''}" data-style="${escapeAttr(s.name)}" data-group="${s.group}" data-category="${s.category}" role="radio" aria-checked="false" aria-label="${escapeAttr(displayName + ' - ' + displayHelp + (isBestseller ? ' (' + badgeLabel.toLowerCase() + ')' : ''))}" title="${escapeAttr(displayHelp)}">
        ${isBestseller ? `<span class="knf__style-badge" aria-hidden="true">${escapeHtml(badgeLabel)}</span>` : ''}
        ${s.swatch_url ? `<img class="knf__style-img" src="${escapeAttr(s.swatch_url)}" alt="${escapeAttr(displayName)}" loading="lazy">` : `<span class="knf__style-img knf__style-img--placeholder"></span>`}
        <span class="knf__style-name" translate="no">${escapeHtml(displayName)}</span>
      </button>
    `;}).join("");

    wrap.addEventListener("click", e => {
      const btn = e.target.closest(".knf__style");
      if (!btn) return;
      $$(".knf__style", wrap).forEach(b => {
        b.classList.remove("is-active");
        b.setAttribute("aria-checked", "false");
      });
      btn.classList.add("is-active");
      btn.setAttribute("aria-checked", "true");
      state.styl = btn.dataset.style;
      onStyleChange();
    });
  }

  // -- 2. Renderowanie system swatch grid (zamiast dropdown) + typ + listener dla presetów
  function renderSystemTyp() {
    const sysSel = $("[data-knf-system]");
    const typSel = $("[data-knf-typ]");
    const sysGrid = $("[data-knf-sys-grid]");

    // Shadow select (form submit + LIP source of truth)
    sysSel.innerHTML = `<option value="">${escapeHtml(selectPh)}</option>` +
      cfg.rules.common.systems_ikea.map(s => `<option value="${escapeAttr(s)}">${escapeHtml(tSystem(s))}</option>`).join("");

    // Visual grid swatches - tylko nazwa systemu jako kafelek (Konrad: "bez zdjecia")
    if (sysGrid) {
      sysGrid.innerHTML = cfg.rules.common.systems_ikea.map(s => {
        const ht = tSysHelp(s) || s;
        const label = tSystem(s); // "NA WYMIAR" → "CUSTOM"/"NA MÍRU" w EN/CS
        return `
          <button type="button" class="knf__syschip" data-sys="${escapeAttr(s)}" role="radio" aria-checked="false" title="${escapeAttr(ht)}">
            <span class="knf__syschip-label">${escapeHtml(label)}</span>
          </button>
        `;
      }).join("");

      sysGrid.addEventListener("click", e => {
        const btn = e.target.closest(".knf__syschip");
        if (!btn) return;
        $$(".knf__syschip", sysGrid).forEach(b => { b.classList.remove("is-active"); b.setAttribute("aria-checked", "false"); });
        btn.classList.add("is-active");
        btn.setAttribute("aria-checked", "true");
        sysSel.value = btn.dataset.sys;
        // Update helptext widoczny pod gridem
        const sysHelpEl = $("[data-knf-sys-help]");
        if (sysHelpEl) sysHelpEl.textContent = tSysHelp(btn.dataset.sys);
        sysSel.dispatchEvent(new Event("change"));
      });
    }

    renderTypesForSystem("");
    sysSel.addEventListener("change", () => {
      // 10.2026 zestaw (Z4): typ zostaje, jesli istnieje w nowym systemie; inaczej „Drzwi” albo pierwszy typ pierwszej grupy.
      // W zestawie zmiana systemu nie dotyka zadnych wymiarow (lista ma wlasne pozycje), wiec incydent #1322 nie ma gdzie przeciec.
      const keepTyp = SET_MODE && sysSel.value !== "NA WYMIAR" ? typSel.value : "";
      // wer2 24.09: czysc typ PRZED przebudowa - inaczej renderTypesForSystem zachowywal go i podswietlal kafelek pustego typu
      if (sysSel.value !== "NA WYMIAR") typSel.value = "";
      $$(".knf__typchip.is-active").forEach(b => { b.classList.remove("is-active"); b.setAttribute("aria-checked", "false"); });   // 24.09: NA WYMIAR zostawial stary kafelek (podpis „NA WYMIAR, Szuflada”)
      renderTypesForSystem(sysSel.value);
      // Nie resetuj typu dla NA WYMIAR - renderTypesForSystem już ustawia "Front niestandardowy" auto
      if (sysSel.value !== "NA WYMIAR") typSel.value = "";
      if (SET_MODE && sysSel.value && sysSel.value !== "NA WYMIAR") {
        const opts = [...typSel.options].map(o => o.value).filter(Boolean);
        const pick = opts.includes(keepTyp) ? keepTyp : (opts.includes("Drzwi") ? "Drzwi" : opts[0] || "");
        if (pick) {
          typSel.value = pick;
          const tc = $(`.knf__typchip[data-typ="${cssEscape(pick)}"]`);
          if (tc) { tc.classList.add("is-active"); tc.setAttribute("aria-checked", "true"); const th = $("[data-knf-typ-help]"); if (th) th.textContent = tc.dataset.typHelp || ""; revealTypChip(); }
        }
      }
      renderDimensionPresets();
      // Sync system swatch active state (gdy zmiana z restoreState bypass click)
      if (sysGrid) {
        $$(".knf__syschip", sysGrid).forEach(b => {
          const isActive = b.dataset.sys === sysSel.value;
          b.classList.toggle("is-active", isActive);
          b.setAttribute("aria-checked", isActive ? "true" : "false");
        });
      }
    });
    typSel.addEventListener("change", () => {
      // 10.2026 zestaw: opcjonalne zawiasy i przedluzenie wracaja do pustych przy zmianie typu (uchwyt i sloje zostaja, TEMPO 5.2)
      if (SET_MODE) {
        const h = hingeSel();
        if (h && !h.disabled && !HINGE_REQ_STYLES.has(state.styl) && h.value) { h.value = ""; h.dataset.user = ""; h.dispatchEvent(new Event("change", { bubbles: true })); }
        const x = extSel();
        if (x && x.value) { x.value = ""; x.dispatchEvent(new Event("change", { bubbles: true })); }
      }
      renderDimensionPresets();
    });
  }

  // Renumeracja kroków po ukryciu/pokazaniu Krok 5 (NA WYMIAR) - alias dla renumberSteps (zdefiniowane niżej).
  // Zachowane jako wrapper żeby uniknąć NPE gdy wywołane przed inicjalizacją renumberSteps.
  function renumberVisibleSteps() { renumberSteps(); }

  // -- 2a. Filtruj typy elementu wg wybranego systemu.
  // Logika:
  //   - "Z presetami IKEA (SYSTEM)": typy które Globo skonfigurował dla tego systemu (klucze dimensions[system])
  //   - "Inne typy (wymiary ręcznie)": universal_types - Cargo/Listwa/Drzwi narożne/Front zmywarki - dostępne dla każdego systemu
  //   - Bez systemu: pokaż wszystkie typy, bez optgroup
  function renderTypesForSystem(system) {
    const typSel = $("[data-knf-typ]");      // shadow source-of-truth (form submit)
    const grid = $("[data-knf-typ-grid]");   // visual picker (button chips z ikonami)
    const dimsBySys = cfg.dimensions.dimensions_per_system_type || {};
    const sysDims = dimsBySys[system] || {};
    const universalTypes = cfg.rules.common.universal_types || [];
    const allTypes = cfg.rules.common.types_element || [];

    // 1. Buduj <select> (hidden) - zachowuje exactly tę samą strukturę optgroup co wcześniej (kompat z restoreState)
    let selHtml = `<option value="">${escapeHtml(selectPh)}</option>`;
    let groups = [];  // [{label, types}]

    // i18n inline (Shopify minifier dropped arrow-functions z object literali, więc tutaj wprost)
    const SECT_LABELS = {
      en: { with: `Fronts for ${system}`, no: `Custom sizes for ${system}`, other: "Other element types" },
      cs: { with: `Čela pro ${system}`, no: `Vlastní rozměry pro ${system}`, other: "Jiné typy prvků" },
      de: { with: `Fronten für ${system}`, no: `Sondermaße für ${system}`, other: "Andere Elementtypen" },
      pl: { with: `Fronty do ${system}`, no: `Wymiary niestandardowe dla ${system}`, other: "Inne typy elementów" },
    };
    const SL = SECT_LABELS[locale] || SECT_LABELS.pl;
    const lblWith = SL.with;
    const lblNo   = SL.no;
    const lblOther = SL.other;

    // Krok 5 - różne zachowania:
    //  - brak systemu: placeholder "wybierz system najpierw"
    //  - NA WYMIAR: cały fieldset UKRYTY (no clutter), typ auto-set + renumeracja kroków 6→5, 7→6
    //  - inne systemy: standardowa siatka typów
    const fsTyp = grid?.closest(".knf__fs");
    if (!system || system === "NA WYMIAR") {
      if (system === "NA WYMIAR") {
        // Auto-set typ żeby canAddToCart przeszedł
        typSel.innerHTML = '<option value="Front niestandardowy" selected>Front niestandardowy</option>';
        typSel.value = "Front niestandardowy";
        // UKRYJ cały fieldset - żadnego "typ elementu pomijany" copy
        if (fsTyp) fsTyp.style.display = "none";
        // Wyczyść stary preset label (np. "40cm x 60cm") - dla NA WYMIAR nie chcemy "Wymiar IKEA: 40cm x 60cm" w cart property
        const dlbl = $("[data-knf-dim-label]");
        if (dlbl) dlbl.value = "";
        // Trigger renderDimensionPresets żeby otworzył manual entry mode w Krok 6
        typSel.dispatchEvent(new Event("change"));
      } else {
        typSel.innerHTML = `<option value="">${escapeHtml(selectPh)}</option>`;
        if (grid) {
          const FIRST_PICK_SYS = { en: "Select IKEA system above first.", cs: "Nejprve vyberte systém IKEA výše.", de: "Bitte zuerst IKEA-System oben auswählen.", pl: "Najpierw wybierz system IKEA powyżej." };
          grid.innerHTML = `<p class="knf__muted" style="margin:0;text-align:center">${escapeHtml(FIRST_PICK_SYS[locale] || FIRST_PICK_SYS.pl)}</p>`;
        }
        if (fsTyp) {
          fsTyp.style.display = "";
          fsTyp.classList.add("knf__fs--placeholder");
        }
      }
      renumberVisibleSteps();
      return;
    }
    if (fsTyp) {
      fsTyp.style.display = "";
      fsTyp.classList.remove("knf__fs--placeholder");
    }
    renumberVisibleSteps();
    {
      let presetTypes = Object.keys(sysDims).filter(t => Array.isArray(sysDims[t]) && sysDims[t].length > 0);
      // Panel gięty ślepy R150: dostępny wyłącznie dla stylu lakierowanego gładkiego ("Gładki").
      // Na razie ukryty dla wszystkich stylów (17.09.2026) - przywrócenie: PANEL_HIDDEN = false.
      if (PANEL_HIDDEN || state.styl !== "Gładki") presetTypes = presetTypes.filter(t => t !== PANEL_TYPE);
      // allowed_types per styl (styles_config): styl może ograniczać typy elementów, np. Witryna tylko "Drzwi".
      const styleObjTypes = (cfg.styles.styles || []).find(s => s.name === state.styl);
      const allowedTypes = styleObjTypes && Array.isArray(styleObjTypes.allowed_types) && styleObjTypes.allowed_types.length ? styleObjTypes.allowed_types : null;
      if (allowedTypes) presetTypes = presetTypes.filter(t => allowedTypes.includes(t));
      // 10.2026 zestaw: PAX + „Nadstawka” po filtrze stylu (C2). UX v2 (decyzja Konrada 03.10): METOD „Drzwi narożne” wraca jako zwykly typ, bez karty „Narożnik”
      if (SET_MODE && presetTypes.length) {
        if (CORNER_CARDS_ON && system === "METOD" && presetTypes.includes("Drzwi narożne")) presetTypes = presetTypes.filter(t => t !== "Drzwi narożne").concat(CORNER_TYP);
        if (system === "PAX" && NAD_OK) presetTypes = presetTypes.concat(NAD_TYPE);
      }
      if (presetTypes.length) {
        selHtml += `<optgroup label="${escapeAttr(lblWith)}">` + presetTypes.map(t => `<option value="${escapeAttr(t)}">${escapeHtml(tType(t))}</option>`).join("") + `</optgroup>`;
        groups.push({ label: lblWith, types: presetTypes });
      } else {
        // System bez presetów (np. NA WYMIAR) → pełna lista typów z manual entry
        let fallback = system === "NA WYMIAR"
          ? ["Drzwi", "Szuflada", "Panele boczne", "Cokoły", "Front zmywarki bez nawiertów", "Cokół", "Cargo", "Drzwi narożne"]
          : ["Drzwi", "Szuflada", "Panele boczne", "Cokoły"];
        if (allowedTypes) fallback = fallback.filter(t => allowedTypes.includes(t));
        selHtml += `<optgroup label="${escapeAttr(lblNo)}">` + fallback.map(t => `<option value="${escapeAttr(t)}">${escapeHtml(tType(t))}</option>`).join("") + `</optgroup>`;
        groups.push({ label: lblNo, types: fallback });
      }
      // Universal types REMOVED - Globo per-system pokazuje TYLKO typy które IKEA produkuje dla danego systemu.
      // (Wcześniej PLATSA pokazywało "Cargo/Front zmywarki/Listwa/Drzwi narożne" co nie pasuje do PLATSA.)
    }
    // 24.09.2026 (audyt): zmiana stylu na METOD i przywracanie stanu kasowaly wybrany typ - przebudowa
    // listy zerowala select. Zmiana SYSTEMU nadal czysci typ jawnie (handler sysSel).
    const prevTyp = typSel.value;
    typSel.innerHTML = selHtml;
    if (prevTyp && [...typSel.options].some(o => o.value === prevTyp)) typSel.value = prevTyp;

    // 2. Buduj visual grid - 1 sekcja per group, każdy chip z ikona + label (runda 3: uklad jak na live, ikony z kompletu knf-ico-*, w zestawie licznik sztuk)
    if (!grid) return;
    const chipHtml = t => {
      // Pobierz helptext per typ z dimensions JSON (pierwszy preset ma _typ_helptext meta).
      // Wariant per-locale z metafielda (_typ_helptext_de itd.) ma priorytet;
      // fallback: tTypHelp (mapa TYP_HELP_DE / template EN/CS) na surowym PL.
      const sysDimsX = (cfg.dimensions.dimensions_per_system_type || {})[system] || {};
      const presetsForT = sysDimsX[t] || [];
      const typHelpLoc = (locale !== "pl" && presetsForT[0]?.[`_typ_helptext_${locale}`]) || "";
      const typHelpRaw = presetsForT[0]?._typ_helptext || "";
      const typHelp = t === CORNER_TYP ? sx("T_TYPE_CORNER_HELP") : t === NAD_TYPE ? sx("HELP_NAD") : (typHelpLoc || tTypHelp(typHelpRaw, system, t));
      const tDisplay = typChipLabel(system, t);
      // Typy które są nazwami międzynarodowymi/własnymi (Cargo = IKEA hardware, nie tłumaczyć).
      // Reszta opisowa polska (Szuflada/Drzwi/Cokoły) tłumaczona przez Chrome i tType().
      const typTrAttr = t === "Cargo" ? ' translate="no"' : '';
      return `
          <button type="button" class="knf__typchip" data-typ="${escapeAttr(t)}" data-typ-help="${escapeAttr(typHelp)}" role="radio" aria-checked="false" title="${escapeAttr(typHelp || tDisplay)}">
            <span class="knf__typchip-ico" aria-hidden="true">${icoImg(system, t, "knf__typchip-img")}</span>
            <span class="knf__typchip-label"${typTrAttr}>${escapeHtml(tDisplay)}</span>${SET_MODE ? '<span class="knf__typchip-n" data-knf-typ-n></span>' : ""}
          </button>`;
    };
    grid.innerHTML = groups.map(g => `
      ${g.label ? `<p class="knf__typgrid-section">${escapeHtml(g.label)}</p>` : ""}
      <div class="knf__typgrid-row">${g.types.map(chipHtml).join("")}
      </div>
    `).join("");
    if (SET_MODE) updateTypCounts();

    // 3. Click handler - picker → ustaw shadow select + dispatch change + helptext.
    // Guard: renderTypesForSystem woła się przy każdej zmianie systemu, a grid to trwały
    // element - bez guarda handlery się kumulowały (N zmian systemu = N× dispatch change).
    if (!grid.dataset.knfTypBound) {
      grid.dataset.knfTypBound = "1";
      grid.addEventListener("click", e => {
        const btn = e.target.closest(".knf__typchip");
        if (!btn) return;
        $$(".knf__typchip", grid).forEach(b => { b.classList.remove("is-active"); b.setAttribute("aria-checked", "false"); });
        btn.classList.add("is-active");
        btn.setAttribute("aria-checked", "true");
        typSel.value = btn.dataset.typ;
        // Update typ helptext: pierwszy preset z _typ_helptext flagą lub data-typ-help atrybut
        const typHelpEl = $("[data-knf-typ-help]");
        if (typHelpEl) typHelpEl.textContent = btn.dataset.typHelp || "";
        typSel.dispatchEvent(new Event("change"));
        revealTypChip();
      });
    }

    // 4. Sync: jeśli typSel ma już value (np. po restore), aktywuj chip
    if (typSel.value) {
      const active = grid.querySelector(`.knf__typchip[data-typ="${cssEscape(typSel.value)}"]`);
      if (active) { active.classList.add("is-active"); active.setAttribute("aria-checked", "true"); }
    }
  }

  // -- 2a'. Grubość frontu (Krok wymiarów) - tylko dla NA WYMIAR. Select wypełniany raz, widoczność per system.
  function renderThickness() {
    const wrap = $("[data-knf-thickness]");
    if (!wrap) return;
    const TI = THICKNESS_I18N[locale] || THICKNESS_I18N.pl;
    if (!state.grubosc) state.grubosc = "18 mm";
    wrap.innerHTML = `
      <label class="knf__label" for="knf-grubosc">${escapeHtml(TI.label)}</label>
      <select id="knf-grubosc" class="knf__select" name="properties[Grubość frontu]" data-knf-grubosc disabled></select>`;
    const sel = $("[data-knf-grubosc]", wrap);
    syncThicknessStd();
    sel.addEventListener("change", () => {
      state.grubosc = sel.value;
      updateM2Display();
      tryUpdate();
    });
  }

  // 25.09 (Konrad): w NA WYMIAR opcja standardowa = grubosc stylu (Linea i Hampton 19 mm, reszta 18).
  // 04.10.2026 (Konrad): 16 mm z dopłatą +10% jak 30 mm (NA WYMIAR = „Front niestandardowy”, 12 mm zostaje jak na live)
  function stdGrub() { return frontThicknessMm(state.styl) === 19 ? "19 mm" : "18 mm"; }
  function syncThicknessStd() {
    const sel = $("[data-knf-grubosc]"); if (!sel) return;
    const std = stdGrub(), opts = ["12 mm", "16 mm", std, "30 mm"];
    const html = opts.map(v => `<option value="${v}"${v === std ? " data-std" : ""}>${escapeHtml(thickLbl(v, std))}</option>`).join("");
    if (sel.dataset.sig !== html) { sel.dataset.sig = html; sel.innerHTML = html; }
    if (!opts.includes(state.grubosc) || ["18 mm", "19 mm"].includes(state.grubosc)) state.grubosc = std;
    if (sel.value !== state.grubosc) sel.value = state.grubosc;
  }
  function updateThicknessVisibility(sys) {
    const wrap = $("[data-knf-thickness]");
    if (!wrap) return;
    const sel = $("[data-knf-grubosc]");
    const isCustom = sys === "NA WYMIAR";
    wrap.hidden = !isCustom;
    if (sel) {
      sel.disabled = !isCustom;  // disabled = nie trafia do FormData → property tylko dla NA WYMIAR
      if (!isCustom) { syncThicknessStd(); sel.value = stdGrub(); state.grubosc = stdGrub(); }
      else syncThicknessStd();
    }
  }

  // -- 2b. Render wymiarów IKEA jako presetów po wybraniu system + typ
  function renderDimensionPresets() {
    const sys = $("[data-knf-system]").value;
    const typ = $("[data-knf-typ]").value;
    updateThicknessVisibility(sys);
    // Zmiana System/Typ unieważnia wybrany preset: wyczyść wymiary.
    // Bez tego stare wymiary przeciekały do innego typu
    // (incydent #1322: "front zmywarki 25×80" po zakresie 0.50 = stary preset narożny).
    // 10.2026: mnoznik kompletu A+B (isBundlePreset) usuniety - narożnik to osobne pozycje zestawu (TEMPO 3).
    if (state.szer || state.wys) {
      state.szer = null;
      state.wys = null;
      const inSzerReset = $("[data-knf-szer]");
      const inWysReset = $("[data-knf-wys]");
      if (inSzerReset) inSzerReset.value = "";
      if (inWysReset) inWysReset.value = "";
      const lblReset = $("[data-knf-dim-label]");
      if (lblReset) lblReset.value = "";
      $$(".knf__preset").forEach(b => b.classList.remove("is-active"));
      updateM2Display();
      tryUpdate();
    }
    // Panel gięty ślepy: tylko lakier jednostronny - wymuś stronę (jedyny istniejący wariant).
    // Sam select zdejmie renderStrona() przy najbliższym tryUpdate().
    if (typ === PANEL_TYPE) state.strona = "Jednostronnie";
    const presetsWrap = $("[data-knf-presets]");
    const manualWrap = $("[data-knf-dims-manual]");
    const hint = $("[data-knf-dim-hint]");
    const dimsBySys = cfg.dimensions.dimensions_per_system_type || {};
    // Limit transportowy (conditional_rules.common.max_*): czego nie wyślemy, tego nie pokazujemy.
    // Bez filtra preset byłby klikalny, a walidacja i tak zablokowałaby koszyk.
    const cmn = cfg.rules.common;
    const presets = ((dimsBySys[sys] || {})[typ] || [])
      .filter(p => p.szer_mm <= cmn.max_szerokosc_mm && p.wys_mm <= cmn.max_wysokosc_mm);

    if (!sys || !typ) {
      presetsWrap.hidden = true;
      manualWrap.hidden = true;
      // Dynamiczny komunikat - pokazuj tylko brakujące pola, nie wszystkie
      const HINTS = {
        en: { both: "Select IKEA system and Element type first to see available dimensions.", sys: "Select IKEA system to see available dimensions.", typ: "Select Element type to see available dimensions." },
        cs: { both: "Nejprve vyberte Systém IKEA a Typ prvku, abyste viděli dostupné rozměry.", sys: "Vyberte Systém IKEA, abyste viděli dostupné rozměry.", typ: "Vyberte Typ prvku, abyste viděli dostupné rozměry." },
        de: { both: "Bitte zuerst IKEA-System und Elementtyp wählen, um verfügbare Maße zu sehen.", sys: "Bitte IKEA-System wählen, um verfügbare Maße zu sehen.", typ: "Bitte Elementtyp wählen, um verfügbare Maße zu sehen." },
        pl: { both: "Wybierz najpierw System IKEA i Typ elementu, by zobaczyć dostępne wymiary.", sys: "Wybierz System IKEA, by zobaczyć dostępne wymiary.", typ: "Wybierz Typ elementu, by zobaczyć dostępne wymiary." },
      };
      const H = HINTS[locale] || HINTS.pl;
      if (!sys && !typ) {
        hint.textContent = H.both;
      } else if (!sys) {
        hint.textContent = H.sys;
      } else {
        hint.textContent = H.typ;
      }
      hint.hidden = false;
      return;
    }
    // Zestaw (TEMPO 2.3/8.2): kafelek = +1 szt., karty narożnika i nadstawki, „Inny wymiar” z przyciskiem Dodaj
    if (SET_MODE) { renderTiles(); return; }

    if (presets.length === 0) {
      presetsWrap.hidden = true;
      manualWrap.hidden = false;
      // Manual entry - inputs editable
      $("[data-knf-szer]").readOnly = false;
      $("[data-knf-wys]").readOnly = false;
      $("[data-knf-szer]").classList.remove("knf__input--locked");
      $("[data-knf-wys]").classList.remove("knf__input--locked");
      // Dla NA WYMIAR - schowaj hint, inputy mm są self-explanatory (UX: don't tell what's obvious)
      if (sys === "NA WYMIAR") {
        hint.hidden = true;
      } else {
        const MANUAL_HINT = { en: "Enter front dimensions manually (in mm).", cs: "Zadejte rozměry čela ručně (v mm).", de: "Maße der Front manuell eingeben (in mm).", pl: "Wpisz wymiary frontu ręcznie (w mm)." };
        hint.textContent = MANUAL_HINT[locale] || MANUAL_HINT.pl;
        hint.hidden = false;
      }
      return;
    }

    hint.hidden = true;  // ZAWSZE ukryj gdy są presets do wyboru
    presetsWrap.hidden = false;
    manualWrap.hidden = true;  // ukryj manual inputs do czasu wyboru presetu
    // Live price calc: ceil(szer*wys/1e6 * 20)/20 * pricePerM2 (per styl, group string -> int)
    const styleObj = cfg.styles.styles.find(s => s.name === state.styl);
    const pricePerM2 = parseInt(styleObj?.group, 10) || 0;

    // Sortuj po szer_mm, potem wys_mm - żeby buttony tej samej szerokości były obok siebie
    const sortedPresets = [...presets].sort((a, b) => (a.szer_mm - b.szer_mm) || (a.wys_mm - b.wys_mm));

    // wer2 24.09: dopiski paneli BESTÅ sa w metafieldzie tylko po polsku - tlumaczymy tekst kafelka (data-label zostaje PL dla Pro-Hand)
    const PRESET_WORDS = {
      en: [["Panel Boczny", "Side panel"], ["Panel Górny (boki 12 mm)", "Top panel (12 mm sides)"], ["Panel Górny (boki 18 mm)", "Top panel (18 mm sides)"]],
      de: [["Panel Boczny", "Seitenpaneel"], ["Panel Górny (boki 12 mm)", "Oberpaneel (Seiten 12 mm)"], ["Panel Górny (boki 18 mm)", "Oberpaneel (Seiten 18 mm)"]],
      cs: [["Panel Boczny", "Boční panel"], ["Panel Górny (boki 12 mm)", "Horní panel (boky 12 mm)"], ["Panel Górny (boki 18 mm)", "Horní panel (boky 18 mm)"]],
    };
    const tPresetLabel = lbl => (PRESET_WORDS[locale] || []).reduce((a, [pl, tr]) => a.replace(pl, tr), lbl || "");
    let lastSzer = null;
    presetsWrap.innerHTML = sortedPresets.map(p => {
      const pLbl = tPresetLabel(p.label);
      const m2 = Math.max(0.10, Math.ceil((p.szer_mm * p.wys_mm / 1_000_000) * 20) / 20);
      let cena, tip;
      if (typ === PANEL_TYPE) {
        // Panel gięty: cena stała z dedykowanego wariantu (per wysokość), bez m².
        const pv = panelVariantFor(p.wys_mm);
        cena = pv ? formatPrice(pv.price, pv.option1) : '';
        tip = cena ? `${pLbl} → ${cena}` : pLbl;
      } else {
        const hintCents = Math.round(m2 * pricePerM2 * 100);
        cena = pricePerM2 ? (PROMO ? formatPricePromo(hintCents, 1, state.styl) : formatPricePLN(hintCents, state.styl)) : '';
        tip = cena ? `${pLbl} - ${formatM2(m2)} → ${cena}` : pLbl;
      }
      const mmLine = `${p.szer_mm}×${p.wys_mm} mm`;
      const dataLabel = p.label;
      const breakBefore = lastSzer !== null && p.szer_mm !== lastSzer;
      lastSzer = p.szer_mm;
      const isBestseller = !!p.bestseller;
      return (breakBefore ? '<div class="knf__preset-rowbreak"></div>' : '') + `
      <button type="button" class="knf__preset${isBestseller ? ' knf__preset--bestseller' : ''}" data-szer-mm="${p.szer_mm}" data-wys-mm="${p.wys_mm}" data-label="${escapeAttr(dataLabel)}" title="${escapeAttr(tip)}" aria-label="${escapeAttr(tip + (isBestseller ? ' (bestseller)' : ''))}">
        ${isBestseller ? '<span class="knf__preset-badge" aria-hidden="true">BESTSELLER</span>' : ''}
        <span class="knf__preset-label">${escapeHtml(pLbl)}</span>
        <span class="knf__preset-mm">${escapeHtml(mmLine)}</span>
      </button>
    `;}).join("") + '<div class="knf__preset-rowbreak"></div>' + (() => {
      const CUSTOM_DIM = {
        en: { label: "Custom dimension", mm: "enter in mm", title: "Type your own dimensions in fields below (mm)", aria: "Custom dimension - enter manually" },
        cs: { label: "Vlastní rozměr", mm: "zadejte v mm", title: "Zadejte vlastní rozměry v polích níže (mm)", aria: "Vlastní rozměr - zadejte ručně" },
        de: { label: "Sondermaß", mm: "in mm eingeben", title: "Eigene Maße in den Feldern unten eingeben (mm)", aria: "Sondermaß - manuell eingeben" },
        pl: { label: "Inny wymiar", mm: "wpisz w mm", title: "Wpisz własne wymiary w polach poniżej (mm)", aria: "Inny wymiar - wpisz ręcznie" },
      };
      const CD = CUSTOM_DIM[locale] || CUSTOM_DIM.pl;
      return `
      <button type="button" class="knf__preset knf__preset--custom" data-custom="1" title="${escapeAttr(CD.title)}" aria-label="${escapeAttr(CD.aria)}">
        <span class="knf__preset-label">${escapeHtml(CD.label)}</span>
        <span class="knf__preset-mm">${escapeHtml(CD.mm)}</span>
      </button>
    `;})();

    presetsWrap.addEventListener("click", onPresetPick, { once: false });
  }

  function onPresetPick(e) {
    const btn = e.target.closest(".knf__preset");
    if (!btn) return;
    $$(".knf__preset").forEach(b => b.classList.remove("is-active"));
    btn.classList.add("is-active");

    const manualWrap = $("[data-knf-dims-manual]");
    const inSzer = $("[data-knf-szer]");
    const inWys = $("[data-knf-wys]");
    const labelOut = $("[data-knf-dim-label]");

    if (btn.dataset.custom) {
      // Niestandardowy - odblokuj inputy do manual wpisania
      manualWrap.hidden = false;
      inSzer.readOnly = false;
      inWys.readOnly = false;
      inSzer.classList.remove("knf__input--locked");
      inWys.classList.remove("knf__input--locked");
      labelOut.value = "Niestandardowy";
      inSzer.value = "";
      inWys.value = "";
      state.szer = null;
      state.wys = null;
      updateM2Display();
      tryUpdate();
      inSzer.focus();
    } else {
      // Preset wybrany - wartości fixed, inputs readonly (klient widzi szer/wys ale nie zmienia)
      manualWrap.hidden = false;
      inSzer.readOnly = true;
      inWys.readOnly = true;
      inSzer.classList.add("knf__input--locked");
      inWys.classList.add("knf__input--locked");
      const szer = parseInt(btn.dataset.szerMm, 10);
      const wys = parseInt(btn.dataset.wysMm, 10);
      inSzer.value = szer;
      inWys.value = wys;
      labelOut.value = btn.dataset.label;
      state.szer = szer;
      state.wys = wys;
      updateM2Display();
      tryUpdate();
    }
  }

  // -- 3. Wymiary + m² calc
  function bindDims() {
    const inSzer = $("[data-knf-szer]");
    const inWys = $("[data-knf-wys]");
    const onChange = () => {
      state.szer = parseInt(inSzer.value, 10) || null;
      state.wys = parseInt(inWys.value, 10) || null;
      updateM2Display();
      tryUpdate();
    };
    inSzer.addEventListener("input", onChange);
    inWys.addEventListener("input", onChange);
  }

  // Klucze/wartości dopłat - MUSZĄ zgadzać się 1:1 z labelami w metafieldzie conditional_rules.
  // Rozjazd = mnożnik się nie włączy i klient płaci mniej. Asercja w assertCondKeysMatchRules().
  const COND_KEY_SLOJE = "Słoje przechodzące między frontami (+20%)";
  const COND_KEY_WYKONCZENIE = "Wykończenie";
  const COND_VAL_POLYSK = "Połysk (+15%)";

  // Jednorazowa diagnostyka przy boot: czy metafield conditional_rules nadal zawiera
  // dokładnie te labele/wartości, od których zależą dopłaty w calcM2.
  function assertCondKeysMatchRules() {
    const defs = (cfg && cfg.rules && cfg.rules.definitions) || {};
    const labels = Object.values(defs).map(d => d.label);
    if (!labels.includes(COND_KEY_SLOJE)) {
      console.warn(`[knf] UWAGA: brak conditionala "${COND_KEY_SLOJE}" w metafieldzie - dopłata +20% słoje nie naliczy się`);
    }
    const wyk = Object.values(defs).find(d => d.label === COND_KEY_WYKONCZENIE);
    if (wyk && !(wyk.values || []).includes(COND_VAL_POLYSK)) {
      console.warn(`[knf] UWAGA: wartość "${COND_VAL_POLYSK}" nie występuje w conditionalu "${COND_KEY_WYKONCZENIE}" - dopłata +15% połysk nie naliczy się`);
    }
  }

  // 10.2026 (TEMPO 6.2): ta sama arytmetyka z parametrami - liczy kafelki i pozycje zestawu; calcM2() = nakladka na stan.
  function calcM2() {
    const sys = $("[data-knf-system]")?.value || "";
    const g = SET_MODE && !state.sample_mode && sys !== "NA WYMIAR" ? adderGrub(sys, $("[data-knf-typ]")?.value || "") : null;
    return calcM2For(state.szer, state.wys, state.conditional, g || state.grubosc);
  }
  function calcM2For(szer, wys, condIn, grubosc) {
    if (!szer || !wys) return null;
    const exact = (szer * wys) / 1_000_000;
    // Precyzja 0.05 m² (zamiast 0.1) - 38 zakresów (0.10 → 1.95)
    const billed = Math.max(0.10, Math.ceil(exact * 20) / 20);
    // Mnożniki dopłat: Słoje przechodzące +20%, Połysk +15%, grubość 16 i 30 mm +10%
    const cond = condIn || {};
    const slojeOn = cond[COND_KEY_SLOJE] === "Tak";
    const polyskOn = cond[COND_KEY_WYKONCZENIE] === COND_VAL_POLYSK;
    const gruboscOn = grubosc === "30 mm" || grubosc === "16 mm";  // NA WYMIAR i grubosc per element (04.10.2026); 12 mm i standard bez doplaty
    // 04.10.2026 (Konrad, decyzja 1): grubosc +10% liczona od powierzchni PRZED zaokragleniem do 0,05 m² (mala blenda nie skacze o caly prog);
    // Słoje i Połysk jak dotad od powierzchni rozliczanej
    const billedG = gruboscOn ? Math.max(0.10, Math.ceil(exact * 1.10 * 20 - 1e-9) / 20) : billed;
    let multiplier = 1.0;
    if (slojeOn) multiplier *= 1.20;
    if (polyskOn) multiplier *= 1.15;
    // BEZ capa do 2.0 - jeśli billed×multiplier przekracza 2.0 m², findVariant nie znajdzie
    // wariantu, canAddToCart blokuje, a warning "powierzchnia > 2.0 m² + kontakt" się pokazuje.
    // (Cap ukrywał przekroczenie: klient płacił za 2.0 m² zamiast np. 2.28 m² - cichy niedobór.)
    const billedForVariant = multiplier > 1.0
      ? Math.max(0.10, Math.ceil(billedG * multiplier * 20) / 20)
      : billedG;
    if (gruboscOn) multiplier *= 1.10;   // do opisu doplat (updateM2Display), nie do liczenia progu
    return { exact, billed, billedForVariant, slojeOn, polyskOn, gruboscOn, gruboscHit: billedG > billed, multiplier };
  }

  function updateM2Display() {
    const m2 = calcM2();
    const elActual = $("[data-knf-m2-actual]");
    const elBilled = $("[data-knf-m2-billed]");
    const warn = $("[data-knf-dim-warn]");

    if (!m2) {
      elActual.textContent = "-";
      elBilled.textContent = "-";
      warn.hidden = true;
      return;
    }

    // Panel gięty: rozliczany per wysokość, nie per m² - pokaż realne m² ale bez "rozliczane jako"
    if (isPanelTyp()) {
      elActual.textContent = formatM2(m2.exact, 3);
      elBilled.textContent = "-";
      warn.hidden = true;
      return;
    }

    elActual.textContent = formatM2(m2.exact, 3);
    // Pokazuj dopłaty (+20% słoje / +15% połysk / +10% grubość) gdy aktywne i zmieniają próg (grubość liczona przed zaokrągleniem może się zmieścić w tym samym)
    if (m2.multiplier > 1.0 && m2.billedForVariant > m2.billed) {
      const parts = [formatM2(m2.billed)];
      if (m2.slojeOn) parts.push(LM("sloje"));
      if (m2.polyskOn) parts.push(LM("polysk"));
      if (m2.gruboscHit) parts.push(LM("grubosc"));   // tylko gdy grubosc zmienia prog
      parts.push(`= ${formatM2(m2.billedForVariant)}`);
      elBilled.textContent = parts.join(" ");
    } else {
      elBilled.textContent = formatM2(m2.billed);
    }

    const c = cfg.rules.common;
    const overWidth = state.szer && (state.szer < c.min_szerokosc_mm || state.szer > c.max_szerokosc_mm);
    const overHeight = state.wys && (state.wys < c.min_wysokosc_mm || state.wys > c.max_wysokosc_mm);
    // overM2 sprawdza billedForVariant (z +20% jeśli słoje) - próg = największy REALNY koszyk wariantu
    const overM2 = m2.billedForVariant > MAX_M2_BUCKET;

    if (overM2) {
      warn.hidden = false;
      const extras = [];
      if (m2.slojeOn) extras.push(LM("sloje"));
      if (m2.polyskOn) extras.push(LM("polysk"));
      if (m2.gruboscHit) extras.push(LM("grubosc"));
      const reason = extras.length ? ` (${LM("after")} ${extras.join(", ")} = ${m2.billedForVariant.toFixed(2)} m²)` : "";
      warn.innerHTML = `${LMax("areaOver")}${reason}. ${LM("contact")}: <a href="/pages/contact">kontakt@ogony.pl</a>`;
    } else if (overWidth || overHeight) {
      warn.hidden = false;
      warn.textContent = `${LM("dimRange")} (${LM("widthW")} ${c.min_szerokosc_mm}-${c.max_szerokosc_mm} mm, ${LM("heightW")} ${c.min_wysokosc_mm}-${c.max_wysokosc_mm} mm).`;
    } else {
      warn.hidden = true;
    }
  }

  // -- 4. Strona lakierowania
  // Lakierowanie jedno/dwustronne jako opcja stylu (select w siatce "Opcje stylu", obok
  // Wykończenia i Kierunku usłojenia). Dopłata +440 zł/m² dotyczy WYŁĄCZNIE stylów lakierowanych -
  // fornir i sklejka mają drugą stronę w standardzie, a panel gięty ślepy ma tylko wariant
  // jednostronny. Poza lakierem select znika i stan twardo wraca na "Jednostronnie", żeby
  // findVariant() nie szukał wariantu, którego klient nie może już wybrać.
  //
  // Widget dokładamy z JS, bo renderConditional() przepisuje innerHTML kontenera przy każdej
  // zmianie stylu - statyczny markup z sekcji zostałby skasowany.
  function renderStrona() {
    const fsCond = $("[data-knf-fs-conditional]");
    const wrap = $("[data-knf-conditional]");
    if (!fsCond || !wrap) return;
    const styleObj = cfg.styles.styles.find(s => s.name === state.styl);
    const isLakier = ((styleObj && styleObj.category) || "lakier") === "lakier";
    const show = !!state.styl && isLakier && !isPanelTyp() && !state.sample_mode;
    let col = $("[data-knf-strona-col]");

    if (!show) {
      if (col) col.remove();
      if (state.strona !== "Jednostronnie") state.strona = "Jednostronnie";
      // fsCond.hidden zostawiamy tak, jak ustawił renderConditional - o widoczności
      // decydują wtedy wyłącznie pozostałe opcje stylu.
      return;
    }

    if (!col) {
      col = document.createElement("div");
      col.className = "knf__col";
      col.setAttribute("data-knf-strona-col", "");
      col.innerHTML = `
        <label class="knf__label" for="knf-strona">${escapeHtml(L_STRONA.label)}</label>
        <select class="knf__select" id="knf-strona" data-knf-strona>
          <option value="Jednostronnie">${escapeHtml(L_STRONA.one)}</option>
          <option value="Dwustronnie">${escapeHtml(L_STRONA.two)}</option>
        </select>
        <small class="knf__cond-help" data-knf-strona-help></small>
      `;
      wrap.appendChild(col);
      col.querySelector("[data-knf-strona]").addEventListener("change", e => {
        state.strona = e.target.value;
        tryUpdate();
        if (SET_MODE && !recalcMute) recalcSet("strona");   // 10.2026: lista i ceny kafelkow (TEMPO 5.1)
      });
    }

    const sel = col.querySelector("[data-knf-strona]");
    if (sel.value !== state.strona) sel.value = state.strona;
    // Etap B (11.2026): dopłata w EUR zależy od kursu stylu - etykieta liczona przy każdej zmianie stylu
    if (getActiveCurrency(state.styl).code !== "PLN" && locale !== "cs") sel.options[1].textContent = `${L_STRONA.two.replace(/\s*\(.*\)$/, "")} (+${formatRatePerM2(440, state.styl)})`;
    const help = col.querySelector("[data-knf-strona-help]");
    if (help) help.textContent = state.strona === "Dwustronnie" ? L_STRONA.helpTwo : L_STRONA.helpOne;
    // Styl bez innych opcji warunkowych (np. Linea) ma ukryty fieldset - odkrywamy go dla samego lakierowania
    fsCond.hidden = false;
  }

  // Wykończenie „Pół-mat" jako jawna wartość domyślna (2026-07-30).
  // Powód: wybór Pół-mat / Połysk (+15%) mają tylko 3 style (Gładki, Grip, Frezowany uchwyt),
  // bo tylko one mają w conditional_rules opcję `polysk_polmat`. Przy pozostałych 11 lakierowanych
  // klient nie widział ŻADNEJ informacji o wykończeniu i o to często pyta - a zamówienie szło do
  // Pro-Hand bez tej pozycji. Dokładamy wiersz tylko do odczytu i wpisujemy wartość do stanu,
  // więc trafia też do właściwości koszyka („Opcje: Pół-mat · ...").
  // ŚWIADOMIE nie dodajemy tu wyboru Połysku: to, czy dany front da się zrobić w połysku,
  // jest decyzją produkcyjną, nie interfejsową. Gdyby miał być - wystarczy dopisać `polysk_polmat`
  // do reguł danego stylu w metafieldzie i ten wiersz sam zniknie na rzecz normalnego selecta.
  function renderFinishNote() {
    const fsCond = $("[data-knf-fs-conditional]");
    const wrap = $("[data-knf-conditional]");
    if (!fsCond || !wrap) return;
    const styleObj = cfg.styles.styles.find(s => s.name === state.styl);
    const isLakier = ((styleObj && styleObj.category) || "lakier") === "lakier";
    const maBoxWyboru = !!wrap.querySelector(`[data-knf-cond="${cssEscape(COND_KEY_WYKONCZENIE)}"]`);
    const show = !!state.styl && isLakier && !maBoxWyboru && !isPanelTyp() && !state.sample_mode;
    let col = $("[data-knf-finish-note]");

    if (!show) {
      if (col) col.remove();
      return;
    }
    // Etykieta i wartosc bierzemy z definicji w metafieldzie - zeby brzmialy identycznie
    // jak w stylach, ktore maja pelny wybor (i zeby tlumaczenia szly z jednego miejsca).
    const def = (cfg.rules.definitions && cfg.rules.definitions.polysk_polmat) || null;
    const valuesPL = (def && def.values) || ["Pół-mat"];
    const matPL = valuesPL.find(v => /^Pół-?mat$/i.test(v)) || valuesPL[0];
    const valuesLoc = (def && locale !== "pl" && def[`values_${locale}`]) || valuesPL;
    const matLoc = valuesLoc[valuesPL.indexOf(matPL)] || matPL;
    const label = def ? t(def, "label") : COND_KEY_WYKONCZENIE;
    const helptexts = (def && ((locale !== "pl" && def[`helptexts_${locale}`]) || def.helptexts)) || {};
    const help = helptexts[matPL] || "";

    // Wartosc do stanu ZAWSZE w wersji polskiej - calcM2() i properties porownuja stringi PL.
    state.conditional[COND_KEY_WYKONCZENIE] = matPL;

    if (!col) {
      col = document.createElement("div");
      col.className = "knf__col";
      col.setAttribute("data-knf-finish-note", "");
      wrap.appendChild(col);
    }
    col.innerHTML = `
      <span class="knf__label">${escapeHtml(label)}</span>
      <p class="knf__finish-note">${escapeHtml(matLoc)}</p>
      ${help ? `<small class="knf__cond-help">${escapeHtml(help)}</small>` : ""}
    `;
    fsCond.hidden = false;
  }

  // -- 5. Style change → kolor + conditional opcje
  let lastPaletteKolor = null;
  function onStyleChange() {
    const styleObj = cfg.styles.styles.find(s => s.name === state.styl);
    if (!styleObj) return;

    $("[data-knf-style-info]").textContent = t(styleObj, "helptext") || "";
    // Zapamiętaj custom kolor przed reset (klient wpisał F&B Borrowed Light, zmienia styl -
    // niech custom kolor zostanie, nie wraca do Biel)
    // wer2 24.09: styl drewniany nie ma palety (brak .knf__color) - kolor sprzed niego ma wrocic po powrocie na lakier
    const fromWood = !$('.knf__color');
    const wasCustom = !!$('.knf__color--custom.is-active') || (fromWood && !!state.custom_kolor);
    const prevCustomKolor = wasCustom ? state.custom_kolor : null;
    const prevCustomHex = wasCustom ? $('[data-knf-kolor-custom]')?.dataset.hex : "";
    if (state.kolor) lastPaletteKolor = state.kolor;
    const prevKolor = state.kolor || (fromWood ? lastPaletteKolor : null);   // 24.09 (audyt): zmiana stylu zachowuje kolor, jesli jest w nowej palecie
    // RESET PRZED renderColors - dla sklejki/forniru renderColors auto-ustawi kolor na label palette
    state.kolor = null;
    $("[data-knf-kolor-out]").value = "";
    recalcMute++;   // 10.2026: automatyczny wybor koloru ponizej nie liczy sie jako „zmiana koloru zestawu”
    try {
    renderColors(styleObj.category);
    renderConditional(state.styl);
    // Restore custom kolor jeśli klient miał wpisany F&B/RAL/NCS (paleta lakier ma custom_color_field)
    const customBtn = $('.knf__color--custom');
    if (prevCustomKolor && customBtn) {
      customBtn.dataset.knfNoFocus = "1";
      customBtn.click();
      delete customBtn.dataset.knfNoFocus;
      const customInp = $('[data-knf-kolor-custom]');
      if (customInp) {
        customInp.value = prevCustomKolor;
        if (prevCustomHex) customInp.dataset.hex = prevCustomHex;
        customInp.dispatchEvent(new Event("input"));
      }
    } else {
      // Auto-pick pierwszego koloru gdy paleta ma chips (parytet z Globo: zawsze default kolor)
      const sameColorBtn = prevKolor ? $(`.knf__color[data-color="${cssEscape(prevKolor)}"]`) : null;
      const firstColorBtn = sameColorBtn || $('.knf__color:not(.knf__color--custom)');
      if (firstColorBtn) firstColorBtn.click();
    }
    } finally { recalcMute--; }
    if (state.sample_mode) updateSampleFinishOptions(styleObj.name);
    renderSampleCta();
    refreshSampleDim();
    // Lista typow zalezy od stylu: panel giety (METOD, tylko Gładki) i allowed_types (np. Witryna tylko Drzwi).
    // wer2 24.09: wczesniej przebudowa tylko dla METOD, wiec Witryne dalo sie zamowic jako szuflade FAKTUM.
    const curSys = $("[data-knf-system]").value;
    if (curSys && curSys !== "NA WYMIAR") {
      const typSel = $("[data-knf-typ]");
      const prevT = typSel.value;
      renderTypesForSystem(curSys);
      if (prevT && !typSel.value) renderDimensionPresets();   // typ wypadl z listy nowego stylu
    }
    tryUpdate();
    // 10.2026 zestaw (D2, TEMPO 5.1): przeliczenie listy z komunikatem i ceny kafelkow zawsze (dzis zostawaly stare)
    if (SET_MODE && !state.sample_mode) { if (recalcMute) renderTiles(); else recalcSet("style"); }
  }

  // Pokazuje CTA próbki pod listą stylów po wybraniu stylu - wstawia nazwę stylu w tytule.
  // Ukryta gdy state.sample_mode (sekcja niepotrzebna w trybie próbki - banner robi swoje).
  function renderSampleCta() {
    const cta = $("[data-knf-sample-cta]");
    if (!cta) return;
    const styleSpan = $("[data-knf-sample-cta-style]");
    if (!state.styl || state.sample_mode) {
      cta.hidden = true;
      if (styleSpan) styleSpan.textContent = "";
      return;
    }
    cta.hidden = false;
    if (styleSpan) styleSpan.textContent = tStyle(state.styl);
  }

  // Odświeża wszystkie miejsca z wymiarem próbki wg wybranego stylu:
  // meta CTA pod stylami, blok read-only (tryb próbki) oraz hidden property do koszyka.
  function refreshSampleDim() {
    const dim = sampleDimMM(state.styl);
    $$("[data-knf-sample-cta-dim]").forEach(e => { e.textContent = dim; });
    const block = $("[data-knf-sample-dim-value]");
    if (block) block.textContent = dim;
    const dimProp = $("[data-knf-sample-dim-prop]");
    if (dimProp && state.sample_mode) dimProp.value = dim;
  }

  // Kolory aktualnie renderowanej palety - źródło danych dla karty szczegółu koloru
  let activePaletteColors = null;

  function renderColors(category) {
    const wrap = $("[data-knf-colors]");
    const customWrap = $("[data-knf-custom-color]");
    const palette = cfg.colors[category];
    activePaletteColors = (palette && palette.colors) || null;
    // Ukryj kartę szczegółu przy każdym re-renderze - auto-pick pierwszego koloru pokaże ją ponownie
    const detailCard = $("[data-knf-color-detail]");
    if (detailCard) detailCard.hidden = true;

    // Park customWrap POZA wrap (do fieldsetu) zanim wyczyścimy wrap.innerHTML - inaczej zginie
    // przy każdym re-renderze (po tym jak wcześniej .after() przeniósł go do środka wrap).
    if (customWrap && customWrap.parentElement === wrap) {
      const fs = wrap.closest(".knf__fs") || wrap.parentElement;
      if (fs) fs.appendChild(customWrap);
    }

    const inlineSpan = $("[data-knf-kolor-inline]");
    const NO_PALETTE_MSG = { en: "Color follows from selected style", cs: "Barva vyplývá z vybraného stylu", de: "Farbe ergibt sich aus dem gewählten Stil", pl: "Kolor wynika z wybranego stylu" };
    if (!palette || (!palette.colors.length && !palette.custom_color_field)) {
      const lbl = (palette && t(palette, "label")) || NO_PALETTE_MSG[locale] || NO_PALETTE_MSG.pl;
      const sub = palette ? t(palette, "subtitle") : "";
      // Wpisz tylko KRÓTKI label do legendy KROK 2 (inline obok "Kolor / wykończenie") - bez długiego opisu (zostaje w title hover)
      if (inlineSpan) {
        inlineSpan.innerHTML = `<span class="knf__legend-inline-sep">·</span><span class="knf__legend-inline-text">${escapeHtml(lbl)}</span>`;
        if (sub) inlineSpan.title = sub;
        inlineSpan.hidden = false;
      }
      wrap.innerHTML = "";
      wrap.style.display = "none";
      customWrap.hidden = true;
      $("[data-knf-kolor-out]").value = lbl;
      return;
    }
    // Reset gdy paleta ma kolory - pokazuj grid, ukryj inline
    if (inlineSpan) { inlineSpan.innerHTML = ""; inlineSpan.hidden = true; }
    wrap.style.display = "";

    wrap.innerHTML = palette.colors.map(c => {
      const cName = t(c, "name");
      const cHelp = t(c, "helptext");
      return `
      <button type="button" class="knf__color" data-color="${escapeAttr(c.name)}" style="--kclr: ${escapeAttr(c.hex)}" role="radio" aria-checked="false" aria-label="${escapeAttr(cName + (cHelp ? ' - ' + cHelp : ''))}" title="${escapeAttr(cHelp || cName)}">
        <span class="knf__color-swatch"></span>
        <span class="knf__color-text">
          <span class="knf__color-name">${escapeHtml(cName)}</span>
          ${cHelp ? `<span class="knf__color-help">${escapeHtml(cHelp)}</span>` : ''}
        </span>
      </button>
    `;}).join("");

    if (palette.custom_color_field) {
      const OTHER_COLOR = { en: "Any color", cs: "Libovolná barva", de: "Beliebige Farbe", pl: "Dowolny kolor" };
      const targetLbl = OTHER_COLOR[locale] || OTHER_COLOR.pl;
      let customLbl = t(palette, "custom_color_label") || targetLbl;
      // Lokalny override do czasu update metafieldu custom.colors_palette w Shopify:
      // 1) "i kod" → "lub kod" (wszystkie locale)  2) dorzucenie palety ICA  3) PL prefix → locale-aware
      customLbl = customLbl
        .replace(/\bnazwę i kod\b/i, 'nazwę lub kod')
        .replace(/\bName und Code eingeben\b/, 'Name oder Code eingeben')
        .replace(/\btype name and code\b/i, 'type name or code')
        .replace(/\bzadejte název a kód\b/i, 'zadejte název nebo kód')
        .replace('NCS / Farrow', 'NCS / ICA / Farrow')
        .replace('NCS, Farrow', 'NCS, ICA, Farrow')
        .replace(/^Inny kolor\b/, targetLbl)
        .replace(/^Other color\b/i, OTHER_COLOR.en)
        .replace(/^Jiná barva\b/i, OTHER_COLOR.cs);
      const BS_LABEL = { en: "BESTSELLER", cs: "BESTSELLER", de: "BESTSELLER", pl: "BESTSELLER" };
      const BS = BS_LABEL[locale] || BS_LABEL.pl;
      // Na kafelku krótki tytuł + podtytuł z paletami; pełny label zostaje w title/aria (a11y)
      wrap.insertAdjacentHTML("beforeend", `
        <button type="button" class="knf__color knf__color--custom" data-color="__custom__" title="${escapeAttr(customLbl)}" aria-label="${escapeAttr(customLbl)}">
          <span class="knf__color-swatch knf__color-swatch--custom">+</span>
          <span class="knf__color-text">
            <span class="knf__color-name">${escapeHtml(targetLbl)}</span>
            <span class="knf__color-custom-sub">RAL Classic / NCS Index / ICA / Farrow & Ball</span>
          </span>
          <span class="knf__color-badge">${escapeHtml(BS)}</span>
        </button>
      `);
      // Przenieś custom-color input zaraz po custom buttonie (żeby pojawiał się tuż pod nim, nie na końcu palety)
      const customBtn = wrap.querySelector(".knf__color--custom");
      const customInputWrap = $("[data-knf-custom-color]");
      // 24.09 (audyt): pole pod siatka, nie w srodku rzedu (na telefonie rzad jest przewijany w bok i pole mialo 26 px)
      if (customBtn && customInputWrap) wrap.after(customInputWrap);
    }

    wrap.addEventListener("click", onColorPick);
  }

  function onColorPick(e) {
    const btn = e.target.closest(".knf__color");
    if (!btn) return;
    $$(".knf__color").forEach(b => {
      b.classList.remove("is-active");
      b.setAttribute("aria-checked", "false");
    });
    btn.classList.add("is-active");
    btn.setAttribute("aria-checked", "true");

    if (btn.dataset.color === "__custom__") {
      const detailCard = $("[data-knf-color-detail]");
      if (detailCard) detailCard.hidden = true;
      $("[data-knf-custom-color]").hidden = false;
      const customInput = $("[data-knf-kolor-custom]");
      // Zero kolor-out na czas wpisywania - submit blokowany do momentu wpisania koloru
      $("[data-knf-kolor-out]").value = customInput.value || "";
      state.kolor = null;
      tryUpdate();
      // redesign 24.09: wybor z palety (knf-palety.js) nie otwiera klawiatury; nasluch dopinany raz (wczesniej przy kazdym kliknieciu)
      if (!btn.dataset.knfNoFocus) customInput.focus();
      if (!customInput.dataset.knfBound) {
        customInput.dataset.knfBound = "1";
        customInput.addEventListener("input", () => {
          state.custom_kolor = customInput.value;
          $("[data-knf-kolor-out]").value = customInput.value;
          tryUpdate();
        });
      }
    } else {
      $("[data-knf-custom-color]").hidden = true;
      // 24.09.2026 (audyt, krytyczny): wyczysc kolor wlasny - wczesniej zostawal w polu i wygrywal w koszyku
      const ci = $("[data-knf-kolor-custom]");
      if (ci) { ci.value = ""; delete ci.dataset.hex; }
      state.custom_kolor = null;
      state.kolor = btn.dataset.color;
      $("[data-knf-kolor-out]").value = btn.dataset.color;
      renderColorDetail(btn.dataset.color);
      tryUpdate();
    }
    // 10.2026 zestaw: kolor dotyczy calej listy - komunikat i miniatury (cena bez zmian)
    if (SET_MODE && !recalcMute && !state.sample_mode && state.set.length) recalcSet("color");
  }

  // Karta szczegółu koloru pod paletą - duży swatch + pochodzenie (Farrow & Ball / NCS)
  // + opis. Dane z metafieldu colors_palette: origin, code (numeracja wewnętrzna), badge (BODBYN).
  function renderColorDetail(colorName) {
    const wrap = $("[data-knf-colors]");
    if (!wrap) return;
    let card = $("[data-knf-color-detail]");
    if (!card) {
      card = document.createElement("div");
      card.className = "knf__color-detail";
      card.setAttribute("data-knf-color-detail", "");
      wrap.after(card);
    }
    const c = activePaletteColors && activePaletteColors.find(x => x.name === colorName);
    if (!c) { card.hidden = true; return; }
    const badge = c.badge ? `<span class="knf__color-detail-badge">${escapeHtml(t(c, "badge"))}</span>` : "";
    const origin = c.origin ? `<span class="knf__color-detail-origin">${escapeHtml(c.origin)}</span>` : "";
    const help = t(c, "helptext") ? `<span class="knf__color-detail-help">${escapeHtml(t(c, "helptext"))}</span>` : "";
    card.innerHTML = `
      <span class="knf__color-detail-swatch" style="--kclr: ${escapeAttr(c.hex)}" aria-hidden="true"></span>
      <span class="knf__color-detail-body">
        <strong class="knf__color-detail-name">${escapeHtml(t(c, "name"))}${badge}</strong>
        ${origin}
        ${help}
      </span>`;
    card.hidden = false;
  }

  // -- 6. Conditional opcje per styl (z reveal text input dla "Niestandardowa")
  function renderConditional(styleName) {
    const fs = $("[data-knf-fs-conditional]");
    const wrap = $("[data-knf-conditional]");
    // 10.2026 zestaw: opcje per element (E: uchwyt, zawiasy, slojenie) ida do „ustawien dodawanych elementow” nad kafelkami,
    // wspolne (G: wykonczenie, sloje przechodzace) zostaja w kroku 3. Z1: zawiasy dla kazdego stylu (styleOpts).
    const adderCond = SET_MODE ? $("[data-knf-adder-cond]") : null;
    const optKeys = styleOpts(styleName);
    if (!optKeys.length) {
      fs.hidden = true;
      wrap.innerHTML = "";
      if (adderCond) adderCond.innerHTML = "";
      state.conditional = {};
      return;
    }
    // Reset state przed renderem - zapobiega stale wartościom (np. "Wykończenie": "Połysk (+15%)") gdy nowy styl nie ma danej opcji.
    state.conditional = {};
    const gParts = [], eParts = [];
    optKeys.forEach(optKey => {
      const def = cfg.rules.definitions[optKey];
      if (!def) return;
      (adderCond && isELabel(def.label) ? eParts : gParts).push(condColHtml(def, styleName));
    });
    fs.hidden = !gParts.length;
    wrap.innerHTML = gParts.join("");
    if (adderCond) adderCond.innerHTML = eParts.join("");
    bindCondSelects(wrap, adderCond);
  }
  function condColHtml(def, styleName) {
    {
      // Lokalizowana etykieta + wartości (PL fallback jeśli brak _en)
      const displayLabel = t(def, "label");
      const valuesPL = def.values || [];
      const valuesLocal = (locale !== "pl" && def[`values_${locale}`]) ? def[`values_${locale}`] : valuesPL;
      // Wykończenie default → "Pół-mat" (cena bazowa). Klient nie powinien startować z dopłatą +15% Połysk.
      let effectiveDefault = def.default;
      if (def.label === "Wykończenie") {
        const matVal = valuesPL.find(v => /^Pół-mat$|^Półmat$/i.test(v));
        if (matVal) effectiveDefault = matVal;
      }
      // lista wartosci dla stylu (Round: rogi; pozostale style bez rogow) - indeksy wspolne dla wszystkich jezykow
      const idxs = def.perStyle && def.perStyle[styleName]
        ? def.perStyle[styleName].map(v => valuesPL.indexOf(v)).filter(i => i >= 0)
        : valuesPL.map((v, i) => i).filter(i => !(def.hideElse || []).includes(valuesPL[i]));
      if (effectiveDefault && !idxs.some(i => valuesPL[i] === effectiveDefault)) effectiveDefault = valuesPL[idxs[0]];   // wer3: pusty default (zawiasy) zostaje pusty
      const opts = idxs.map(i => {
        const v = valuesPL[i], display = valuesLocal[i] || v;
        return `<option value="${escapeAttr(v)}" ${v === effectiveDefault ? "selected" : ""}>${escapeHtml(display)}</option>`;
      }).join("");
      if (effectiveDefault) state.conditional[def.label] = effectiveDefault;
      // Czy ten dropdown ma jakąś wartość zawierającą "Niestandardow"? Jeśli tak - przygotuj reveal slot.
      const hasCustom = valuesPL.some(v => /Niestandardow|Custom/i.test(v));
      const customSlot = hasCustom ? `
        <input type="text" class="knf__input knf__input--custom" placeholder="${escapeAttr(condCustomPh)}" name="properties[${escapeAttr(def.label)} - opis]" data-knf-cond-custom="${escapeAttr(def.label)}" enterkeyhint="done" hidden>
      ` : "";
      // Helptext per wybrana wartość (Globo): pokazuje opis aktualnej selekcji
      const helptexts = (locale !== "pl" && def[`helptexts_${locale}`]) || def.helptexts || {};
      const initialHelp = helptexts[effectiveDefault] || helptexts[def.default] || "";
      const helptextSlot = `<small class="knf__cond-help" data-knf-cond-help="${escapeAttr(def.label)}">${escapeHtml(initialHelp)}</small>`;
      // UX v2 pkt 1: opcja z jedna wartoscia (np. „Pół-mat” przy Linei) - klasa dla CSS, pokazywana jako zwykly tekst
      return `
        <div class="knf__col${idxs.length === 1 ? " knf__col--single" : ""}">
          <label class="knf__label">${escapeHtml(displayLabel)}</label>
          <select class="knf__select" data-knf-cond="${escapeAttr(def.label)}" name="properties[${escapeAttr(def.label)}]">${opts}</select>
          ${helptextSlot}
          ${customSlot}
        </div>
      `;
    }
  }
  function bindCondSelects(wrap, adderCond) {
    $$("[data-knf-cond]", wrap).concat(adderCond ? $$("[data-knf-cond]", adderCond) : []).forEach(sel => {
      sel.addEventListener("change", () => {
        state.conditional[sel.dataset.knfCond] = sel.value;
        // Update helptext under select dla wybranej wartości
        const optKey = Object.keys(cfg.rules.definitions).find(k => cfg.rules.definitions[k].label === sel.dataset.knfCond);
        const def = optKey ? cfg.rules.definitions[optKey] : null;
        const helpEl = root.querySelector(`[data-knf-cond-help="${sel.dataset.knfCond}"]`);
        if (helpEl && def) {
          const helps = (locale !== "pl" && def[`helptexts_${locale}`]) || def.helptexts || {};
          helpEl.textContent = helps[sel.value] || "";
        }
        // Reveal/hide custom input dla "Niestandardow*" / "Custom"
        const customInput = root.querySelector(`[data-knf-cond-custom="${sel.dataset.knfCond}"]`);
        if (customInput) {
          const isCustom = /Niestandardow|Custom/i.test(sel.value);
          customInput.hidden = !isCustom;
          if (isCustom) customInput.focus();
          else customInput.value = "";
        }
        // Słoje przechodzące zmienia billedForVariant w calcM2 - odśwież display
        updateM2Display();
        tryUpdate();
        // 10.2026 zestaw: opcja wspolna (G) przelicza cala liste i ceny kafelkow (TEMPO 5.1)
        if (SET_MODE && !isELabel(sel.dataset.knfCond) && !recalcMute) recalcSet("cond");
      });
    });
  }

  // -- 7. Variant lookup + price update
  // 10.2026 (TEMPO 6.2): findVariant() = nakladka na findVariantFor(stan biezacy).
  function findVariant() {
    return findVariantFor({ styl: state.styl, strona: state.strona, typ: $("[data-knf-typ]")?.value || "", szer: state.szer, wys: state.wys, cond: state.conditional, grubosc: state.grubosc });
  }
  function findVariantFor(p) {
    // Panel gięty: cena stała per wysokość → wariant dobierany po wysokości, nie po m².
    if (isPanelTypName(p.typ)) {
      return p.wys ? panelVariantFor(p.wys) : null;
    }
    const m2 = calcM2For(p.szer, p.wys, p.cond, p.grubosc);
    if (!p.styl || !m2) return null;
    // Format option2 z dwoma miejscami po przecinku (0.10, 0.15, ..., 1.95) - match Shopify variant option values
    // billedForVariant zawiera +20% jeśli "Słoje przechodzące = Tak" - variant z odpowiednio większą m² doliczy dopłatę
    const zakresStr = m2.billedForVariant.toFixed(2);
    return VARIANT_INDEX.get(`${p.styl}|${zakresStr}|${p.strona}`) || null;
  }

  // -- Pływający pasek zakupu (mobile) - decyzja Konrada 2026-07-30, po odrzuceniu
  // wariantu z echem ceny w przepływie strony. Dokujemy ORYGINALNY blok .knf__cta
  // (position:fixed przez klasę), nie kopię - cena w pasku to fizycznie ten sam element
  // co w formularzu, więc nie może się rozjechać.
  //
  // Pasek pokazuje się WYŁĄCZNIE gdy wszystkie warunki naraz:
  //   1. ekran ≤600 px (desktop nigdy),
  //   2. znany jest wariant → realna cena; nigdy stany „wybierz X" (to był główny
  //      grzech wersji sprzed tygodnia - pasek pouczał, zanim wniósł wartość),
  //   3. prawdziwy blok CTA jest PONIŻEJ widoku (znacznik 1 px w jego miejscu w
  //      przepływie - obserwowanie samego CTA po dokowaniu dałoby pętlę),
  //   4. konfigurator jest w widoku (poniżej, przy FAQ/stopce, pasek znika),
  //   5. klient nie edytuje pola - klawiatura ekranowa iOS + position:fixed to
  //      loteria (pasek potrafi wylądować na środku ekranu albo zasłonić pole),
  //   6. nie trwa dodawanie do koszyka i nie jest otwarta żadna szuflada <dialog>
  //      (po dodaniu otwiera się szuflada koszyka - pasek nie może pod nią sterczeć),
  //   7. nie tryb próbki.
  // stickyApply woła też tryUpdate() (zmiana wariantu) i pageshow (powrót z checkoutu
  // przez bfcache przywraca stronę ze stanem sprzed wyjścia - obserwatory trzeba
  // przeliczyć, inaczej zostaje pasek-zombie).
  let stickyApply = null;
  const MOBILE_MQ = window.matchMedia("(max-width: 989px)"); // redesign 2026-09-23: pasek takze na tablecie (bylo 600)
  const onMqChange = (mq, fn) => {
    if (mq.addEventListener) mq.addEventListener("change", fn);
    else if (mq.addListener) mq.addListener(fn);   // Safari < 14
  };
  function bindStickyCta() {
    const cta = root.querySelector(".knf__cta");
    const form = root.querySelector(".knf__form");
    const variantInput = $("[data-knf-variant-id]");
    if (!cta || !form || !variantInput || !("IntersectionObserver" in window)) return;

    const sentinel = document.createElement("div");
    sentinel.setAttribute("data-knf-cta-sentinel", "");
    sentinel.style.cssText = "height:1px;margin:0;padding:0;pointer-events:none";
    cta.parentNode.insertBefore(sentinel, cta);

    let ctaBelow = false, knfInView = false, editing = false;
    const drawerOpen = () => !!document.querySelector("dialog[open]");

    const apply = () => {
      const dock = MOBILE_MQ.matches
        && knfInView
        && ctaBelow
        && !editing
        && (!state.sample_mode || SAMPLE_ONLY)   // strona probki: pasek z „Dodaj próbkę” tez przypiety
        && !drawerOpen();   // 24.09 (Konrad): pasek od poczatku, takze przed wyborem wymiaru - przycisk mowi wtedy, czego brakuje
      cta.classList.toggle("knf__cta--docked", dock);
      form.classList.toggle("knf__form--docked", dock);
    };
    stickyApply = apply;

    // top > 0 => znacznik PONIŻEJ ekranu (CTA przed nami) => dokuj. Po przewinięciu
    // za CTA top < 0 i pasek znika. -72px od dołu: pasek chowa się odrobinę wcześniej,
    // żeby nie nachodził na wjeżdżający prawdziwy blok.
    new IntersectionObserver(([e]) => {
      ctaBelow = !e.isIntersecting && e.boundingClientRect.top > 0;
      apply();
    }, { rootMargin: "0px 0px -72px 0px" }).observe(sentinel);
    new IntersectionObserver(([e]) => { knfInView = e.isIntersecting; apply(); }).observe(root);

    // Klawiatura ekranowa: fokus w polu (mm, uwagi, selecty - picker iOS zasłania dół
    // ekranu) chowa pasek; po blur wraca z opóźnieniem, żeby nie migotał przy
    // przeskakiwaniu między polami.
    form.addEventListener("focusin", e => {
      if (e.target.matches("input:not([type=hidden]), textarea, select")) { editing = true; apply(); }
    });
    form.addEventListener("focusout", () => {
      setTimeout(() => {
        const a = document.activeElement;
        editing = !!(a && form.contains(a) && a.matches("input:not([type=hidden]), textarea, select"));
        apply();
      }, 200);
    });

    // Szuflady (koszyk po dodaniu, menu, chat) - <dialog>. Zamknięcie łapiemy zdarzeniem
    // close (capture, bo nie bąbelkuje); otwarcie szuflady koszyka obserwatorem atrybutu.
    document.addEventListener("close", apply, true);
    const drawerDlg = document.querySelector("cart-drawer-component")?.closest("dialog");
    if (drawerDlg) new MutationObserver(apply).observe(drawerDlg, { attributes: true, attributeFilter: ["open"] });

    window.addEventListener("pageshow", e => {
      if (e.persisted) { delete form.dataset.knfSubmitting; delete form.dataset.knfConfirming; apply(); }
    });
    onMqChange(MOBILE_MQ, apply);
    apply();
  }

  function tryUpdate() {
    syncThicknessStd();
    syncHingeOption();
    syncExtOption();
    syncGrainOption();
    syncSlojeNotes();
    // PRZED renumberSteps() - widoczność kroku "Lakierowanie" zależy od stylu, typu i trybu próbki,
    // a renumberSteps() pomija ukryte fieldsety przy numerowaniu.
    renderStrona();
    renderFinishNote();
    renumberSteps();
    syncAddedPanel();

    // Tryb próbki: cena, variant, button są ustawiane przez enterSampleMode() - pomijamy całą ścieżkę pełnego frontu
    if (state.sample_mode) {
      const priceElS = $("[data-knf-price]");
      const submitBtnS = $("[data-knf-submit]");
      const variantInputS = $("[data-knf-variant-id]");
      if (priceElS) {
        priceElS.classList.remove("knf__price-value--hint");
        priceElS.textContent = SAMPLE_PRICE_FMT || formatPrice(SAMPLE_PRICE_CENTS);
      }
      const omnibusS = $("[data-knf-omnibus]");
      if (omnibusS) omnibusS.hidden = true;  // próbka nie jest w promocji
      if (variantInputS) variantInputS.value = SAMPLE_VARIANT_ID;
      if (SET_MODE) { setPriceLabel(null); const qr = $(".knf__cta .knf__qty-row"); if (qr) qr.hidden = false; }   // probka z poziomu strony frontu
      if (submitBtnS && !isAddBusy()) {
        submitBtnS.textContent = SAMPLE_ADD_BTN_TEXT;
        submitBtnS.classList.remove("knf__btn--incomplete");
        submitBtnS.disabled = false;
      }
      // Summary nie pokazujemy w trybie próbki (overkill dla 89 zł flat)
      renderSummary(null);
      if (stickyApply) stickyApply();
      return;
    }
    // 10.2026 zestaw (TEMPO M8): pasek pokazuje sume zestawu; jak galaz probki - bez sciezki jednego frontu i bez saveState wariantu
    if (SET_MODE) { renderSetBar(); return; }

    const rc = cfg.rules.common;
    const outOfRange = !!(state.szer && state.wys) && (state.szer < rc.min_szerokosc_mm || state.szer > rc.max_szerokosc_mm
      || state.wys < rc.min_wysokosc_mm || state.wys > rc.max_wysokosc_mm);
    const variant = outOfRange ? null : findVariant();
    const priceEl = $("[data-knf-price]");
    const stawkaEl = $("[data-knf-stawka]");
    const submitBtn = $("[data-knf-submit]");
    const variantInput = $("[data-knf-variant-id]");
    const totalRow = $("[data-knf-total-row]");
    const totalEl = $("[data-knf-price-total]");
    // Komunikat Klarna pod ceną usunięty 2026-07-30 - zaśmiecał obszar decyzji zakupowej.
    // Metody płatności klient i tak widzi w koszyku i w checkoucie.
    const styleObj = cfg.styles.styles.find(s => s.name === state.styl);

    if (styleObj && stawkaEl) stawkaEl.textContent = isPanelTyp() ? "" : formatRatePerM2(parseInt(styleObj.group, 10) || 0, styleObj.name);

    // Button zawsze klikalny - bez disabled. Walidacja w submit handler scrolluje do brakującego pola.
    // W trakcie dodawania / pytania o duplikat / "Dodano ✓" przycisku NIE dotykamy (2026-09-12):
    // wcześniej tryUpdate() zdejmował disabled w locie i drugie tapnięcie szło do /cart/add.js.
    const cantAdd = !canAddToCart();
    if (!isAddBusy()) {
      submitBtn.disabled = false;
      submitBtn.classList.toggle("knf__btn--incomplete", cantAdd);

      // Dynamic submit CTA gdy incomplete - pokaż czego brakuje (zwięźle)
      if (cantAdd) {
        let missingHint = L_BTN.DODAJ;
        if (!state.styl) missingHint = L_BTN.MISS_STYL;
        else if (!$("[data-knf-kolor-out]")?.value) missingHint = L_BTN.MISS_KOLOR;
        else if (!$("[data-knf-system]")?.value) missingHint = L_BTN.MISS_SYSTEM;
        else if (!$("[data-knf-typ]")?.value) missingHint = L_BTN.MISS_TYP;
        else if (!state.szer || !state.wys) missingHint = L_BTN.MISS_WYMIAR;
        else if (outOfRange) missingHint = LM("dimRange");
        else if ((calcM2() || {}).billedForVariant > MAX_M2_BUCKET) missingHint = LMax("areaOver2");
        else if ($$("[data-knf-cond-custom]").some(i => !i.hidden && !i.value.trim())) missingHint = missMsg("MISS_OPIS");
        else if (hingeMissing()) missingHint = HINGE_MISS[locale] || HINGE_MISS.pl;
        else if (extMissing()) missingHint = EXT_MISS[locale] || EXT_MISS.pl;
        else if (slojeNotesMissing()) missingHint = SLOJE_MISS[locale] || SLOJE_MISS.pl;
        else if (!findVariant()) missingHint = LM("unavailable");
        submitBtn.textContent = missingHint;
      } else {
        submitBtn.textContent = L_BTN.DODAJ;
      }
    }

    const omnibusEl = $("[data-knf-omnibus]");
    if (!variant) {
      if (omnibusEl) omnibusEl.hidden = true;
      // wer3: „Wybierz typ i wymiar” tylko gdy naprawde ich brakuje (przy za duzym wymiarze komunikat jest w przycisku)
      const pickHint = cantAdd && (!$("[data-knf-typ]")?.value || !state.szer || !state.wys);
      priceEl.textContent = pickHint ? L_BTN.PICK_TYP_DIM : "-";
      priceEl.classList.toggle("knf__price-value--hint", pickHint);
      totalEl.textContent = "-";
      totalRow.hidden = true;
      variantInput.value = "";
      renderSummary(null);
      if (stickyApply) stickyApply();
      return;
    }
    priceEl.classList.remove("knf__price-value--hint");

    if (PROMO) {
      // Cena regularna przekreślona + cena po rabacie; pod spodem najniższa cena z 30 dni (Omnibus).
      priceEl.innerHTML = `<s class="knf__price-old">${escapeHtml(formatPrice(variant.price, variant.option1))}</s>${escapeHtml(formatPricePromo(variant.price, 1, variant.option1))}`;
      if (omnibusEl) { omnibusEl.textContent = `${PROMO.omnibus}: ${omnibusPrice(variant, styleObj)}`; omnibusEl.hidden = false; }
    } else {
      priceEl.textContent = formatPrice(variant.price, variant.option1);
    }
    variantInput.value = variant.id;

    // Total = price × qty (pokaż tylko gdy qty > 1)
    if (state.qty > 1) {
      totalEl.textContent = PROMO ? formatPricePromo(variant.price, state.qty, variant.option1) : formatPrice(variant.price, variant.option1, state.qty);
      totalRow.hidden = false;
    } else {
      totalRow.hidden = true;
    }
    renderSummary(variant);
    if (stickyApply) stickyApply();
    saveState();  // każda zmiana stanu → zapisz do localStorage
  }

  // -- Summary box: live-rendered recap wszystkich wyborów (zamiast czekać na koszyk)
  function renderSummary(variant) {
    const wrap = $("[data-knf-summary]");
    if (!wrap) return;  // summary box może być usunięty z UI (kompaktowy layout)

    if (!state.styl || !variant) {
      const EMPTY_SUMMARY = {
        en: "Choose a style and dimensions to see configuration summary.",
        cs: "Vyberte styl a rozměry, abyste viděli souhrn konfigurace.",
        de: "Wählen Sie Stil und Maße, um die Konfigurationsübersicht zu sehen.",
        pl: "Wybierz styl i wymiary, by zobaczyć podsumowanie konfiguracji.",
      };
      wrap.innerHTML = `<p class="knf__muted">${escapeHtml(EMPTY_SUMMARY[locale] || EMPTY_SUMMARY.pl)}</p>`;
      return;
    }

    const m2 = calcM2();
    const styleObj = cfg.styles.styles.find(s => s.name === state.styl);
    const styleName = styleObj ? t(styleObj, "name") : state.styl;
    const dimLabel = $("[data-knf-dim-label]").value || `${state.szer}×${state.wys} mm`;
    const sys = $("[data-knf-system]").value;
    const typ = $("[data-knf-typ]").value;
    const kolor = $("[data-knf-kolor-out]").value;

    // 3-ci element [k, v, noTranslate] - wartości nazw własnych (METOD/FAKTUM/PAX/PLATSA/BESTÅ
    // i style ogony.pl Linea/Pasmo/Rytm/Wiedeń/Hawana/Monako/Korzeń Muskato itd.) mają
    // translate="no" żeby Chrome Translate ich nie tłumaczył na "Methoden/Strang/Rhythmus" itd.
    // Wartości state/hidden-inputów są kanoniczne PL - tłumaczenie przy renderze
    const kolorDisplay = !kolor ? "-"
      : kolor.startsWith("Inny: ") ? `${L_PROP.V_INNY}: ${kolor.slice(6)}`
      : tColor(kolor);
    const rows = [
      [L.styl, styleName, true],
      [L.system, sys ? tSystem(sys) : "-", true],
      [L.typ, typ ? tType(typ) : "-"],
      [L.wymiary, dimLabel === "Niestandardowy" ? `${state.szer}×${state.wys} mm` : `${dimLabel} (${state.szer}×${state.wys} mm)`],
      [L.powierzchnia, m2 ? `${m2.exact.toFixed(3)} m² → ${m2.billed.toFixed(2)} m²` : "-"],
      [L.kolor, kolorDisplay],
    ];

    // Grubość: NA WYMIAR = select klienta (12/18/30), pozostałe = styles_config.thickness_mm; brak → bez wiersza
    const thicknessWrap = $("[data-knf-thickness]");
    const grLive = (thicknessWrap && !thicknessWrap.hidden && state.grubosc)
      ? state.grubosc
      : (frontThicknessMm(state.styl) ? `${frontThicknessMm(state.styl)} mm` : "");
    if (grLive) rows.splice(4, 0, [L.grubosc, grLive]);

    // Conditional opcje (np. Wykończenie, Lokalizacja uchwytu, Kierunek usłojenia)
    Object.entries(state.conditional).forEach(([k, v]) => {
      const p = tCondPair(k, v);
      rows.push([p.label, p.val]);
    });

    // Strona lakierowania - tylko gdy select istnieje (lakier); dla forniru/sklejki wiersz nie ma sensu
    if ($("[data-knf-strona-col]")) {
      rows.push([L.strona, state.strona === "Dwustronnie" ? L_PROP.V_DWUSTR : L_PROP.V_JEDNOSTR]);
    }

    // Custom uchwyt opis (jeśli wypełniony)
    $$("[data-knf-cond-custom]").forEach(inp => {
      if (!inp.hidden && inp.value.trim()) {
        rows.push([L.uchwyt_opis, inp.value.trim()]);
      }
    });

    rows.push([L.cena_szt, PROMO ? `${formatPricePromo(variant.price, 1, variant.option1)} (-${PROMO.pct}%)` : formatPrice(variant.price, variant.option1)]);
    const PCS = { en: "pcs", cs: "ks", de: "Stk.", pl: "szt." };
    if (state.qty > 1) rows.push([L.ilosc, `${state.qty} ${PCS[locale] || PCS.pl}`]);

    wrap.innerHTML = rows.map(([k, v, noTranslate]) => `
      <div class="knf__sumrow"><span class="knf__sumkey">${escapeHtml(k)}:</span><span class="knf__sumval"${noTranslate ? ' translate="no"' : ''}>${escapeHtml(String(v))}</span></div>
    `).join("");
  }

  // -- Sample mode usunięty całkowicie (Konrad: switch + topbar niepotrzebne).

  // -- Quantity selector: input + ± buttons
  function bindQty() {
    const input = $("[data-knf-qty]");
    const dec = $("[data-knf-qty-dec]");
    const inc = $("[data-knf-qty-inc]");
    if (!input) return;

    const setQty = (n) => {
      const clamped = Math.max(1, Math.min(50, parseInt(n, 10) || 1));
      input.value = clamped;
      state.qty = clamped;
      tryUpdate();
    };

    input.addEventListener("input", () => setQty(input.value));
    dec.addEventListener("click", () => setQty(state.qty - 1));
    inc.addEventListener("click", () => setQty(state.qty + 1));
  }

  function canAddToCart() {
    // Tryb próbki: tylko styl + kolor wymagane (wymiar i system nieistotne - flat 89 zł)
    if (state.sample_mode) {
      if (!state.styl) return false;
      if (!$("[data-knf-kolor-out]").value) return false;
      if (!SAMPLE_VARIANT_ID) return false;
      return true;
    }
    if (!state.styl || !state.szer || !state.wys) return false;
    // Walidacja zakresu wymiarów (manual mode w NA WYMIAR / preset Niestandardowy może wpisać dowolne)
    const c = cfg.rules.common;
    if (state.szer < c.min_szerokosc_mm || state.szer > c.max_szerokosc_mm) return false;
    if (state.wys < c.min_wysokosc_mm || state.wys > c.max_wysokosc_mm) return false;
    const m2 = calcM2();
    if (!m2 || m2.billedForVariant > MAX_M2_BUCKET) return false;
    if (!$("[data-knf-kolor-out]").value) return false;
    if (!$("[data-knf-system]").value || !$("[data-knf-typ]").value) return false;
    // Walidacja: jeśli któryś conditional select ma "Niestandardow*", powiązany _custom input musi być wypełniony
    const customInputs = $$("[data-knf-cond-custom]");
    for (const inp of customInputs) {
      if (!inp.hidden && !inp.value.trim()) return false;
    }
    // Bezpiecznik końcowy: konfiguracja może być kompletna, a i tak nie mieć wariantu w siatce
    // (dziura w danych: brakujący koszyk m², styl bez strony lakierowania itp.). Bez tego CTA
    // zostaje aktywne, /cart/add zwraca 422 i klient widzi tylko alert "spróbuj ponownie".
    if (!findVariant()) return false;
    if (hingeMissing()) return false;
    if (extMissing()) return false;
    if (slojeNotesMissing()) return false;
    return true;
  }

  // Currency info wstrzykiwane przez Liquid (window.knfCurrencyInfo).
  // Pre-test storefront DE 2026-05-12: JS hardkodował pl-PL/PLN, cart drawer EUR - mix walut.
  // Efektywny kurs rynku liczony RAZ z priceRefs (Liquid wstrzykuje ceny prezentacji
  // kilkunastu wariantów). rate = Σ(presentment cents) / Σ(PLN cents tych samych wariantów).
  // To kurs, którym Markets nalicza CHECKOUT (z price adjustment + rounding) - w przeciwieństwie
  // do surowego Shopify.currency.rate, który zaniżał ceny ~7% (np. DK 1,778 zamiast 1,904),
  // przez co konfigurator pokazywał mniej niż klient płaci przy kasie.
  let _knfEffectiveRate; // undefined = jeszcze nie policzony
  function getEffectiveRate(info) {
    if (_knfEffectiveRate !== undefined) return _knfEffectiveRate;
    _knfEffectiveRate = null;
    const refs = (info && info.priceRefs) || [];
    if (Array.isArray(refs) && refs.length && Array.isArray(cfg.variants) && cfg.variants.length) {
      let sumPres = 0, sumPln = 0;
      for (const r of refs) {
        const pln = cfg.variants.find(v => Number(v.id) === Number(r.id));
        const pres = parseFloat(r.p);
        if (pln && pln.price > 0 && pres > 0) { sumPres += pres; sumPln += pln.price; }
      }
      // 2026-10-03: kurs zaokrąglony do 5 miejsc. Suma zaokrąglonych cen zaniżała go o ~4e-6 (0,2868961 zamiast 0,2869)
      // i przy nowym kursie katalogu 764 z 1600 wariantów pokazywało 1 cent mniej niż koszyk. Kursy katalogów mają
      // max 5 miejsc (0,25625, 0,2869), więc zaokrąglenie odtwarza je dokładnie; PLN daje 1.
      if (sumPln > 0) _knfEffectiveRate = Math.round((sumPres / sumPln) * 1e5) / 1e5;
    }
    return _knfEffectiveRate;
  }

  // Etap B audytu cen (11.2026): katalog EUR liczony kursem stylu (styles_config.eur_rate, ten sam co w skrypcie stawek).
  // Styl bez pola (albo bez stylu w wywołaniu) = kurs efektywny jak dotąd.
  function styleEurRate(styl) {
    const s = styl && ((cfg.styles && cfg.styles.styles) || []).find(x => x.name === styl);
    return s ? parseFloat(s.eur_rate) || 0 : 0;
  }
  function getActiveCurrency(styl) {
    const info = window.knfCurrencyInfo || {};
    const shop = (typeof Shopify !== 'undefined' && Shopify.currency) || null;
    const shopRate = shop ? parseFloat(shop.rate) : 0;
    // Priorytet: efektywny kurs prezentacji (== checkout) → kurs z katalogu (info.rate = najtańszy wariant / 6900) → surowy FX → 1.
    // 2026-10-03: od podwyżki katalog EUR (0,2869) ≠ kurs ręczny rynku (0,25625, zostaje dla wysyłki), więc surowy FX jest ostatni.
    const effective = getEffectiveRate(info);
    const code = (shop && shop.active) || info.code || 'PLN';
    return {
      code,
      rate: (code === 'EUR' && styleEurRate(styl)) || effective || parseFloat(info.rate) || shopRate || 1,
      locale: (info.locale || 'pl').replace('_', '-')
    };
  }

  // formatPrice: input = PLN cents (variant.price na storefront API jest natywny PLN,
  // Markets formatuje symbol waluty ale NIE konwertuje wartości). Mnożymy × rate by przeliczyć.
  // (Werify 2026-05-14: na live CZ widać "649,00 Kč" bez rate × → bug; rate × daje prawidłowe ~4067 Kč.)
  // roundMarket: zaokrąglenie do centa DOKŁADNIE jak katalog EUR (skrypt stawek liczy Python round(PLN × kurs, 2)).
  // toFixed liczy z dokładnej wartości binarnej (Intl.NumberFormat z krótkiego zapisu: 129.105 dawało 129,11 przy 129,10
  // w koszyku). Remis jest możliwy tylko przy x = nieparzysta wielokrotność 1/8 - wtedy do parzystej, jak w Pythonie.
  // Sprawdzone 03.10.2026: 1600/1600 wariantów front-ikea zgodnych z koszykiem na ogony.de.
  function roundMarket(x) {
    if (Number.isInteger(x * 8) && !Number.isInteger(x * 4)) {
      const c = x * 100, f = Math.floor(c);
      return (f % 2 === 0 ? f : f + 1) / 100;
    }
    return Number(x.toFixed(2));
  }
  // qty > 1: cena sztuki w walucie rynku × ilość, jak kasa (zaokrąglenie sumy dawało 1 cent różnicy).
  function formatPrice(cents, styl, qty = 1) {
    const { code, rate, locale } = getActiveCurrency(styl);
    const amount = qty > 1 ? Math.round(roundMarket((cents / 100) * rate) * 100) * qty / 100 : roundMarket((cents / 100) * rate);
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount);
  }
  // formatPricePLN: alias dla formatPrice (zostawiamy nazwę dla czytelności call-sites z PLN-input).
  const formatPricePLN = formatPrice;
  // formatPricePromo: cena po promocji (PROMO). Dokładnie jak rabat automatyczny Shopify (sprawdzone
  // w koszyku 17.09.2026 na PLN i EUR): rabat liczony NA SZTUCE w walucie rynku (cena stała z katalogu
  // EUR, stąd najpierw PLN → centy rynku), OBCIĘTY W DÓŁ do grosza, dopiero potem × ilość.
  // Liczenie na groszach PLN i mnożenie kursem dawało 1 cent różnicy.
  function formatPricePromo(cents, qty = 1, styl) {
    const { code, rate, locale } = getActiveCurrency(styl);
    const unit = Math.round(roundMarket((cents / 100) * rate) * 100);
    const after = (unit - Math.floor(unit * PROMO.pct / 100)) * qty;
    return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(after / 100);
  }
  // omnibusPrice: „najniższa cena z 30 dni przed obniżką". Domyślnie cena regularna (stawki stałe od 11.08.2026).
  // Styl z omnibus_group w styles_config (stawka podniesiona W TRAKCIE promocji, np. Orzech i Korzeń Silver
  // 18.09.2026, scripts/omnibus_override_2026_09_18.py): najniższą ceną z 30 dni jest cena promocyjna liczona
  // od STAREJ stawki × zakres m² wariantu, do daty omnibus_until. Po tej dacie (albo bez pól) wraca regularna.
  // Stara cena = stawka × zakres zakłada Dwustronnie = Jednostronnie, co jest prawdą tylko dla fornirów i sklejek
  // (lakier ma stałą dopłatę +440 zł/m²) - dla stylu lakierowanego Dwustronnie nie nadpisujemy.
  function omnibusPrice(variant, styleObj) {
    const og = styleObj ? Number(styleObj.omnibus_group) : 0;
    const until = styleObj && styleObj.omnibus_until ? Date.parse(styleObj.omnibus_until + 'T23:59:59+02:00') : 0;
    const zakres = parseFloat(variant.option2);
    const lakierDwustronnie = styleObj && styleObj.category === 'lakier' && variant.option3 === 'Dwustronnie';
    if (og > 0 && until && Date.now() <= until && zakres > 0 && !lakierDwustronnie) {
      // Procent z omnibus_pct (przy jakiej promocji obowiązywała najniższa cena), nie z bieżącej promocji.
      const pct = Number(styleObj.omnibus_pct) > 0 ? Number(styleObj.omnibus_pct) : PROMO.pct;
      const { code, rate, locale } = getActiveCurrency(variant.option1);
      const unit = Math.round(roundMarket((Math.round(og * zakres * 100) / 100) * rate) * 100);
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format((unit - Math.floor(unit * pct / 100)) / 100);
    }
    return formatPrice(variant.price, variant.option1);
  }

  function formatRatePerM2(plnRatePerM2, styl) {
    const { code, rate, locale } = getActiveCurrency(styl);
    const amount = plnRatePerM2 * rate;
    // 2 miejsca - żeby /m² zgadzał się z kartą kolekcji / unit price (np. DK 2 379,17 DKK/m²),
    // a nie był zaokrąglany do pełnych jednostek (było maximumFractionDigits: 0).
    const formatted = new Intl.NumberFormat(locale, { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    return formatted + '/m²';
  }

  // Format m² wg locale (PL/CS/DE = przecinek, EN = kropka).
  function formatM2(value, decimals = 2) {
    const { locale } = getActiveCurrency();
    return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value) + ' m²';
  }

  // Fokus na brakującym polu po nieudanej walidacji (klawiatura / czytnik ekranu).
  // Przewijanie do elementu - MUSI iść przez scrollIntoView, nie window.scrollTo.
  // Motyw Horizon trzyma scroll strony na div.page-wrapper (html ma overflow:hidden,
  // wysokość 100%), więc window.scrollTo() na tym sklepie NIC nie robi. Efekt: klient
  // klikał „Dodaj do koszyka" przy niekompletnej konfiguracji, strona stała w miejscu
  // i nie było widać, czego brakuje (zgłoszenie 2026-07-30). scrollIntoView przewija
  // właściwego przodka niezależnie od tego, który element jest kontenerem scrolla.
  // scroll-margin-top zastępuje dawne "rect.top - offset".
  function knfScrollTo(el, offset) {
    if (!el) return;
    // redesign 2026-09-23: na telefonie/tablecie pod naglowkiem jest przyklejony pasek podgladu (knf-rysunek.js) - doliczamy jego wysokosc
    const pip = window.matchMedia("(max-width: 989px)").matches ? document.querySelector("[data-knf-pip]") : null;
    el.style.scrollMarginTop = ((offset || 120) + (pip ? pip.offsetHeight : 0)) + "px";
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // preventScroll - smooth scroll robi już scrollToFirstMissingField.
  function focusMissingField(el) {
    const seg = el.querySelector(".knf__seg");   // przelaczniki zamiast selecta (knf-rysunek.js)
    const target = seg ? seg.querySelector(".knf__seg-btn:not(:disabled)")
      : el.matches("input, select, textarea, button") ? el
      : (el.querySelector("input:not([type=hidden]):not([hidden]):not([disabled])") || el.querySelector("select:not([disabled]), textarea, button, [role='radio']"));
    if (target && typeof target.focus === "function") target.focus({ preventScroll: true });
  }

  // wer2 24.09: w zakladkach palet (RAL/ICA/F&B, „NCS / inny kod”) siatka [data-knf-colors] jest ukryta - cel to widoczne pole
  function colorMissTarget() {
    const fs = $("[data-knf-fs-kolor]");
    if (fs && fs.classList.contains("knf--pal-code")) return $("[data-knf-kolor-custom]") || fs;
    if (fs && fs.classList.contains("knf--pal-list")) return $("[data-knf-pal]") || fs;
    return $("[data-knf-colors]");
  }

  // -- Scroll-to-error: znajdź pierwsze niewypełnione pole + scroll + flash highlight
  function scrollToFirstMissingField() {
    // Tryb próbki: walidacja tylko styl + kolor (reszta ukryta)
    if (state.sample_mode) {
      const sampleChecks = [
        { test: () => !state.styl, target: "[data-knf-styles]", msg: missMsg("MISS_STYL") },
        { test: () => !$("[data-knf-kolor-out]").value, target: colorMissTarget, msg: missMsg("MISS_KOLOR_PROBKI") },
      ];
      for (const c of sampleChecks) {
        if (!c.test()) continue;
        const el = typeof c.target === "function" ? c.target() : $(c.target);
        if (!el) continue;
        knfScrollTo(el, 120);
        el.classList.add("knf__error-flash");
        setTimeout(() => el.classList.remove("knf__error-flash"), 2000);
        focusMissingField(el);
        const submitBtn = $("[data-knf-submit]");
        submitBtn.textContent = c.msg;
        setTimeout(() => { if (!isAddBusy()) tryUpdate(); }, 3000);
        return;
      }
      return;
    }
    const checks = [
      { test: () => !state.styl, target: "[data-knf-styles]", msg: missMsg("MISS_STYL") },
      { test: () => !$("[data-knf-kolor-out]").value, target: colorMissTarget, msg: missMsg("MISS_KOLOR") },
      { test: () => !$("[data-knf-system]").value, target: "[data-knf-fs-system-typ]", msg: missMsg("MISS_SYSTEM") },
      { test: () => !$("[data-knf-typ]").value, target: "[data-knf-typ-grid]", msg: missMsg("MISS_TYP") },
      // wer3: NA WYMIAR nie ma presetow - wtedy cel to pola wymiarow
      { test: () => !state.szer || !state.wys, target: () => { const pr = $("[data-knf-presets]"); return pr && !pr.hidden && pr.offsetParent ? pr : $("[data-knf-dims-manual]"); }, msg: missMsg("MISS_WYMIAR") },
      { test: () => {
          const c = cfg.rules.common;
          return state.szer < c.min_szerokosc_mm || state.szer > c.max_szerokosc_mm
            || state.wys < c.min_wysokosc_mm || state.wys > c.max_wysokosc_mm;
        }, target: "[data-knf-dims-manual]", msg: (() => {
          const c = cfg.rules.common;
          return `${LM("dimRange")} (${LM("widthW")} ${c.min_szerokosc_mm}-${c.max_szerokosc_mm}, ${LM("heightW")} ${c.min_wysokosc_mm}-${c.max_wysokosc_mm} mm)`;
        })() },
      { test: () => { const m = calcM2(); return !m || m.billedForVariant > MAX_M2_BUCKET; }, target: "[data-knf-dim-warn]", msg: LMax("areaOver2") },
      // ta sama kolejnosc co podpowiedz na przycisku (tryUpdate)
      { test: () => {
          const customInputs = $$("[data-knf-cond-custom]");
          return customInputs.some(inp => !inp.hidden && !inp.value.trim());
        }, target: "[data-knf-cond-custom]:not([hidden])", msg: missMsg("MISS_OPIS") },
      { test: () => hingeMissing(), target: () => hingeSel()?.closest(".knf__col"), msg: HINGE_MISS[locale] || HINGE_MISS.pl },
      { test: () => extMissing(), target: "[data-knf-ext]", msg: EXT_MISS[locale] || EXT_MISS.pl },
      { test: () => slojeNotesMissing(), target: 'textarea[name="properties[Uwagi klienta]"]', msg: SLOJE_MISS[locale] || SLOJE_MISS.pl },
      // Ostatnia linia obrony: konfiguracja wygląda kompletnie, ale wariantu nie ma w siatce.
      { test: () => !findVariant(), target: "[data-knf-dim-warn]", msg: LM("unavailable") },
    ];
    for (const c of checks) {
      if (!c.test()) continue;
      const el = typeof c.target === "function" ? c.target() : $(c.target);
      if (!el) continue;
      knfScrollTo(el, 120);
      // Highlight target field
      el.classList.add("knf__error-flash");
      setTimeout(() => el.classList.remove("knf__error-flash"), 2000);
      focusMissingField(el);
      // Pokaż message jako toast lub alert
      const submitBtn = $("[data-knf-submit]");
      submitBtn.textContent = c.msg;
      setTimeout(() => { if (!isAddBusy()) tryUpdate(); }, 3000);
      return;
    }
  }

  // -- TRYB PRÓBKI: enter/exit + bind CTA --------------------------------------
  function updateSampleFinishOptions(styleName) {
    const glossLabel = document.querySelector('[data-knf-fs-sample-finish] input[value="Połysk"]')?.closest('label');
    if (!glossLabel) return;
    const hasGloss = STYLES_WITH_GLOSS.has(styleName);
    glossLabel.hidden = !hasGloss;
    if (!hasGloss) {
      const halfmat = document.querySelector('[data-knf-fs-sample-finish] input[value="Pół-mat"]');   // wer3: wartosc radia to „Pół-mat” - polysk zostawal zaznaczony
      if (halfmat) halfmat.checked = true;
    }
  }

  function enterSampleMode(quiet) {
    if (!SAMPLE_VARIANT_ID) {
      console.warn("[knf] Sample variant ID nie ustawiony - tryb próbki niedostępny.");
      return;
    }
    state.sample_mode = true;
    root.classList.add("knf--sample-mode");

    // Wyłącz native HTML required na pełen-front polach - inaczej browser native validation
    // blokuje submit z błędem "An invalid form control with name='properties[Typ elementu]' is not focusable"
    // (pola ukryte CSS-em, ale required wciąż aktywny). JS canAddToCart() sample branch dba o walidację sam.
    $$("[data-knf-system], [data-knf-typ], [data-knf-szer], [data-knf-wys]").forEach(el => {
      if (el.required) { el.dataset.knfRequiredOriginal = "1"; el.required = false; }
    });

    // Synchronizuj atrybut hidden z trybem próbki (renumberSteps używa fs.hidden jako short-circuit
    // - bez tego "Wykończenie próbki" jest skipowane mimo display:block z CSS)
    [
      "[data-knf-sample-banner]",
      "[data-knf-sample-dim]",
      "[data-knf-fs-sample-finish]",
    ].forEach(sel => { const el = $(sel); if (el) el.hidden = false; });

    // Hidden property + bloki z wymiarem próbki (wymiar zależny od stylu) → koszyk/line item
    refreshSampleDim();

    // Force qty 1 dla próbki (klient nie zamawia 5 próbek tego samego)
    const qtyInput = $("[data-knf-qty]");
    if (qtyInput) {
      qtyInput.value = 1;
      qtyInput.disabled = true;
      state.qty = 1;
    }

    if (state.styl) updateSampleFinishOptions(state.styl);
    renderSampleCta(); // ukryje CTA-box (banner przejmuje rolę)
    tryUpdate();       // sample_mode branch ustawia cenę/variant/submit + renumberSteps

    // Scroll do bannera żeby klient widział że tryb się zmienił (strona probki startuje w tym trybie - bez przewijania)
    const banner = $("[data-knf-sample-banner]");
    if (banner && !quiet) {
      knfScrollTo(banner, 80);
    }
  }

  function exitSampleMode() {
    state.sample_mode = false;
    root.classList.remove("knf--sample-mode");

    // Przywróć required na polach pełnego frontu
    $$("[data-knf-system], [data-knf-typ], [data-knf-szer], [data-knf-wys]").forEach(el => {
      if (el.dataset.knfRequiredOriginal === "1") { el.required = true; delete el.dataset.knfRequiredOriginal; }
    });

    // Przywróć hidden attrs (sync z CSS displaying)
    [
      "[data-knf-sample-banner]",
      "[data-knf-sample-dim]",
      "[data-knf-fs-sample-finish]",
    ].forEach(sel => { const el = $(sel); if (el) el.hidden = true; });

    // Wyczyść property wymiaru próbki (nie powinno lecieć w pełnym zamówieniu)
    const dimProp = $("[data-knf-sample-dim-prop]");
    if (dimProp) dimProp.value = "";

    // Odblokuj qty
    const qtyInput = $("[data-knf-qty]");
    if (qtyInput) qtyInput.disabled = false;

    // Pokaż CTA próbki (jeśli styl wciąż wybrany) i przelicz pełną cenę
    renderSampleCta();
    tryUpdate();
    updateSampleFinishOptions("Gładki"); // reset do pełnych opcji po wyjściu z trybu próbki

    // Scroll z powrotem do listy stylów (nie irytuj użytkownika niepotrzebnymi skokami)
    const styles = $("[data-knf-styles]");
    if (styles) {
      knfScrollTo(styles, 80);
    }
  }

  function bindSampleCta() {
    const enterBtn = $("[data-knf-sample-enter]");
    if (enterBtn) enterBtn.addEventListener("click", () => enterSampleMode());

    const backBtn = $("[data-knf-sample-back]");
    if (backBtn) backBtn.addEventListener("click", () => exitSampleMode());
  }

  // Wybór jakie properties polecą do koszyka w trybie próbki.
  // Pro-Hand musi wiedzieć: jaki styl, jaki kolor, jakie wykończenie (półmat/połysk), wymiar.
  // Konrad: gdyby Pro-Hand potrzebował dodatkowych pól (np. SKU lakieru, kierunek słojów dla
  // forniru) - dorzucić tutaj. Default leci jako 4 pola.
  function buildSampleProperties() {
    const props = {};
    const colorOut = $("[data-knf-kolor-out]")?.value || "";
    const styleObjS = (cfg.styles.styles || []).find(s => s.name === state.styl);
    const colorCustom = (styleObjS && styleObjS.category && styleObjS.category !== "lakier") ? "" : ($("[data-knf-kolor-custom]")?.value || "");
    const finishEl = document.querySelector('[data-knf-sample-finish]:checked');
    const finishPl = finishEl ? finishEl.value : "Półmat"; // "Półmat"/"Połysk"

    // 2 linijki dla próbki (translated keys + values per locale):
    // 1. "{K_FRONT_PROBKI}" = "{wymiar} · {styl} · {kolor}" (lub "{V_INNY}: NCS …")
    // 2. "{K_WYK_PROBKI}" = "{V_POLMAT}" / "{V_POLYSK}" (bez +15% bo dla próbki nieaplikowalne)
    const stylTr = state.styl ? tStyle(state.styl) : "";
    const woodS = !!(styleObjS && styleObjS.category && styleObjS.category !== "lakier");
    let kolorTr = woodS ? L_PROP.V_NAT_DREWNO : tColor(colorOut);   // wer3: fornir/sklejka jak pelny front - „Naturalne drewno”
    if (colorCustom && colorCustom.trim() && colorCustom !== colorOut) {
      kolorTr = `${L_PROP.V_INNY}: ${colorCustom.trim()}`;
    }
    const finishTr = /połysk/i.test(finishPl)
      ? L_PROP.V_POLYSK.replace(/\s*\(\+15%\)/, "")
      : L_PROP.V_POLMAT;

    const gr = styleThicknessMm(state.styl);
    const parts = [sampleDimMM(state.styl)];
    if (stylTr) parts.push(stylTr);
    if (kolorTr) parts.push(kolorTr);
    props[L_PROP.K_FRONT_PROBKI] = parts.join(" · ");
    props[L_PROP.K_WYK_PROBKI] = gr ? `${finishTr} · ${L_PROP.V_GRUBOSC}: ${gr} mm` : finishTr;

    // Strukturalne pola dla Pro-Hand (prefix "_" → ukryte w koszyku/checkout)
    const sd = sampleDims(state.styl);
    props["_wys_mm"] = String(sd.wys);
    props["_szer_mm"] = String(sd.szer);
    if (gr) props["_grubosc_mm"] = String(gr);

    // PL fallback dla Pro-Hand - ZAWSZE (też locale pl) + self-labeled (templatka Order Printer
    // renderuje segmenty " | " bez zgadywania pozycji)
    {
      const plParts = [sampleDimMM(state.styl)];
      if (state.styl) plParts.push(state.styl);
      let kolorPl = woodS ? "Naturalne drewno" : (colorCustom && colorCustom.trim() && colorCustom !== colorOut)
        ? `Inny: ${colorCustom.trim()}` : colorOut;
      // wer3: kolor z palety po polsku (knf-palety.js zapisuje polska etykiete w dataset.pl), jak w buildFullFormData
      const ciS = $("[data-knf-kolor-custom]");
      if (!woodS && colorCustom && ciS && ciS.dataset.pl && ciS.dataset.plFor === colorCustom.trim()) kolorPl = kolorPl.replace(colorCustom.trim(), ciS.dataset.pl);
      if (kolorPl) plParts.push(kolorPl);
      const wykPl = gr ? `${finishPl} · Grubość: ${gr} mm` : finishPl;
      props["_pl_summary"] = `Front próbki: ${plParts.join(" · ")} | Wykończenie próbki: ${wykPl}`;
    }
    return props;
  }

  // ==========================================================================
  // DODAWANIE DO KOSZYKA (2026-09-12): jeden performAdd() dla frontu i próbki.
  // Skarga: klient na telefonie dodał ten sam front 14 razy, bo nie widział, że się udało
  // (finally przywracało "Dodaj do koszyka", toast był martwy, alert straszył po sukcesie).
  // Zasady:
  //  1. tylko /cart/add.js może pokazać błąd; po sukcesie żadna gałąź nie pokazuje błędu,
  //  2. potwierdzenie = panel [data-knf-added] w .knf__cta (na mobile jedzie w pasku dokowanym),
  //  3. identyczna próbka w koszyku albo dodana <5 min → pytanie [data-knf-dup]; front bez pytania (19.09.2026),
  //  4. motyw dostaje natywne zdarzenie shopify:cart:lines-update (jak product-form.js):
  //     szuflada (cart-drawer.js), zawartość (component-cart-items.js), licznik (cart-icon.js),
  //     live region (header-actions.js). Bez ręcznego innerHTML i bez martwych cart:refresh.
  // ==========================================================================
  const CART_EVENT_NAME = "shopify:cart:lines-update";
  const LAST_ADDED_TTL_MS = 5 * 60 * 1000;
  // Gdy theme-drawer nie zdefiniuje się w czasie: true = Shopify.actions.openCart() (przekierowanie
  // na /cart, gdy szuflady brak). Domyślnie false: panel "Dodano" z linkiem do koszyka wystarcza.
  const KNF_OPEN_CART_REDIRECT_FALLBACK = false;
  let lastAdded = null;      // { hash, qty, ts }: ostatnia udana konfiguracja (idempotencja)
  let addedState = null;     // { hash, cfgHash, sample, count, label }: widoczny panel "Dodano"
  let pendingRetry = null;   // { formData, sample, cfgHash, variantId, qty, qtyBefore, attempt }
  let btnHold = false;       // przycisk pokazuje "Dodano ✓" przez 2 s, tryUpdate() go nie nadpisuje
  let doneTimer = null;
  let cartEventCtorPromise = null;

  const sleep = (ms) => new Promise(rs => setTimeout(rs, ms));

  function isAddBusy() {
    const form = $("[data-knf-form]");
    return btnHold || !!(form && (form.dataset.knfSubmitting === "1" || form.dataset.knfConfirming === "1"));
  }
  // M1 (10.2026 zestaw): blokada zmian listy tylko na czas POST /cart/add.js i uzgadniania koszyka (bez 2 s „Dodano ✓” z btnHold)
  function setBusy() {
    const form = $("[data-knf-form]");
    return !!(form && form.dataset.knfSubmitting === "1");
  }

  // GA4 (2026-09-19): zdarzenia knf_* idą przez gtag strony (G-YR5JEDYYHY), który aplikacja Google & YouTube
  // ładuje dopiero po zgodzie na cookies. Bez zgody gtag nie istnieje i nic nie wysyłamy. Zdarzenia sprzed
  // załadowania gtag czekają w kolejce najwyżej 15 s. Zastąpiło to Custom Pixel, bo gtag w piaskownicy piksela
  // wysyłał też fałszywe "scroll" z adresem ramki piksela.
  const KNF_GA4 = "G-YR5JEDYYHY";
  const KNF_GA_KEYS = ["locale", "product", "step", "sample", "qty", "attempt", "in_cart", "recent", "variant_id", "message", "rows", "set_qty", "kit", "reason", "nad", "nad_qty"];   // 10.2026: + parametry zestawu
  const knfGaQueue = [];
  let knfGaTimer = null;
  function knfGaSend(name, payload) {
    const p = { send_to: KNF_GA4 };
    KNF_GA_KEYS.forEach((k) => {
      const v = payload[k];
      if (v === undefined || v === null) return;
      p[k] = typeof v === "string" ? v.slice(0, 100) : v;
    });
    if (typeof window.gtag === "function") { window.gtag("event", name, p); return; }
    knfGaQueue.push([name, p]);
    if (knfGaTimer) return;
    let tries = 0;
    knfGaTimer = setInterval(() => {
      tries += 1;
      if (typeof window.gtag === "function") {
        while (knfGaQueue.length) { const [n, q] = knfGaQueue.shift(); window.gtag("event", n, q); }
      }
      if (!knfGaQueue.length || tries >= 30) { clearInterval(knfGaTimer); knfGaTimer = null; knfGaQueue.length = 0; }
    }, 500);
  }

  // Telemetria: dataLayer (dziś brak GTM = no-op) + Shopify.analytics.publish + GA4 przez gtag strony
  function trackKnf(name, params) {
    // wer3: strona probki ma wlasna nazwe produktu w zdarzeniach, zeby nie zawyzac lejka konfiguratora frontow
    const payload = Object.assign({ event: name, locale, product: SAMPLE_ONLY ? "probka-frontu-ikea" : (root.dataset.productHandle || "") }, params || {});
    try { (window.dataLayer = window.dataLayer || []).push(payload); } catch (_) { /* brak GTM */ }
    try { window.Shopify?.analytics?.publish?.(name, payload); } catch (_) { /* pixel niepodpięty */ }
    try { knfGaSend(name, payload); } catch (_) { /* GA4 niedostępne */ }
  }

  // Telemetria kroków (2026-09-18): po jednym zdarzeniu przy PIERWSZYM zakończeniu kroku przez klienta.
  // Liczą się tylko akcje klienta (isTrusted): domyślny zestaw na starcie (Gładki + Biel + FAKTUM),
  // deep link i przywrócenie stanu klikają programowo i nie są krokami. Stan sprawdzamy po obsłudze
  // zdarzenia przez konfigurator (setTimeout 0). Nie zmienia działania konfiguratora.
  const KNF_STEPS = [
    ["knf_step1_style", ".knf__style", () => !!state.styl],
    ["knf_step2_color", ".knf__color, [data-knf-kolor-custom], .knf__pal-c", () => !!$("[data-knf-kolor-out]")?.value],
    ["knf_step3_options", "[data-knf-conditional] select, [data-knf-conditional] input, [data-knf-conditional] .knf__seg-btn", () => true],
    ["knf_step4_system", ".knf__syschip", () => !!$("[data-knf-system]")?.value],
    ["knf_step5_type", ".knf__typchip", () => !!$("[data-knf-typ]")?.value],
    ["knf_step6_dims", ".knf__preset, [data-knf-szer], [data-knf-wys], [data-knf-custom-add], [data-knf-kit-add], [data-knf-nad-add], [data-knf-ask-v]", () => SET_MODE ? state.set.length > 0 : !!(state.szer && state.wys)],   // zestaw: pierwsze dodanie
    ["knf_step7_notes", "textarea[name='properties[Uwagi klienta]']", () => !!$("textarea[name='properties[Uwagi klienta]']")?.value.trim()],
    ["knf_sample_finish", "[data-knf-sample-finish]", () => true],
  ];
  function bindStepTracking() {
    const done = new Set();
    const onUserAction = (e) => {
      if (!e.isTrusted || !(e.target instanceof Element)) return;
      const target = e.target;
      setTimeout(() => {
        try {
          for (const [name, sel, isDone] of KNF_STEPS) {
            if (done.has(name) || !target.closest(sel) || !isDone()) continue;
            done.add(name);
            trackKnf(name, { step: name.replace(/^knf_(step\d_)?/, "") });
          }
        } catch (_) { /* telemetria nie może psuć konfiguratora */ }
      }, 0);
    };
    root.addEventListener("click", onUserAction);
    root.addEventListener("change", onUserAction);
  }

  // /cart.js z limitem czasu. Nigdy nie blokuje ścieżki sukcesu (null = nie wiadomo).
  async function fetchCartSafe(timeoutMs) {
    const ctrl = typeof AbortController === "function" ? new AbortController() : null;
    const timer = setTimeout(() => { if (ctrl) ctrl.abort(); }, timeoutMs || 2500);
    try {
      const r = await fetch(`${CART_ROOT}cart.js`, { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: "application/json" }, cache: "no-store" });
      if (!r.ok) return null;
      return await r.json();
    } catch (_) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  // Tożsamość linii jak w Shopify: wariant + widoczne properties (bez prefiksu "_", bo
  // _pl_summary/_wys_mm wynikają z widocznych). Ilość NIE wchodzi do hasha.
  function hashParts(variantId, entries) {
    const props = entries
      .filter(([k, v]) => !k.startsWith("_") && String(v ?? "").trim() !== "")
      .map(([k, v]) => `${k}=${String(v).trim()}`)
      .sort();
    return `${String(variantId)}|${props.join("\u001f")}`;
  }
  function buildConfigHash(formData) {
    const entries = [];
    for (const [key, value] of formData.entries()) {
      const m = /^properties\[(.+)\]$/.exec(key);
      if (m) entries.push([m[1], value]);
    }
    return hashParts(formData.get("id"), entries);
  }
  function findCartLineByHash(cart, hash) {
    const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
    for (const it of items) {
      if (hashParts(it.variant_id ?? it.id, Object.entries(it.properties || {})) === hash) {
        return { key: it.key, quantity: it.quantity || 0 };
      }
    }
    return null;
  }

  // Klasa zdarzenia z magistrali Horizona. Import mapa (snippets/scripts.liquid) obowiązuje też
  // dla dynamicznego import() ze skryptu klasycznego. Fallback: Event o tym samym kształcie.
  class KnfCartLinesUpdateEvent extends Event {
    constructor(payload) {
      super(CART_EVENT_NAME, { bubbles: true, cancelable: true, composed: true });
      Object.assign(this, payload);
    }
  }
  function createDeferred() {
    let resolve, reject;
    const promise = new Promise((rs, rj) => { resolve = rs; reject = rj; });
    return { promise, resolve, reject };
  }
  function getCartEventCtor() {
    if (!cartEventCtorPromise) {
      cartEventCtorPromise = import("@shopify/events")
        .then(mod => (mod && typeof mod.CartLinesUpdateEvent === "function") ? mod.CartLinesUpdateEvent : null)
        .catch(err => { console.warn("[knf] @shopify/events niedostępne, zdarzenie fallback", err); return null; });
    }
    return cartEventCtorPromise;
  }
  function cartSummaryFromAjax(cart) {
    const money = (cents) => ({ amount: (Number(cents || 0) / 100).toFixed(2), currencyCode: cart.currency || "" });
    return {
      id: cart.token || "",
      totalQuantity: cart.item_count || 0,
      cost: { totalAmount: money(cart.total_price) },
      lines: (cart.items || []).map(it => ({ id: it.key, quantity: it.quantity, cost: { totalAmount: money(it.final_line_price) } })),
      discountCodes: (cart.discount_codes || []).map(d => ({ code: d.code, applicable: !!d.applicable })),
    };
  }
  // Emituje zdarzenie z formularza (poza cart-items-component i poza dialog:modal, jak wymagają
  // cart-drawer.js / component-cart-items.js). Wywoływane PO sukcesie /cart/add.js, bo
  // cart-drawer.js otwiera szufladę optymistycznie już przy dispatchu.
  // 10.2026 zestaw: drugi argument moze byc tablica linii [{ merchandiseId, quantity }], trzeci = action ("add" | "update").
  async function emitCartLinesUpdate(target, variantId, qty) {
    const Ctor = await getCartEventCtor();
    const deferred = (Ctor && typeof Ctor.createPromise === "function") ? Ctor.createPromise() : createDeferred();
    const many = Array.isArray(variantId);
    const lines = many ? variantId.map(l => ({ merchandiseId: String(l.merchandiseId), quantity: l.quantity })) : [{ merchandiseId: String(variantId), quantity: qty }];
    const payload = { action: many ? (qty || "add") : "add", context: "product", lines, promise: deferred.promise };
    target.dispatchEvent(Ctor ? new Ctor(payload) : new KnfCartLinesUpdateEvent(payload));
    return { deferred, Ctor };
  }
  function resolveCartUpdate({ deferred, Ctor }, { cart, sections, itemCount }) {
    let summary = null;
    if (cart) {
      try { summary = (Ctor && typeof Ctor.createCartFromAjaxResponse === "function") ? Ctor.createCartFromAjaxResponse(cart) : cartSummaryFromAjax(cart); }
      catch (_) { summary = cartSummaryFromAjax(cart); }
    }
    deferred.resolve({
      cart: summary,
      detail: { sections: sections || undefined, itemCount, items: cart ? cart.items : [], didError: false, source: "konfigurator-natywny" },
    });
  }
  function readHeaderCount() {
    const icon = document.querySelector("cart-icon");
    if (icon && Number.isFinite(icon.currentCartCount)) return icon.currentCartCount;
    const n = parseInt(document.querySelector('[ref="cartBubbleCount"]')?.textContent || "", 10);
    return Number.isFinite(n) ? n : 0;
  }

  // Szuflada: czekamy na definicję <theme-drawer> (moduł z fetchpriority=low). Bez showModal():
  // dawało okno bez [open] na theme-drawer i z martwym przyciskiem zamknięcia.
  async function ensureCartDrawerOpen(timeoutMs) {
    const drawer = document.getElementById("cart-drawer");
    if (!drawer) return false;
    if (drawer.hasAttribute("open")) return true;
    if (!window.customElements) return false;
    const defined = await Promise.race([
      customElements.whenDefined("theme-drawer").then(() => true),
      sleep(timeoutMs || 2500).then(() => false),
    ]);
    if (!defined) {
      console.warn("[knf] theme-drawer nie zdefiniowany, szuflada bez auto-open");
      if (KNF_OPEN_CART_REDIRECT_FALLBACK) { try { window.Shopify?.actions?.openCart?.(); } catch (_) { /* brak standard actions */ } }
      return false;
    }
    // cart-drawer.js mógł już otworzyć (auto-open na zdarzeniu), nie dubluj
    await sleep(60);
    if (drawer.hasAttribute("open") || drawer.isOpen) return true;
    if (typeof drawer.open === "function") { drawer.open(); return true; }
    return false;
  }
  // Tylko gdy komponenty koszyka nie są jeszcze zdefiniowane (zdarzenie trafiłoby w próżnię):
  // podmiana wnętrza szuflady z odpowiedzi add.js i odsłonięcie licznika.
  function applyDrawerHtmlFallback(html, itemCount) {
    try {
      if (html && !customElements.get("cart-items-component")) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const fresh = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const cur = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
        if (fresh && cur) cur.replaceWith(document.adoptNode(fresh));
      }
      if (!customElements.get("cart-icon")) {
        const bubble = document.querySelector('[ref="cartBubble"]');
        const count = document.querySelector('[ref="cartBubbleCount"]');
        if (count) { count.textContent = itemCount < 100 ? String(itemCount) : ""; count.classList.toggle("hidden", itemCount === 0); }
        if (bubble) bubble.classList.toggle("visually-hidden", itemCount === 0);
      }
    } catch (err) {
      console.warn("[knf] fallback szuflady", err);
    }
  }

  // -- Panel "Dodano": znika przy zmianie konfiguracji (lightHash), zostaje przy zmianie samej ilości
  function lightHash() {
    // Zestaw: panel „Dodano” znika, gdy zmieni sie lista (TEMPO M9) - podpis listy zamiast biezacego frontu
    if (SET_MODE && !state.sample_mode) return "Z|" + state.set.map(l => lineSig(l) + "#" + l.qty).join(";");
    return [
      state.sample_mode ? "S" : "F", state.styl, state.szer, state.wys, state.strona,
      $("[data-knf-kolor-out]")?.value, $("[data-knf-kolor-custom]")?.value,
      $("[data-knf-system]")?.value, $("[data-knf-typ]")?.value, $("[data-knf-grubosc]")?.value,
      JSON.stringify(state.conditional || {}),
      $$("[data-knf-cond-custom]").map(i => i.value).join(","),
      $('textarea[name="properties[Uwagi klienta]"]')?.value,
    ].join("|");
  }
  function currentConfigLabel(sample) {
    const styl = state.styl ? tStyle(state.styl) : "";
    const dim = sample ? sampleDimMM(state.styl) : ((state.szer && state.wys) ? `${state.szer}×${state.wys} mm` : "");
    return [styl, dim].filter(Boolean).join(" · ");
  }
  function updateAddedCount(n) {
    const el = $("[data-knf-added-count]");
    if (addedState) addedState.count = n;
    const sep = (addedState && addedState.label) ? "· " : "";
    if (el) el.textContent = (n != null && n > 0) ? sep + L_ADD.IN_CART.replace("{n}", n).replace("{pcs}", pcsWord()) : "";
  }
  function showAddedState(info) {
    const panel = $("[data-knf-added]");
    const btn = $("[data-knf-submit]");
    addedState = Object.assign({ hash: lightHash() }, info);
    if (panel) {
      const title = $("[data-knf-added-title]");
      const label = $("[data-knf-added-label]");
      const next = $("[data-knf-added-next]");
      if (title) title.textContent = info.set ? info.title : (info.sample ? L_ADD.ADDED_SAMPLE : L_ADD.ADDED_TITLE);   // zestaw: „Dodano 5 poz. (9 szt.)” (D9)
      if (label) label.textContent = info.label || "";
      if (next) next.textContent = info.set ? sx("T_ADD_MORE") : (info.sample ? L_ADD.ADD_NEXT_SAMPLE : L_ADD.ADD_NEXT);
      const goC = $("[data-knf-added-cart]");
      if (goC) goC.textContent = info.set ? sx("T_VIEW_CART") : L_ADD.GO_CART;
      updateAddedCount(info.count);
      hideInlineError();
      panel.hidden = false;
      root.classList.add("knf--added");
    }
    if (btn) {
      btn.disabled = false;
      btn.classList.remove("knf__btn--incomplete");
      btn.classList.add("knf__btn--done");
      btn.textContent = L_ADD.BTN_DONE;
    }
    btnHold = true;
    clearTimeout(doneTimer);
    doneTimer = setTimeout(() => {
      btnHold = false;
      if (btn) btn.classList.remove("knf__btn--done");
      tryUpdate();
    }, 2000);
    if (stickyApply) stickyApply();
  }
  function hideAddedState() {
    addedState = null;
    const panel = $("[data-knf-added]");
    if (panel) panel.hidden = true;
    root.classList.remove("knf--added");
    if (stickyApply) stickyApply();
  }
  function syncAddedPanel() {
    if (addedState && addedState.hash !== lightHash()) hideAddedState();
  }

  // -- Pytanie o duplikat: form.dataset.knfConfirming blokuje submit do odpowiedzi
  function confirmDuplicate() {
    const panel = $("[data-knf-dup]");
    const form = $("[data-knf-form]");
    const yes = $("[data-knf-dup-yes]");
    const no = $("[data-knf-dup-no]");
    if (!panel || !form || !yes || !no) return Promise.resolve(true);
    return new Promise(resolve => {
      const q = $("[data-knf-dup-q]");
      if (q) q.textContent = L_ADD.DUP_Q_SAMPLE;
      hideInlineError();
      hideAddedState();
      form.dataset.knfConfirming = "1";
      panel.hidden = false;
      root.classList.add("knf--dup");
      const done = (answer) => {
        yes.removeEventListener("click", onYes);
        no.removeEventListener("click", onNo);
        panel.hidden = true;
        root.classList.remove("knf--dup");
        delete form.dataset.knfConfirming;
        resolve(answer);
      };
      const onYes = () => done(true);
      const onNo = () => done(false);
      yes.addEventListener("click", onYes);
      no.addEventListener("click", onNo);
      if (stickyApply) stickyApply();
      try { no.focus({ preventScroll: true }); } catch (_) { /* stare Safari */ }
    });
  }

  // -- Błąd inline (tylko gdy /cart/add.js naprawdę nie przyjął pozycji)
  function showInlineError(message) {
    const panel = $("[data-knf-error]");
    if (!panel) return;
    const msg = $("[data-knf-error-msg]");
    if (msg) msg.textContent = message || L_ADD.ERR_TITLE;
    const retry = $("[data-knf-error-retry]");
    if (retry) { retry.disabled = false; retry.hidden = false; retry.textContent = L_ADD.ERR_RETRY; }   // hidden: M6 kontakt zamiast ponowienia
    hideAddedState();
    panel.hidden = false;
    root.classList.add("knf--error");
    if (stickyApply) stickyApply();
  }
  function hideInlineError() {
    const panel = $("[data-knf-error]");
    if (panel) panel.hidden = true;
    root.classList.remove("knf--error");
  }
  function resetQty() {
    const inp = $("[data-knf-qty]");
    if (inp) inp.value = 1;
    state.qty = 1;
    tryUpdate();
  }

  // Enter na klawiaturze telefonu = implicit submission = kolejne pełne dodanie bez potwierdzenia.
  // Enter w polu: przejście do [data-knf-next] albo schowanie klawiatury (blur), nigdy submit.
  function bindEnterGuard(form) {
    form.addEventListener("keydown", e => {
      if (e.key !== "Enter" || e.isComposing) return;
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || t.type === "submit" || t.type === "button") return;
      e.preventDefault();
      // 10.2026 zestaw: Enter w polu wysokosci „Inny wymiar” = „Dodaj” (tylko lista, nie koszyk; cofa „−” / „Usuń”)
      if (SET_MODE && customOpen && t.matches("[data-knf-wys]")) { addCustom(); return; }
      const next = t.dataset.knfNext ? form.querySelector(t.dataset.knfNext) : null;
      if (next && !next.disabled && !next.closest("[hidden]")) {
        next.focus();
        if (typeof next.select === "function") next.select();
      } else {
        t.blur();
      }
    });
  }

  function bindAddedPanels(form) {
    // Teksty statyczne paneli (liquid ma PL, JS podstawia locale)
    const goCart = $("[data-knf-added-cart]");
    if (goCart) goCart.textContent = L_ADD.GO_CART;
    // 10.2026 zestaw (D9): „Zobacz koszyk” otwiera szuflade (link /cart zostaje, gdy szuflady brak)
    if (goCart && SET_MODE) goCart.addEventListener("click", async e => {
      if (!(addedState && addedState.set) || !document.getElementById("cart-drawer")) return;
      e.preventDefault();
      if (!(await ensureCartDrawerOpen(2500))) window.location.href = goCart.href;
    });
    const yes = $("[data-knf-dup-yes]");
    if (yes) yes.textContent = L_ADD.DUP_YES;
    const no = $("[data-knf-dup-no]");
    if (no) no.textContent = L_ADD.DUP_NO;
    const retry = $("[data-knf-error-retry]");
    if (retry) {
      retry.textContent = L_ADD.ERR_RETRY;
      retry.addEventListener("click", () => { verifyThenRetry(form); });
    }
    const next = $("[data-knf-added-next]");
    if (next) next.addEventListener("click", () => {
      const sample = !!(addedState && addedState.sample);
      const wasSet = !!(addedState && addedState.set);
      hideAddedState();
      if (sample) { knfScrollTo($("[data-knf-styles]"), 120); return; }
      if (wasSet) { const tg = $("[data-knf-typ-grid]"); knfScrollTo((tg && tg.closest(".knf__fs")) || tg, 100); return; }   // „Dodaj kolejne elementy w tym stylu”
      const manual = $("[data-knf-dims-manual]");
      const szer = $("[data-knf-szer]");
      const target = (manual && !manual.hidden && szer) ? szer : ($("[data-knf-presets]") || $("[data-knf-dim-hint]"));
      const fs = target ? target.closest(".knf__fs") : null;
      knfScrollTo(fs || target, 100);
      if (target === szer) setTimeout(() => { try { szer.focus({ preventScroll: true }); szer.select(); } catch (_) { /* brak pola */ } }, 350);
    });

    // Zmiana konfiguracji (nie ilości) chowa panel "Dodano", także notatki, których tryUpdate nie widzi
    form.addEventListener("input", syncAddedPanel);
    form.addEventListener("change", syncAddedPanel);

    // bfcache: stan "Dodano" odświeżony z /cart.js albo schowany
    window.addEventListener("pageshow", async e => {
      if (!e.persisted || !addedState) return;
      const cart = await fetchCartSafe(2500);
      const line = cart ? findCartLineByHash(cart, addedState.cfgHash) : null;
      if (line) updateAddedCount(line.quantity); else hideAddedState();
    });
  }

  // TRYB PRÓBKI: minimalny payload, variant próbki + 4 properties (Styl/Kolor/Wykończenie/Wymiar próbki).
  // Pełen-front properties (System IKEA, Wymiary mm, Klasa wysyłki itd.) NIE lecą do koszyka próbki.
  function buildSampleFormData() {
    const formData = new FormData();
    formData.set("id", SAMPLE_VARIANT_ID);
    formData.set("quantity", "1");
    const sampleProps = buildSampleProperties();
    for (const [k, v] of Object.entries(sampleProps)) {
      if (v != null && String(v).trim() !== "") formData.set(`properties[${k}]`, v);
    }
    return formData;
  }

  // PEŁNY FRONT (sciezka awaryjna SET_MODE = false): migawka z formularza -> buildLineProps (C2: jedna funkcja buduje properties).
  // 10.2026: dawne cialo funkcji (skladanie Front/Kolor/System / Typ/Opcje i _pl_summary) przeniesione do buildLineProps 1:1.
  function buildFullFormData(form) {
    const formData = new FormData(form);
    const cond = state.conditional || {};
    const HANDLED = new Set([COND_KEY_WYKONCZENIE, COND_KEY_SLOJE]);
    const opt = {}, opis = {};
    Object.keys(cond).forEach(label => {
      const v = cond[label];
      if (HANDLED.has(label) || !v || !String(v).trim()) return;
      opt[label] = v;
      const d = $(`[data-knf-cond-custom="${label}"]`)?.value;
      if (d && d.trim()) opis[label] = d.trim();
    });
    const line = {
      kind: "front",
      sys: formData.get("properties[System IKEA]") || "",
      typ: formData.get("properties[Typ elementu]") || "",
      szer: formData.get("properties[Szerokość mm]") || state.szer || "",
      wys: formData.get("properties[Wysokość mm]") || state.wys || "",
      dim: formData.get("properties[Wymiar]") || "",
      opt, opis,
      grubosc: formData.get("properties[Grubość frontu]") || null,
      tag: null,
    };
    const props = buildLineProps(line, snapshotCommon(), { uwagi: formData.get("properties[Uwagi klienta]") || "" });
    const out = new FormData();
    out.set("id", formData.get("id") || "");
    out.set("quantity", formData.get("quantity") || "1");
    Object.keys(props).forEach(k => { if (String(props[k]).trim()) out.set(`properties[${k}]`, props[k]); });
    return out;
  }

  // Jedyna ścieżka dodawania. Zwraca true po sukcesie.
  async function performAdd(form, formData, opts) {
    opts = opts || {};
    const sample = !!opts.sample;
    const attempt = opts.attempt || 1;
    const submitBtn = $("[data-knf-submit]");
    const idleText = sample ? SAMPLE_ADD_BTN_TEXT : L_BTN.DODAJ;
    const variantId = String(formData.get("id") || "");
    const qty = Math.max(1, parseInt(formData.get("quantity"), 10) || 1);
    const cfgHash = buildConfigHash(formData);
    let qtyBefore = opts.qtyBefore || 0;
    const setBusy = (on) => {
      if (!submitBtn) return;
      submitBtn.disabled = on;
      submitBtn.textContent = on ? L_BTN.ADDING : idleText;
      if (on) submitBtn.classList.remove("knf__btn--incomplete");
    };

    form.dataset.knfSubmitting = "1";
    setBusy(true);
    hideInlineError();

    // 1. Idempotencja: identyczna linia już w koszyku albo (gdy koszyka nie da się odczytać)
    //    ta sama konfiguracja dodana <5 min. Klient może potwierdzić "Tak, dodaj".
    //    Pytanie tylko przy próbce. Front idzie od razu, bo w kuchni często jest kilka
    //    identycznych (decyzja Konrada 19.09.2026); qtyBefore liczymy dalej dla licznika.
    if (!opts.skipDupCheck) {
      const cartBefore = await fetchCartSafe(1500);
      const line = cartBefore ? findCartLineByHash(cartBefore, cfgHash) : null;
      const recent = !cartBefore && !!(lastAdded && lastAdded.hash === cfgHash && (Date.now() - lastAdded.ts) < LAST_ADDED_TTL_MS);
      qtyBefore = line ? line.quantity : 0;
      if ((line || recent) && sample) {
        setBusy(false);
        delete form.dataset.knfSubmitting;
        trackKnf("knf_duplicate_blocked", { sample, in_cart: qtyBefore, recent });
        const ok = await confirmDuplicate();
        if (!ok) { tryUpdate(); return false; }
        return performAdd(form, formData, { sample, attempt, qtyBefore, skipDupCheck: true });
      }
    }

    // 2. /cart/add.js: JEDYNE miejsce, które może pokazać błąd
    let data;
    try {
      formData.set("sections", DRAWER_SECTION_ID);
      formData.set("sections_url", window.location.pathname);
      const r = await fetch(`${CART_ROOT}cart/add.js`, { method: "POST", body: formData, headers: { Accept: "application/json" } });
      let body = null;
      try { body = await r.json(); } catch (_) { /* nie-JSON, np. strona błędu */ }
      if (!r.ok || !body || body.status) {
        const err = new Error(`status_${r.status}`);
        err.description = body ? (body.description || body.message || "") : "";
        throw err;
      }
      data = body;
    } catch (err) {
      console.error("[knf] add failed", err);
      delete form.dataset.knfSubmitting;
      pendingRetry = { formData, sample, cfgHash, variantId, qty, qtyBefore, attempt };
      showInlineError(err.description || L_ADD.ERR_TITLE);
      trackKnf("knf_add_error", { sample, attempt, message: String(err.description || err.message || "") });
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = L_ADD.ERR_BTN; }
      setTimeout(() => { if (!isAddBusy()) tryUpdate(); }, 2000);
      return false;
    }

    // 3. Sukces. Od tego miejsca nic nie pokazuje błędu ani alertu.
    const prevCount = readHeaderCount();
    lastAdded = { hash: cfgHash, qty, ts: Date.now() };
    pendingRetry = null;
    delete form.dataset.knfSubmitting;
    showAddedState({ sample, cfgHash, label: currentConfigLabel(sample), count: qtyBefore + qty });
    trackKnf("knf_add_success", { sample, qty, attempt, variant_id: variantId });
    resetQty();
    try {
      await afterAddSuccess(form, { variantId, qty, cfgHash, sections: data && data.sections, itemCount: prevCount + qty });
    } catch (err) {
      console.warn("[knf] po dodaniu (nieblokujące)", err);
    }
    return true;
  }

  // Zdarzenie dla motywu + licznik z /cart.js + szuflada. Nigdy nie pokazuje błędu.
  async function afterAddSuccess(form, { variantId, qty, cfgHash, sections, itemCount }) {
    const ev = await emitCartLinesUpdate(form, variantId, qty);
    resolveCartUpdate(ev, { cart: null, sections, itemCount });
    applyDrawerHtmlFallback(sections ? sections[DRAWER_SECTION_ID] : null, itemCount);
    const drawerP = ensureCartDrawerOpen(2500);
    const cart = await fetchCartSafe(2500);
    if (cart) {
      const line = findCartLineByHash(cart, cfgHash);
      if (line) updateAddedCount(line.quantity);
      if (cart.item_count !== itemCount) {
        const icon = document.querySelector("cart-icon");
        if (icon && typeof icon.renderCartBubble === "function") icon.renderCartBubble(cart.item_count, false);
      }
    }
    await drawerP;
  }

  // "Spróbuj ponownie": najpierw /cart.js. Jeśli linia urosła, sukces bez drugiego POST.
  async function verifyThenRetry(form) {
    if (pendingSet) { if (!isAddBusy()) await retrySet(form); return; }   // 10.2026 zestaw: uzgodnienie per linia (reconcileSet)
    const p = pendingRetry;
    if (!p || isAddBusy()) return;
    const retryBtn = $("[data-knf-error-retry]");
    const msg = $("[data-knf-error-msg]");
    if (retryBtn) retryBtn.disabled = true;
    if (msg) msg.textContent = L_ADD.ERR_CHECKING;
    const cart = await fetchCartSafe(2500);
    const line = cart ? findCartLineByHash(cart, p.cfgHash) : null;
    if (retryBtn) retryBtn.disabled = false;
    if (line && line.quantity > p.qtyBefore) {
      pendingRetry = null;
      lastAdded = { hash: p.cfgHash, qty: p.qty, ts: Date.now() };
      hideInlineError();
      showAddedState({ sample: p.sample, cfgHash: p.cfgHash, label: currentConfigLabel(p.sample), count: line.quantity });
      trackKnf("knf_add_retry_verified", { sample: p.sample, attempt: p.attempt });
      resetQty();
      try {
        const ev = await emitCartLinesUpdate(form, p.variantId, p.qty);
        resolveCartUpdate(ev, { cart, sections: null, itemCount: cart.item_count });
        await ensureCartDrawerOpen(2500);
      } catch (err) {
        console.warn("[knf] po weryfikacji (nieblokujące)", err);
      }
      return;
    }
    hideInlineError();
    await performAdd(form, p.formData, { sample: p.sample, attempt: p.attempt + 1, qtyBefore: p.qtyBefore, skipDupCheck: true });
  }

  // -- 8. Inicjalizacja + walidatorzy submit
  function bindSubmit() {
    const form = $("[data-knf-form]");
    // Notatki: live save
    const notesEl = form.querySelector('textarea[name="properties[Uwagi klienta]"]');
    if (notesEl) notesEl.addEventListener("input", saveState);
    form.addEventListener("submit", async e => {
      // Jeden przycisk submit, ale blokada zostaje: drugie tapnięcie w trakcie /cart/add.js
      // albo w trakcie pytania o duplikat = nic (dawniej duplikat pozycji w koszyku).
      e.preventDefault();
      if (form.dataset.knfSubmitting === "1" || form.dataset.knfConfirming === "1") return;
      // 10.2026 zestaw: jedno żądanie cart/add.js z items[] dla calej listy (D3)
      if (SET_MODE && !state.sample_mode) {
        if (!canAddSet()) { scrollToFirstSetIssue(); return; }
        await performAddSet(form);
        return;
      }
      if (!canAddToCart()) { scrollToFirstMissingField(); return; }
      // STAY ON PAGE flow: /cart/add.js przez performAdd(), konfiguracja ZOSTAJE
      // (klient zmienia np. tylko wymiary dla kolejnego elementu).
      const sample = !!state.sample_mode;
      const formData = sample ? buildSampleFormData() : buildFullFormData(form);
      await performAdd(form, formData, { sample });
    });

    $("[data-knf-system]").addEventListener("change", tryUpdate);
    $("[data-knf-typ]").addEventListener("change", tryUpdate);

    // Custom input live validation: zmiana wartości → odśwież submit state
    document.addEventListener("input", e => {
      if (e.target.matches("[data-knf-cond-custom]")) tryUpdate();
    });
  }

  // -- Renumber kroki dynamicznie żeby nie było luk (Krok 1, 2, 4 → Krok 1, 2, 3)
  // Wywoływane po każdej zmianie widoczności fieldsetów (renderConditional, renderTypesForSystem, sysSel change).
  function renumberSteps() {
    const stepLabel = locale === "en" ? "Step" : locale === "de" ? "Schritt" : "Krok";
    // wer3: najpierw same odczyty (bez przeplatania z zapisami), zapis tylko przy zmianie - kazdy zapis budzil
    // obserwator rysunku i jedno klikniecie stylu dawalo ~20 przerysowan (telefon 2-3x wolniejszy niz live)
    // runda 3: zwiniete kroki 1-3 (linia wspolnych wyborow) zachowuja numery, wiec System IKEA zostaje krokiem 4 jak na live
    const col = root.classList.contains("knf--collapsed");
    const vis = $$(".knf__fs").filter(fs => fs.querySelector(".knf__step") && !fs.hidden && (getComputedStyle(fs).display !== "none" || (col && fs.matches("[data-knf-common]"))));
    vis.forEach((fs, i) => {
      const stepEl = fs.querySelector(".knf__step"), n = String(i + 1), txt = `${stepLabel} ${n}`;
      if (stepEl.textContent !== txt) stepEl.textContent = txt;
      if (stepEl.dataset.n !== n) stepEl.dataset.n = n; // redesign 2026-09-23: CSS pokazuje sama cyfre (::before), tekst zostaje dla czytnikow
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // ==========================================================================
  // ZESTAW FRONTOW (10.2026, TEMPO 6): model danych. Pozycja = migawka typu, wymiaru i opcji elementu (E);
  // opcje wspolne (G: styl, kolor, strona, wykonczenie, sloje) zyja w stanie strony i licza sie przy kazdym odczycie.
  // ==========================================================================
  let customOpen = false;    // formularz „Inny wymiar” otwarty w trybie zestawu
  let recalcMute = 0;        // > 0: kliki programowe (start, przywracanie, auto-kolor) nie przeliczaja listy z komunikatem
  let uidSeq = 0;
  let lastUid = null;        // ostatnio dodana / zmieniana pozycja (rysunek, panel)
  const newUid = () => "k" + (Date.now() % 1679616).toString(36) + (uidSeq++).toString(36);
  const tileKey = (sys, typ, szer, wys) => `${sys}|${typ}|${szer}|${wys}`;
  const styleObjOf = styl => (cfg.styles.styles || []).find(s => s.name === styl) || null;
  const defByLabel = label => { const d = cfg.rules.definitions || {}; const k = Object.keys(d).find(x => d[x].label === label); return k ? d[k] : null; };
  // Klucze opcji stylu; Z1: zawiasy dla kazdego stylu (opcjonalne poza stylami z uchwytem)
  function styleOpts(styl) {
    const r = (cfg.rules.rules || {})[styl];
    const opts = (r && Array.isArray(r.options)) ? r.options.slice() : [];
    if (SET_MODE && HINGE_OPTIONAL && cfg.rules.definitions && cfg.rules.definitions[HINGE_KEY] && !opts.includes(HINGE_KEY)) opts.push(HINGE_KEY);
    return opts;
  }
  function styleHasLabel(styl, label) {
    return styleOpts(styl).some(k => cfg.rules.definitions[k] && cfg.rules.definitions[k].label === label);
  }
  // Wartosci PL opcji dostepne w stylu (Round: rogi uchwytu; pozostale bez rogow) - jak renderConditional
  function styleValues(styl, def) {
    const v = def.values || [];
    return def.perStyle && def.perStyle[styl] ? def.perStyle[styl].filter(x => v.includes(x)) : v.filter(x => !(def.hideElse || []).includes(x));
  }
  function tOptVal(label, val) { return tCondPair(label, val).val; }
  // Kolor aktywny jako hex (miniatury): paleta albo kolor z palet RAL/ICA/F&B
  function activeHex() {
    const col = $(".knf__color.is-active");
    if (col && !col.classList.contains("knf__color--custom")) return col.style.getPropertyValue("--kclr").trim();
    const ci = $("[data-knf-kolor-custom]");
    return (ci && ci.dataset.hex) || (window.knfPalHex && ci ? window.knfPalHex(ci.value) || "" : "");
  }
  // Opcje wspolne zestawu (G) - czytane z tych samych miejsc co dawny buildFullFormData
  function snapshotCommon() {
    const styl = state.styl || "";
    const so = styleObjOf(styl);
    const isWood = !!(so && so.category && so.category !== "lakier");
    const ci = $("[data-knf-kolor-custom]");
    const cond = state.conditional || {};
    return {
      styl, category: (so && so.category) || "lakier", isWood,
      kolorOut: $("[data-knf-kolor-out]")?.value || "",
      custom: isWood ? "" : (ci ? ci.value : ""),
      customPl: ci ? ci.dataset.pl || "" : "", customPlFor: ci ? ci.dataset.plFor || "" : "",
      hex: activeHex(), strona: state.strona || "Jednostronnie",
      wyk: cond[COND_KEY_WYKONCZENIE] || "", sloje: cond[COND_KEY_SLOJE] || "",
    };
  }
  // Kolor do koszyka: { tr: wartosc w jezyku rynku, pl: do _pl_summary } - 1:1 z dawnym buildFullFormData
  function colorParts(c) {
    let kolor = (c.isWood || (c.kolorOut && /usłojenia|naturaln|sklejka/i.test(c.kolorOut))) ? "Naturalne drewno" : c.kolorOut;
    const kc = c.custom || "";
    if (kc && kc.trim() && kc !== kolor) kolor = `Inny: ${kc.trim()}`;
    let pl = kolor, tr = kolor;
    if (kolor && /usłojenia|naturaln|sklejka/i.test(kolor)) { tr = L_PROP.V_NAT_DREWNO; pl = "Naturalne drewno"; }
    if (kolor && kolor.startsWith("Inny: ")) { tr = `${L_PROP.V_INNY}: ${kolor.slice(6)}`; pl = kolor; }
    else if (kolor && tr === kolor) tr = tColor(kolor);
    if (kc && c.customPl && c.customPlFor === kc.trim()) pl = pl.replace(kc.trim(), c.customPl);
    return { pl, tr };
  }
  const lineCond = c => ({ [COND_KEY_WYKONCZENIE]: c.wyk, [COND_KEY_SLOJE]: c.sloje });
  // BLEND_RATE "gladki": blendy i panele dzielace w stylach lakierowanych po stawce Gładki (decyzja Konrada otwarta)
  function lineStyl(l, c) {
    if (BLEND_RATE === "gladki" && noDrillTyp(l.typ) && c.category === "lakier" && styleObjOf("Gładki")) return "Gładki";
    return c.styl;
  }
  // Konrad 06.10.2026: panele, blendy, cokoly i listwy w zestawie zawsze jednostronnie, takze przy frontach dwustronnych
  // (cena i opis „Opcje” dla produkcji z tej samej funkcji)
  const lineStrona = (l, c) => (isPanelTypName(l.typ) || noHandleTyp(l.typ)) ? "Jednostronnie" : (c.strona || "Jednostronnie");
  function lineVariant(l, c) {
    if (l.kind === "korpus") return null;
    return findVariantFor({ styl: lineStyl(l, c), strona: lineStrona(l, c), typ: l.typ, szer: l.szer, wys: l.wys, cond: lineCond(c), grubosc: l.grubosc });
  }
  // Grosze/centy rynku na sztuke: tak samo liczy kasa (kurs efektywny, promocja na sztuce obcieta w dol)
  function marketUnit(cents, styl) {
    const { rate } = getActiveCurrency(styl);
    const unit = Math.round(roundMarket((cents / 100) * rate) * 100);   // jak kasa: roundMarket (Python round) z 03.10.2026
    return PROMO ? unit - Math.floor(unit * PROMO.pct / 100) : unit;
  }
  function fmtMarket(cents, short) {
    const { code, locale: loc } = getActiveCurrency();
    const o = { style: "currency", currency: code };
    if (short && cents % 100 === 0) { o.minimumFractionDigits = 0; o.maximumFractionDigits = 0; }
    return new Intl.NumberFormat(loc, o).format(cents / 100);
  }
  // Cena sztuki pozycji w walucie rynku; korpus = cena z data-korpus (juz w walucie rynku, bez kursu - jak probka)
  function lineUnit(l, c) {
    if (l.kind === "korpus") return KORPUS ? Number(KORPUS.p) : null;
    const v = lineVariant(l, c);
    return v ? marketUnit(v.price, v.option1) : null;
  }
  function lineNeedsExt(l) {
    if (l.kind === "korpus") return false;
    return l.typ === EXT_TYP || (l.dim === "Niestandardowy" && l.sys !== "NA WYMIAR" && !NO_DRILL.test(l.typ || ""));
  }
  // Podpis pozycji (laczenie przy dodaniu): typ + wymiar + opcje elementu + kontekst + uwaga
  function lineSig(l) {
    if (l.kind === "korpus") return ["K", l.grp, l.szer, l.wys, l.gl, l.gap, l.polka ? 1 : 0, l.fronty ? 1 : 0, l.note || ""].join("|");
    const o = l.opt || {}, d = l.opis || {};
    const os = Object.keys(o).sort().map(k => `${k}=${o[k]}`).join(",");
    const ds = Object.keys(d).sort().map(k => `${k}=${d[k]}`).join(",");
    return [l.sys, l.typ, l.szer, l.wys, l.dim || "", os, ds, l.grubosc || "", l.tag || "", l.note || "", l.grp || ""].join("|");
  }
  // Kierunek slojow wymuszony wymiarem lub typem (fornir, sklejka): cokol zawsze poziomo (Konrad 04.10.2026), element < 10 cm wzdluz dluzszego boku
  function forcedGrain(styl, typ, szer, wys) {
    const so = styleObjOf(styl);
    if (!so || !so.category || so.category === "lakier" || !styleHasLabel(styl, GRAIN_LABEL)) return "";
    if (isPlinthTyp(typ)) return "Poziomy";
    return szer > 0 && wys > 0 && szer !== wys && Math.min(szer, wys) < GRAIN_MIN_MM ? (wys < szer ? "Poziomy" : "Pionowy") : "";
  }
  window.knfForcedGrain = (typ, szer, wys) => (SET_MODE && !state.sample_mode ? forcedGrain(state.styl, typ, szer, wys) : "");   // rysunek glowny
  // Wymuszenia (dawniej syncGrainOption / syncHingeOption na stanie): drewno < 10 cm = sloje wzdluz; uchwyt z boku = zawiasy przeciwne
  function forcedOpts(opt, styl, typ, szer, wys) {
    const g = forcedGrain(styl, typ, szer, wys);
    if (g) opt[GRAIN_LABEL] = g;
    if (needsHinge(typ) && hingeMode(styl, typ) !== "none") {
      const loc = opt["Lokalizacja uchwytu"] || "";
      const f = /^Prawa/.test(loc) ? "Lewa" : /^Lewa/.test(loc) ? "Prawa" : "";
      if (f) opt[HINGE_LABEL] = f;
    } else delete opt[HINGE_LABEL];
    return opt;
  }
  const condSel = label => root.querySelector(`select[data-knf-cond="${cssEscape(label)}"]`);
  // Migawka „ustawien dodawanych elementow” (selecty E nad kafelkami) dla konkretnego typu i wymiaru
  function snapshotAdder(typ, szer, wys, dim) {
    const styl = state.styl || "";
    const opt = {}, opis = {};
    if (!noHandleTyp(typ) && styleHasLabel(styl, "Lokalizacja uchwytu")) {
      const s = condSel("Lokalizacja uchwytu");
      if (s && s.value) {
        opt["Lokalizacja uchwytu"] = s.value;
        const d = $(`[data-knf-cond-custom="${cssEscape("Lokalizacja uchwytu")}"]`);
        if (/Niestandardow/i.test(s.value) && d && d.value.trim()) opis["Lokalizacja uchwytu"] = d.value.trim();
      }
    }
    if (needsHinge(typ) && hingeMode(styl, typ) !== "none") {
      const s = hingeSel();
      if (s && s.value) opt[HINGE_LABEL] = s.value;
    }
    if (styleHasLabel(styl, GRAIN_LABEL)) {
      const s = condSel(GRAIN_LABEL);
      const v = s ? (s.disabled && s.dataset.user ? s.dataset.user : s.value) : "";
      if (v) opt[GRAIN_LABEL] = v;
    }
    if (lineNeedsExt({ typ, dim, sys: $("[data-knf-system]")?.value || "" })) {
      const s = extSel();
      if (s && !s.disabled && s.value) opt[EXT_LABEL] = s.value;
    }
    forcedOpts(opt, styl, typ, szer, wys);
    return { opt, opis };
  }
  // Pozycja z biezacych ustawien dodawacza
  function adderLine(sys, typ, szer, wys, dim, extra) {
    const s = snapshotAdder(typ, szer, wys, dim);
    return Object.assign({ kind: "front", sys, typ, szer, wys, dim: dim || "", opt: s.opt, opis: s.opis,
      grubosc: sys === "NA WYMIAR" ? (state.grubosc || stdGrub()) : adderGrub(sys, typ), tag: null, kit: null, note: "", grp: null }, extra || {});
  }
  // Kod braku pozycji: "" = OK; hinge, ext, opis, type, range, area, variant (TEMPO 6.2)
  function validateLine(l, c) {
    if (l.kind === "korpus") return !KORPUS ? "variant" : (!NAD_PRICES[l.szer] ? "range" : "");
    const dps = cfg.dimensions.dimensions_per_system_type || {};
    const virtual = [NAD_DOOR_TYP, KIT_TYP_A, KIT_TYP_B, "Front niestandardowy"].includes(l.typ);
    if (!virtual && dps[l.sys] && Object.keys(dps[l.sys]).length && !dps[l.sys][l.typ]) return "type";
    const so = styleObjOf(c.styl);
    if (!virtual && so && Array.isArray(so.allowed_types) && so.allowed_types.length && !so.allowed_types.includes(l.typ)) return "type";
    const cm = cfg.rules.common;
    if (l.szer < cm.min_szerokosc_mm || l.szer > cm.max_szerokosc_mm || l.wys < cm.min_wysokosc_mm || l.wys > cm.max_wysokosc_mm) return "range";
    const lim = TYPE_LIMITS[l.typ];
    if (lim) { const s = Math.min(l.szer, l.wys); if (s < lim.min || s > lim.max) return "range"; }
    const m2 = calcM2For(l.szer, l.wys, lineCond(c), l.grubosc);
    if (!isPanelTypName(l.typ) && m2 && m2.billedForVariant > MAX_M2_BUCKET) return "area";
    if (!lineVariant(l, c)) return "variant";
    const o = l.opt || {};
    if (Object.keys(o).some(k => /Niestandardow/i.test(o[k] || "") && !String((l.opis || {})[k] || "").trim())) return "opis";
    if (hingeMode(lineStyl(l, c), l.typ) === "required" && !o[HINGE_LABEL]) return "hinge";
    if (lineNeedsExt(l) && !o[EXT_LABEL]) return "ext";
    return "";
  }
  function missText(code, l, c) {
    if (code === "hinge") return HINGE_MISS[locale] || HINGE_MISS.pl;
    if (code === "ext") return EXT_MISS[locale] || EXT_MISS.pl;
    if (code === "opis") return missMsg("MISS_OPIS");
    if (code === "type") return sx("T_ROW_UNAVAIL", { styl: tStyle(c.styl) });
    if (code === "area") return LMax("areaOver2");
    if (code === "range") {
      if (l.kind === "korpus") return sx("NAD_W_ASK", { n: Math.round(l.szer / 10) });
      const lim = TYPE_LIMITS[l.typ];
      return lim ? sx(lim.msg) : LM("dimRange");
    }
    return LM("unavailable");
  }
  // Segmenty „Opcje” pozycji frontu: [{ pl, tr }] - kolejnosc jak dzis: wykonczenie, strona, sloje, kontekst, opcje E, grubosc
  function lineSegs(l, c) {
    const out = [];
    const styl = lineStyl(l, c);
    if (c.wyk) out.push({ pl: c.wyk, tr: tFinish(c.wyk) });
    const strona = lineStrona(l, c);
    out.push(strona === "Dwustronnie" ? { pl: "Dwustronnie", tr: L_PROP.V_DWUSTR } : { pl: "Jednostronnie", tr: L_PROP.V_JEDNOSTR });
    if (c.sloje === "Tak") out.push({ pl: "Słoje przechodzące: Tak (+20%)", tr: L_PROP.V_SLOJE_TAK });
    if (l.tag) out.push({ pl: l.tag, tr: tTag(l.tag), ctx: 1 });
    if (noDrillTyp(l.typ)) out.push({ pl: SET_I18N.pl.V_NO_DRILL, tr: sx("V_NO_DRILL"), ctx: 1 });
    const o = l.opt || {}, d = l.opis || {};
    const keys = E_ORDER.filter(k => o[k]).concat(Object.keys(o).filter(k => !E_ORDER.includes(k) && o[k]));
    keys.forEach(label => {
      const p = tCondPair(label, o[label]);
      const op = d[label] && String(d[label]).trim() ? ` (${String(d[label]).trim()})` : "";
      out.push({ pl: `${label}: ${o[label]}${op}`, tr: `${p.label}: ${p.val}${op}`, e: label });
    });
    const gr = l.grubosc || (frontThicknessMm(styl) ? `${frontThicknessMm(styl)} mm` : "");
    if (gr) out.push({ pl: `Grubość: ${gr}`, tr: `${L_PROP.V_GRUBOSC}: ${gr}`, gr });
    return out;
  }
  // C2: JEDYNA funkcja budujaca properties frontu (czysta). Klucze i kolejnosc 1:1 z dawnym buildFullFormData:
  // Uwagi klienta, Front, Kolor, System / Typ, Opcje, _wys_mm, _szer_mm, _grubosc_mm, _pl_summary.
  function buildLineProps(l, c, o) {
    if (l.kind === "korpus") return propsKorpus(l, c, o);
    const P = {};
    if (o && o.uwagi && String(o.uwagi).trim()) P["Uwagi klienta"] = o.uwagi;
    const styl = lineStyl(l, c);
    const stylTr = styl ? tStyle(styl) : "";
    const szerCm = Math.round(Number(l.szer) / 10), wysCm = Math.round(Number(l.wys) / 10);
    const wymiarCmFinal = (l.szer && l.wys && szerCm && wysCm) ? `${szerCm}×${wysCm} cm` : "";
    const wymiarMm = (l.szer && l.wys) ? ` (${l.szer}×${l.wys} mm)` : "";
    // Wymiar spoza presetu IKEA: "Niestandardowy" (Inny wymiar) lub pusty (NA WYMIAR / brak presetów)
    const isCustomDim = !l.dim || l.dim === "Niestandardowy";
    const customDimTag = isCustomDim ? ` · ${CUSTOM_DIM_TAG[locale] || CUSTOM_DIM_TAG.pl}` : "";
    const col = colorParts(c);
    const front = [];
    if (stylTr) front.push(stylTr);
    if (wymiarCmFinal) front.push(wymiarCmFinal + wymiarMm + customDimTag);
    else if (wymiarMm) front.push(wymiarMm.trim().slice(1, -1) + customDimTag);
    if (front.length) P[L_PROP.K_FRONT] = front.join(" · ");
    if (col.tr) P[L_PROP.K_KOLOR] = col.tr;
    const sysTyp = [l.sys ? tSystem(l.sys) : "", l.typ ? tType(l.typ) : ""].filter(Boolean).join(" · ");
    if (sysTyp) P[L_PROP.K_SYS_TYP] = sysTyp;
    const segs = lineSegs(l, c);
    if (segs.length) P[L_PROP.K_OPCJE] = segs.map(s => s.tr).join(" · ");
    if (l.wys) P._wys_mm = String(l.wys);
    if (l.szer) P._szer_mm = String(l.szer);
    const gr = segs.find(s => s.gr);
    if (gr) P._grubosc_mm = gr.gr.replace(/\D+/g, "");
    // PL podsumowanie dla Pro-Hand (Order Printer) - zawsze, self-labeled
    const plCustomTag = isCustomDim ? ` · ${CUSTOM_DIM_TAG.pl}` : "";
    const plFront = [];
    if (styl) plFront.push(styl);
    if (wymiarCmFinal) plFront.push(wymiarCmFinal + wymiarMm + plCustomTag);
    else if (wymiarMm) plFront.push(wymiarMm.trim().slice(1, -1) + plCustomTag);
    const plSysTyp = [l.sys, l.typ].filter(Boolean).join(" · ");
    const plSummary = [
      plFront.length ? `Front: ${plFront.join(" · ")}` : "",
      col.pl ? `Kolor: ${col.pl}` : "",
      plSysTyp ? `System / Typ: ${plSysTyp}` : "",
      segs.length ? `Opcje: ${segs.map(s => s.pl).join(" · ")}` : "",
    ].filter(Boolean).join(" | ");
    if (plSummary) P._pl_summary = plSummary;
    return P;
  }
  // C3: korpus nadstawki - klucze tylko z KNOWN_KEYS Order Printera (System / Typ, Kolor, Opcje, Uwagi klienta) + ukryte _*
  function propsKorpus(l, c, o) {
    const P = {};
    if (o && o.uwagi && String(o.uwagi).trim()) P["Uwagi klienta"] = o.uwagi;
    const w = Number(l.szer), h = Number(l.wys), g = Number(l.gl), gap = Number(l.gap);
    const doorsN = (NAD.DOORS[w] || []).length;
    const cmTxt = mm => new Intl.NumberFormat(locale === "en" ? "en" : locale, { maximumFractionDigits: 1 }).format(mm / 10);
    P[L_PROP.K_SYS_TYP] = sx("KP_SYSTYP");
    P[L_PROP.K_KOLOR] = sx("KP_KOLOR");
    const segs = [`${Math.round(w / 10)}×${Math.round(h / 10)}×${Math.round(g / 10)} cm (${w}×${h}×${g} mm)`, sx(l.polka ? "KP_SHELF1" : "KP_SHELF0"), sx("KP_HINGES", { n: 2 * doorsN }), sx("KP_CEIL", { cm: cmTxt(gap) })];
    if (!l.fronty) segs.push(sx("KP_NOFRONTS"));
    P[L_PROP.K_OPCJE] = segs.join(" · ");
    P._szer_mm = String(w);
    P._wys_mm = String(h);
    P._gl_mm = String(g);
    P._szczelina_mm = String(gap);
    const plOp = [`Głębokość: ${Math.round(g / 10)} cm (${g} mm)`, l.polka ? "Półka: 1 szt." : "Półka: brak", `Zawiasy: ${2 * doorsN} szt. w komplecie`, `Do sufitu: ${gap} mm`];
    if (!l.fronty) plOp.push("Fronty: bez frontów");
    P._pl_summary = `Front: Korpus · ${Math.round(w / 10)}×${Math.round(h / 10)} cm (${w}×${h} mm) | Kolor: Biały | System / Typ: PAX · ${NAD_KORPUS_TYP} | Opcje: ${plOp.join(" · ")}`;
    return P;
  }
  // Suma zestawu w walucie rynku (TEMPO 6.3): Σ cena sztuki × ilosc; issues = pozycje niepelne
  function setTotals(c) {
    c = c || snapshotCommon();
    let qty = 0, cents = 0, issues = 0;
    state.set.forEach(l => {
      qty += l.qty;
      const u = lineUnit(l, c);
      if (u != null) cents += u * l.qty;
      if (validateLine(l, c)) issues += 1;
    });
    return { rows: state.set.length, qty, cents, issues };
  }
  // +n szt. do pozycji o tym samym podpisie albo nowa pozycja; limit 50 szt. na pozycje (jak dzis ilosc)
  function addLine(line, n) {
    n = n || 1;
    const sig = lineSig(line);
    let row = state.set.find(l => lineSig(l) === sig);
    if (row) {
      if (row.qty + n > 50) return null;
      row.qty += n;
    } else {
      if (n > 50) return null;
      row = Object.assign({ uid: newUid(), kind: "front", opt: {}, opis: {}, grubosc: null, tag: null, kit: null, note: "", grp: null }, line, { qty: n });
      state.set.push(row);
    }
    row.ts = Date.now();
    lastUid = row.uid;
    return row;
  }
  // „−” na kafelku: pozycja zgodna z biezacymi ustawieniami, a gdy jej nie ma - ostatnio zmieniana z tym wymiarem
  function subTile(key) {
    const cand = state.set.filter(l => l.kind !== "korpus" && !l.grp && !l.tag && tileKey(l.sys, l.typ, l.szer, l.wys) === key);
    if (!cand.length) return null;
    const p = key.split("|");
    const sig = lineSig(adderLine(p[0], p[1], Number(p[2]), Number(p[3]), cand[0].dim));
    const row = cand.find(l => lineSig(l) === sig) || cand.slice().sort((a, b) => b.ts - a.ts)[0];
    row.qty -= qtyStep(row.typ);
    row.ts = Date.now();
    if (row.qty <= 0) state.set.splice(state.set.indexOf(row), 1);
    return row;
  }
  const tileCount = key => state.set.reduce((a, l) => a + (l.kind !== "korpus" && !l.grp && !l.tag && tileKey(l.sys, l.typ, l.szer, l.wys) === key ? l.qty : 0), 0);
  // Nazwa pozycji do listy, ogloszen i komunikatow: „Drzwi 60×80”, „Nadstawka PAX 75×28×58 cm”
  function lineName(l) {
    if (l.kind === "korpus") return sx("NAD_ROW", { d: `${Math.round(l.szer / 10)}×${Math.round(l.wys / 10)}×${Math.round(l.gl / 10)} cm` });
    const m = /^([\d,.]+)\s*cm\s*x\s*([\d,.]+)\s*cm/i.exec(l.dim || "");
    const dims = m ? `${m[1]}×${m[2]}` : l.dim === "Nadstawka" ? `${Math.round(l.szer / 10)}×${Math.round(l.wys / 10)}` : `${l.szer}×${l.wys} mm`;
    return `${tType(l.typ)} ${dims}`;
  }
  // -- Nadstawka: drzwi z korpusu (C2/C3). Styl z uchwytem: para Lewa/Prawa po N szt., uchwyt domyslnie „Dół”.
  const nadDepthCm = gl => (Number(gl) === 350 ? 35 : 58);
  function nadDoorLines(k) {
    const r = nadCompute(Number(k.szer), Number(k.gap), nadDepthCm(k.gl));
    const d = r.doors;
    if (!d.length || !k.fronty) return [];
    const styl = state.styl || "";
    const req = hingeMode(styl, NAD_DOOR_TYP) === "required";
    const tag = `Do nadstawki: ${Math.round(k.szer / 10)}×${Math.round(k.wys / 10)} cm`;
    const base = () => {
      const opt = {};
      if (styleHasLabel(styl, "Lokalizacja uchwytu")) opt["Lokalizacja uchwytu"] = "Dół";
      if (styleHasLabel(styl, GRAIN_LABEL)) { const s = condSel(GRAIN_LABEL), gd = defByLabel(GRAIN_LABEL); const v = (s && (s.dataset.user || s.value)) || (gd && gd.default) || ""; if (v) opt[GRAIN_LABEL] = v; }
      return opt;
    };
    const mk = (opt, qty) => ({ uid: newUid(), kind: "front", sys: "PAX", typ: NAD_DOOR_TYP, szer: d[0].szer, wys: d[0].wys, dim: "Nadstawka",
      opt: forcedOpts(opt, styl, NAD_DOOR_TYP, d[0].szer, d[0].wys), opis: {}, grubosc: null, tag, kit: null, note: "", grp: k.grp, qty, ts: Date.now() });
    if (d.length === 2 && req) return [mk(Object.assign(base(), { [HINGE_LABEL]: "Lewa" }), k.qty), mk(Object.assign(base(), { [HINGE_LABEL]: "Prawa" }), k.qty)];
    return [mk(base(), d.length * k.qty)];
  }
  // Zmiana stylu: para L/P przydzielana sama (bez pozycji „niepelnych”), korpus bez zmian
  function nadRegroup(c) {
    state.set.filter(l => l.kind === "korpus" && l.fronty).forEach(k => {
      const doors = state.set.filter(l => l.grp === k.grp && l.kind !== "korpus");
      if (!doors.length || (NAD.DOORS[k.szer] || []).length !== 2) return;
      const want = hingeMode(lineStyl(doors[0], c), NAD_DOOR_TYP) === "required";
      const have = doors.some(l => (l.opt || {})[HINGE_LABEL]);
      if (want === have) return;
      const notes = doors.map(l => l.note).filter(Boolean).join(" ");
      state.set = state.set.filter(l => !doors.includes(l));
      const fresh = nadDoorLines(k);
      if (notes) fresh.forEach(l => { l.note = notes; });
      state.set.splice(state.set.indexOf(k) + 1, 0, ...fresh);
    });
  }
  // Zmiana ilosci korpusu w liscie: drzwi tej nadstawki rosna proporcjonalnie
  function nadSyncDoorQty(k, oldQty) {
    if (!oldQty || oldQty === k.qty) return;
    state.set.filter(l => l.grp === k.grp && l.kind !== "korpus").forEach(l => {
      l.qty = Math.max(1, Math.min(50, Math.round(l.qty * k.qty / oldQty)));
    });
  }

  // -- Przeliczenie zestawu po zmianie opcji wspolnych (TEMPO 5.1). Pozycje dostaja domyslne / tracą niedostepne opcje E.
  let lastSetCents = null;
  function recalcSet(reason) {
    if (!SET_MODE || state.sample_mode) return;
    const c = snapshotCommon();
    const dropped = [], defs = [];
    const note = (arr, s) => { if (s && !arr.includes(s)) arr.push(s); };
    const fixLabel = (l, label, styl, keepDefault) => {
      const def = defByLabel(label);
      const o = l.opt;
      const has = styleHasLabel(styl, label) && !(label === "Lokalizacja uchwytu" && noHandleTyp(l.typ));
      if (!has) {
        if (o[label]) { delete o[label]; if (l.opis) delete l.opis[label]; const lb = tCondPair(label, "").label; note(dropped, locale === "de" ? lb : lb.toLowerCase()); }   // S2: rzeczowniki DE wielka litera
        return;
      }
      if (!def) return;
      const allowed = styleValues(styl, def);
      if (o[label] && allowed.includes(o[label])) return;
      const dv = keepDefault && allowed.includes(keepDefault) ? keepDefault : (allowed.includes(def.default) ? def.default : "");
      if (dv) { o[label] = dv; note(defs, sx("T_RECALC_DEF", { label: tCondPair(label, dv).label, value: tCondPair(label, dv).val })); }
      else delete o[label];
    };
    // M3: NA WYMIAR - grubosc standardowa idzie za stylem zestawu (Linea/Hampton 19 mm, reszta 18); 12, 16 i 30 mm to wybor klienta
    const tG = frontThicknessMm(c.styl), stdG = tG === 19 ? "19 mm" : "18 mm";
    state.set.forEach(l => {
      if (l.kind === "korpus") return;
      if (l.sys === "NA WYMIAR" && ["18 mm", "19 mm"].includes(l.grubosc)) l.grubosc = stdG;
      // 04.10.2026: grubosc per element - standard nowego stylu = null; grubosc, ktorej styl nie ma (np. drewno), wraca do standardu
      if (l.sys !== "NA WYMIAR" && l.grubosc) {
        const ls = lineStyl(l, c);
        if (l.grubosc === thickStd(ls)) l.grubosc = null;
        else if (!thickOpts(ls, l.typ, l.sys).includes(l.grubosc)) { note(defs, sx("T_RECALC_GRUB", { mm: l.grubosc, styl: tStyle(c.styl) })); l.grubosc = null; }
      }
      l.opt = l.opt || {};
      const styl = lineStyl(l, c);
      fixLabel(l, "Lokalizacja uchwytu", styl, l.typ === NAD_DOOR_TYP ? "Dół" : "");
      fixLabel(l, GRAIN_LABEL, styl, "");
      if (hingeMode(styl, l.typ) === "none" && l.opt[HINGE_LABEL]) delete l.opt[HINGE_LABEL];
      forcedOpts(l.opt, styl, l.typ, l.szer, l.wys);
    });
    nadRegroup(c);
    // S10: po zmianie stylu dwie pozycje moga stac sie identyczne (np. Grip -> Gładki gubi uchwyt) - scalamy je (uid starszej)
    const merged = [];
    state.set.forEach(l => {
      const sig = l.kind === "korpus" ? null : lineSig(l);
      const ex = sig ? merged.find(x => x.kind !== "korpus" && lineSig(x) === sig) : null;
      if (ex && ex.qty + l.qty <= 50) { ex.qty += l.qty; ex.ts = Math.max(ex.ts || 0, l.ts || 0); }
      else merged.push(l);
    });
    if (merged.length !== state.set.length) state.set = merged;
    const t = setTotals(c);
    const msgs = [];
    if (t.rows) {
      if (reason === "color") msgs.push(sx("T_RECALC_COLOR", { qty: t.qty }));
      else if (reason !== "restore" && lastSetCents != null && lastSetCents !== t.cents) msgs.push(sx("T_RECALC", { styl: tStyle(c.styl), old: fmtMarket(lastSetCents), new: fmtMarket(t.cents) }));
      if (dropped.length) msgs.push(sx("T_RECALC_DROP", { list: dropped.join(", ") }));
      defs.forEach(d => msgs.push(d));
      if (t.issues) msgs.push(sx("T_TO_FIX", { n: t.issues }) + ".");
    }
    setMsg(msgs.join(" "));
    renderTiles();
    afterSetChange({ silent: true });
  }

  // ==========================================================================
  // ZESTAW: renderowanie kafelkow (TEMPO M3, D6b, D7, 8.1)
  // ==========================================================================
  const FINE_PTR = window.matchMedia && window.matchMedia("(pointer: fine)").matches;
  const PRESET_WORDS_SET = {
    en: [["Panel Boczny", "Side panel"], ["Panel Górny (boki 12 mm)", "Top panel (12 mm sides)"], ["Panel Górny (boki 18 mm)", "Top panel (18 mm sides)"]],
    de: [["Panel Boczny", "Seitenpaneel"], ["Panel Górny (boki 12 mm)", "Oberpaneel (Seiten 12 mm)"], ["Panel Górny (boki 18 mm)", "Oberpaneel (Seiten 18 mm)"]],
    cs: [["Panel Boczny", "Boční panel"], ["Panel Górny (boki 12 mm)", "Horní panel (boky 12 mm)"], ["Panel Górny (boki 18 mm)", "Horní panel (boky 18 mm)"]],
  };
  const tPresetWords = s => (PRESET_WORDS_SET[locale] || []).reduce((a, [pl, tr]) => a.replace(pl, tr), s || "");
  // „60cm x 80cm Panel Górny…” -> { w: "60", cm: "60×80", extra: "Panel Górny…" }
  function parsePresetLabel(lbl, szer, wys) {
    const m = /^([\d,.]+)\s*cm\s*x\s*([\d,.]+)\s*cm\s*(.*)$/i.exec(lbl || "");
    if (m) return { w: m[1], cm: `${m[1]}×${m[2]}`, extra: m[3].trim() };
    return { w: String(Math.round(szer / 10)), cm: `${Math.round(szer / 10)}×${Math.round(wys / 10)}`, extra: "" };
  }
  const HINGE_SHORT = { pl: ["L", "P"], en: ["L", "R"], de: ["L", "R"], cs: ["L", "P"] };
  function tileHtml(sys, typ, p, c, x) {
    x = x || {};
    const key = tileKey(sys, typ, p.szer_mm, p.wys_mm) + (x.corner ? "|corner" : "");
    const pl = parsePresetLabel(p.label, p.szer_mm, p.wys_mm);
    if (typ === BESTA_SIDE || typ === BESTA_TOP) pl.extra = "";   // typ i naglowek grupy mowia juz, czy to bok, czy gora
    let cents = null;
    if (x.corner || x.pair) { let ok = true, sum = 0; (x.corner ? cornerLines(sys) : kitLines("B")).forEach(([l, n]) => { const u = lineUnit(l, c); if (u == null) ok = false; else sum += u * n; }); cents = ok ? sum : null; }
    else { const v = lineVariant({ kind: "front", typ, szer: p.szer_mm, wys: p.wys_mm, grubosc: adderGrub(sys, typ) }, c); cents = v ? marketUnit(v.price, v.option1) : null; }
    const ab = !x.corner && !x.pair && abPairTyp(typ);
    if (ab && cents != null) cents *= 2;
    const price = cents != null ? fmtMarket(cents, true) : "";
    const label = x.corner ? sx("T_CORNER_TILE") : x.pair || ab ? sx("T_PAIR_TILE", { cm: pl.cm }) : pl.cm;
    const name = x.corner ? sx("T_CORNER_NAME") : x.pair ? `${sx("T_PAIR_NAME")} ${pl.cm}` : `${tType(typ)} ${pl.cm}`;
    // UX v2 pkt 5: bez „+” i bez BESTSELLER; w zestawie pasek − N + na dole kafelka (caly kafelek dalej = +1)
    return `<div class="knf__tile${x.corner ? " knf__tile--corner" : ""}${x.pair ? " knf__tile--pair" : ""}" data-knf-tile="${escapeAttr(key)}"${x.corner ? " data-knf-corner" : ""}${x.pair ? ` data-knf-pair="${x.pair}"` : ""}${ab ? " data-knf-abpair" : ""}>
      <button type="button" class="knf__preset" data-szer-mm="${p.szer_mm}" data-wys-mm="${p.wys_mm}" data-label="${escapeAttr(p.label)}" aria-label="${escapeAttr(sx("T_TILE_ADD", { name, price: price || "-" }))}">
        ${x.corner ? icoImg(sys, "naroznik", "knf__preset-ico") : ""}
        <span class="knf__preset-label">${escapeHtml(label)}</span>
        <span class="knf__preset-mm">${p.szer_mm}×${p.wys_mm} mm</span>
        ${pl.extra ? `<span class="knf__preset-extra">${escapeHtml(tPresetWords(pl.extra))}</span>` : ""}
        <span class="knf__preset-price">${escapeHtml(price)}</span>
        ${p.bestseller && !x.corner ? `<span class="knf__preset-pop">${escapeHtml(sx("T_POPULAR"))}</span>` : ""}
      </button>
      <div class="knf__tile-qty" data-knf-tile-qty hidden>
        <button type="button" class="knf__tile-btn" data-knf-minus aria-label="${escapeAttr(sx("T_TILE_SUB", { name }))}">−</button>
        <span class="knf__tile-count" data-knf-count aria-hidden="true"></span>
        <button type="button" class="knf__tile-btn" data-knf-plus aria-label="${escapeAttr(sx("T_TILE_ADD", { name, price: price || "-" }))}">+</button>
      </div>
      <span class="knf__tile-split" data-knf-split aria-hidden="true"></span>
    </div>`;
  }
  let pgSeq = 0;
  function tilesHtml(sys, typ, presets, c) {
    const sorted = presets.slice().sort((a, b) => (a.szer_mm - b.szer_mm) || (a.wys_mm - b.wys_mm));
    const groups = [];
    // panele gorne BESTÅ: grupy wg grubosci bokow („boki 18 mm” / „boki 12 mm”), bo szerokosc panelu = zabudowa + 2 x bok
    const sidesOf = p => ((/boki\s*(\d+)\s*mm/i.exec(p.label || "") || [])[1] || "");
    sorted.forEach(p => {
      const w = typ === BESTA_TOP ? sidesOf(p) : parsePresetLabel(p.label, p.szer_mm, p.wys_mm).w;
      let g = groups.find(x => x.w === w);
      if (!g) groups.push(g = { w, items: [] });
      g.items.push(p);
    });
    if (typ === BESTA_TOP) groups.sort((a, b) => Number(b.w) - Number(a.w));
    // Z3: naglowki tylko, gdy ktoras szerokosc ma 2+ presety
    const heads = GROUPS_UI_ON && groups.some(g => g.items.length > 1);
    const list = heads ? groups : [{ w: "", items: sorted }];
    // UX v2 pkt 9: Blenda METOD/FAKTUM - pierwszy kafelek „Narożne · 2 szt.” (w grupie 10 cm); METOD „Drzwi narożne” 25×80 = para A+B
    const cornerP = typ === BLENDA && CORNER_H[sys] ? sorted.find(p => p.szer_mm === 100 && p.wys_mm === CORNER_H[sys]) : null;
    const cornerG = cornerP ? (list.find(g => !g.w || g.w === "10") || list[0]) : null;
    const xOf = p => sys === "METOD" && typ === "Drzwi narożne" && p.szer_mm === KITS.B.doors[0].szer && p.wys_mm === KITS.B.doors[0].wys ? { pair: "B" } : null;
    return list.map(g => {
      const id = `knf-pg-${pgSeq++}`;
      const h = heads ? `<p class="knf__pgroup-h" id="${id}">${escapeHtml(sx(typ === BESTA_TOP ? "T_BESTA_SIDES" : "T_WIDTH", { n: g.w }))}</p>` : "";
      const lab = heads ? `aria-labelledby="${id}"` : `aria-label="${escapeAttr(tType(typ))}"`;
      const first = g === cornerG ? tileHtml(sys, typ, cornerP, c, { corner: true }) : "";
      return `<div class="knf__pgroup">${h}<div class="knf__pgrid" role="group" ${lab}>${first}${g.items.map(p => tileHtml(sys, typ, p, c, xOf(p))).join("")}</div></div>`;
    }).join("");
  }
  // 04.10.2026 (Konrad): „Twój wymiar” - przy elementach z nawiertami kafelek mowi, ze otwory (puszki zawiasow) dopasujemy do wymiaru
  function customTileHtml(typ) {
    const note = !typ || NO_DRILL.test(typ) ? "" : sx(needsHinge(typ) ? "T_CUSTOM_HINGE" : "T_CUSTOM_DRILL");
    return `<div class="knf__pgroup knf__pgroup--custom"><div class="knf__pgrid"><div class="knf__tile knf__tile--custom${note ? " knf__tile--custom-note" : ""}"><button type="button" class="knf__preset knf__preset--custom" data-custom="1" aria-expanded="${customOpen ? "true" : "false"}">
      <span class="knf__preset-label">${escapeHtml(sx("T_CUSTOM_LBL"))}</span><span class="knf__preset-mm">${escapeHtml(sx("T_CUSTOM_MM"))}</span>${note ? `<span class="knf__preset-note">${escapeHtml(note)}</span>` : ""}</button></div></div></div>`;
  }
  // Blendy narożne (UX v2 pkt 9): 2 szt. 10 cm na wysokosc frontu systemu, segment „Do szafki narożnej” + „Bez nawiertów”
  const TAG_CORNER = "Do szafki narożnej";
  const CORNER_H = { METOD: 797, FAKTUM: 695 };
  function cornerLines(sys) {
    const h = CORNER_H[sys];
    if (!h) return [];
    const p = (((cfg.dimensions.dimensions_per_system_type || {})[sys] || {})[BLENDA] || []).find(x => x.szer_mm === 100 && x.wys_mm === h);
    return [[adderLine(sys, BLENDA, 100, h, p ? p.label : presetLabel("10", String(Math.round(h / 10))), { tag: TAG_CORNER }), 2]];
  }
  const cornerCount = sys => Math.ceil(state.set.reduce((a, l) => a + (l.sys === sys && l.typ === BLENDA && l.tag === TAG_CORNER ? l.qty : 0), 0) / 2);
  // Ustawienia dodawacza (UX v2 pkt 4): bez naglowka; tylko styl z uchwytem - jedna linia „Zawiasy · Uchwyt”; usłojenie i przedluzenie bez stalego bloku
  function syncAdder(typ) {
    const ad = $("[data-knf-adder-opts]");
    if (!ad) return;
    const h = ad.querySelector(".knf__adder-h");
    if (h) h.remove();
    const setLbl = (col, txt) => { const l = col && col.querySelector(".knf__label"); if (l && l.textContent !== txt) l.textContent = txt; };
    const loc = condSel("Lokalizacja uchwytu")?.closest(".knf__col");
    if (loc && loc.hidden !== (noHandleTyp(typ) || typ === NAD_TYPE)) loc.hidden = noHandleTyp(typ) || typ === NAD_TYPE;
    setLbl(loc, sx("T_LOC_SHORT"));
    // jedna zwarta linia „Zawiasy: [Lewa] [Prawa] · Uchwyt: [Góra ▾]” - zawiasy pierwsze, uchwyt jako lista (knf-rysunek.js pomija [data-knf-noseg])
    const hc = hingeSel()?.closest(".knf__col");
    if (hc && loc && hc.parentNode === loc.parentNode && (loc.compareDocumentPosition(hc) & Node.DOCUMENT_POSITION_FOLLOWING)) loc.before(hc);
    const ls = condSel("Lokalizacja uchwytu");
    if (ls && !ls.hasAttribute("data-knf-noseg")) ls.setAttribute("data-knf-noseg", "");
    const gr = condSel(GRAIN_LABEL)?.closest(".knf__col");
    if (gr && ad.contains(gr) && !gr.hidden) gr.hidden = true;
    const nad = typ === NAD_TYPE;
    // 04.10.2026 (Konrad): „Grubość” dodawanych elementow w tej samej linii (segmenty; knf-rysunek.js zamienia select [data-knf-grubadd])
    const sysA = $("[data-knf-system]")?.value || "", cls = thickCls(typ), topts = nad ? [] : thickOpts(state.styl, typ, sysA);
    let tc = ad.querySelector("[data-knf-grubadd-col]");
    if (topts.length && !tc) {
      tc = document.createElement("div");
      tc.className = "knf__col"; tc.setAttribute("data-knf-grubadd-col", "");
      tc.innerHTML = '<label class="knf__label" for="knf-grubadd"></label><select class="knf__select" id="knf-grubadd" data-knf-grubadd></select>';
      (ad.querySelector("[data-knf-adder-cond]") || ad).appendChild(tc);
      tc.querySelector("select").addEventListener("change", e => {
        const k = thickCls($("[data-knf-typ]")?.value || "");
        state.grubAdd[k] = e.target.value === thickStd(state.styl) ? null : e.target.value;
        renderTiles(); syncCustomForm(); updateM2Display(); saveSet();
      });
    }
    if (tc) {
      if (topts.length) {
        const std = thickStd(state.styl), sel = tc.querySelector("select");
        const html = topts.map(v => `<option value="${v}">${escapeHtml(thickLbl(v, std))}</option>`).join("");
        if (sel.dataset.sig !== html) { sel.dataset.sig = html; sel.innerHTML = html; }
        const want = topts.includes(state.grubAdd[cls]) ? state.grubAdd[cls] : std;   // 30 mm z blend zostaje zapamietane po wizycie w cokolach (adderGrub i tak filtruje)
        if (sel.value !== want) sel.value = want;
        setLbl(tc, (THICKNESS_I18N[locale] || THICKNESS_I18N.pl).short);
      }
      if (tc.hidden !== !topts.length) tc.hidden = !topts.length;
    }
    // styl bez uchwytu (np. Gładki): linia tylko dla grubosci, zawiasy jak dotad w okienku przy dodaniu
    const req = HINGE_REQ_STYLES.has(state.styl);
    const vis = !nad && !!typ && (req ? [...ad.querySelectorAll(".knf__col")].some(col => !col.hidden) : topts.length > 0);
    if (ad.hidden !== !vis) ad.hidden = !vis;
    if (ad.classList.contains("knf__adder--line") !== vis) ad.classList.toggle("knf__adder--line", vis);
    if (ad.classList.contains("knf__adder--grubonly") !== (vis && !req)) ad.classList.toggle("knf__adder--grubonly", vis && !req);
  }
  function renderTiles() {
    if (!SET_MODE || state.sample_mode) return;
    const sys = $("[data-knf-system]")?.value || "", typ = $("[data-knf-typ]")?.value || "";
    const presetsWrap = $("[data-knf-presets]"), manualWrap = $("[data-knf-dims-manual]"), hint = $("[data-knf-dim-hint]");
    if (!presetsWrap || !manualWrap || !hint) return;
    bindTiles();
    syncAdder(typ);
    if (!sys || !typ) return;
    const c = snapshotCommon();
    const cmn = cfg.rules.common;
    const dps = cfg.dimensions.dimensions_per_system_type || {};
    closeAsk();
    // Linia nad siatka (system, typ i instrukcja) idzie do podpowiedzi [data-knf-dim-hint]; legenda kroku 6 zostaje jak na live
    const setHint = txt => { if (hint.textContent !== txt) hint.textContent = txt; if (hint.hidden !== !txt) hint.hidden = !txt; };
    const setLine = setHint;
    if ((CORNER_CARDS_ON && typ === CORNER_TYP && sys === "METOD") || typ === NAD_TYPE) {
      customOpen = false;
      manualWrap.hidden = true;
      setLine(`${tSystem(sys)} · ${typChipLabel(sys, typ)}`);
      presetsWrap.hidden = false;
      presetsWrap.innerHTML = typ === NAD_TYPE ? nadHtml(c) : kitsHtml(c);
      if (typ === NAD_TYPE) nadUpdate();
      updateTileCounts();
      return;
    }
    const presets = ((dps[sys] || {})[typ] || []).filter(p => p.szer_mm <= cmn.max_szerokosc_mm && p.wys_mm <= cmn.max_wysokosc_mm);
    if (!presets.length) {
      // NA WYMIAR i systemy bez presetow: formularz otwarty od razu (TEMPO 5.4)
      presetsWrap.hidden = true;
      presetsWrap.innerHTML = "";
      const MANUAL_HINT = { en: "Enter front dimensions manually (in mm).", cs: "Zadejte rozměry čela ručně (v mm).", de: "Maße der Front manuell eingeben (in mm).", pl: "Wpisz wymiary frontu ręcznie (w mm)." };
      setLine("");
      setHint(sys === "NA WYMIAR" ? "" : MANUAL_HINT[locale] || MANUAL_HINT.pl);
      openCustom(true);
      return;
    }
    setLine(sx(FINE_PTR ? "T_HINT_LINE_CLICK" : "T_HINT_LINE", { sys: tSystem(sys), typ: typChipLabel(sys, typ) }));
    presetsWrap.hidden = false;
    presetsWrap.innerHTML = tilesHtml(sys, typ, presets, c) + customTileHtml(typ);
    manualWrap.hidden = !customOpen;
    updateTileCounts();
  }
  // Liczniki, pasek − N +, podzial L/P na kafelkach (bez przerysowania siatki)
  let activeTileKey = null;
  function updateTileCounts() {
    const wrap = $("[data-knf-presets]");
    if (!wrap) return;
    const hs = HINGE_SHORT[locale] || HINGE_SHORT.pl;
    $$(".knf__tile[data-knf-tile]", wrap).forEach(tile => {
      const key = tile.dataset.knfTile;
      // UX v2: kafelek „Narożne” liczy komplety blend, „para A+B” komplety drzwi łamanych
      const n = tile.hasAttribute("data-knf-corner") ? cornerCount(key.split("|")[0]) : tile.dataset.knfPair ? kitCount(tile.dataset.knfPair) : tile.hasAttribute("data-knf-abpair") ? Math.ceil(tileCount(key) / 2) : tileCount(key);
      const cnt = tile.querySelector("[data-knf-count]"), bar = tile.querySelector("[data-knf-tile-qty]"), sp = tile.querySelector("[data-knf-split]");
      if (cnt && cnt.textContent !== (n ? String(n) : "")) cnt.textContent = n ? String(n) : "";
      if (bar && bar.hidden !== !n) bar.hidden = !n;
      tile.classList.toggle("has-count", n > 0);
      tile.classList.toggle("is-active", key === activeTileKey && n > 0);
      if (sp) {
        let l = 0, r = 0;
        state.set.forEach(x => { if (x.kind !== "korpus" && !x.grp && !x.tag && tileKey(x.sys, x.typ, x.szer, x.wys) === key) { if ((x.opt || {})[HINGE_LABEL] === "Lewa") l += x.qty; if ((x.opt || {})[HINGE_LABEL] === "Prawa") r += x.qty; } });
        const st = (l || r) ? [l ? `${hs[0]}${l}` : "", r ? `${hs[1]}${r}` : ""].filter(Boolean).join(" · ") : "";
        if (sp.textContent !== st) sp.textContent = st;
      }
    });
    $$("[data-knf-kit]", wrap).forEach(card => {
      const id = card.dataset.knfKit;
      if (id === "C") return;
      const n = kitCount(id);
      const cnt = card.querySelector("[data-knf-kit-count]"), mn = card.querySelector("[data-knf-kit-minus]");
      if (cnt) cnt.textContent = n ? sx("T_KIT_ADDED", { n }) : "";
      if (mn) mn.hidden = !n;
    });
  }
  function markActive(tile, key) {
    activeTileKey = key || null;
    $$(".knf__tile.is-active").forEach(t => { if (t !== tile) t.classList.remove("is-active"); });
    if (tile) tile.classList.add("is-active");
  }
  // Krotka animacja „+1” i drganie przy limicie 50 szt. (klasy dla CSS)
  function pulse(el, cls, ms) { if (!el) return; el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); setTimeout(() => el.classList.remove(cls), ms || 260); }
  function announce(txt) { const live = $("[data-knf-live]"); if (live) { live.textContent = ""; setTimeout(() => { live.textContent = txt; }, 30); } }
  function maxed(el) { pulse(el, "is-shake", 400); announce(sx("T_MAX50")); setMsg(sx("T_MAX50")); }
  // -- Dymek po dodaniu (UX v2 pkt 6): „Dodano: Drzwi 60×80 · w zestawie 3 szt. · Cofnij”, 3 s; Cofnij = odjecie tego, co dodano
  let toastTimer = null, toastUndo = null;
  function toastEl() {
    let el = $("[data-knf-toast]");
    if (!el) {
      const box = $("[data-knf-set]");
      if (!box) return null;
      el = document.createElement("div");
      el.className = "knf__toast";
      el.setAttribute("data-knf-toast", "");
      el.setAttribute("role", "status");
      el.setAttribute("aria-live", "polite");
      box.insertBefore(el, box.firstChild);
    }
    if (!el.dataset.knfBound) { el.dataset.knfBound = "1"; el.addEventListener("click", e => { if (e.target.closest("[data-knf-toast-undo]")) undoToast(); }); }
    return el;
  }
  function showToast(text, undo) {
    const el = toastEl();
    if (!el) return;
    toastUndo = undo && undo.length ? undo : null;
    // Liquid: staly region [data-knf-toast] w .knf__cta (pusty = niewidoczny) - JS wstawia / czysci jedno dziecko
    el.innerHTML = `<p class="knf__toast-msg"><span class="knf__toast-t">${escapeHtml(text)}</span>` + (toastUndo ? ` <button type="button" class="knf__link" data-knf-toast-undo>${escapeHtml(sx("T_UNDO"))}</button>` : "") + "</p>";
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 3000);
  }
  function hideToast() {
    clearTimeout(toastTimer);
    toastUndo = null;
    const el = $("[data-knf-toast]");
    if (el && el.firstChild) el.innerHTML = "";
  }
  function undoToast() {
    if (setBusy() || !toastUndo) return;
    toastUndo.forEach(([uid, n]) => {
      const l = state.set.find(x => x.uid === uid);
      if (!l) return;
      if (l.kind === "korpus") { state.set = state.set.filter(x => x !== l && !(l.grp && x.grp === l.grp)); return; }
      l.qty -= n;
      l.ts = Date.now();
      if (l.qty <= 0) state.set = state.set.filter(x => x !== l);
    });
    hideToast();
    afterSetChange({});
  }
  // Przedluzenie (UX v2 pkt 4) bez stalego bloku: pytanie przy kazdym dodaniu, potem wybor wraca do pustego
  function resetExt() {
    const x = extSel();
    if (SET_MODE && x && x.value) { x.value = ""; x.dispatchEvent(new Event("change", { bubbles: true })); }
  }
  // Okienko wymaganej opcji na kafelku / karcie (TEMPO M5): zawiasy albo przedluzenie; wybor ustawia dodawacz i dodaje
  let askCtx = null;
  // S13: returnFocus - fokus wraca na kafelek / przycisk karty, z ktorego okienko wyszlo (zamiast zginac z usunietym okienkiem)
  function closeAsk(returnFocus) {
    const ctx = askCtx;
    $$("[data-knf-ask]").forEach(a => a.remove());
    askCtx = null;
    if (!returnFocus || !ctx) return;
    const el = ctx.kit ? $(`[data-knf-kit="${cssEscape(ctx.kit)}"] [data-knf-kit-add]`) : ctx.tile ? $(`.knf__tile[data-knf-tile="${cssEscape(ctx.tile)}"] .knf__preset`) : ctx.custom ? $("[data-knf-custom-add]") : null;
    if (el) try { el.focus({ preventScroll: true }); } catch (_) { /* stare Safari */ }
  }
  function showAsk(host, kind, ctx) {
    closeAsk();
    const label = kind === "hinge" ? HINGE_LABEL : EXT_LABEL;
    const vals = kind === "hinge" ? ["Lewa", "Prawa"] : ["Góra", "Dół", "Bok"];
    const ea = ctx && ctx.custom ? EXT_ASK_CUSTOM : EXT_ASK;
    const q = kind === "hinge" ? sx("T_HINGE_Q") : SET_MODE ? (ea[locale] || ea.pl) : sx("T_EXT_Q");
    const div = document.createElement("div");
    div.className = "knf__ask";
    div.setAttribute("data-knf-ask", "");
    div.setAttribute("role", "dialog");
    div.setAttribute("aria-label", q);
    div.innerHTML = `<p>${escapeHtml(q)}</p>` + vals.map(v => `<button type="button" class="knf__seg-btn" data-knf-ask-v="${escapeAttr(v)}">${escapeHtml(tOptVal(label, v))}</button>`).join("");
    host.appendChild(div);
    askCtx = Object.assign({ kind, label }, ctx);
    const b = div.querySelector("button");
    if (b) b.focus({ preventScroll: true });
    // M9: okienko przy dole ekranu nie moze wejsc pod zadokowany pasek (telefon) - dosuwamy wlasciwy kontener przewijania
    const r = div.getBoundingClientRect(), bar = $(".knf__cta--docked");
    const lim = (bar ? bar.getBoundingClientRect().top : window.innerHeight) - 16;
    if (r.bottom > lim) { const box = scrollBox(div); try { box.scrollBy({ top: r.bottom - lim, behavior: "smooth" }); } catch (_) { box.scrollTop += r.bottom - lim; } }
  }
  // Ustawia wartosc selecta dodawacza tak, jak klik w przelacznik (change -> stan, rysunek, przelaczniki)
  function setAdderValue(label, v) {
    const s = label === EXT_LABEL ? extSel() : condSel(label);
    if (!s || s.value === v) return;
    if (s.disabled && label !== EXT_LABEL) return;
    s.value = v;
    s.dispatchEvent(new Event("change", { bubbles: true }));
  }
  // Brak wymaganej opcji dla dodania (kafelek / karta): "hinge" | "ext" | "opis" | ""
  function adderMiss(sys, typ, line) {
    if (hingeMode(state.styl, typ) === "required" && !line.opt[HINGE_LABEL]) return "hinge";
    if (lineNeedsExt(line) && !line.opt[EXT_LABEL]) return "ext";
    if (Object.keys(line.opt).some(k => /Niestandardow/i.test(line.opt[k] || "") && !(line.opis || {})[k])) return "opis";
    return "";
  }
  function flashOpis() {
    const inp = $("[data-knf-cond-custom]:not([hidden])");
    if (!inp) return;
    knfScrollTo(inp.closest(".knf__col") || inp, 120);
    pulse(inp, "knf__error-flash", 2000);
    try { inp.focus({ preventScroll: true }); } catch (_) { /* stare Safari */ }
  }
  // Kafelek wymiaru = +1 szt. (TEMPO 2.3)
  function addFromTile(tile) {
    if (setBusy()) return;
    if (tile.hasAttribute("data-knf-corner") || tile.dataset.knfPair) { addSpecial(tile); return; }
    const btn = tile.querySelector(".knf__preset");
    if (!btn) return;
    const kp = String(tile.dataset.knfTile || "").split("|");   // system i typ z kafelka (karta C narożnika = zwykle „Drzwi”)
    const sys = kp[0] || "", typ = kp[1] || "";
    const szer = Number(btn.dataset.szerMm), wys = Number(btn.dataset.wysMm), dim = btn.dataset.label || "";
    const line = adderLine(sys, typ, szer, wys, dim);
    const miss = adderMiss(sys, typ, line);
    if (miss === "hinge" || miss === "ext") { showAsk(tile, miss, { tile: tile.dataset.knfTile }); return; }
    if (miss === "opis") { flashOpis(); return; }
    const step = qtyStep(typ);
    const row = addLine(line, step);
    if (!row) { maxed(tile); return; }
    if (line.opt[EXT_LABEL]) resetExt();
    const key = tileKey(sys, typ, szer, wys);
    markActive(tile, key);
    pulse(tile, "is-bump");
    try { if (navigator.vibrate) navigator.vibrate(8); } catch (_) { /* brak wibracji */ }
    showToast(sx("T_TOAST", { name: lineName(row), n: tileCount(key) }), [[row.uid, step]]);
    afterSetChange({ fresh: row.uid, anchor: tile });
  }
  // UX v2 pkt 9: kafelek „Narożne · 2 szt.” (2 blendy z segmentem „Do szafki narożnej”) i „25×80 · para A+B” (2 pozycje drzwi łamanych)
  function addSpecial(tile) {
    const key = tile.dataset.knfTile, sys = key.split("|")[0];
    const corner = tile.hasAttribute("data-knf-corner");
    const lines = corner ? cornerLines(sys) : kitLines(tile.dataset.knfPair);
    if (!lines.length) return;
    const door = lines[0][0];
    if (!corner) {
      const miss = adderMiss(sys, door.typ, door);
      if (miss === "hinge") { showAsk(tile, "hinge", { tile: key }); return; }
      if (miss === "opis") { flashOpis(); return; }
    }
    if (lines.some(([l, n]) => state.set.some(x => lineSig(x) === lineSig(l) && x.qty + n > 50))) { maxed(tile); return; }
    const undo = [];
    let last = null;
    lines.forEach(([l, n]) => { const r = addLine(l, n); if (r) { last = r; undo.push([r.uid, n]); } });
    markActive(tile, key);
    pulse(tile, "is-bump");
    trackKnf("knf_kit_add", { kit: corner ? "corner" : tile.dataset.knfPair });
    const n = corner ? state.set.reduce((a, l) => a + (l.sys === sys && l.typ === BLENDA && l.tag === TAG_CORNER ? l.qty : 0), 0) : 2 * kitCount(tile.dataset.knfPair);
    showToast(sx("T_TOAST", { name: corner ? sx("T_CORNER_NAME") : sx("T_PAIR_NAME"), n }), undo);
    afterSetChange({ fresh: last && last.uid, anchor: tile });
  }
  // „−” na kafelku specjalnym: 2 blendy narożne albo para A+B (z ostatnio zmienianych pozycji)
  function takeQty(pred, n) {
    const rows = state.set.filter(pred).sort((a, b) => b.ts - a.ts);
    for (const r of rows) { if (n <= 0) break; const d = Math.min(n, r.qty); r.qty -= d; n -= d; r.ts = Date.now(); }
    state.set = state.set.filter(r => r.qty > 0);
  }
  function subSpecial(tile) {
    const sys = tile.dataset.knfTile.split("|")[0];
    if (tile.hasAttribute("data-knf-corner")) takeQty(l => l.sys === sys && l.typ === BLENDA && l.tag === TAG_CORNER, 2);
    else subKit(tile.dataset.knfPair);
    afterSetChange({});
  }
  // Jedna delegacja na [data-knf-presets] (zamiast addEventListener przy kazdym renderze)
  function bindTiles() {
    const wrap = $("[data-knf-presets]");
    if (!wrap || wrap.dataset.knfSetBound) return;
    wrap.dataset.knfSetBound = "1";
    wrap.addEventListener("click", onTileClick);
    wrap.addEventListener("input", onNadInput);
    wrap.addEventListener("change", onNadInput);
    wrap.addEventListener("keydown", e => { if (e.key === "Escape" && askCtx) { e.preventDefault(); closeAsk(true); } });
    // UX v2: okienko przedluzenia przy „Inny wymiar” siedzi w formularzu pod siatka
    const manual = $("[data-knf-dims-manual]");
    if (manual) {
      manual.addEventListener("click", e => { if (!setBusy()) onAskClick(e.target); });
      manual.addEventListener("keydown", e => { if (e.key === "Escape" && askCtx) { e.preventDefault(); closeAsk(true); } });
    }
    document.addEventListener("click", e => { if (askCtx && !e.target.closest("[data-knf-ask], .knf__tile, [data-knf-kit-add], [data-knf-custom-add]")) closeAsk(); });
  }
  // Odpowiedz w okienku pytania; true = obsluzone
  function onAskClick(t) {
    const askBtn = t.closest("[data-knf-ask-v]");
    if (!askBtn || !askCtx) return false;
    const ctx = askCtx;
    closeAsk(true);
    setAdderValue(ctx.label, askBtn.dataset.knfAskV);
    if (ctx.custom) { addCustom(); return true; }
    if (ctx.kit) { addKit(ctx.kit); return true; }
    const tile = $(`.knf__tile[data-knf-tile="${cssEscape(ctx.tile)}"]`);
    if (tile) addFromTile(tile);
    return true;
  }
  function onTileClick(e) {
    if (setBusy()) return;   // M1: w trakcie POST /cart/add.js lista sie nie zmienia
    const t = e.target;
    if (onAskClick(t)) return;
    const minus = t.closest("[data-knf-minus]");
    if (minus) {
      const tile = minus.closest(".knf__tile");
      if (tile && (tile.hasAttribute("data-knf-corner") || tile.dataset.knfPair)) { subSpecial(tile); return; }
      const row = tile ? subTile(tile.dataset.knfTile) : null;
      if (row) { hideToast(); announce(sx("T_LIVE_SUB", { name: lineName(row), n: tileCount(tile.dataset.knfTile) })); afterSetChange({}); }
      return;
    }
    const plus = t.closest("[data-knf-plus]");
    if (plus) { const tile = plus.closest(".knf__tile[data-knf-tile]"); if (tile) addFromTile(tile); return; }
    if (t.closest(".knf__preset--custom")) { openCustom(!customOpen); return; }
    if (t.closest("[data-knf-kit], [data-knf-nad]") && onCardClick(t)) return;
    const preset = t.closest(".knf__preset");
    const tile = preset && preset.closest(".knf__tile[data-knf-tile]");
    if (tile) addFromTile(tile);
  }

  // -- „Inny wymiar” (TEMPO M6, D8): formularz pod siatka + „Dodaj · cena”; wymiar = preset -> pozycja presetu
  function customPreset() {
    const sys = $("[data-knf-system]")?.value || "", typ = $("[data-knf-typ]")?.value || "";
    const szer = Number($("[data-knf-szer]")?.value) || 0, wys = Number($("[data-knf-wys]")?.value) || 0;
    if (!szer || !wys) return null;
    return (((cfg.dimensions.dimensions_per_system_type || {})[sys] || {})[typ] || []).find(p => p.szer_mm === szer && p.wys_mm === wys) || null;
  }
  function openCustom(on) {
    const manualWrap = $("[data-knf-dims-manual]");
    if (!manualWrap) return;
    const was = customOpen;
    customOpen = !!on;
    manualWrap.hidden = !customOpen;
    const inSzer = $("[data-knf-szer]"), inWys = $("[data-knf-wys]"), lbl = $("[data-knf-dim-label]");
    [inSzer, inWys].forEach(i => { if (i) { i.readOnly = false; i.classList.remove("knf__input--locked"); } });
    if (customOpen && !was && lbl) lbl.value = "Niestandardowy";
    const btn = $("[data-knf-presets] .knf__preset--custom");
    if (btn) btn.setAttribute("aria-expanded", customOpen ? "true" : "false");
    if (customOpen && !was && inSzer && $("[data-knf-system]")?.value !== "NA WYMIAR") inSzer.focus();
    updateM2Display();
    syncCustomForm();
    tryUpdate();
  }
  // Dla wpisanych wymiarow: pozycja, jaka doda przycisk (albo null + powod)
  function customLine() {
    const sys = $("[data-knf-system]")?.value || "", typ = $("[data-knf-typ]")?.value || "";
    const szer = Number($("[data-knf-szer]")?.value) || 0, wys = Number($("[data-knf-wys]")?.value) || 0;
    if (!sys || !typ || !szer || !wys) return null;
    const pr = customPreset();
    const dim = pr ? pr.label : (sys === "NA WYMIAR" || !(((cfg.dimensions.dimensions_per_system_type || {})[sys] || {})[typ] || []).length ? "" : "Niestandardowy");
    return adderLine(sys, typ, szer, wys, dim);
  }
  function syncCustomForm() {
    if (!SET_MODE || state.sample_mode) return;
    const btn = $("[data-knf-custom-add]"), hint = $("[data-knf-custom-hint]");
    if (!btn || !hint) return;
    // M8: kolumna „Przedłużenie” przy otwartym „Inny wymiar” stoi w formularzu nad „Dodaj”, po zamknieciu wraca do ustawien nad kafelkami
    const ext = $("[data-knf-ext]"), adder = $("[data-knf-adder-opts]");
    if (ext && adder) {
      if (customOpen && ext.nextElementSibling !== btn) btn.parentNode.insertBefore(ext, btn);
      else if (!customOpen && ext.parentNode !== adder) adder.appendChild(ext);
    }
    if (btn.hidden !== !customOpen) btn.hidden = !customOpen;
    if (!btn.classList.contains("knf__btn--secondary")) btn.classList.add("knf__btn--secondary");   // UX v2 pkt 8: jedyny czarny przycisk = „Do koszyka”
    if (!customOpen) { if (!hint.hidden) hint.hidden = true; return; }
    const l = customLine(), c = snapshotCommon();
    // UX v2 pkt 5: bez pol-widm - powierzchnia i cena dopiero po wpisaniu obu wymiarow
    const sumCol = $("[data-knf-dims-manual] .knf__col--summary");
    if (sumCol && sumCol.hidden !== !l) sumCol.hidden = !l;
    const parts = [];
    let price = "", limErr = false;
    if (l) {
      const u = lineUnit(l, c);
      price = u != null ? fmtMarket(u * qtyStep(l.typ)) : "";
      const pr = customPreset();
      if (pr) parts.push(escapeHtml(sx("T_CUSTOM_STD", { cm: parsePresetLabel(pr.label, pr.szer_mm, pr.wys_mm).cm })));
      const lim = TYPE_LIMITS[l.typ], s = Math.min(l.szer, l.wys);
      if (lim && (s < lim.min || s > lim.max)) {
        limErr = true;
        parts.push(escapeHtml(sx(lim.msg)));
        if (lim.wide && s > lim.max && sysHasTyp(l.sys, lim.wideTo)) parts.push(`${escapeHtml(sx(lim.wide))} <button type="button" class="knf__link" data-knf-switch-typ="${escapeAttr(lim.wideTo)}">${escapeHtml(sx(lim.wideBtn))}</button>`);
      } else if (!NO_DRILL.test(l.typ) && l.sys !== "NA WYMIAR" && s < 150 && sysHasTyp(l.sys, BLENDA)) {
        parts.push(`${escapeHtml(sx("T_NARROW"))} <button type="button" class="knf__link" data-knf-switch-typ="${escapeAttr(BLENDA)}">${escapeHtml(sx("T_NARROW_BTN"))}</button>`);
      }
      const inSet = state.set.filter(x => lineSig(x) === lineSig(l)).reduce((a, x) => a + x.qty, 0);
      if (inSet) parts.push(escapeHtml(sx("T_CUSTOM_IN", { n: inSet })));
    }
    // S9: wymiar poza limitem typu (blenda, panel dzielacy) - przycisk wyglada na niekompletny i nie pokazuje ceny
    const bt = price && !limErr ? sx("T_CUSTOM_ADD", { price }) : sx("T_CUSTOM_ADD", { price: "" }).replace(/\s*·\s*$/, "");
    if (btn.textContent !== bt) btn.textContent = bt;
    if (btn.classList.contains("knf__btn--incomplete") !== limErr) btn.classList.toggle("knf__btn--incomplete", limErr);
    const hh = parts.join("<br>");
    if (hint.dataset.sig !== hh) { hint.dataset.sig = hh; hint.innerHTML = hh; }
    if (hint.hidden !== !parts.length) hint.hidden = !parts.length;
  }
  const sysHasTyp = (sys, typ) => !!(((cfg.dimensions.dimensions_per_system_type || {})[sys] || {})[typ] || []).length;
  function addCustom() {
    if (setBusy()) return;
    const l = customLine();
    const c = snapshotCommon();
    const field = $("[data-knf-dims-manual]");
    if (!l) { pulse(field, "knf__error-flash", 2000); $("[data-knf-szer]")?.focus(); return; }
    const miss = adderMiss(l.sys, l.typ, l);
    if (miss === "opis") { flashOpis(); return; }
    // UX v2 pkt 4: zawiasy (styl z uchwytem) i przedluzenie - okienko pytania w formularzu, nie staly blok
    if (miss === "hinge" || miss === "ext") { if (field) showAsk(field, miss, { custom: true }); return; }
    const code = validateLine(l, c);
    if (code) { setMsg(missText(code, l, c)); pulse(field, "knf__error-flash", 2000); return; }
    const step = qtyStep(l.typ);
    const row = addLine(l, step);
    if (!row) { maxed($("[data-knf-custom-add]")); return; }
    if (l.opt[EXT_LABEL]) resetExt();
    showToast(sx("T_TOAST", { name: lineName(row), n: row.qty }), [[row.uid, step]]);
    afterSetChange({ fresh: row.uid, anchor: $("[data-knf-custom-add]") });
  }
  // „Przełącz na Blendę / Panele boczne”: zmiana typu z zachowaniem wpisanych wymiarow
  function switchTypKeep(typ) {
    const szer = $("[data-knf-szer]")?.value || "", wys = $("[data-knf-wys]")?.value || "";
    const chip = $(`.knf__typchip[data-typ="${cssEscape(typ)}"]`);
    if (!chip) return;
    chip.click();
    openCustom(true);
    const a = $("[data-knf-szer]"), b = $("[data-knf-wys]");
    if (a && b) { a.value = szer; b.value = wys; a.dispatchEvent(new Event("input")); b.dispatchEvent(new Event("input")); }
  }

  // -- Narożnik METOD: karty A/B/C (TEMPO 3). Karta dodaje realne sztuki jako osobne pozycje listy (bez ukrytego ×2).
  function kitLines(id) {
    if (id === "A") {
      const k = KITS.A, bw = state.kit.A.blend;
      return [
        [adderLine("METOD", k.door.typ, k.door.szer, k.door.wys, kitPresetLabel(k.door.typ, k.door.szer, k.door.wys), { tag: k.tag, kit: "A" }), 1],
        [adderLine("METOD", BLENDA, bw, k.blendH, blendLabel(bw), { tag: k.tag, kit: "A" }), 2],
      ];
    }
    if (id === "B") {
      return KITS.B.doors.map(d => [adderLine("METOD", d.typ, d.szer, d.wys, kitPresetLabel("Drzwi narożne", d.szer, d.wys), { tag: KITS.B.tag, kit: "B" }), 1]);
    }
    return [];
  }
  function kitCount(id) {
    const typ = id === "A" ? KITS.A.door.typ : KIT_TYP_A;
    return state.set.reduce((a, l) => a + (l.typ === typ && l.tag === KITS[id].tag ? l.qty : 0), 0);
  }
  function kitPrice(id, c) {
    let sum = 0, ok = true;
    kitLines(id).forEach(([l, n]) => { const u = lineUnit(l, c); if (u == null) ok = false; else sum += u * n; });
    return ok ? sum : null;
  }
  function kitsHtml(c) {
    const card = (id, inner) => `<article class="knf__kit" data-knf-kit="${id}">${KIT_PLAN[id]}
      <p class="knf__kit-h">${escapeHtml(sx(KITS[id].h))}</p>
      <p class="knf__kit-code" translate="no">${escapeHtml(sx("T_KIT_CODE", { codes: KITS[id].codes }))}</p>
      <p class="knf__kit-desc">${escapeHtml(sx(id === "C" && FINE_PTR ? "T_KIT_C_DESC_CLICK" : KITS[id].d))}</p>${inner}</article>`;
    const foot = id => {
      const p = kitPrice(id, c), pf = p != null ? fmtMarket(p) : "-";
      return `<strong data-knf-kit-price>${escapeHtml(pf)}</strong>
        <button type="button" class="knf__btn" data-knf-kit-add>${escapeHtml(sx("T_KIT_ADD", { price: pf }))}</button>
        <span data-knf-kit-count></span>
        <button type="button" class="knf__tile-minus" data-knf-kit-minus aria-label="${escapeAttr(sx("T_KIT_SUB"))}" hidden>−</button>`;
    };
    const bw = state.kit.A.blend;
    const cmNum = mm => new Intl.NumberFormat(locale === "en" ? "en" : locale, { maximumFractionDigits: 1 }).format(mm / 10);
    const blendSeg = `<p class="knf__label">${escapeHtml(sx("T_KIT_BLEND"))}</p>
      <div class="knf__seg" data-knf-kit-blend role="radiogroup" aria-label="${escapeAttr(sx("T_KIT_BLEND"))}">${KITS.A.blends.map(w => `<button type="button" class="knf__seg-btn" role="radio" data-v="${w}" aria-checked="${w === bw ? "true" : "false"}">${escapeHtml(w === 100 ? sx("T_KIT_REC", { n: cmNum(w) }) : `${cmNum(w)} cm`)}</button>`).join("")}</div>
      <p class="knf__hint">${escapeHtml(sx("T_KIT_BLEND_HINT"))}</p>`;
    const cPresets = KITS.C.wys.map(h => {
      const lbl = kitPresetLabel("Drzwi", KITS.C.szer, h);
      return { label: lbl, szer_mm: KITS.C.szer, wys_mm: h };
    });
    const cGrid = `<div class="knf__pgrid" role="group" aria-label="${escapeAttr(sx("T_KIT_C"))}">${cPresets.map(p => tileHtml("METOD", "Drzwi", p, c)).join("")}</div>`;
    return `<div class="knf__kits">${card("A", blendSeg + foot("A"))}${card("B", foot("B"))}${card("C", cGrid)}</div>`;
  }
  function addKit(id) {
    if (setBusy()) return;
    const lines = kitLines(id);
    const card = $(`[data-knf-kit="${id}"]`);
    const door = lines[0] && lines[0][0];
    if (door) {
      const miss = adderMiss("METOD", door.typ, door);
      if (miss === "hinge" && card) { showAsk(card, "hinge", { kit: id }); return; }
      if (miss === "opis") { flashOpis(); return; }
    }
    if (lines.some(([l, n]) => state.set.some(x => lineSig(x) === lineSig(l) && x.qty + n > 50))) { maxed(card); return; }
    let last = null;
    lines.forEach(([l, n]) => { last = addLine(l, n) || last; });
    trackKnf("knf_kit_add", { kit: id });
    announce(sx("T_LIVE_ADDED", { name: sx(KITS[id].h), n: kitCount(id) }));
    afterSetChange({ fresh: last && last.uid, anchor: card });
  }
  // „−” na karcie: 1 drzwi i 2 blendy (A) albo A i B (B), z ostatnio zmienianych pozycji
  function subKit(id) {
    const take = (pred, n) => {
      const rows = state.set.filter(pred).sort((a, b) => b.ts - a.ts);
      for (const r of rows) { if (n <= 0) break; const d = Math.min(n, r.qty); r.qty -= d; n -= d; r.ts = Date.now(); }
      state.set = state.set.filter(r => r.qty > 0);
    };
    const tag = KITS[id].tag;
    if (id === "A") { take(l => l.typ === KITS.A.door.typ && l.tag === tag, 1); take(l => l.typ === BLENDA && l.tag === tag, 2); }
    else { take(l => l.typ === KIT_TYP_A && l.tag === tag, 1); take(l => l.typ === KIT_TYP_B && l.tag === tag, 1); }
    afterSetChange({});
  }
  // Klik w karcie narożnika / nadstawki; true = obsluzone
  function onCardClick(t) {
    const kit = t.closest("[data-knf-kit]");
    if (kit) {
      const id = kit.dataset.knfKit;
      const seg = t.closest("[data-knf-kit-blend] .knf__seg-btn");
      if (seg) {
        state.kit.A.blend = Number(seg.dataset.v) || 100;
        $$("[data-knf-kit-blend] .knf__seg-btn", kit).forEach(b => b.setAttribute("aria-checked", b === seg ? "true" : "false"));
        const c = snapshotCommon(), p = kitPrice("A", c), pf = p != null ? fmtMarket(p) : "-";
        const pe = kit.querySelector("[data-knf-kit-price]"), ab = kit.querySelector("[data-knf-kit-add]");
        if (pe) pe.textContent = pf;
        if (ab) ab.textContent = sx("T_KIT_ADD", { price: pf });
        saveSet();
        return true;
      }
      if (t.closest("[data-knf-kit-add]")) { addKit(id); return true; }
      if (t.closest("[data-knf-kit-minus]")) { subKit(id); return true; }
      return false;   // karta C: zwykle kafelki „Drzwi”
    }
    if (t.closest("[data-knf-nad]")) return onNadClick(t);
    return false;
  }

  // -- Nadstawka PAX: karta zamiast kafelkow (decyzje C2). Bez <select>, bez name, przelaczniki .knf__seg.
  const NAD_MAIL = "mailto:kontakt@ogony.pl?subject=" + encodeURIComponent("Nadstawka PAX");
  const nadCm = v => new Intl.NumberFormat(locale === "en" ? "en" : locale, { maximumFractionDigits: 1 }).format(v);
  function nadSeg(attr, vals, cur, fmt, label) {   // S13: radiogroup z etykieta pola
    return `<div class="knf__seg" ${attr} role="radiogroup" aria-label="${escapeAttr(label || "")}">${vals.map(v => `<button type="button" class="knf__seg-btn" role="radio" data-v="${v}" aria-checked="${String(v) === String(cur) ? "true" : "false"}">${escapeHtml(fmt(v))}</button>`).join("")}</div>`;
  }
  function nadHtml(c) {
    const n = state.nad;
    // UX v2 pkt 10: do klikniecia tylko szerokosci z cena (dzis 75 cm); reszta jako zdanie z kontaktem, bez wylaczonych przyciskow
    const wOk = NAD.W.filter(w => NAD_PRICES[w]);
    if (wOk.length && !wOk.includes(n.w)) n.w = wOk[0];
    const st = [c.styl ? tStyle(c.styl) : "", colorParts(c).tr].filter(Boolean).join(" · ");
    const cmL = v => `${v} cm`;
    return `<article class="knf__nad" data-knf-nad>
      <p class="knf__nad-h">${escapeHtml(sx("NAD_TITLE"))}</p>
      <p class="knf__nad-desc">${escapeHtml(sx("NAD_DESC", { styl: st }))}</p>
      <div class="knf__nad-field"><p class="knf__label">${escapeHtml(sx("NAD_W"))}</p>${nadSeg("data-knf-nad-w", wOk, n.w, v => cmL(v / 10), sx("NAD_W"))}${wOk.length < NAD.W.length ? `<p class="knf__nad-note">${escapeHtml(sx("NAD_W_NOTE"))} <a href="${NAD_MAIL}">kontakt@ogony.pl</a></p>` : ""}</div>
      <div class="knf__nad-row">
        <div class="knf__nad-field"><p class="knf__label">${escapeHtml(sx("NAD_D"))}</p>${nadSeg("data-knf-nad-d", NAD.D, n.d, cmL, sx("NAD_D"))}</div>
        <div class="knf__nad-field"><p class="knf__label">${escapeHtml(sx("NAD_H"))}</p>${nadSeg("data-knf-nad-h", NAD.H, n.h, cmL, sx("NAD_H"))}</div>
      </div>
      <div class="knf__nad-field"><label class="knf__label" for="knf-nad-gap">${escapeHtml(sx("NAD_GAP"))}</label>
        <span class="knf__nad-gap"><input id="knf-nad-gap" class="knf__input" type="text" inputmode="decimal" autocomplete="off" enterkeyhint="done" data-knf-nad-gap value="${escapeAttr(n.gap)}"><span class="knf__unit">cm</span></span>
        <p class="knf__nad-note">${escapeHtml(sx("NAD_GAP_HINT"))}</p></div>
      <p class="knf__nad-msg" data-knf-nad-msg hidden></p>
      <p data-knf-nad-out></p>
      <div class="knf__nad-field"><p class="knf__label">${escapeHtml(sx("NAD_SHELF"))}</p>${nadSeg("data-knf-nad-polka", [0, 1], "", v => sx(v ? "NAD_YES" : "NAD_NO"), sx("NAD_SHELF"))}</div>
      <p class="knf__nad-note">${escapeHtml(sx("NAD_HINGES"))}</p>
      <div class="knf__nad-qty"><span class="knf__label">${escapeHtml(sx("NAD_QTY"))}</span><div class="knf__qty">
        <button type="button" class="knf__qty-btn" data-knf-nad-dec aria-label="${escapeAttr(sx("NAD_QTY_DEC"))}">−</button>
        <input type="number" class="knf__qty-input" inputmode="numeric" min="1" max="${NAD.QTY_MAX}" step="1" value="${n.qty}" data-knf-nad-qty aria-label="${escapeAttr(sx("NAD_QTY"))}">
        <button type="button" class="knf__qty-btn" data-knf-nad-inc aria-label="${escapeAttr(sx("NAD_QTY_INC"))}">+</button></div></div>
      <label class="knf__nad-check"><input type="checkbox" data-knf-nad-fronty${n.fronty ? " checked" : ""}> ${escapeHtml(sx("NAD_FRONTS"))}</label>
      <p class="knf__nad-msg" data-knf-nad-warn${n.fronty ? " hidden" : ""}>${escapeHtml(sx("NAD_NOFRONTS"))}</p>
      <div class="knf__nad-sum" data-knf-nad-sum></div>
      <button type="button" class="knf__btn knf__btn--secondary" data-knf-nad-add></button>
    </article>`;
  }
  // Stan karty -> wynik: { ok, msg (html), stop, k (korpus), doors[], gapMm, polka }
  function nadEval() {
    const n = state.nad;
    const r = { ok: false, msg: "", stop: false };
    const raw = String(n.gap == null ? "" : n.gap).replace(",", ".").trim();
    let v = parseFloat(raw);
    if (!raw || !isFinite(v)) { if (raw) r.msg = escapeHtml(sx("NAD_GAP_REQ")); r.reason = sx("NAD_BTN_GAP"); return r; }   // UX v2: bez bledu przed wpisaniem - powod na przycisku
    v = Math.floor(v * 2) / 2;   // S5: w dol do 0,5 cm - korpus nigdy wyzszy niz pomiar - 10 mm
    const gapMm = Math.round(v * 10);
    if (v >= 250 && v <= 1000) {
      r.msg = `${escapeHtml(sx("NAD_MM", { v: nadCm(v), cm: nadCm(v / 10) }))} <button type="button" class="knf__link" data-knf-nad-fix="${v / 10}">${escapeHtml(sx("NAD_FIX"))}</button>`;
      r.stop = true;
      return r;
    }
    if (gapMm < NAD.GAP_MIN_MM) {
      // 04.10.2026 (Konrad): PAX „Przedłużone” ida w dol do podlogi, a front dluzszy niz 240 cm nie pojedzie - zamiast przekierowania kontakt
      r.msg = `${escapeHtml(sx("NAD_LOW"))} <a href="${NAD_MAIL}">kontakt@ogony.pl</a>`;
      r.stop = true;
      return r;
    }
    if (gapMm > NAD.GAP_MAX_MM) { r.msg = `${escapeHtml(sx("NAD_HIGH"))} <a href="${NAD_MAIL}">kontakt@ogony.pl</a>`; r.stop = true; return r; }
    const calc = nadCompute(n.w, gapMm, n.d);
    Object.assign(r, { gapMm, gapCm: v, k: calc.korpus, doors: calc.doors, polka: n.polka == null ? calc.korpus.wys >= NAD.SHELF_MIN_MM : !!n.polka });
    if (!NAD_PRICES[n.w]) { r.msg = `${escapeHtml(sx("NAD_W_ASK", { n: n.w / 10 }))} <a href="${NAD_MAIL}">kontakt@ogony.pl</a>`; r.stop = true; return r; }
    r.ok = true;
    return r;
  }
  // Odswiezenie wyniku, komunikatow i sumy bez przerysowania karty (fokus w polu szczeliny zostaje)
  function nadUpdate() {
    const card = $("[data-knf-nad]");
    if (!card || !KORPUS) return;
    const n = state.nad, r = nadEval(), c = snapshotCommon();
    const msg = card.querySelector("[data-knf-nad-msg]"), out = card.querySelector("[data-knf-nad-out]");
    // M7: zapis tylko przy zmianie - change/blur z klawiatury nie podmienia przycisku („Popraw”) przed click
    if (msg) { if (msg.dataset.sig !== r.msg) { msg.dataset.sig = r.msg; msg.innerHTML = r.msg; } if (msg.hidden !== !r.msg) msg.hidden = !r.msg; msg.classList.toggle("knf__nad-msg--stop", !!r.stop); }
    if (out) {
      const k = r.k;
      if (!k) out.textContent = "";
      else {
        const kTxt = `${Math.round(k.szer / 10)}×${Math.round(k.wys / 10)}×${Math.round(k.gl / 10)} cm (${k.szer}×${k.wys}×${k.gl} mm)`;
        const d = r.doors[0];
        const dTxt = d ? `${r.doors.length}× ${Math.round(d.szer / 10)}×${Math.round(d.wys / 10)} cm (${d.szer}×${d.wys} mm)` : "";
        out.textContent = sx(n.fronty && d ? "NAD_OUT" : "NAD_OUT_NOD", { k: kTxt, d: dTxt, c: nadCm(n.h + r.gapCm) });
      }
    }
    $$("[data-knf-nad-polka] .knf__seg-btn", card).forEach(b => b.setAttribute("aria-checked", String(!!Number(b.dataset.v) === !!r.polka && !!r.k)));
    const warn = card.querySelector("[data-knf-nad-warn]");
    if (warn) warn.hidden = !!n.fronty;
    const sum = card.querySelector("[data-knf-nad-sum]"), add = card.querySelector("[data-knf-nad-add]");
    // UX v2 pkt 10: podsumowanie raz - korpus i drzwi, kazde z jedna cena (bez ceny sztuki i bez powtorzenia na przycisku)
    const kLabel = sx("NAD_SUM_K", { n: n.qty, price: "" }).replace(/ · $/, "");
    const rows = r.k ? [[kLabel, fmtMarket(Number(KORPUS.p) * n.qty)]] : [];
    if (r.k && n.fronty) {
      const doors = nadDoorLines({ szer: n.w, wys: r.k.wys, gl: r.k.gl, gap: r.gapMm, qty: n.qty, fronty: true, grp: "" });
      let dc = 0, dq = 0, okD = true;
      doors.forEach(l => { const u = lineUnit(l, c); dq += l.qty; if (u == null) okD = false; else dc += u * l.qty; });
      if (dq) rows.push([sx("NAD_SUM_D", { n: dq, styl: c.styl ? tStyle(c.styl) : "" }), okD ? fmtMarket(dc) : "-"]);
    }
    if (sum) { const sh = rows.map(([a, b]) => `<p class="knf__nad-sumrow"><span>${escapeHtml(a)}</span><span>${escapeHtml(b)}</span></p>`).join(""); if (sum.dataset.sig !== sh) { sum.dataset.sig = sh; sum.innerHTML = sh; } }
    if (add) { const at = r.ok || !r.reason ? sx("NAD_ADD") : r.reason; if (add.textContent !== at) add.textContent = at; add.disabled = !r.ok; }
  }
  function onNadInput(e) {
    const t = e.target;
    if (!t.closest || !t.closest("[data-knf-nad]")) return;
    const n = state.nad;
    if (t.matches("[data-knf-nad-gap]")) n.gap = t.value;
    else if (t.matches("[data-knf-nad-qty]")) { if (e.type === "change" || t.value !== "") n.qty = Math.max(1, Math.min(NAD.QTY_MAX, parseInt(t.value, 10) || 1)); if (e.type === "change") t.value = n.qty; }
    else if (t.matches("[data-knf-nad-fronty]")) n.fronty = !!t.checked;
    else return;
    nadUpdate();
    saveSet();
  }
  function onNadClick(t) {
    const n = state.nad, card = t.closest("[data-knf-nad]");
    const seg = t.closest(".knf__seg-btn");
    const grp = seg && seg.closest("[data-knf-nad-w], [data-knf-nad-d], [data-knf-nad-h], [data-knf-nad-polka]");
    if (grp) {
      const v = Number(seg.dataset.v);
      if (grp.hasAttribute("data-knf-nad-w")) n.w = v;
      else if (grp.hasAttribute("data-knf-nad-d")) n.d = v;
      else if (grp.hasAttribute("data-knf-nad-h")) n.h = v;
      else n.polka = v ? 1 : 0;
      if (!grp.hasAttribute("data-knf-nad-polka")) $$(".knf__seg-btn", grp).forEach(b => b.setAttribute("aria-checked", b === seg ? "true" : "false"));
      nadUpdate();
      saveSet();
      return true;
    }
    const qb = t.closest("[data-knf-nad-dec], [data-knf-nad-inc]");
    if (qb) {
      n.qty = Math.max(1, Math.min(NAD.QTY_MAX, n.qty + (qb.hasAttribute("data-knf-nad-inc") ? 1 : -1)));
      const inp = card.querySelector("[data-knf-nad-qty]");
      if (inp) inp.value = n.qty;
      nadUpdate();
      saveSet();
      return true;
    }
    const fix = t.closest("[data-knf-nad-fix]");
    if (fix) {
      n.gap = fix.dataset.knfNadFix;
      const inp = card.querySelector("[data-knf-nad-gap]");
      if (inp) inp.value = n.gap;
      nadUpdate();
      saveSet();
      return true;
    }
    if (t.closest("[data-knf-nad-add]")) { addNad(card); return true; }
    return false;
  }
  // „Dodaj nadstawki do zestawu”: korpus × N + drzwi nadstawki (C3), powiazane grp
  function addNad(card) {
    if (setBusy()) return;
    const r = nadEval(), n = state.nad;
    if (!r.ok || !KORPUS) { nadUpdate(); return; }
    const grp = "n" + newUid();
    const k = { uid: newUid(), kind: "korpus", grp, sys: "PAX", typ: NAD_KORPUS_TYP, szer: r.k.szer, wys: r.k.wys, gl: r.k.gl, gap: r.gapMm,
      ramaH: n.h, polka: !!r.polka, fronty: !!n.fronty, qty: n.qty, note: "", ts: Date.now() };
    state.set.push(k);
    nadDoorLines(k).forEach(l => state.set.push(l));
    lastUid = k.uid;
    trackKnf("knf_nad_add", { nad: 1, nad_qty: n.qty });
    showToast(sx("T_TOAST", { name: lineName(k), n: n.qty }), [[k.uid, n.qty]]);
    pulse(card, "is-bump");
    afterSetChange({ fresh: k.uid, anchor: card });
  }

  // ==========================================================================
  // ZESTAW: lista „Twój zestaw” (TEMPO M7, 8.1)
  // ==========================================================================
  let setExpanded = false;      // „Pokaż wszystkie” na telefonie
  const rowOpen = {};           // uid -> "opts" | "note"
  let rowAskDel = null;         // uid korpusu z pytaniem „Usunąć też drzwi?”
  let undoSnap = null, undoTimer = null;
  function setMsg(text, undo) {
    const html = text ? escapeHtml(text) + (undo ? ` <button type="button" class="knf__link" data-knf-set-undo>${escapeHtml(sx("T_UNDO"))}</button>` : "") : "";
    const m = $("[data-knf-set-msg]");
    if (m) m.innerHTML = html;
    const cm = $("[data-knf-common-msg]");
    if (cm) cm.textContent = text || "";
  }
  // Migawka do „Cofnij” (6 s) przed usunieciem / wyczyszczeniem
  function withUndo(msg, fn) {
    undoSnap = JSON.parse(JSON.stringify(state.set));
    fn();
    setMsg(msg, true);
    clearTimeout(undoTimer);
    undoTimer = setTimeout(() => { undoSnap = null; const b = $("[data-knf-set-undo]"); if (b) b.remove(); }, 6000);
    afterSetChange({ keepMsg: true });
  }
  // M2: po dodaniu do koszyka „Cofnij” nie moze wrocic pozycji, ktore juz sa w koszyku
  function dropUndo() {
    undoSnap = null;
    clearTimeout(undoTimer);
    const b = $("[data-knf-set-undo]");
    if (b) b.remove();
    setMsg("");
  }
  function miniFor(l, c, i) {
    if (l.kind === "korpus") return '<svg viewBox="0 0 32 40" width="32" height="40" aria-hidden="true" fill="#fff" stroke="currentColor" stroke-width="1.2"><rect x="3" y="12" width="26" height="16" rx="1"/><line x1="16" y1="12" x2="16" y2="28"/></svg>';
    if (typeof window.knfMini !== "function") return "";
    const o = l.opt || {}, loc = o["Lokalizacja uchwytu"] || "";
    try {
      return window.knfMini({ style: lineStyl(l, c), hex: c.hex, szer: l.szer, wys: l.wys, loc: loc || "Góra", hinge: o[HINGE_LABEL] || "", horiz: o[GRAIN_LABEL] === "Poziomy",
        gloss: /^Połysk/.test(c.wyk || ""), typ: l.typ, customOn: !c.hex && !!c.custom, locCustom: /Niestandardow/i.test(loc) || noHandleTyp(l.typ) }, 32, 40, i) || "";
    } catch (_) { return ""; }
  }
  // Opcje E do edycji w wierszu (przyciski zamiast selectow, bez name i bez data-knf-cond)
  function rowOptDefs(l, c) {
    if (l.kind === "korpus") return [];
    const styl = lineStyl(l, c), out = [];
    const so = styleObjOf(styl), wood = !!(so && so.category && so.category !== "lakier");
    const locDef = defByLabel("Lokalizacja uchwytu"), gDef = defByLabel(GRAIN_LABEL);
    if (!noHandleTyp(l.typ) && locDef && styleHasLabel(styl, "Lokalizacja uchwytu")) out.push({ label: "Lokalizacja uchwytu", vals: styleValues(styl, locDef) });
    const hm = hingeMode(styl, l.typ);
    if (needsHinge(l.typ) && hm !== "none") {
      const loc = (l.opt || {})["Lokalizacja uchwytu"] || "";
      out.push({ label: HINGE_LABEL, vals: (hm === "optional" ? [""] : []).concat(["Lewa", "Prawa"]), lock: /^(Lewa|Prawa)/.test(loc) });
    }
    if (gDef && styleHasLabel(styl, GRAIN_LABEL)) out.push({ label: GRAIN_LABEL, vals: styleValues(styl, gDef), lock: wood && (isPlinthTyp(l.typ) || (l.szer !== l.wys && Math.min(l.szer, l.wys) < GRAIN_MIN_MM)) });
    if (lineNeedsExt(l)) out.push({ label: EXT_LABEL, vals: ["Góra", "Dół", "Bok"] });
    const tv = thickOpts(styl, l.typ, l.sys);
    if (tv.length) out.push({ label: GRUB_KEY, vals: tv });
    return out;
  }
  function optBtns(l, label, vals, lock) {
    const g = label === GRUB_KEY, std = g ? thickStd(state.styl) : "";
    const cur = g ? l.grubosc || std : (l.opt || {})[label] || "";
    return vals.map(v => `<button type="button" class="knf__seg-btn" role="radio" data-knf-row-set="${escapeAttr(label)}" data-v="${escapeAttr(v)}" aria-checked="${v === cur ? "true" : "false"}"${lock ? " disabled" : ""}>${escapeHtml(g ? thickLbl(v, std) : v ? tOptVal(label, v) : sx("T_HINGE_ANY"))}</button>`).join("");
  }
  function rowEditHtml(l, c) {
    const mode = rowOpen[l.uid];
    let h = "";
    if (mode === "opts") {
      rowOptDefs(l, c).forEach(d => {
        const lbl = d.label === GRUB_KEY ? (THICKNESS_I18N[locale] || THICKNESS_I18N.pl).short : d.label === HINGE_LABEL && d.vals[0] === "" ? sx("T_HINGE_OPT") : tCondPair(d.label, "").label;
        h += `<p class="knf__label">${escapeHtml(lbl)}</p><div class="knf__seg" role="radiogroup" aria-label="${escapeAttr(lbl)}">${optBtns(l, d.label, d.vals, d.lock)}</div>`;
        if (d.label === "Lokalizacja uchwytu" && /Niestandardow/i.test((l.opt || {})[d.label] || "")) {
          h += `<input type="text" class="knf__input" data-knf-row-opis="${escapeAttr(d.label)}" value="${escapeAttr((l.opis || {})[d.label] || "")}" placeholder="${escapeAttr(condCustomPh)}" aria-label="${escapeAttr(condCustomPh)}">`;
        }
      });
    }
    h += `<textarea class="knf__textarea" rows="2" data-knf-row-note placeholder="${escapeAttr(sx("T_ROW_NOTE_PH"))}" aria-label="${escapeAttr(sx("T_ROW_NOTE_PH"))}">${escapeHtml(l.note || "")}</textarea>`;
    return h;
  }
  function rowMissHtml(l, code, c) {
    const txt = escapeHtml(missText(code, l, c));
    if (code === "hinge") return `<p class="knf__row-miss">! ${txt} ${optBtns(l, HINGE_LABEL, ["Lewa", "Prawa"], false)}</p>`;
    if (code === "ext") return `<p class="knf__row-miss">! ${txt} ${optBtns(l, EXT_LABEL, ["Góra", "Dół", "Bok"], false)}</p>`;
    if (code === "opis") return `<p class="knf__row-miss">! ${txt} <button type="button" class="knf__link" data-knf-row-opts>${escapeHtml(sx("T_ROW_OPTS"))}</button></p>`;
    return `<p class="knf__row-miss">! ${txt}</p>`;
  }
  function rowSub(l, c) {
    if (l.kind === "korpus") return [sx(l.polka ? "NAD_ROW_SUB" : "NAD_ROW_SUB0"), l.fronty ? "" : sx("KP_NOFRONTS")].filter(Boolean).join(" · ");
    const segs = lineSegs(l, c).filter(s => s.e || s.ctx || (s.gr && l.grubosc && l.sys !== "NA WYMIAR")).map(s => s.tr);
    // S8: NA WYMIAR - wymiar mm jest juz w nazwie, w podpisie grubosc (18 i 30 mm nie wygladaja tak samo)
    if (l.sys === "NA WYMIAR") return [tSystem(l.sys), `${L_PROP.V_GRUBOSC}: ${l.grubosc || stdGrub()}`].concat(segs).join(" · ");
    return [tSystem(l.sys), `${l.szer}×${l.wys} mm`].concat(segs).join(" · ");
  }
  function rowHtml(l, c, vis, i) {
    const code = validateLine(l, c);
    const unit = lineUnit(l, c);
    const name = lineName(l);
    const open = rowOpen[l.uid];
    const hid = vis && !vis.has(l.uid) ? " hidden" : "";
    const ask = rowAskDel === l.uid ? `<p class="knf__row-ask">${escapeHtml(sx("NAD_DEL_Q"))} <button type="button" class="knf__link" data-knf-row-delgrp="1">${escapeHtml(sx("T_YES"))}</button> <button type="button" class="knf__link" data-knf-row-delgrp="0">${escapeHtml(sx("T_NO"))}</button></p>` : "";
    const max = l.kind === "korpus" ? NAD.QTY_MAX : 50;
    return `<li class="knf__row${code ? " is-miss" : ""}${l._fail ? " is-failed" : ""}" data-uid="${escapeAttr(l.uid)}"${hid}>
      <span class="knf__row-mini" aria-hidden="true">${miniFor(l, c, i)}</span>
      <div class="knf__row-main"><p class="knf__row-title">${escapeHtml(name)}</p><p class="knf__row-sub">${escapeHtml(rowSub(l, c))}</p>${code ? rowMissHtml(l, code, c) : ""}${ask}</div>
      <div class="knf__row-qty knf__qty"><button type="button" class="knf__qty-btn" data-knf-row-dec aria-label="${escapeAttr(sx("T_ROW_DEC", { name }))}">−</button><input class="knf__qty-input" type="number" inputmode="numeric" min="1" max="${max}" step="1" value="${l.qty}" data-knf-row-qty aria-label="${escapeAttr(sx("T_ROW_QTY", { name }))}"><button type="button" class="knf__qty-btn" data-knf-row-inc aria-label="${escapeAttr(sx("T_ROW_INC", { name }))}">+</button></div>
      <div class="knf__row-price"><strong>${unit != null ? escapeHtml(fmtMarket(unit * l.qty)) : "-"}</strong>${unit != null && l.qty > 1 ? `<small>${escapeHtml(sx("T_ROW_UNIT", { price: fmtMarket(unit, true) }))}</small>` : ""}</div>
      <div class="knf__row-actions">${rowOptDefs(l, c).length ? `<button type="button" data-knf-row-opts aria-expanded="${open === "opts" ? "true" : "false"}">${escapeHtml(sx("T_ROW_OPTS"))}</button>` : ""}<button type="button" data-knf-row-note-toggle aria-expanded="${open === "note" ? "true" : "false"}">${escapeHtml(sx("T_ROW_NOTE"))}</button><button type="button" data-knf-row-del>${escapeHtml(sx("T_ROW_DEL"))}</button></div>
      <div class="knf__row-edit" data-knf-row-edit${open ? "" : " hidden"}>${open ? rowEditHtml(l, c) : ""}</div>
    </li>`;
  }
  function renderSet(freshUid) {
    if (!SET_MODE || state.sample_mode) return;
    const list = $("[data-knf-set-list]");
    if (!list) return;
    bindSetList();
    const c = snapshotCommon(), t = setTotals(c);
    lastSetCents = t.cents;
    const cnt = $("[data-knf-set-count]");
    if (cnt) cnt.textContent = t.rows ? sx("T_SET_COUNT", { rows: t.rows, qty: t.qty }) : "";
    const clr = $("[data-knf-set-clear]");
    if (clr) clr.hidden = !t.rows;
    const empty = $("[data-knf-set-empty]");
    if (empty) { empty.hidden = !!t.rows; const et = sx(sentToCart ? "T_SET_SENT" : "T_SET_EMPTY"); if (empty.textContent !== et) empty.textContent = et; }   // UX v2: instrukcja tylko nad siatka
    // telefon: od 7 pozycji 6 ostatnio zmienianych + „Pokaż wszystkie”
    let vis = null;
    if (MOBILE_MQ.matches && !setExpanded && t.rows > 6) {
      vis = new Set(state.set.slice().sort((a, b) => b.ts - a.ts).slice(0, 6).map(l => l.uid));
      // S11: para narożnika / komplet nadstawki zawsze w calosci (drzwi A bez B i blend wprowadzaja w blad)
      const gk = l => l.grp || (l.kit ? `kit:${l.kit}:${l.tag || ""}` : "");
      const gs = new Set(state.set.filter(l => vis.has(l.uid) && gk(l)).map(gk));
      state.set.forEach(l => { if (gk(l) && gs.has(gk(l))) vis.add(l.uid); });
    }
    list.innerHTML = state.set.map((l, i) => rowHtml(l, c, vis, i)).join("");
    const more = $("[data-knf-set-more]");
    if (more) { more.hidden = !vis; more.textContent = sx("T_SET_MORE", { n: t.rows }); }
    const sum = $("[data-knf-set-sum]");
    if (sum) sum.textContent = t.rows ? fmtMarket(t.cents) : "-";
    const tl = $("[data-knf-set-total-label]");
    if (tl) tl.textContent = sx("T_TOTAL", { qty: t.qty });
    if (freshUid) {
      const li = list.querySelector(`[data-uid="${cssEscape(freshUid)}"]`);
      if (li) {
        li.classList.add("is-fresh");
        setTimeout(() => li.classList.remove("is-fresh"), 900);
        // M5: przewijamy sama liste (bez scrollIntoView - strona nie skacze), zeby nowy wiersz byl widoczny
        const lr = list.getBoundingClientRect(), r = li.getBoundingClientRect();
        if (r.bottom > lr.bottom) list.scrollTop += r.bottom - lr.bottom;
        else if (r.top < lr.top) list.scrollTop -= lr.top - r.top;
      }
    }
    syncListFade(list);
    renderForget();
    syncCommonBar();
  }
  // Lista z wlasnym przewijaniem (desktop): cieniowanie krawedzi, gdy nad albo pod widokiem sa jeszcze wiersze
  function syncListFade(list) {
    if (!list) return;
    const upd = () => {
      const top = list.scrollTop > 2, bottom = list.scrollTop + list.clientHeight < list.scrollHeight - 2;
      list.classList.toggle("is-more-top", top);
      list.classList.toggle("is-more-bottom", bottom);
    };
    if (!list.dataset.knfFade) { list.dataset.knfFade = "1"; list.addEventListener("scroll", upd, { passive: true }); }
    upd();
  }
  // B3 „Nie zapomnij”: METOD/FAKTUM bez paneli bocznych / cokolow / blend w tym systemie - klik przelacza dodawacz (niczego nie dodaje)
  function renderForget() {
    const box = $("[data-knf-forget]");
    if (!box) return;
    const last = state.set.slice().sort((a, b) => b.ts - a.ts).find(l => l.sys === "METOD" || l.sys === "FAKTUM");
    const miss = last ? ["Panele boczne", "Cokoły", BLENDA].filter(t => sysHasTyp(last.sys, t) && !state.set.some(l => l.sys === last.sys && l.typ === t)) : [];
    box.hidden = !miss.length;
    box.innerHTML = miss.length ? `<span>${escapeHtml(sx("T_FORGET"))}</span> ` + miss.map(t => `<button type="button" class="knf__chip" data-knf-forget-typ="${escapeAttr(t)}" data-knf-forget-sys="${escapeAttr(last.sys)}">+ ${escapeHtml(tType(t))}</button>`).join(" ") : "";
  }
  function goToTyp(sys, typ) {
    const sysSel = $("[data-knf-system]");
    if (sys && sysSel && sysSel.value !== sys) { const sb = $(`.knf__syschip[data-sys="${cssEscape(sys)}"]`); if (sb) sb.click(); }
    const tb = $(`.knf__typchip[data-typ="${cssEscape(typ)}"]`);
    if (tb && !tb.classList.contains("is-active")) tb.click();
    const pr = $("[data-knf-presets]");
    const fs = pr ? pr.closest(".knf__fs") : null;
    if (fs) knfScrollTo(fs, 100);
  }
  // -- Linia wspolnych wyborow (D13): zwiniecie krokow 1-3 po 1. pozycji, „Zmień” / „Gotowe”
  let commonUserOpen = false;
  function syncCommonBar() {
    const bar = $("[data-knf-commonbar]");
    if (!bar || !SET_MODE || state.sample_mode) return;
    const col = root.classList.contains("knf--collapsed");
    // integracja: zapis tylko przy zmianie (kazdy zapis budzi obserwator rysunku); zmiana widocznosci linii = nowa numeracja krokow
    const setT = (el, s) => { if (el && el.textContent !== s) el.textContent = s; };
    const setH = (el, h) => { if (el && el.hidden !== h) el.hidden = h; };
    const barHid = !(col || state.set.length);
    if (bar.hidden !== barHid) { bar.hidden = barHid; renumberSteps(); }
    const c = snapshotCommon();
    const parts = [c.styl ? tStyle(c.styl) : "", colorParts(c).tr];
    if (c.category === "lakier") { if (c.wyk) parts.push(tFinish(c.wyk)); parts.push(c.strona === "Dwustronnie" ? L_PROP.V_DWUSTR : L_PROP.V_JEDNOSTR); }
    setT($("[data-knf-commonbar-text]"), parts.filter(Boolean).join(" · "));
    const mini = $("[data-knf-commonbar-mini]");
    if (mini) { const m = miniFor({ kind: "front", typ: "Drzwi", szer: 597, wys: 797, opt: {} }, c, 999); if (mini.dataset.sig !== m) { mini.dataset.sig = m; mini.innerHTML = m; } }
    setT(bar.querySelector(".knf__step-title"), sx("T_COMMON"));
    const ed = $("[data-knf-common-edit]");
    setH(ed, !col); setT(ed, sx("T_CHANGE"));
    // „Gotowe” zwija z powrotem kroki 1-3 rozwiniete przez „Zmień” (tylko gdy zestaw ma pozycje)
    const done = $("[data-knf-common-done]");
    setH(done, col || !state.set.length); setT(done, sx("T_DONE"));
    const cm = $("[data-knf-common-msg]");
    if (cm && !col && state.set.length && !cm.textContent) cm.textContent = sx("T_COMMON_NOTE", { qty: state.set.reduce((a, l) => a + l.qty, 0) });
  }
  // Kontener przewijania (Horizon przewija div.page-wrapper, nie window)
  function scrollBox(el) {
    for (let p = el && el.parentElement; p && p !== document.body; p = p.parentElement) {
      const oy = getComputedStyle(p).overflowY;
      if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) return p;
    }
    return document.scrollingElement || document.documentElement;
  }
  // Zwiniecie / rozwiniecie z kompensacja przewiniecia: element pod palcem zostaje w tym samym miejscu ekranu
  function setCollapsed(on, anchor) {
    if (root.classList.contains("knf--collapsed") !== on) {
      const top = anchor && anchor.isConnected ? anchor.getBoundingClientRect().top : null;
      root.classList.toggle("knf--collapsed", on);
      const cm = $("[data-knf-common-msg]");
      if (cm) cm.textContent = "";
      renumberSteps();
      if (top !== null && anchor.isConnected) {
        const d = anchor.getBoundingClientRect().top - top;
        if (d) scrollBox(anchor).scrollTop += d;
      }
    }
    syncCommonBar();
    if (stickyApply) stickyApply();
  }
  function collapseCommon(anchor) { if (!commonUserOpen) setCollapsed(true, anchor); }
  // Po kazdej zmianie listy: lista, liczniki, formularz, pasek, zapis
  let sentToCart = false;      // UX v2 pkt 6: po dodaniu do koszyka pusta lista mowi „Lista wysłana do koszyka.” (do nastepnego dodania)
  function afterSetChange(info) {
    info = info || {};
    if (info.fresh && !info.keepMsg) setMsg("");
    if (info.fresh && sentToCart) { sentToCart = false; root.classList.remove("knf--sent"); }
    if (!state.set.length) rowAskDel = null;
    renderSet(info.fresh);
    updateTileCounts();
    updateTypCounts();
    syncCustomForm();
    if (state.set.length && info.anchor) collapseCommon(info.anchor);
    tryUpdate();
    saveSet();
  }
  const rowOf = el => { const li = el.closest("[data-uid]"); return li ? state.set.find(l => l.uid === li.dataset.uid) || null : null; };
  function setRowQty(l, q) {
    const max = l.kind === "korpus" ? NAD.QTY_MAX : 50;
    const old = l.qty, st = qtyStep(l.typ);   // 09.10.2026: A+B (2-frontowe) tez z pola liczby tylko parami (3 -> 4)
    l.qty = Math.max(st, Math.min(max, Math.ceil((parseInt(q, 10) || st) / st) * st));
    if (l.qty === old) return false;
    if (q > max) setMsg(sx("T_MAX50"));
    l.ts = Date.now();
    lastUid = l.uid;
    if (l.kind === "korpus") nadSyncDoorQty(l, old);
    return true;
  }
  function removeRow(l, withGroup) {
    const gone = withGroup ? state.set.filter(x => x === l || (l.grp && x.grp === l.grp)) : [l];
    const n = gone.reduce((a, x) => a + x.qty, 0);
    withUndo(sx("T_ROW_REMOVED", { name: lineName(l), n }), () => { state.set = state.set.filter(x => !gone.includes(x)); rowAskDel = null; });
  }
  function clearSet() {
    const n = state.set.reduce((a, x) => a + x.qty, 0);
    if (!n) return;
    withUndo(sx("T_SET_CLEARED", { n }), () => { state.set = []; rowAskDel = null; });
    const rn = $("[data-knf-set-restore]");
    if (rn) rn.hidden = true;
  }
  // Jedna delegacja na blok listy + linie wspolnych + formularz „Inny wymiar”
  function bindSetList() {
    if (root.dataset.knfSetUi) return;
    root.dataset.knfSetUi = "1";
    const box = $("[data-knf-set]");
    if (box) bindSetBox(box);
    const ed = $("[data-knf-common-edit]");
    if (ed) ed.addEventListener("click", () => { commonUserOpen = true; setCollapsed(false, ed.closest("[data-knf-commonbar]")); });
    const done = $("[data-knf-common-done]");
    if (done) done.addEventListener("click", () => { commonUserOpen = false; setCollapsed(true, $("[data-knf-commonbar]")); knfScrollTo($("[data-knf-commonbar]"), 100); });
    const add = $("[data-knf-custom-add]");
    if (add) add.addEventListener("click", addCustom);
    const ch = $("[data-knf-custom-hint]");
    if (ch) ch.addEventListener("click", e => { const b = e.target.closest("[data-knf-switch-typ]"); if (b) switchTypKeep(b.dataset.knfSwitchTyp); });
    const price = $(".knf__cta .knf__price:not(.knf__price--total)");
    if (price) {
      price.setAttribute("role", "button");
      price.setAttribute("tabindex", "0");
      const go = () => knfScrollTo($("[data-knf-set]"), 100);
      price.addEventListener("click", go);
      price.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } });
    }
  }
  function bindSetBox(box) {
    box.addEventListener("click", e => {
      const t = e.target;
      if (setBusy() && t.closest("[data-knf-set-undo], [data-knf-set-clear], [data-knf-set-restore-clear], [data-knf-row-dec], [data-knf-row-inc], [data-knf-row-set], [data-knf-row-del], [data-knf-row-delgrp]")) return;   // M1
      if (t.closest("[data-knf-set-undo]")) {
        if (undoSnap) { state.set = undoSnap; undoSnap = null; setMsg(""); afterSetChange({}); }
        return;
      }
      if (t.closest("[data-knf-set-clear], [data-knf-set-restore-clear]")) { clearSet(); return; }
      if (t.closest("[data-knf-set-restore-show]")) { const r = $("[data-knf-set-restore]"); if (r) r.hidden = true; knfScrollTo($("[data-knf-set-list]") || box, 120); return; }
      if (t.closest("[data-knf-set-more]")) { setExpanded = true; renderSet(); return; }
      const fg = t.closest("[data-knf-forget-typ]");
      if (fg) { goToTyp(fg.dataset.knfForgetSys, fg.dataset.knfForgetTyp); return; }
      const l = rowOf(t);
      if (!l) return;
      if (t.closest("[data-knf-row-dec], [data-knf-row-inc]")) {
        const st = qtyStep(l.typ);   // A+B (2-frontowe): parami, minimum 2 szt.
        if (setRowQty(l, Math.max(st, l.qty + (t.closest("[data-knf-row-inc]") ? st : -st)))) afterSetChange({});
        return;
      }
      const set = t.closest("[data-knf-row-set]");
      if (set && !set.disabled) {
        const label = set.dataset.knfRowSet, v = set.dataset.v || "";
        if (label === GRUB_KEY) {   // 04.10.2026: grubosc pozycji; ta sama pozycja w tej grubosci juz jest - scalamy
          l.grubosc = v === thickStd(state.styl) ? null : v;
          l.ts = Date.now();
          const tw = state.set.find(x => x !== l && x.kind !== "korpus" && lineSig(x) === lineSig(l));
          if (tw && tw.qty + l.qty <= 50) { tw.qty += l.qty; tw.ts = l.ts; state.set = state.set.filter(x => x !== l); }
          afterSetChange({});
          return;
        }
        l.opt = l.opt || {};
        if (v) l.opt[label] = v; else delete l.opt[label];
        if (label === "Lokalizacja uchwytu" && !/Niestandardow/i.test(v) && l.opis) delete l.opis[label];
        if (label === "Lokalizacja uchwytu" && /Niestandardow/i.test(v)) rowOpen[l.uid] = "opts";
        forcedOpts(l.opt, lineStyl(l, snapshotCommon()), l.typ, l.szer, l.wys);
        l.ts = Date.now();
        afterSetChange({});
        return;
      }
      if (t.closest("[data-knf-row-opts]")) { rowOpen[l.uid] = rowOpen[l.uid] === "opts" ? "" : "opts"; renderSet(); return; }
      if (t.closest("[data-knf-row-note-toggle]")) {
        rowOpen[l.uid] = rowOpen[l.uid] ? "" : "note";
        renderSet();
        const ta = box.querySelector(`[data-uid="${cssEscape(l.uid)}"] [data-knf-row-note]`);
        if (ta) ta.focus({ preventScroll: true });
        return;
      }
      const dg = t.closest("[data-knf-row-delgrp]");
      if (dg) { removeRow(l, dg.dataset.knfRowDelgrp === "1"); return; }
      if (t.closest("[data-knf-row-del]")) {
        if (l.kind === "korpus" && state.set.some(x => x !== l && x.grp === l.grp)) { rowAskDel = l.uid; renderSet(); return; }
        removeRow(l, false);
      }
    });
    box.addEventListener("input", e => {
      const t = e.target;
      if (t.matches('textarea[name="properties[Uwagi klienta]"]')) { state.setNote = t.value; saveSet(); tryUpdate(); return; }
      const l = rowOf(t);
      if (!l) return;
      if (t.matches("[data-knf-row-note]")) { l.note = t.value; saveSet(); return; }
      if (t.matches("[data-knf-row-opis]")) { l.opis = l.opis || {}; l.opis[t.dataset.knfRowOpis] = t.value; saveSet(); tryUpdate(); }
    });
    box.addEventListener("change", e => {
      const t = e.target;
      const l = rowOf(t);
      if (!l) return;
      if (t.matches("[data-knf-row-qty]")) { if (setBusy()) { t.value = l.qty; return; } const ch = setRowQty(l, t.value); t.value = l.qty; if (ch) afterSetChange({}); }
      else if (t.matches("[data-knf-row-opis], [data-knf-row-note]")) { l.ts = Date.now(); afterSetChange({}); }
    });
  }

  // ==========================================================================
  // ZESTAW: pasek / CTA (TEMPO M8) i walidacja zestawu
  // ==========================================================================
  // Sloje przechodzace „Tak”: opis laczenia w uwagach do zestawu albo w uwadze ktorejs pozycji
  const setSlojeMissing = () => slojeNotesMissing() && !state.set.some(l => String(l.note || "").trim());
  function setPriceLabel(text) {
    const lbl = $(".knf__cta .knf__price:not(.knf__price--total) .knf__price-label");
    if (!lbl) return;
    const tn = lbl.firstChild && lbl.firstChild.nodeType === 3 ? lbl.firstChild : null;
    if (lbl.dataset.t === undefined) lbl.dataset.t = tn ? tn.nodeValue : "";
    if (text === null) text = lbl.dataset.t;   // null = tekst z Liquid (tryb probki na stronie frontu)
    if (tn) { if (tn.nodeValue !== text) tn.nodeValue = text; }
    else lbl.insertBefore(document.createTextNode(text), lbl.firstChild);
  }
  function renderSetBar() {
    syncAdder($("[data-knf-typ]")?.value || "");
    syncCustomForm();
    syncCommonBar();
    syncSampleLink();
    const c = snapshotCommon(), t = setTotals(c);
    const priceEl = $("[data-knf-price]"), submitBtn = $("[data-knf-submit]");
    const qtyRow = $(".knf__cta .knf__qty-row");
    if (qtyRow) qtyRow.hidden = true;
    const totalRow = $("[data-knf-total-row]");
    if (totalRow) totalRow.hidden = true;
    const om = $("[data-knf-omnibus]");
    if (om) om.hidden = true;
    const so = styleObjOf(state.styl), stawkaEl = $("[data-knf-stawka]");
    if (so && stawkaEl) stawkaEl.textContent = formatRatePerM2(parseInt(so.group, 10) || 0, so.name);
    // Runda 3: pasek jak na live - w spoczynku „Cena za sztukę: Wybierz typ i wymiar” | przycisk w obrysie z nastepnym krokiem;
    // z pozycjami „Zestaw · 9 szt.” + suma (klik = lista) | czarny „Do koszyka”
    setPriceLabel(t.rows ? sx("T_BAR_LABEL", { qty: t.qty }) : null);
    if (priceEl) {
      priceEl.classList.toggle("knf__price-value--hint", !t.rows);
      const pt = t.rows ? fmtMarket(t.cents) : sentToCart ? sx("T_SET_SENT") : L_BTN.PICK_TYP_DIM;
      if (priceEl.textContent !== pt) priceEl.textContent = pt;
    }
    const pb = $(".knf__cta .knf__price:not(.knf__price--total)");
    if (pb && pb.classList.contains("is-link") !== !!t.rows) pb.classList.toggle("is-link", !!t.rows);
    const vi = $("[data-knf-variant-id]");
    if (vi) vi.value = "";
    const noSys = !$("[data-knf-system]")?.value;
    if (submitBtn && !isAddBusy()) {
      let txt = sx("T_TO_CART"), inc = false;
      const typNow = $("[data-knf-typ]")?.value || "";
      if (!t.rows) { inc = true; txt = noSys ? sx("T_BAR_SYS") : !typNow ? L_BTN.MISS_TYP : sx(FINE_PTR ? "T_BAR_CLICK" : "T_BAR_TAP"); }
      else if (!state.styl) { txt = missMsg("MISS_STYL"); inc = true; }
      else if (!$("[data-knf-kolor-out]")?.value) { txt = missMsg("MISS_KOLOR"); inc = true; }
      else if (t.issues) { txt = sx("T_BAR_FIX", { n: t.issues }); inc = true; }
      else if (setSlojeMissing()) { txt = SLOJE_MISS[locale] || SLOJE_MISS.pl; inc = true; }
      submitBtn.disabled = false;   // pusty zestaw: przycisk w obrysie prowadzi do brakujacego kroku (scrollToFirstSetIssue)
      submitBtn.classList.toggle("knf__btn--incomplete", inc);
      if (submitBtn.textContent !== txt) submitBtn.textContent = txt;
    }
    renderSummary(null);
    if (stickyApply) stickyApply();
    // Pusty zestaw nie ma klucza knf:set - wspolne + system + typ zapisuje stary klucz (restoreState bez wymiaru i ilosci)
    if (!t.rows) saveState();
  }
  function canAddSet() {
    if (!state.set.length || !state.styl || !$("[data-knf-kolor-out]")?.value) return false;
    const c = snapshotCommon();
    if (state.set.some(l => validateLine(l, c))) return false;
    return !setSlojeMissing();
  }
  // Pierwszy problem zestawu: przewiniecie + podswietlenie (bez okien), tekst na przycisku 3 s
  function scrollToFirstSetIssue() {
    const btn = $("[data-knf-submit]");
    const show = (el, msg) => {
      if (!el) return;
      knfScrollTo(el, 120);
      el.classList.add("knf__error-flash");
      setTimeout(() => el.classList.remove("knf__error-flash"), 2000);
      focusMissingField(el);
      if (btn && msg) { btn.textContent = msg; setTimeout(() => { if (!isAddBusy()) tryUpdate(); }, 3000); }
    };
    if (!state.styl || !$("[data-knf-kolor-out]")?.value) {
      if (root.classList.contains("knf--collapsed")) { commonUserOpen = true; setCollapsed(false); }
      show(!state.styl ? $("[data-knf-styles]") : colorMissTarget(), missMsg(!state.styl ? "MISS_STYL" : "MISS_KOLOR"));
      return;
    }
    if (!state.set.length) {
      // bez systemu pasek prowadzi do kroku „System IKEA”
      if (!$("[data-knf-system]")?.value) { show($("[data-knf-sys-grid]"), sx("T_BAR_SYS")); return; }
      const pr = $("[data-knf-presets]");
      show(pr && !pr.hidden ? pr : ($("[data-knf-typ-grid]") || $("[data-knf-dims-manual]")), sx(FINE_PTR ? "T_BAR_CLICK" : "T_BAR_TAP"));
      return;
    }
    const c = snapshotCommon();
    const bad = state.set.find(l => validateLine(l, c));
    if (bad) {
      if (MOBILE_MQ.matches && !setExpanded && state.set.length > 6) { setExpanded = true; renderSet(); }
      show($(`[data-knf-set-list] [data-uid="${cssEscape(bad.uid)}"]`), missText(validateLine(bad, c), bad, c));
      return;
    }
    if (setSlojeMissing()) {
      const d = $("[data-knf-set-notes]");
      if (d) d.open = true;
      show(notesEl(), SLOJE_MISS[locale] || SLOJE_MISS.pl);
    }
  }
  // ==========================================================================
  // ZESTAW: koszyk (TEMPO 6.4-6.5). Jedno żądanie JSON items[], identyczne linie sumowane, nigdy ponowny POST calej listy.
  // ==========================================================================
  let pendingSet = null;    // { uids:Set, items, before } po bledzie albo czesciowym dodaniu
  const cartQty = (cart, hash) => { const l = cart ? findCartLineByHash(cart, hash) : null; return l ? l.quantity : 0; };
  // Pozycje -> items[] (uwagi do zestawu tylko przy pierwszej pozycji, D11; prefiks zawsze po polsku, Z8)
  function setItems(rows, c) {
    const out = [];
    const note = String(state.setNote || "").trim();
    rows.forEach((l, i) => {
      const uw = [String(l.note || "").trim(), i === 0 && note ? SET_I18N.pl.T_SET_PREFIX + note : ""].filter(Boolean).join(" | ");
      const props = buildLineProps(l, c, { uwagi: uw });
      const v = l.kind === "korpus" ? null : lineVariant(l, c);
      const id = l.kind === "korpus" ? Number(KORPUS && KORPUS.id) : Number(v && v.id);
      if (!id) return;
      const hash = hashParts(id, Object.entries(props));
      const ex = out.find(x => x.hash === hash);
      // M1: sent = wyslane sztuki per pozycja listy (uid), cents = kwota wyslana - po odpowiedzi odejmujemy dokladnie to
      const cents = (lineUnit(l, c) || 0) * l.qty;
      if (ex) { ex.quantity += l.qty; ex.uids.push(l.uid); ex.sent[l.uid] = l.qty; ex.cents += cents; }
      else { const it = { id, quantity: l.qty, properties: props, hash, uids: [l.uid], sent: {}, cents, korpus: l.kind === "korpus" }; it.sent[l.uid] = l.qty; out.push(it); }
    });
    return out;
  }
  // Odejmuje z listy wyslane sztuki pozycji (item.sent); n = ile sztuk itemu weszlo (domyslnie wszystkie). Zwraca uidy do usuniecia.
  function takeSent(i, n, drop, markFail) {
    let g = n == null ? i.quantity : n;
    i.uids.forEach(u => {
      const l = state.set.find(x => x.uid === u);
      if (!l) return;
      const d = Math.min(g, (i.sent && i.sent[u]) || 0);
      l.qty -= d; g -= d;
      if (l.qty <= 0) drop.add(u); else if (markFail) l._fail = 1;
    });
  }
  async function performAddSet(form, rowsIn) {
    const c = snapshotCommon();
    const rows = (rowsIn || state.set).filter(l => !validateLine(l, c));
    const items = setItems(rows, c);
    if (!items.length) return false;
    const submitBtn = $("[data-knf-submit]");
    form.dataset.knfSubmitting = "1";
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = L_BTN.ADDING; submitBtn.classList.remove("knf__btn--incomplete"); }
    hideInlineError();
    hideAddedState();
    const before = await fetchCartSafe(1500);
    let data;
    try {
      const r = await fetch(`${CART_ROOT}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        // S3: Shopify odwraca kolejnosc items[] w koszyku - wysylamy od konca, wiec koszyk i zamowienie ida w kolejnosci listy (uwagi D11 na gornej linii)
        body: JSON.stringify({ items: items.slice().reverse().map(i => ({ id: i.id, quantity: i.quantity, properties: i.properties })), sections: DRAWER_SECTION_ID, sections_url: window.location.pathname }),
      });
      let body = null;
      try { body = await r.json(); } catch (_) { /* nie-JSON */ }
      if (!r.ok || !body || body.status) {
        const err = new Error(`status_${r.status}`);
        err.description = body ? (body.description || body.message || "") : "";
        throw err;
      }
      data = body;
    } catch (err) {
      console.error("[knf] add set failed", err, err.description || "");
      trackKnf("knf_set_add_error", { rows: items.length, set_qty: items.reduce((a, i) => a + i.quantity, 0), message: String(err.description || err.message || "") });
      try { await reconcileSet(form, items, before, err); }   // M1: blokada listy trwa do konca uzgadniania koszyka
      finally { delete form.dataset.knfSubmitting; }
      return false;
    }
    delete form.dataset.knfSubmitting;
    await setAddSuccess(form, items, data && data.sections);
    return true;
  }
  async function setAddSuccess(form, items, sections, action) {
    // M1: panel „Dodano” liczony z tego, co wyslano (items), nie z biezacej listy; z listy znika tylko wyslana ilosc
    const qty = items.reduce((a, i) => a + i.quantity, 0);
    const cents = items.reduce((a, i) => a + (i.cents || 0), 0);
    const rowsN = items.reduce((a, i) => a + i.uids.length, 0);
    const nadQty = items.filter(i => i.korpus).reduce((a, i) => a + i.quantity, 0);
    const prevCount = readHeaderCount();
    const drop = new Set();
    items.forEach(i => takeSent(i, null, drop, false));
    state.set = state.set.filter(l => !drop.has(l.uid));
    state.set.forEach(l => { delete l._fail; });
    pendingSet = null;
    fail422 = null;
    hideInlineError();
    dropUndo();   // M2
    hideToast();
    sentToCart = true;
    root.classList.add("knf--sent");
    afterSetChange({});
    showAddedState({ set: true, sample: false, title: sx("T_ADDED_SET", { rows: rowsN, qty }), label: fmtMarket(cents), count: null });
    trackKnf("knf_set_add_success", { rows: rowsN, set_qty: qty, nad: nadQty ? 1 : 0, nad_qty: nadQty || undefined });
    try {
      const itemQty = items.reduce((a, i) => a + i.quantity, 0);
      const itemCount = prevCount + itemQty;
      const ev = await emitCartLinesUpdate(form, items.map(i => ({ merchandiseId: i.id, quantity: i.quantity })), action || "add");
      resolveCartUpdate(ev, { cart: null, sections, itemCount });
      applyDrawerHtmlFallback(sections ? sections[DRAWER_SECTION_ID] : null, itemCount);
      const drawerP = ensureCartDrawerOpen(2500);
      const cart = await fetchCartSafe(2500);
      if (cart && cart.item_count !== itemCount) {
        const icon = document.querySelector("cart-icon");
        if (icon && typeof icon.renderCartBubble === "function") icon.renderCartBubble(cart.item_count, false);
      }
      await drawerP;
    } catch (err) {
      console.warn("[knf] po dodaniu zestawu (nieblokujące)", err);
    }
  }
  // M6: osobne zadanie /cart/add.js dla czesci pozycji (bez sekcji szuflady); true = przyjete
  async function postSetItems(list) {
    try {
      const r = await fetch(`${CART_ROOT}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: list.slice().reverse().map(i => ({ id: i.id, quantity: i.quantity, properties: i.properties })) }),
      });
      let body = null;
      try { body = await r.json(); } catch (_) { /* nie-JSON */ }
      if (r.ok && body && !body.status) return true;
      console.error("[knf] add set item failed", r.status, body ? (body.description || body.message || "") : "");
    } catch (e) { console.error("[knf] add set item failed", e); }
    return false;
  }
  let fail422 = null;   // M6: uidy pozycji odrzuconych przez 422 przy ostatniej probie
  const SET_MAIL = "mailto:kontakt@ogony.pl?subject=" + encodeURIComponent("Zestaw frontów");
  // M6: ta sama odmowa drugi raz z rzedu - zamiast „Spróbuj ponownie” kontakt (jak przy nadstawce 50/100 cm)
  function showSetContact() {
    const msg = $("[data-knf-error-msg]"), rb = $("[data-knf-error-retry]");
    if (msg) msg.innerHTML = `${escapeHtml(msg.textContent)} ${escapeHtml(sx("T_ADD_CONTACT"))} <a href="${SET_MAIL}">kontakt@ogony.pl</a>`;
    if (rb) rb.hidden = true;
  }
  // Blad / brak odpowiedzi: /cart.js po linii. Wszystko weszlo -> sukces bez drugiego POST; czesc -> zostaja tylko brakujace.
  async function reconcileSet(form, items, before, err) {
    // S4: bez odczytu koszyka „przed” nie da sie policzyc, co weszlo (identyczna linia mogla juz byc w koszyku)
    let after = before ? await fetchCartSafe(2500) : null;
    const submitBtn = $("[data-knf-submit]");
    const is422 = !!err && err.message === "status_422";
    const gotOf = (cart, i) => Math.max(0, cartQty(cart, i.hash) - cartQty(before, i.hash));
    // M6: 422 = Shopify odrzucil cale zadanie (nic nie weszlo) - pozycje po jednej, poprawne wchodza (od konca: kolejnosc listy, S3)
    if (after && is422 && items.length > 1 && items.every(i => gotOf(after, i) === 0)) {
      for (const i of items.slice().reverse()) await postSetItems([i]);
      after = await fetchCartSafe(2500);
    }
    if (after) {
      const miss = [], full = [];
      items.forEach(i => { const got = gotOf(after, i); if (got >= i.quantity) full.push(i); else miss.push({ i, got }); });
      if (!miss.length) { trackKnf("knf_add_retry_verified", { rows: items.length }); await setAddSuccess(form, items, null); return; }
      // w pelni dodane pozycje znikaja z listy (tylko wyslana ilosc, M1), czesciowo dodane zostaja z iloscia brakujaca
      const drop = new Set();
      full.forEach(i => takeSent(i, null, drop, false));
      miss.forEach(({ i, got }) => takeSent(i, got, drop, true));
      if (full.length || miss.some(m => m.got)) dropUndo();   // M2: czesc juz w koszyku - „Cofnij” nie moze jej wrocic
      state.set = state.set.filter(l => !drop.has(l.uid));
      const missUids = new Set();
      miss.forEach(({ i }) => i.uids.forEach(u => { if (!drop.has(u)) missUids.add(u); }));
      pendingSet = { uids: missUids, items: miss.map(({ i }) => i), before };
      const names = miss.map(({ i }) => { const l = state.set.find(x => i.uids.includes(x.uid)); return l ? lineName(l) : ""; }).filter(Boolean);
      const mh = [...missUids];   // po uid, nie hashu: uwaga do zestawu (D11) przy ponowieniu trafia na inna pozycje i zmienia jej hash
      const again = is422 && !!fail422 && mh.length > 0 && mh.every(u => fail422.has(u));
      fail422 = is422 ? new Set(mh) : null;
      afterSetChange({});
      if (full.length) {
        trackKnf("knf_set_add_partial", { rows: full.length, set_qty: full.reduce((a, i) => a + i.quantity, 0) });
        try { const ev = await emitCartLinesUpdate(form, full.map(i => ({ merchandiseId: i.id, quantity: i.quantity })), "update"); resolveCartUpdate(ev, { cart: after, sections: null, itemCount: after.item_count }); } catch (_) { /* nieblokujace */ }
      }
      // komunikat zawsze w jezyku rynku (opis Shopify tylko w console.error); przy 422 nazwy odrzuconych pozycji
      if (full.length || is422) {
        showInlineError(sx("T_PARTIAL", { n: miss.length, m: items.length, list: names.join(", ") }));
        const rb = $("[data-knf-error-retry]");
        if (rb && full.length) rb.textContent = sx("T_ADD_MISSING");
      } else showInlineError(L_ADD.ERR_TITLE);
      if (again) showSetContact();
    } else {
      // koszyka nie da sie odczytac: nic nie zmieniamy w liscie, „Spróbuj ponownie” najpierw sprawdzi koszyk
      pendingSet = { uids: new Set(items.reduce((a, i) => a.concat(i.uids), [])), items, before };
      showInlineError(L_ADD.ERR_TITLE);
    }
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = L_ADD.ERR_BTN; }
    setTimeout(() => { if (!isAddBusy()) tryUpdate(); }, 2000);
  }
  async function retrySet(form) {
    const p = pendingSet;
    if (!p) return;
    const retryBtn = $("[data-knf-error-retry]"), msg = $("[data-knf-error-msg]");
    if (retryBtn) retryBtn.disabled = true;
    if (msg) msg.textContent = L_ADD.ERR_CHECKING;
    form.dataset.knfSubmitting = "1";   // M1: lista zablokowana takze na czas sprawdzania koszyka
    const after = p.before ? await fetchCartSafe(2500) : null;   // S4: bez „przed” nie weryfikujemy (falszywy sukces)
    delete form.dataset.knfSubmitting;
    if (retryBtn) retryBtn.disabled = false;
    if (after && p.items.every(i => cartQty(after, i.hash) - cartQty(p.before, i.hash) >= i.quantity)) { await setAddSuccess(form, p.items, null); return; }
    hideInlineError();
    const rows = state.set.filter(l => p.uids.has(l.uid));
    pendingSet = null;
    if (rows.length) await performAddSet(form, rows); else tryUpdate();
  }
  // ==========================================================================
  // ZESTAW: zapis i przywracanie (TEMPO 6.6, D10) - klucz knf:set:<handle>, 7 dni, przywrocenie zawsze z jawna nota
  // ==========================================================================
  const SET_KEY = `knf:set:${root.dataset.productHandle || root.dataset.productId || "default"}`;
  let saveSetTimer = null;
  const plainObj = o => { const r = {}; if (o && typeof o === "object") Object.keys(o).forEach(k => { if (o[k] != null && o[k] !== "") r[String(k)] = String(o[k]); }); return r; };
  function cleanLine(l) {
    const r = { uid: l.uid, kind: l.kind, sys: l.sys, typ: l.typ, szer: l.szer, wys: l.wys, qty: l.qty, note: l.note || "", grp: l.grp || null, ts: l.ts };
    if (l.kind === "korpus") return Object.assign(r, { gl: l.gl, gap: l.gap, ramaH: l.ramaH, polka: !!l.polka, fronty: !!l.fronty });
    return Object.assign(r, { dim: l.dim || "", opt: l.opt || {}, opis: l.opis || {}, grubosc: l.grubosc || null, tag: l.tag || null, kit: l.kit || null });
  }
  function sanitizeLine(l) {
    if (!l || typeof l !== "object") return null;
    const kind = l.kind === "korpus" ? "korpus" : "front";
    const r = { uid: String(l.uid || newUid()), kind, sys: String(l.sys || ""), typ: String(l.typ || ""), szer: Number(l.szer) || 0, wys: Number(l.wys) || 0,
      qty: Math.max(1, Math.min(kind === "korpus" ? NAD.QTY_MAX : 50, parseInt(l.qty, 10) || 1)), note: String(l.note || ""), grp: l.grp ? String(l.grp) : null, ts: Number(l.ts) || Date.now() };
    if (!r.typ || !r.szer || !r.wys) return null;
    if (kind === "front" && qtyStep(r.typ) === 2) r.qty = Math.min(50, Math.ceil(r.qty / 2) * 2);   // 09.10.2026: A+B z linku/zapisu parami
    if (kind === "korpus") return Object.assign(r, { gl: Number(l.gl) || 580, gap: Number(l.gap) || 0, ramaH: Number(l.ramaH) || 236, polka: !!l.polka, fronty: l.fronty !== false });
    return Object.assign(r, { dim: String(l.dim || ""), opt: plainObj(l.opt), opis: plainObj(l.opis), grubosc: ["12 mm", "16 mm", "18 mm", "19 mm", "30 mm"].includes(String(l.grubosc)) ? String(l.grubosc) : null, tag: l.tag ? String(l.tag) : null, kit: l.kit === "A" || l.kit === "B" ? l.kit : null });
  }
  function saveSet() {
    if (!SET_MODE) return;
    clearTimeout(saveSetTimer);
    saveSetTimer = setTimeout(saveSetNow, 250);
  }
  function saveSetNow() {
    try {
      if (!state.set.length) { localStorage.removeItem(SET_KEY); return; }   // pusty zestaw = brak klucza
      const ci = $("[data-knf-kolor-custom]"), cond = state.conditional || {};
      localStorage.setItem(SET_KEY, JSON.stringify({
        v: 1, ts: Date.now(),
        common: { styl: state.styl, kolor: state.kolor, custom: state.custom_kolor || "", customHex: ci ? ci.dataset.hex || "" : "", customPl: ci ? ci.dataset.pl || "" : "", customPlFor: ci ? ci.dataset.plFor || "" : "",
          strona: state.strona, wyk: cond[COND_KEY_WYKONCZENIE] || "", sloje: cond[COND_KEY_SLOJE] || "", grubosc: state.grubosc || "", grubAdd: state.grubAdd },
        adder: { sys: $("[data-knf-system]")?.value || "", typ: $("[data-knf-typ]")?.value || "" },
        kit: state.kit, nad: state.nad, set: state.set.map(cleanLine), setNote: state.setNote || "", zrodlo: state.setZrodlo || "",
      }));
    } catch (_) { /* tryb prywatny / limit - lista dziala bez zapisu */ }
  }
  function importSetFromUrl() {
    const url = new URL(window.location.href);
    const z = url.searchParams.get("zestaw");
    if (!z) return false;
    url.searchParams.delete("zestaw");
    const clean = url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash;
    root.style.visibility = "hidden";
    const done = () => window.location.replace(clean);
    decodeSetParam(z).then(p => {
      if (!p || !Array.isArray(p.set) || !p.set.length) return done();
      const src = z.length + ":" + z.slice(-24);
      try {
        const cur = JSON.parse(localStorage.getItem(SET_KEY) || "null");
        if (!(cur && cur.zrodlo === src && (Date.now() - (Number(cur.ts) || 0)) <= STORAGE_TTL_MS)) localStorage.setItem(SET_KEY, JSON.stringify(Object.assign({}, p, { v: 1, ts: Date.now(), zrodlo: src })));
      } catch (_) { /* brak localStorage - konfigurator startuje bez zestawu */ }
      done();
    }).catch(done);
    return true;
  }
  async function decodeSetParam(z) {
    if (!/^z1\.[A-Za-z0-9_-]+$/.test(z) || typeof DecompressionStream !== "function") return null;
    const b64 = z.slice(3).replace(/-/g, "+").replace(/_/g, "/");
    const bin = Uint8Array.from(atob(b64 + "===".slice((b64.length + 3) % 4)), c => c.charCodeAt(0));
    const txt = await new Response(new Blob([bin]).stream().pipeThrough(new DecompressionStream("deflate-raw"))).text();
    return JSON.parse(txt);
  }
  function loadSet() {
    try {
      const raw = localStorage.getItem(SET_KEY);
      const p = raw ? JSON.parse(raw) : null;
      if (!p || p.v !== 1 || !Array.isArray(p.set) || !p.set.length || (Date.now() - (Number(p.ts) || 0)) > STORAGE_TTL_MS) { if (p) localStorage.removeItem(SET_KEY); return null; }
      return p;
    } catch (_) { return null; }
  }
  // Kolejnosc jak przy pierwszym wyborze: styl -> strona -> kolor -> opcje G -> system -> typ; potem lista + przeliczenie + nota
  function restoreSet() {
    if (!SET_MODE) return false;
    const p = loadSet();
    if (!p) return false;
    // S6: zapis z localStorage moze byc uszkodzony / z innej wersji - wspolne tylko jako obiekt, kolor wlasny jako tekst
    const cm = (p.common && typeof p.common === "object") ? p.common : {};
    cm.custom = String(cm.custom || "");
    recalcMute++;
    try {
      if (cm.styl) { const b = $(`.knf__style[data-style="${cssEscape(cm.styl)}"]`); if (b) b.click(); }
      if (cm.strona) { state.strona = cm.strona; const s = $("[data-knf-strona]"); if (s) s.value = cm.strona; }
      if (cm.grubAdd && typeof cm.grubAdd === "object") ["nd", "fr"].forEach(k => { state.grubAdd[k] = ["12 mm", "16 mm", "30 mm"].includes(cm.grubAdd[k]) ? cm.grubAdd[k] : null; });
      if (cm.kolor) { const b = $(`.knf__color[data-color="${cssEscape(cm.kolor)}"]`); if (b) b.click(); }
      else if (cm.custom) {
        const cb = $('.knf__color[data-color="__custom__"]');
        if (cb) {
          cb.dataset.knfNoFocus = "1"; cb.click(); delete cb.dataset.knfNoFocus;
          const inp = $("[data-knf-kolor-custom]");
          if (inp) {
            inp.value = cm.custom;
            if (cm.customHex) inp.dataset.hex = cm.customHex;
            if (cm.customPl && cm.customPlFor === cm.custom.trim()) { inp.dataset.pl = cm.customPl; inp.dataset.plFor = cm.customPlFor; }
            inp.dispatchEvent(new Event("input"));
          }
        }
      }
      [[COND_KEY_WYKONCZENIE, cm.wyk], [COND_KEY_SLOJE, cm.sloje]].forEach(([lab, v]) => {
        const s = condSel(lab);
        if (s && v && s.value !== v && [...s.options].some(o => o.value === v)) { s.value = v; s.dispatchEvent(new Event("change", { bubbles: true })); }
      });
      if (cm.grubosc) state.grubosc = cm.grubosc;
      if (p.kit && p.kit.A && KITS.A.blends.includes(Number(p.kit.A.blend))) state.kit.A.blend = Number(p.kit.A.blend);
      if (p.nad && typeof p.nad === "object") {
        const n = p.nad;
        if (NAD.W.includes(Number(n.w))) state.nad.w = Number(n.w);
        if (NAD.D.includes(Number(n.d))) state.nad.d = Number(n.d);
        if (NAD.H.includes(Number(n.h))) state.nad.h = Number(n.h);
        state.nad.gap = n.gap == null ? "" : String(n.gap);
        state.nad.polka = n.polka == null ? null : (n.polka ? 1 : 0);
        state.nad.qty = Math.max(1, Math.min(NAD.QTY_MAX, parseInt(n.qty, 10) || 1));
        state.nad.fronty = n.fronty !== false;
      }
      state.set = p.set.map(sanitizeLine).filter(Boolean);
      state.setNote = String(p.setNote || "");
      state.setZrodlo = String(p.zrodlo || "");
      const ta = notesEl();
      if (ta) { ta.value = state.setNote; const d = $("[data-knf-set-notes]"); if (d && state.setNote) d.open = true; }
      const ad = p.adder || {};
      if (ad.sys) { const sb = $(`.knf__syschip[data-sys="${cssEscape(ad.sys)}"]`); if (sb && !sb.classList.contains("is-active")) sb.click(); }
      if (ad.typ) { const tb = $(`.knf__typchip[data-typ="${cssEscape(ad.typ)}"]`); if (tb && !tb.classList.contains("is-active")) tb.click(); }
    } finally { recalcMute--; }
    lastSetCents = null;
    recalcSet("restore");
    const t = setTotals();
    if (!t.rows) return false;
    let date = "";
    try { date = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(Number(p.ts))); } catch (_) { date = ""; }
    const txt = sx("T_RESTORED", { date, rows: t.rows, qty: t.qty });
    const note = $("[data-knf-set-restore]"), nt = $("[data-knf-set-restore-text]");
    if (note && nt) { nt.textContent = txt; note.hidden = false; }
    setCollapsed(true);
    // M10 + S15: nota tez pod linia wspolnych i nad lista (przegladarka przywraca przewiniecie, nota na gorze bywa poza ekranem)
    setMsg(txt);
    trackKnf("knf_set_restore", { rows: t.rows, set_qty: t.qty, reason: "restore" });
    return true;
  }
  // Z5: deep link / stary zapis z wymiarem niczego nie dodaje - podswietla kafelek (albo otwiera „Inny wymiar” z wartosciami)
  function highlightTile(szer, wys, typ, noScroll) {
    if (!SET_MODE || !szer || !wys) return false;
    const mark = el => {
      if (!el) return false;
      el.classList.add("is-target");
      setTimeout(() => el.classList.remove("is-target"), 1200);
      if (!noScroll) knfScrollTo(el, 140);
      return true;
    };
    const sys = $("[data-knf-system]")?.value || "";
    if (CORNER_CARDS_ON && sys === "METOD" && typ === "Drzwi narożne") {
      const cc = $(`.knf__typchip[data-typ="${cssEscape(CORNER_TYP)}"]`);
      if (cc && !cc.classList.contains("is-active")) cc.click();
      return mark($(`[data-knf-kit="${Number(szer) === 254 ? "B" : "A"}"]`));
    }
    const btn = $(`[data-knf-presets] .knf__tile .knf__preset[data-szer-mm="${Number(szer)}"][data-wys-mm="${Number(wys)}"]`);
    if (btn) return mark(btn.closest(".knf__tile"));
    if (!$("[data-knf-typ]")?.value || $("[data-knf-typ]")?.value === CORNER_TYP || $("[data-knf-typ]")?.value === NAD_TYPE) return false;
    openCustom(true);
    const a = $("[data-knf-szer]"), b = $("[data-knf-wys]");
    if (a && b) { a.value = szer; b.value = wys; a.dispatchEvent(new Event("input")); b.dispatchEvent(new Event("input")); }
    return mark($("[data-knf-dims-manual]"));
  }
  // D13: system z adresu, z ktorego klient przyszedl (kolekcja / artykul METOD, FAKTUM, PAX, PLATSA, BESTÅ)
  function sysFromReferrer() {
    try {
      const r = document.referrer ? new URL(document.referrer) : null;
      if (!r || r.host !== window.location.host) return "";
      const p = decodeURIComponent(r.pathname).toLowerCase();
      const hit = [["metod", "METOD"], ["faktum", "FAKTUM"], ["pax", "PAX"], ["platsa", "PLATSA"], ["bestå", "BESTÅ"], ["besta", "BESTÅ"]].find(([k]) => p.includes(k));
      return hit && (cfg.rules.common.systems_ikea || []).includes(hit[1]) ? hit[1] : "";
    } catch (_) { return ""; }
  }
  // Statyczne teksty bloku listy (Liquid ma stan przed startem JS) + tryb awaryjny
  function initSetUi() {
    if (!SET_MODE) {
      // SET_MODE = false: zostaje tylko pole uwag (stara sciezka czyta je z FormData)
      const box = $("[data-knf-set]");
      if (box) {
        $$(".knf__set-head, [data-knf-set-restore], [data-knf-set-msg], [data-knf-set-list], [data-knf-set-empty], [data-knf-set-more], [data-knf-forget], .knf__set-total", box).forEach(el => { el.hidden = true; });
        const d = $("[data-knf-set-notes]");
        if (d) d.open = true;
      }
      return;
    }
    root.classList.add("knf--set");
    const h = $("#knf-set-h");
    if (h) h.textContent = sx("T_SET");
    const ns = $("[data-knf-set-notes] > summary");
    if (ns) ns.textContent = sx("T_SET_NOTES");
    const rs = $("[data-knf-set-restore-show]"), rc = $("[data-knf-set-restore-clear]"), cl = $("[data-knf-set-clear]");
    if (rs) rs.textContent = sx("T_SHOW");
    if (rc) rc.textContent = sx("T_SET_CLEAR");
    if (cl) cl.textContent = sx("T_SET_CLEAR");
    bindSetList();
  }

  // D13: link do probki ze stylem i kolorem zestawu (atrybut = bazowy URL bez query)
  function syncSampleLink() {
    const st = state.styl || "", ko = state.kolor || "";
    $$("a[data-knf-sample-link]").forEach(a => {
      const base = String(a.dataset.knfSampleLink || a.getAttribute("href") || "").split("?")[0];
      if (!base) return;
      const href = st ? `${base}?style=${encodeURIComponent(st)}${ko ? `&kolor=${encodeURIComponent(ko)}` : ""}` : base;
      if (a.getAttribute("href") !== href) a.setAttribute("href", href);
    });
  }

  // -- BOOT
  renderStyles();
  renderSystemTyp();
  renderThickness();
  bindDims();
  bindQty();
  bindSampleCta();
  bindSubmit();
  bindEnterGuard($("[data-knf-form]"));
  bindAddedPanels($("[data-knf-form]"));
  bindStickyCta();
  try { bindStepTracking(); trackKnf("knf_view", { step: "view" }); } catch (_) { /* telemetria */ }
  getCartEventCtor(); // rozgrzewka importu @shopify/events, żeby submit nie czekał na moduł

  // Deep linking - pre-fill state z URL params (np. /products/front-ikea?style=Pasmo&kolor=Beż%20Kamienny&system=FAKTUM)
  // Pozwala Konradowi udostępniać linki "kup ten styl" w mediach społecznościowych, blogu, mailu
  function applyUrlParams() {
    const p = new URLSearchParams(window.location.search);
    // 10.2026: wyzwalacz obejmuje tez aliasy (styl, color, type, width, height) - wczesniej obslugiwane, ale nieuruchamiane
    if (![...p.keys()].some(k => /^(style|styl|kolor|color|system|typ|type|szer|width|wys|height)$/i.test(k))) return false;
    const styl = p.get("style") || p.get("styl");
    const kolor = p.get("kolor") || p.get("color");
    const system = p.get("system");
    const typ = p.get("typ") || p.get("type");
    const szer = p.get("szer") || p.get("width");
    const wys = p.get("wys") || p.get("height");
    if (styl) {
      const btn = $(`.knf__style[data-style="${cssEscape(styl)}"]`);
      if (btn) btn.click();
    }
    if (kolor) {
      setTimeout(() => {
        const cBtn = $(`.knf__color[data-color="${cssEscape(kolor)}"]`);
        if (cBtn) cBtn.click();
      }, 200);
    }
    if (system) {
      setTimeout(() => {
        const sysBtn = $(`.knf__syschip[data-sys="${cssEscape(system)}"]`);
        if (sysBtn) sysBtn.click();
      }, 350);
    }
    if (typ) {
      setTimeout(() => {
        // zestaw: METOD „Drzwi narożne” = karta „Narożnik” (TEMPO 5.6); ?system=PAX&typ=Nadstawka otwiera karte nadstawki
        const tv = CORNER_CARDS_ON && SET_MODE && typ === "Drzwi narożne" && $("[data-knf-system]")?.value === "METOD" ? CORNER_TYP : typ;
        const typBtn = $(`.knf__typchip[data-typ="${cssEscape(tv)}"]`);
        if (typBtn && !typBtn.classList.contains("is-active")) typBtn.click();
      }, 500);
    }
    if (szer && wys) {
      setTimeout(() => {
        // Zestaw (Z5): podswietlenie kafelka zamiast dodania
        if (SET_MODE) { highlightTile(parseInt(szer, 10), parseInt(wys, 10), typ || ""); return; }
        // Try preset first
        const preset = $(`[data-knf-presets] button[data-szer-mm="${szer}"][data-wys-mm="${wys}"]`);
        if (preset) preset.click();
        else {
          // Manual entry - wer3: jak klik w „Inny wymiar” (od tego zalezy wymog „Przedłużenie” przy elementach z nawiertami)
          const customBtn = $("[data-knf-presets] .knf__preset--custom");
          if (customBtn) customBtn.click();
          $("[data-knf-szer]").value = szer;
          $("[data-knf-wys]").value = wys;
          $("[data-knf-szer]").dispatchEvent(new Event("input"));
          $("[data-knf-wys]").dispatchEvent(new Event("input"));
        }
      }, 700);
    }
    return true;
  }

  // Diagnostyka: czy metafield conditional_rules zgadza się z kluczami dopłat w calcM2
  assertCondKeysMatchRules();

  // 10.2026 zestaw (TEMPO 5.7): najpierw lista z knf:set (zawsze, takze z linkiem - styl z linku wygrywa i przelicza liste
  // z komunikatem), potem link, potem dawny knf:state (tylko wspolne + system + typ), na koncu domyslne.
  // 10.2026 automat wycen: zestaw z linku (?zestaw=z1.<base64url(deflate-raw(JSON zapisu knf:set))>) - zapis jak wlasny
  // i przeladowanie bez parametru, dalej zwykle restoreSet(). Ten sam link drugi raz nie nadpisuje zmian klienta (zrodlo).
  if (SET_MODE && !SAMPLE_ONLY && importSetFromUrl()) return;
  initSetUi();
  // S6: uszkodzony zapis zestawu nie moze wysypac konfiguratora - kasujemy go i startujemy jak bez zapisu
  let fromSet = false;
  try { fromSet = SET_MODE && !SAMPLE_ONLY && restoreSet(); }
  catch (e) { console.warn("[knf] uszkodzony zapis zestawu", e); state.set = []; try { localStorage.removeItem(SET_KEY); } catch (_) { /* brak dostepu */ } }
  // Restore z localStorage PRZED tryUpdate, silent (bez notice - Konrad usunął)
  const fromUrl = applyUrlParams();
  const restored = !fromUrl && !fromSet && restoreState();
  if (!restored && !fromUrl && !fromSet) {
    // Brak zapisanego stanu → happy-path defaults zgodne z Globo:
    // Gładki + Biel Klasyczna RAL 9016 + FAKTUM (decyzja Konrada 2026-09-09: Gładki = bestseller,
    // najtańszy styl i najczęstszy zamiennik FAKTUM). Klient widzi pełny preset, nie pusty layout.
    const defaultStyleBtn = $('.knf__style[data-style="Gładki"]');
    if (defaultStyleBtn) defaultStyleBtn.click();

    // Po renderColors: kliknij domyślny kolor (pierwszy z palety = Biel Klasyczna RAL 9016)
    const defaultColorBtn = $('.knf__color[data-color="Biel Klasyczna RAL 9016"]') || $('.knf__color:not(.knf__color--custom)');
    if (defaultColorBtn) defaultColorBtn.click();

    // Domyślny system FAKTUM (najpopularniejszy w Globo) - klik na swatch wywoła change event
    // 10.2026 (D13): w zestawie system z adresu, z ktorego klient przyszedl (kolekcja METOD -> METOD), inaczej FAKTUM
    // UX v2 pkt 1 (03.10.2026): w zestawie BEZ domyslnego systemu - tylko z adresu kolekcji; strona probki / stara sciezka jak dotad
    const defSys = SET_MODE ? sysFromReferrer() : "FAKTUM";
    const defaultSysBtn = defSys ? $(`.knf__syschip[data-sys="${cssEscape(defSys)}"]`) : null;
    if (defaultSysBtn) defaultSysBtn.click();

    tryUpdate();
  }
  // 24.09: zapis albo link ze stylem, ktorego juz nie ma (np. ukryta Witryna) - Gładki zamiast pustego wyboru
  if (!state.styl) { const d = $('.knf__style[data-style="Gładki"]'); if (d) { d.click(); tryUpdate(); } }
  if (SAMPLE_ONLY) enterSampleMode(true);
  if (SET_MODE) {
    renderSet();
    tryUpdate();
    // knfMini (knf-rysunek.js) moze pojawic sie po pierwszym renderSet - dorysuj miniatury
    if (typeof window.knfMini !== "function") setTimeout(() => { if (state.set.length || state.styl) renderSet(); }, 800);
  }
})();

